import React, { useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import supabase from '../config/supabase';
import ParsWizardModal from './ParsWizardModal';
import { useOfflineCountSync } from './useOfflineCountSync';
import CustomModal from './CustomModal';
import CountWorkflowModal from './CountWorkflowModal';
import { queryCountSessionsActive } from '../utils/directFetch';
import { freezeSnapshotsForCompletedSession, normalizeSnapshotPrices, summarizeCountValueByCategory } from '../utils/countItemReporting';
import { fetchTemplateMeta } from '../utils/sessionLifecycle';
import { recordPriceHistory } from '../utils/priceHistory';
import { computeDayShiftMs, shiftTimestamp } from '../utils/backdateShift';
import {
  buildVendorContactInfo,
  getVendorSendReadiness,
  loadVendorDirectory,
  normalizeVendorLabel,
  resolveVendorForName,
} from '../utils/vendorContacts';
import VendorAutocompleteInput from './VendorAutocompleteInput';
import { assignVendorToItem } from '../utils/vendorResolution';
import { getCurrentUser as getStoredUser, formatUserCompact, fetchSuperAdminOwnedStoreIds, creatorOverrideLabel, resolveCreatorLabel } from '../utils/userDisplay';
import { useVendorDirectory } from '../utils/useVendorDirectory';
import { applySharedOrderMathBulk, computeSharedOrderData, isFlexibleSource, flexiblePackageConstraint } from '../utils/sharedOrderMath';
import { fetchComparisonItems, fetchFlexibleSourceItemIds, fetchOptionsForItems, updateOrderingRules } from '../services/purchaseOptionService';
import {
  createOptionForItem,
  findInventoryItemAfterDuplicateMerge,
} from '../services/itemCrossReferenceService';
import { buildNeedsFromCountItems } from '../services/purchaseAllocationService';
import {
  createOrOpenPlanForCount,
  fetchShoppingCandidateGraphForStore,
  fetchWorkingSetGroupsForStore,
} from '../services/purchasingPlanService';
import { resolveFavoredMembers } from '../services/favoredItemService';
import { fetchEquivalentGroupComparisons } from '../services/itemRelationshipService';
import { planCountGroupFold, restrictGroupsToItems } from '../utils/countGroupFold';
import { applyInferredScheduleOnComplete } from '../services/inferredScheduleService';
import {
  persistLastUsedCountFormats,
  fetchLastUsedCountFormats,
  lastUsedKeyFor,
} from '../services/countFormatMemoryService';
import ParTransitionPrompt from './ParTransitionPrompt';
import {
  detectPendingParTransitions,
  getParRecommendation,
  applyParDecision,
  applyExactEquivalentRefresh,
  recordParTransition,
  fetchParTransitions,
} from '../services/parTransitionService';
import { detectParTransition, parTransitionKey } from '../utils/parTransition';
import { hasVendorChoices } from '../utils/purchasePlanning';
import { groupMembersFor, expandOptionsAcrossGroup } from '../utils/relatedItemGroups';
import { shoppingCandidateItemIdsForItem } from '../../../supabase/functions/_shared/shoppingCandidates.mjs';
import MultiVendorBadge from './MultiVendorBadge';
import { canonicalLinkage, purchaseOptionsSummary, costCellForSummary, comparableUnitCost, formatUnitPrice, resolveSelectedOption } from '../utils/itemCostSummary';
import CountFormatControl from './CountFormatControl';
import {
  fetchSelectedOptionsForCountItems,
  setCountFormatForLines,
  fetchCountFormatsForCountItems,
} from '../services/countItemSelectionService';
import PurchaseOptionsEditor from './PurchaseOptionsEditor';
import PackageCountSheet from './PackageCountSheet';
import BottleCountOverlay, { BottleGlyph } from './BottleCountOverlay';
import {
  bottleEligibilityForItem,
  tenthsFromCountValue,
  applyBottleTenths,
} from '../utils/bottleCounting.js';
import VendorUnitFlow from './VendorUnitFlow';
import {
  resolveCountPresentation,
  fallbackCountLadder,
  packageQuantityFromNormalized,
  planPackageSwitch,
  describeMixedCountAction,
  expressInCountFormat,
  SWITCH_ACTIONS,
} from '../utils/countPresentation';
import { resolveGroupCountAnchor } from '../utils/groupCountAnchor';
import { resolveEntry, formatQuantity, resolveRowPar, PAR_STATUS, buildParNormalizationPatch, isMissingParNormalizationColumnError, PAR_NORMALIZATION_SELECT, parNormalizationFrom } from '../utils/countUnitModel';
import { fetchCountLadders, normalizeEntries } from '../services/purchaseUnitsService';
import {
  fetchEntriesForCountItems,
  saveEntriesForCountItem,
  clearEntriesForCountItem,
} from '../services/countEntryService';
import {
  fetchAllCountItems,
  findEmptyTemplateSessions,
  isSessionEmpty,
  dedupeCountItemRows,
  buildMissingCountItemsFromTemplate,
  SeedRegistry,
} from '../services/countItemIntegrity';
import { propagateGlobalCountFieldsToParents, formatPropagationToast } from '../services/sessionFieldPropagation';
import { persistTapeReorder, persistTapeDelete } from '../services/tapeEditPropagation';
import { createDraftPurchaseOrdersFromCount } from '../services/purchaseOrderService';
import { recordOrderingHubEvent } from '../services/orderingHubService';
import { cycleToFriendlyLabel, cycleToScheduleDisplay, getNextOccurrence, inventoryPeriodConfigToCycle, nextScheduleDow } from './cycleHelpers.mjs';
import { locationCode } from '../utils/locationCode';
import { SessionTimelineStack, earlierRunsMonthLabel } from './SessionTimelineStack';
import { getTemplateColor, EOP_TITLE_COLOR, ORDER_TITLE_COLOR } from '../utils/templateColor';
import { getCategoryIcon, ORDER_TEMPLATE_ICON, EOP_TEMPLATE_ICON } from '../utils/categoryIcon';
import BarcodeScanAndAddModal from './BarcodeScanAndAddModal';
import Reports from './Reports';
import CountCardAvailableReports, { isEOPSession } from './CountCardAvailableReports';
import HelpTooltip, { HelpTipButton, HelpInfoButton } from './HelpTooltip';
import ShimmerLoader from './ShimmerLoader';
import {
  filterCountItemsToLiveInventory,
  shouldHideArchivedCountItems,
} from '../utils/countItemLiveness';
import {
  COUNT_COMPLETION_NEXT_ACTION,
  COUNT_COMPLETION_STEP,
  completionOrderingDestination,
  countItemsEligibleForCompletionOrdering,
  describeDraftCreationOutcome,
  summarizeCountCompletion,
} from '../utils/countCompletionWorkflow';
import CountParSetupCard, { storedParFromFocusedSetup } from './CountParSetupCard';

// Package metadata for flexible-source items, used only to label the vendor on
// the count UI. It never changes NEED, and it no longer carries a minimum to
// display: package choice, MOQ and BUY rounding happen in Savings Review after
// compatibility with the count unit is verified.
const buildFlexibleConstraints = (rows, flexibleIds, optionsByItemId, selectionsByCountItemId) => {
  const constraints = {};
  // The vendor choice propagates to every sibling line, but legacy rows can
  // lag — any row's recorded choice speaks for the whole item.
  const selectionByInvId = {};
  (rows || []).forEach((row) => {
    const invId = row?.inventory_item_id ?? row?.item_snapshot?.id ?? row?.id;
    if (invId == null) return;
    const selected = selectionsByCountItemId?.[row.id] ?? null;
    if (selected != null && selectionByInvId[String(invId)] == null) {
      selectionByInvId[String(invId)] = selected;
    }
  });
  (rows || []).forEach((row) => {
    const invId = row?.inventory_item_id ?? row?.item_snapshot?.id ?? row?.id;
    if (invId == null) return;
    const key = String(invId);
    if (key in constraints) return;
    if (!isFlexibleSource(row, flexibleIds)) return;
    const options = optionsByItemId?.[invId] || optionsByItemId?.[key] || [];
    if (!options.length) return;
    const { option } = resolveSelectedOption(options, selectionByInvId[key] ?? null, {});
    constraints[key] = flexiblePackageConstraint(option);
  });
  return constraints;
};

// Columns the overview reads off each cached count_items row. Shared by the
// card-metadata prefetch (which selects exactly these plus identity columns)
// and the countItemFieldsChanged patch, so a per-item write from the template
// editor updates the same fields the card renders from.
// min_order_qty / min_order_unit are deliberately absent: no card, tile or tape
// on a counting surface renders a minimum order quantity any more.
const CACHED_COUNT_ITEM_FIELDS = ['par_level', 'par_unit', 'par_defaulted'];
const CACHED_COUNT_ITEM_SELECT =
  `id, inventory_item_id, item_snapshot, location, count_position, ${CACHED_COUNT_ITEM_FIELDS.join(', ')}`;

// A template row's PAR, read the way it was stored. The legacy pair alone
// cannot express "1 Vendor A case = 12 bottles": seeding a session from it
// hands the new count a bare wrapper word with no package, which every
// resolver refuses — so the item drops out of ordering, or reads as 1 where
// the kitchen meant 12. The quintet is selected alongside and degrades to the
// legacy list on a store behind on migration 20260822120000, because a
// missing column must never stop a count from being created.
const TEMPLATE_PAR_LEGACY_SELECT = 'inventory_item_id, par_level, par_unit, par_defaulted, location';
const TEMPLATE_PAR_SELECT = `${TEMPLATE_PAR_LEGACY_SELECT}, ${PAR_NORMALIZATION_SELECT}`;

// Zero is a real, explicit stocking decision (keep none on hand), not the
// absence of a PAR. Only null/blank/invalid/negative values are missing.
const hasStoredParValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0;
};

const fetchTemplateItemsWithPar = async (templateId) => {
  const run = (columns) => supabase
    .from('count_template_items')
    .select(columns)
    .eq('template_id', templateId);
  const full = await run(TEMPLATE_PAR_SELECT);
  if (full.error && isMissingParNormalizationColumnError(full.error)) {
    return run(TEMPLATE_PAR_LEGACY_SELECT);
  }
  return full;
};

// Instructional tooltips on this page are metadata-driven: each trigger points
// at a helpTopics registry entry via helpTopicId (visible copy + authored AI
// question) and renders through the shared HelpTooltip, which portals the
// bubble above the sidebar/CI tray and can open Counterintelligence for a
// deeper explanation. See docs/help-tooltips.md.

// ── Overview palette — one meaning per hue ─────────────────────────────────
// The redesigned Templates & Counts overview uses a disciplined color system:
// terracotta is the ONLY action color (labels carry the state, not the hue),
// green means completed, amber means needs attention. Everything else is
// warm-neutral chrome. Identity colors (EOP orange / Orders cobalt) survive
// only as the card's left accent + section headers — never as status.
const OV_INK = '#3E2723';           // primary text
const OV_INK_SOFT = '#8B7B73';      // secondary text
const OV_ACTION = '#C0603F';        // terracotta — every primary button
const OV_GOOD_DOT = '#4CAF50';      // status dot: all good / ready
const OV_ATTN_DOT = '#F9A825';      // status dot: needs attention / open
const OV_LINE = '#E8DDD0';          // hairlines
const OV_CARD_BG = '#FFFDF9';       // card surface

const ovPrimaryBtnStyle = ({ fullWidth = true } = {}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: fullWidth ? '100%' : 'auto',
  minHeight: '48px',
  padding: '10px 18px',
  border: 'none',
  borderRadius: '12px',
  backgroundColor: OV_ACTION,
  color: '#FFF6F1',
  fontFamily: 'inherit',
  fontSize: '0.98rem',
  fontWeight: 700,
  letterSpacing: '0.01em',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
});

const ovQuietBtnStyle = ({ fullWidth = true } = {}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  width: fullWidth ? '100%' : 'auto',
  minHeight: '48px',
  padding: '10px 18px',
  border: `1.5px solid ${OV_LINE}`,
  borderRadius: '12px',
  backgroundColor: OV_CARD_BG,
  color: OV_INK,
  fontFamily: 'inherit',
  fontSize: '0.95rem',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
});

// "Sub-recipe" marker for count/preview rows — soft indigo, deliberately a
// different hue from the neutral gray Multi-vendor pill so both can coexist.
const SUB_RECIPE_PILL_STYLE = {
  display: 'inline-block', padding: '2px 8px', borderRadius: 999,
  background: '#EEF0FA', color: '#4A5AA8', border: '1px solid #C9D2EC',
  fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.02em',
  whiteSpace: 'nowrap', marginLeft: '4px', flexShrink: 0,
};

/**
 * The marker for an item the kitchen MAKES rather than buys.
 *
 * It says PREPARED IN HOUSE, not "Internal" and not a vendor name, because
 * the counter's question at the shelf is "where does this come from" and the
 * honest answer is the prep kitchen. Same indigo family as the sub-recipe
 * pill — they are the same idea at two stages — but its own row so an item
 * that is both a prepared item and, unusually, also purchasable still reads
 * correctly.
 */
const PREPARED_PILL_STYLE = {
  ...SUB_RECIPE_PILL_STYLE,
  background: '#E8F1EC', color: '#2F6B4F', border: '1px solid #BFD9CB',
};

const isPreparedSnapshot = (snap, item) => (
  (snap?.item_type ?? item?.item_type ?? item?.item_snapshot?.item_type) === 'prepared'
);

/**
 * Prepared items are labelled, and their source named — "Made from House
 * Pizza Sauce sub-recipe" — so a counter can tell at a glance why this row
 * has no vendor rather than assuming its vendor is missing.
 */
const renderPreparedBadge = (item, snap) => (
  isPreparedSnapshot(snap, item)
    ? <span style={PREPARED_PILL_STYLE} title="Produced in house from a sub-recipe">PREPARED IN HOUSE</span>
    : null
);

const TEMPLATE_ONBOARDING_ACK_STORAGE_KEY = 'mm_tpl_onboarding_acked';
const TEMPLATE_ONBOARDING_USER_ACK_STORAGE_PREFIX = 'mm_tpl_onboarding_acked:';

const getTemplateOnboardingAckStorageKey = (userId) => (
  userId ? `${TEMPLATE_ONBOARDING_USER_ACK_STORAGE_PREFIX}${userId}` : TEMPLATE_ONBOARDING_ACK_STORAGE_KEY
);

const readTemplateOnboardingAck = (userId) => {
  try {
    const key = getTemplateOnboardingAckStorageKey(userId);
    return !!JSON.parse(localStorage.getItem(key) || 'false');
  } catch (_) {
    return false;
  }
};

const writeTemplateOnboardingAck = (userId) => {
  try {
    localStorage.setItem(getTemplateOnboardingAckStorageKey(userId), 'true');
  } catch (_) {}
};

const getCurrentStoredUserId = () => {
  try {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    return currentUser?.id || null;
  } catch (_) {
    return null;
  }
};

function PortalCardMenu({ menuKey, openMenuId, setOpenMenuId, buttonStyle, buttonLabel = '⋯', items, minWidth = '160px' }) {
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, bottom: 'auto', right: 0 });
  const isOpen = openMenuId === menuKey;

  useEffect(() => {
    if (!isOpen) return;
    const updatePos = () => {
      if (btnRef.current) {
        const r = btnRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - r.bottom;
        const menuHeight = 220;
        const openAbove = spaceBelow < menuHeight + 8 && r.top > menuHeight + 8;
        setPos(openAbove
          ? { bottom: window.innerHeight - r.top + 4, top: 'auto', right: window.innerWidth - r.right }
          : { top: r.bottom + 4, bottom: 'auto', right: window.innerWidth - r.right }
        );
      }
    };
    updatePos();
    const close = () => setOpenMenuId(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpenMenuId(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  return (
    <>
      <button
        ref={btnRef}
        data-card-menu
        style={buttonStyle}
        onClick={(e) => { e.stopPropagation(); setOpenMenuId(isOpen ? null : menuKey); }}
      >
        {buttonLabel}
      </button>
      {isOpen && createPortal(
        <div
          data-card-menu
          style={{
            position: 'fixed',
            top: pos.top,
            bottom: pos.bottom,
            right: pos.right,
            backgroundColor: '#fff',
            border: '1px solid #E8DDD0',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 1200,
            minWidth,
            padding: '4px 0',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {items.map((item, i) => (
            <button
              key={item.label || i}
              data-card-menu
              style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.8rem', color: '#5A4A42', cursor: 'pointer', transition: 'background-color 0.1s' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FAF6F2'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              onClick={item.action}
            >
              <span style={{ width: '18px', textAlign: 'center' }}>{item.icon}</span>{item.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

// Random test quantities for fabricated counts. Each RUN first draws a global
// scale, then rolls 1–50 per item and applies the scale. Without the run
// scale, every test count's total value converges to the same number
// (~25.5 × the summed item prices — the per-item randomness averages out
// across a full count), which makes period-over-period comparisons useless.
// The 0.4×–1.6× spread keeps individual quantities realistic while totals
// differ meaningfully between runs.
export function makeRandomCountRoller() {
  const runScale = 0.4 + Math.random() * 1.2;
  return () => String(Math.max(1, Math.round((Math.floor(Math.random() * 50) + 1) * runScale)));
}

// Super-admin-only dialog behind the "Backdated copy…" card-menu action.
// Duplicates a count session with every timestamp shifted to the chosen day so
// realistic historical data can be fabricated for end-of-period testing.
// Exported for standalone rendering in dev harnesses/tests.
export function BackdateCopyModal({ modal, onChangeDate, onToggleRandomize, onCancel, onConfirm }) {
  if (!modal) return null;
  return createPortal(
    <div
      onClick={() => { if (!modal.busy) onCancel(); }}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(58,46,40,0.45)', zIndex: 10500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#FFFFFF', borderRadius: '12px', padding: '20px', width: '100%', maxWidth: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
        <h6 style={{ color: '#5A4A42', fontWeight: 700, marginBottom: '6px' }}>
          🧪 Backdated copy <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#8B7B73' }}>(super admin)</span>
        </h6>
        <p style={{ fontSize: '0.82rem', color: '#8B7B73', marginBottom: '12px', lineHeight: 1.5 }}>
          Duplicates “{modal.session.name}” — items, statuses and counted values included —
          with every date shifted so the copy appears to have happened on the day you pick.
          Use it to fabricate historical data for end-of-period reports and notifications.
        </p>
        <label htmlFor="backdate-copy-date" style={{ fontSize: '0.78rem', fontWeight: 600, color: '#5A4A42', display: 'block', marginBottom: '4px' }}>
          Pretend this count happened on
        </label>
        <input
          id="backdate-copy-date"
          type="date"
          className="form-control"
          value={modal.date}
          onChange={(e) => onChangeDate(e.target.value)}
          disabled={modal.busy}
          style={{ borderColor: '#D4C4B0', color: '#5A4A42', marginBottom: '12px' }}
        />
        <label htmlFor="backdate-copy-randomize" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.8rem', color: '#5A4A42', marginBottom: '12px', cursor: modal.busy ? 'default' : 'pointer' }}>
          <input
            id="backdate-copy-randomize"
            type="checkbox"
            checked={modal.randomize !== false}
            onChange={(e) => onToggleRandomize(e.target.checked)}
            disabled={modal.busy}
            style={{ marginTop: '2px' }}
          />
          <span>
            Give items new random counts
            <span style={{ display: 'block', fontSize: '0.72rem', color: '#8B7B73' }}>
              Re-rolls every counted quantity so this copy's total differs from the original —
              uncheck for an exact clone of the counted values.
            </span>
          </span>
        </label>
        {modal.error && (
          <div className="alert alert-danger py-2" style={{ fontSize: '0.8rem' }} role="alert">
            {modal.error}
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={onCancel}
            disabled={modal.busy}
            style={{ border: '1px solid #D4C4B0', color: '#5A4A42', backgroundColor: 'transparent' }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={onConfirm}
            disabled={modal.busy || !modal.date}
            style={{ backgroundColor: '#5A4A42', color: '#FFF8EF', fontWeight: 600, border: 'none' }}
          >
            {modal.busy ? 'Copying…' : 'Create backdated copy'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function TemplateActionClusterWithConnector({
  isMobileOverview,
  isTemplateParent,
  showConnector,
  templateChip,
  buttonElement,
  menuElement,
  secondaryElement,
  clusterRow = false,
}) {
  const clusterRef = useRef(null);
  const btnRef = useRef(null);
  // connector holds { startX, startY } where startX is the horizontal offset
  // from the spine to the button center, and startY is the vertical distance
  // from the button's bottom edge to the header row's bottom edge.
  // The SVG is positioned at (top: 100% - startY) so the line visually
  // originates from just below the action button.
  const [connector, setConnector] = useState(null);

  useLayoutEffect(() => {
    if (!showConnector) { setConnector(null); return undefined; }
    function measure() {
      const btn = btnRef.current;
      const cluster = clusterRef.current;
      if (!btn || !cluster) return;
      // SVG is absolutely positioned against the nearest positioned ancestor
      // (the renderHeader row); express coords in that ancestor's frame.
      let row = cluster.parentElement;
      while (row && row !== document.body) {
        const pos = window.getComputedStyle(row).position;
        if (pos === 'relative' || pos === 'absolute' || pos === 'fixed') break;
        row = row.parentElement;
      }
      if (!row || row === document.body) return;
      const rowRect = row.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      const spineRowX = rowRect.width * 0.05 + (isMobileOverview ? 7 : 11);
      const btnCenterRowX = (btnRect.left + btnRect.width / 2) - rowRect.left;
      const nextX = Math.round(btnCenterRowX - spineRowX);
      // How far the button's bottom edge is above the row's bottom edge.
      // This lets us start the SVG at the button bottom rather than the row bottom.
      const nextY = Math.max(0, Math.round(rowRect.bottom - btnRect.bottom));
      setConnector((prev) => {
        if (prev && prev.startX === nextX && prev.startY === nextY) return prev;
        return { startX: nextX, startY: nextY };
      });
    }
    measure();
    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    window.addEventListener('resize', schedule);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(schedule);
      if (clusterRef.current) ro.observe(clusterRef.current);
      if (btnRef.current) ro.observe(btnRef.current);
    }
    return () => {
      window.removeEventListener('resize', schedule);
      if (ro) ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [showConnector, isMobileOverview, isTemplateParent]);

  const buttonWithRef = React.isValidElement(buttonElement)
    ? React.cloneElement(buttonElement, { ref: btnRef })
    : buttonElement;

  return (
    <>
      <div
        ref={clusterRef}
        style={{
          display: 'flex',
          gap: '4px',
          ...(clusterRow
            ? { alignItems: 'center' }
            : isMobileOverview && isTemplateParent
              ? { flexDirection: 'column', alignItems: 'center' }
              : { alignItems: 'center' }),
          flexShrink: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {templateChip}
        {buttonWithRef}
        {menuElement}
        {secondaryElement}
      </div>
      {showConnector && connector != null && (() => {
        const { startX, startY } = connector;
        // Base heights of the path below the row bottom (unchanged from before).
        const baseH = isMobileOverview ? 54 : 70;
        // Total SVG height extends upward by startY to reach the button bottom.
        const totalH = baseH + startY;
        // The SVG's top is at the button's bottom edge; y=0 is the button bottom,
        // y=startY is the row bottom (original y=0), y=totalH is the spine start.
        const s = startY;
        const points = isMobileOverview
          ? `${startX},0 ${startX},${14 + s} ${startX - 20},${34 + s} 20,${34 + s} 0,${54 + s}`
          : `${startX},0 ${startX},${20 + s} ${startX - 25},${45 + s} 25,${45 + s} 0,${70 + s}`;
        return (
          <svg
            style={{
              position: 'absolute',
              top: `calc(100% - ${startY}px)`,
              left: isMobileOverview ? 'calc(5% + 7px)' : 'calc(5% + 11px)',
              overflow: 'visible',
              pointerEvents: 'none',
              zIndex: 1,
              display: 'block',
            }}
            width="1"
            height={totalH}
            fill="none"
          >
            <polyline
              points={points}
              stroke="#7BC67E"
              strokeWidth="1.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      })()}
    </>
  );
}

// FlashingBadge component for visual feedback
function FlashingBadge({ value, flash, backgroundColor, color, children }) {
  return (
    <span
      className={`badge ${flash ? "flash-once" : ""}`}
      style={{ backgroundColor, color }}
    >
      {children || value}
    </span>
  );
}

// TemplateUpdatedLabel — slow crossfade between the action label
// (e.g. "New Order" / "New Count") and "UPDATED" so a user can quickly tell
// the template has changed since the last run without being distracted.
// The button width stays constant via a hidden sizer set to the wider of the
// two strings.
function TemplateUpdatedLabel({ label, updatedText = 'UPDATED', holdMs = 2200, fadeMs = 1100 }) {
  const [showUpdated, setShowUpdated] = useState(false);
  useEffect(() => {
    const id = setInterval(() => setShowUpdated(prev => !prev), holdMs);
    return () => clearInterval(id);
  }, [holdMs]);
  const sizer = (label || '').length >= updatedText.length ? label : updatedText;
  const layer = {
    position: 'absolute', inset: 0, display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center',
    transition: `opacity ${fadeMs}ms ease-in-out`,
  };
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <span aria-hidden="true" style={{ visibility: 'hidden', whiteSpace: 'nowrap' }}>{sizer}</span>
      <span style={{ ...layer, opacity: showUpdated ? 0 : 1 }}>{label}</span>
      <span style={{ ...layer, opacity: showUpdated ? 1 : 0 }}>{updatedText}</span>
    </span>
  );
}

// CategoryIconFader — single icon slot for mobile template cards.
// Single-category: renders the one icon statically.
// Multi-category: true dual-layer cross-fade cycles through every icon in
//   order — the outgoing icon fades out while the incoming icon fades in
//   simultaneously. Long dwell (3 s) and gentle fade (700 ms) keep it subtle.
// A fixed 1.5em × 1.5em outer box prevents any horizontal layout jitter as
//   different-width glyphs swap in and out.
// Respects prefers-reduced-motion (shows first icon only, no animation).
function CategoryIconFader({ icons, sizeRem = '1.0rem' }) {
  // curIdx / nxtIdx track which of the two layers is "active".
  // phase: 'idle' | 'fading'
  const [curIdx, setCurIdx] = useState(0);
  const [nxtIdx, setNxtIdx] = useState(1);
  const [phase, setPhase] = useState('idle');
  const multi = icons && icons.length > 1;
  const prefersReduced = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (!multi || prefersReduced) return undefined;
    const holdMs = 3000;
    const fadeMs = 700;
    let timeout;
    const scheduleNext = (fromIdx) => {
      timeout = setTimeout(() => {
        const toIdx = (fromIdx + 1) % icons.length;
        setNxtIdx(toIdx);
        setPhase('fading');
        timeout = setTimeout(() => {
          setCurIdx(toIdx);
          setPhase('idle');
          scheduleNext(toIdx);
        }, fadeMs);
      }, holdMs);
    };
    scheduleNext(0);
    return () => clearTimeout(timeout);
  }, [multi, icons && icons.length, prefersReduced]);

  if (!icons || icons.length === 0) return null;

  if (!multi || prefersReduced) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, width: '1.5em', height: '1.5em', fontSize: sizeRem, lineHeight: 1,
      }}>
        {icons[0]}
      </span>
    );
  }

  const isFading = phase === 'fading';
  const layerBase = { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 700ms ease-in-out' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, position: 'relative', width: '1.5em', height: '1.5em', fontSize: sizeRem, lineHeight: 1,
    }}>
      <span style={{ ...layerBase, opacity: isFading ? 0 : 1 }}>{icons[curIdx]}</span>
      <span style={{ ...layerBase, opacity: isFading ? 1 : 0 }}>{icons[nxtIdx]}</span>
    </span>
  );
}

// OfflineSyncIndicator — shows in the unused space left of the item name
// during a mobile count session. Four states per spec:
//   synced   → green broadcast/wifi icon
//   pending  → yellow phone icon (Saved on this phone)
//   syncing  → neutral spinner
//   failed   → red warning icon
function OfflineSyncIndicator({ syncStatus, isOnline }) {
  let icon, color, label;
  if (syncStatus === 'syncing') {
    icon = 'fa-solid fa-rotate fa-spin';
    color = '#9E9E9E';
    label = 'Syncing';
  } else if (syncStatus === 'failed') {
    icon = 'fa-solid fa-triangle-exclamation';
    color = '#D32F2F';
    label = 'Sync problem';
  } else if (syncStatus === 'pending' || !isOnline) {
    icon = 'fa-solid fa-mobile-screen';
    color = '#F59E0B';
    label = 'Saved on this phone';
  } else {
    icon = 'fa-solid fa-wifi';
    color = '#43A047';
    label = 'Synced';
  }
  return (
    <span
      title={label}
      aria-label={label}
      style={{
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '0.8rem',
        color,
        lineHeight: 1,
        width: '1.4rem',
        textAlign: 'center',
        flexShrink: 0,
      }}
    >
      <i className={icon} />
    </span>
  );
}

function CountCloseProgressIndicator({ subStep, error, onRetry }) {
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'modal_backdrop_show' }));
      }
    } catch (_) {}
    return () => {
      try {
        if (typeof window !== 'undefined' && window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'modal_backdrop_hide' }));
        }
      } catch (_) {}
    };
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={
        error
          ? `Count save failed: ${error}`
          : `Saving count${subStep ? `. ${subStep}` : '…'}`
      }
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(10,15,25,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 12000,
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
        paddingLeft: '1rem', paddingRight: '1rem',
      }}
    >
      <div className="ccp-card">
        {error ? (
          <>
            <div className="ccp-icon ccp-icon-error">
              <i className="fa-solid fa-triangle-exclamation" />
            </div>
            <div className="ccp-primary">Save failed</div>
            <div className="ccp-sub ccp-sub-error">{error}</div>
            {onRetry && (
              <div className="ccp-actions">
                <button className="ccp-btn-retry" onClick={onRetry}>Retry</button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="ccp-icon ccp-icon-pulse">
              <i className="fa-solid fa-box-archive" />
            </div>
            <div className="ccp-primary">Saving count…</div>
            <div className="ccp-sub">{subStep || '\u00A0'}</div>
          </>
        )}
      </div>
      <style>{`
        .ccp-card {
          background: #fff; border-radius: 14px; padding: 2rem 2.5rem;
          text-align: center; min-width: 240px; max-width: 320px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.22); border: 1px solid #e8ddd0;
          display: flex; flex-direction: column; align-items: center; gap: 0.6rem;
        }
        .ccp-icon { font-size: 2.4rem; color: #7B5E45; margin-bottom: 0.4rem; }
        @media (prefers-reduced-motion: no-preference) {
          .ccp-icon-pulse { animation: ccp-pulse 1.4s ease-in-out infinite; }
          @keyframes ccp-pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.42; transform: scale(0.86); }
          }
        }
        .ccp-icon-error { color: #c62828 !important; }
        .ccp-primary { font-weight: 700; color: #3d2518; font-size: 1.05rem; }
        .ccp-sub { font-size: 0.85rem; color: #7a6e66; min-height: 1.2em; }
        .ccp-sub-error { color: #c62828; }
        .ccp-actions { display: flex; gap: 0.5rem; margin-top: 0.4rem; }
        .ccp-btn-retry {
          padding: 0.45rem 1.1rem; border-radius: 6px; font-weight: 700;
          background: #7B5E45; color: #fff; border: none; cursor: pointer;
          font-size: 0.88rem;
        }
        .ccp-btn-retry:hover { background: #5a4433; }
        @media (max-width: 480px) {
          .ccp-card { min-width: 78vw; padding: 1.5rem 1.25rem; }
        }
      `}</style>
    </div>
  );
}

// Post-completion Order Report review. Shown between the "Send orders to
// vendors?" prompt and the VendorOrderSendSheet so the user can preview
// what will be sent grouped by vendor, and choose between sending or
// hopping back into count-entry mode to fix values before sending.
// onReviewAllocation (optional): when provided, a secondary "Review purchase
// allocation" button opens the multi-vendor PurchaseAllocationReview modal.
const OrderReviewOverlay = ({ sessionName, vendorData, onClose, onEdit, onSend, onReviewAllocation }) => {
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'modal_backdrop_show' }));
      }
    } catch (_) {}
    return () => {
      try {
        if (typeof window !== 'undefined' && window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'modal_backdrop_hide' }));
        }
      } catch (_) {}
    };
  }, []);

  const fmtMoney = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '$0.00';
    return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  };
  const fmtQty = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '0';
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
  };
  const totalOrderValue = vendorData.reduce((sum, v) => sum + (Number(v?.totalOrderValue) || 0), 0);
  const totalOrderLines = vendorData.reduce((sum, v) => sum + (Array.isArray(v?.items) ? v.items.length : 0), 0);
  const isEmpty = vendorData.length === 0;

  return (
    <div className="orr-overlay" onClick={onClose}>
      <div className="orr-sheet" onClick={(event) => event.stopPropagation()}>
        <div className="orr-header">
          <div>
            <h5>🛒 Order Report</h5>
            <p>{sessionName}</p>
          </div>
          <button type="button" className="orr-close" onClick={onClose} aria-label="Close order review">&times;</button>
        </div>

        <div className="orr-summary">
          <div><strong>Vendors:</strong> {vendorData.length}</div>
          <div><strong>Order Lines:</strong> {totalOrderLines}</div>
          <div><strong>Total Order Value:</strong> {fmtMoney(totalOrderValue)}</div>
        </div>

        <div className="orr-body">
          {isEmpty ? (
            <div className="orr-empty">No vendor orders to review.</div>
          ) : (
            vendorData.map((vendor, vIdx) => {
              const contactLine = [
                vendor?.contact?.contactName,
                vendor?.contact?.contactEmail,
                vendor?.contact?.contactPhone,
              ].filter(Boolean).join(' · ');
              const items = Array.isArray(vendor?.items) ? vendor.items : [];
              return (
                <div className="orr-vendor" key={vendor?.vendorId || vendor?.vendorName || vIdx}>
                  <div className="orr-vendor-head">
                    <div>
                      <strong>🚚 {vendor?.vendorName || 'Unknown vendor'}</strong>
                      {contactLine ? <small>{contactLine}</small> : null}
                      {!vendor?.canSend && vendor?.sendReadinessReason ? (
                        <small className="orr-warn">{vendor.sendReadinessReason}</small>
                      ) : null}
                    </div>
                    <div className="orr-vendor-total">{fmtMoney(vendor?.totalOrderValue)}</div>
                  </div>
                  <div className="orr-items">
                    <div className="orr-item-row orr-item-head">
                      <span className="orr-col-desc">Item</span>
                      <span className="orr-col-num">On Hand</span>
                      <span className="orr-col-num">PAR</span>
                      <span className="orr-col-num">Order</span>
                      <span className="orr-col-num">Unit $</span>
                      <span className="orr-col-num">Total</span>
                    </div>
                    {items.map((item, iIdx) => (
                      <div className="orr-item-row" key={iIdx}>
                        <span className="orr-col-desc">
                          {item.description}
                          {item.brand ? <small> · {item.brand}</small> : null}
                          {item.location && item.location !== '-' ? <small> · {item.location}</small> : null}
                        </span>
                        <span className="orr-col-num">{fmtQty(item.quantity)}</span>
                        <span className="orr-col-num">{fmtQty(item.parLevel)}</span>
                        <span className="orr-col-num">{fmtQty(item.orderQuantity)}</span>
                        <span className="orr-col-num">{fmtMoney(item.unitPrice)}</span>
                        <span className="orr-col-num">{fmtMoney(item.orderValue)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="orr-actions">
          <button type="button" className="orr-btn orr-btn-secondary" onClick={onEdit}>
            Edit
          </button>
          {onReviewAllocation ? (
            <button type="button" className="orr-btn orr-btn-secondary" onClick={onReviewAllocation}>
              Review purchase allocation
            </button>
          ) : null}
          <button
            type="button"
            className="orr-btn orr-btn-primary"
            onClick={onSend}
            disabled={isEmpty}
          >
            Send to Vendors
          </button>
        </div>
      </div>

      <style>{`
        .orr-overlay {
          position: fixed; inset: 0; background: rgba(10,15,25,0.6);
          display: flex; align-items: center; justify-content: center;
          z-index: 2000;
          padding-top: calc(env(safe-area-inset-top, 0px) + 0.75rem);
          padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 0.75rem);
          padding-left: 0.75rem; padding-right: 0.75rem;
        }
        .orr-sheet {
          background: #fff; border: 2px solid #d4c4b0; border-radius: 10px;
          max-width: 980px; width: 100%;
          max-height: calc(92vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px));
          display: flex; flex-direction: column;
          box-shadow: 0 6px 24px rgba(0,0,0,0.18);
        }
        .orr-header {
          display: flex; justify-content: space-between; align-items: flex-start;
          padding: 0.9rem 1.1rem; border-bottom: 1px solid #e8ddd0;
        }
        .orr-header h5 { margin: 0; color: #3d2518; font-size: 1.15rem; font-weight: 600; }
        .orr-header p { margin: 0.15rem 0 0; color: #7a6e66; font-size: 0.82rem; }
        .orr-close {
          border: none; background: none; font-size: 1.6rem; line-height: 1;
          color: #8b7b73; cursor: pointer;
        }
        .orr-summary {
          display: flex; flex-wrap: wrap; gap: 1rem;
          padding: 0.6rem 1.1rem; border-bottom: 1px solid #f2ebe5;
          background: #faf6f2; font-size: 0.85rem; color: #5a4a42;
        }
        .orr-body { padding: 0.6rem 1.1rem; overflow-y: auto; flex: 1; }
        .orr-empty { color: #8b7b73; text-align: center; padding: 1.5rem 0; }
        .orr-vendor {
          border: 1px solid #ebe1d8; border-radius: 8px; background: #fff;
          margin-bottom: 0.75rem; overflow: hidden;
        }
        .orr-vendor-head {
          display: flex; justify-content: space-between; align-items: flex-start;
          gap: 0.75rem; padding: 0.55rem 0.75rem;
          background: #f5ede3; border-bottom: 1px solid #ebe1d8;
        }
        .orr-vendor-head strong { color: #3d2518; font-size: 0.95rem; display: block; }
        .orr-vendor-head small { display: block; color: #7a6e66; font-size: 0.75rem; margin-top: 0.15rem; }
        .orr-warn { color: #b76e2a !important; }
        .orr-vendor-total { font-weight: 600; color: #3d2518; white-space: nowrap; }
        .orr-items { padding: 0.25rem 0.5rem 0.5rem; }
        .orr-item-row {
          display: grid;
          grid-template-columns: minmax(0, 2.4fr) repeat(5, minmax(0, 1fr));
          gap: 0.4rem; padding: 0.35rem 0.4rem; font-size: 0.82rem;
          color: #5a4a42; border-bottom: 1px solid #f6efe6;
        }
        .orr-item-row:last-child { border-bottom: none; }
        .orr-item-head { font-weight: 600; color: #7a6e66; font-size: 0.74rem; text-transform: uppercase; }
        .orr-col-desc { min-width: 0; word-break: break-word; }
        .orr-col-desc small { display: inline; color: #8b7b73; font-size: 0.72rem; }
        .orr-col-num { text-align: right; white-space: nowrap; }
        .orr-actions {
          display: flex; justify-content: flex-end; gap: 0.6rem;
          padding: 0.75rem 1.1rem; border-top: 1px solid #e8ddd0; background: #fff;
        }
        .orr-btn {
          border-radius: 6px; padding: 0.55rem 1.1rem; font-size: 0.88rem;
          font-weight: 600; cursor: pointer; min-width: 110px;
        }
        .orr-btn-secondary { background: #fff; border: 1px solid #d4c4b0; color: #5a4a42; }
        .orr-btn-primary { background: #c8b5a0; border: 1px solid #c8b5a0; color: #fff; }
        .orr-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        @media (max-width: 640px) {
          .orr-sheet { max-height: 96vh; }
          .orr-item-row {
            grid-template-columns: repeat(5, minmax(0, 1fr));
            gap: 0.15rem 0.4rem;
          }
          .orr-col-desc { grid-column: 1 / -1; font-weight: 600; text-align: left; }
          .orr-item-head .orr-col-desc { display: none; }
          .orr-item-head { font-size: 0.68rem; padding: 0.2rem 0.25rem; }
          .orr-item-row { padding: 0.4rem 0.25rem; font-size: 0.78rem; }
        }
      `}</style>
    </div>
  );
};

// Smoothly mounts/unmounts a panel with a ~200ms slide+fade animation.
// Uses the CSS grid `grid-template-rows: 0fr → 1fr` trick so we don't need to
// measure heights. Children are unmounted while closed (so `autoFocus` on the
// search inputs still fires when the panel re-opens).
const AnimatedSearchPanel = ({ open, children }) => {
  const [render, setRender] = useState(open);
  const [visible, setVisible] = useState(open);
  useEffect(() => {
    if (open) {
      setRender(true);
      const r = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(r);
    }
    setVisible(false);
    const t = setTimeout(() => setRender(false), 200);
    return () => clearTimeout(t);
  }, [open]);
  if (!render) return null;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: visible ? '1fr' : '0fr',
        opacity: visible ? 1 : 0,
        transition: 'grid-template-rows 200ms ease, opacity 200ms ease',
      }}
    >
      <div style={{ overflow: 'hidden', minHeight: 0 }}>{children}</div>
    </div>
  );
};

// Module-level, deliberately: the mobile shell mounts this component twice and
// the seeder is called from two places. A per-instance guard would reset on
// each mount, which is exactly when the double-fire happens. This registry is
// why an "empty template" can be seeded at most once per page load.
const templateSeedRegistry = new SeedRegistry();

// Unlike template seeding, draft reconciliation must be allowed to run again
// after a later item-link approval. Concurrent mobile/StrictMode mounts share
// one promise, then each refreshes the repaired rows before rendering.
const draftTemplateSyncInFlight = new Map();

async function reconcileDraftWithTemplateParent(sessionId, session, workingSetGroups = null) {
  const { data: templateSessions, error: templateSessionErr } = await supabase
    .from('count_sessions')
    .select('id')
    .eq('count_group_id', session.count_group_id)
    .eq('is_template', true)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (templateSessionErr) throw templateSessionErr;

  const templateSessionId = templateSessions?.[0]?.id;
  if (!templateSessionId || String(templateSessionId) === String(sessionId)) return 0;

  const { rows: templateRows, error: templateRowsErr } = await fetchAllCountItems(
    supabase,
    templateSessionId,
  );
  if (templateRowsErr) throw templateRowsErr;

  const dedupedTemplateRows = dedupeCountItemRows(templateRows);
  const templateInventoryIds = [...new Set(
    dedupedTemplateRows.map((row) => row.inventory_item_id).filter(Boolean),
  )];
  if (templateInventoryIds.length === 0) return 0;

  const { data: templateInventory, error: templateInventoryErr } = await supabase
    .from('inventory_items')
    .select('id, is_active, deleted_at')
    .in('id', templateInventoryIds);
  if (templateInventoryErr) throw templateInventoryErr;

  const liveTemplateRows = filterCountItemsToLiveInventory(
    dedupedTemplateRows,
    templateInventory,
    { preserveUnknown: false },
  );
  const { rows: currentRows, error: currentRowsErr } = await fetchAllCountItems(supabase, sessionId);
  if (currentRowsErr) throw currentRowsErr;

  const toInsert = buildMissingCountItemsFromTemplate(sessionId, currentRows, liveTemplateRows, { groups: workingSetGroups });
  if (toInsert.length === 0) return 0;

  const { data: insertedRows, error: insertErr } = await supabase
    .from('count_items')
    .insert(toInsert)
    .select();
  if (insertErr) throw insertErr;
  return insertedRows?.length || 0;
}

const CountInterface = ({ onCancel, sessionId, startWithSession, mobileTool = false }) => {
  const { resolveVendor } = useVendorDirectory();

  const [step, setStep] = useState(startWithSession ? 'counting' : 'select-session'); // 'select-session', 'counting'
  const [loading, setLoading] = useState(false);
  // False until the first count_sessions read settles. `loading` cannot stand
  // in for it: it starts false and only flips once loadCountSessions is under
  // way, so the overview painted its greeting and two empty template sections
  // — "Nothing to count yet" — for the whole of the bundle-to-query gap, then
  // replaced them with the real templates. Seeded true on the session-first
  // mount (count-session.html / count-mobile.html), which loads one session
  // instead of the overview list and so never resolves this.
  const [sessionsLoaded, setSessionsLoaded] = useState(Boolean(startWithSession && sessionId));
  const [countSessions, setCountSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [countItems, setCountItems] = useState([]);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState(0);
  const [sessionItemsCache, setSessionItemsCache] = useState({}); // Cache for session items
  const [tooltipData, setTooltipData] = useState({ show: false, content: '', x: 0, y: 0 });
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [hideTimeout, setHideTimeout] = useState(null);
  const [expandedStacks, setExpandedStacks] = useState(new Set()); // Track which stacks are expanded
  const [expandedStackChildren, setExpandedStackChildren] = useState(new Set()); // Track which stacks reveal children beyond the default top 2
  const [expandedCards, setExpandedCards] = useState(new Set()); // Track which single session rows are expanded
  const [recentActivityExpanded, setRecentActivityExpanded] = useState(false); // Expand the Recent activity tray past its collapsed 3-row view
  const [viewedRunIds, setViewedRunIds] = useState(() => new Set()); // Run IDs the user has already looked at (hydrated from localStorage)
  // Inventory item ids with ≥2 allocatable purchase options ("flexible-source").
  // During counting their math remains exact NEED = PAR − on hand. Vendor
  // package, MOQ, and increment math belongs to Savings Review.
  // Empty Set = feature off (e.g. table not deployed).
  const [flexibleSourceItemIds, setFlexibleSourceItemIds] = useState(() => new Set());
  // inventory_item_id → flexiblePackageConstraint() of the vendor product the
  // item's lines resolve to (see buildFlexibleConstraints). null = no resolved
  // package, keep the plain NEED. Rebuilt on session load and whenever a
  // vendor is chosen for a line.
  const [flexibleConstraintsByItemId, setFlexibleConstraintsByItemId] = useState({});
  // Purchase option rows per inventory item (inventory_item_id → options[]),
  // powering the Multi-vendor badge in the item preview lists. {} = feature
  // off (e.g. purchase_options table not deployed yet).
  const [purchaseOptionsByItemId, setPurchaseOptionsByItemId] = useState({});
  // The related-group fold for the open session (utils/countGroupFold): which
  // groups folded, who represents each, and how many sibling rows are hidden.
  // Empty when nothing folded — every consumer treats that as the old
  // item-scoped world.
  const [countGroupFold, setCountGroupFold] = useState({ foldsByGroupId: new Map(), groups: null, hiddenRowCount: 0, hiddenRows: [] });
  // Group-pooled option lists: each grouped item's pool is the union of its
  // working-set siblings' options — the pool the Savings Review shops, shown
  // on the count card's Multi-vendor badge. Identity map when nothing folded.
  const pooledOptionsByItemId = useMemo(
    () => expandOptionsAcrossGroup(purchaseOptionsByItemId, countGroupFold.groups),
    [purchaseOptionsByItemId, countGroupFold.groups],
  );
  // Item whose vendor products are open in the "Manage vendor products" modal:
  // { inventoryItemId, name, countUnit, storeId } | null.
  const [vendorProductsItem, setVendorProductsItem] = useState(null);
  // Mixed-unit counting. The ladder — how much one case, one bottle, one
  // gallon of an item is worth — is built once per session by the server and
  // cached here, so typing recomputes locally and counting survives a walk-in
  // with no signal. {} means the feature is unavailable (function not
  // deployed, or offline before the first load), and the count tool falls back
  // to the single-number entry it has always had.
  const [countLaddersByItemId, setCountLaddersByItemId] = useState({});
  // Raw entries already stored for each count line (count_item_id → rows[]).
  const [countEntriesByCountItemId, setCountEntriesByCountItemId] = useState({});
  // Which physical package each line is COUNTED in (count_item_id → the four
  // count_display_* columns). Distinct from selected_purchase_option_id, which
  // is who the line will be BOUGHT from — a store may count Costco tubs and
  // order from Sysco, and nothing on a count screen writes the ordering
  // column. {} means the columns are not deployed, and every line resolves to
  // the latest verified purchase or the item's own unit.
  const [countFormatsByCountItemId, setCountFormatsByCountItemId] = useState({});
  // Template-scoped "what package was this location counted in last time",
  // keyed `${inventoryItemId}::${location}` — merged into the resolver row.
  const [lastUsedFormatsByKey, setLastUsedFormatsByKey] = useState({});
  // The literal text typed into the count input while a package format is
  // active, per count line ({ value, rungKey }). The typed "2" means two
  // BAGS; `current_count`/`actual_quantity` hold the normalized quantity, and
  // this buffer is what keeps the input showing the number the person typed
  // (and mid-typing states like "2.") instead of a derived equivalent. Only
  // trusted while the line's active rung still matches `rungKey`.
  const [packageCountDraftByItemId, setPackageCountDraftByItemId] = useState({});
  // Debounce timers for mirroring typed package counts into
  // count_item_quantity_entries (count_item_id → timer).
  const entryWriteTimersRef = useRef({});
  useEffect(() => () => {
    // The mirror is best-effort working detail; the count itself persists
    // through the ordinary path. Firing into an unmounted component is worse
    // than dropping a mirror.
    Object.values(entryWriteTimersRef.current).forEach(clearTimeout);
    entryWriteTimersRef.current = {};
  }, []);
  // A package switch awaiting the add-another/change-this decision.
  const [packageSwitchPrompt, setPackageSwitchPrompt] = useState(null);
  // The count line whose package sheet is open, and whether a save is running.
  const [packageSheetItem, setPackageSheetItem] = useState(null);
  // The visual bottle-fraction slider (liquor/wine lines only). Opened only
  // when the counter asks for it, from the count card's bottle tab — a typed
  // decimal never summons it. { itemIndex, initialTenths, wholePart } | null.
  const [bottleSheet, setBottleSheet] = useState(null);
  // "Your usual package changed" — the PAR-transition prompt. Checked once
  // per item per session, at the natural point of use (opening the item's
  // card), never on a keystroke. { item, transition, storedRow,
  // recommendation, busy } | null.
  const [parTransitionPrompt, setParTransitionPrompt] = useState(null);
  const parTransitionCheckedRef = useRef(new Set());
  const [packageSheetSaving, setPackageSheetSaving] = useState(false);
  const [overviewFilter, setOverviewFilter] = useState('all'); // 'all' | 'latest' | 'drafts'
  const [cardPreviewTab, setCardPreviewTab] = useState({}); // Active tab per session id
  const [cardPreviewStatus, setCardPreviewStatus] = useState({}); // 'loading'|'success'|'error' per session id
  const [sessionCategoryIconCache, setSessionCategoryIconCache] = useState({}); // sessionId → [emoji, ...]
  const [stackReportGroupId, setStackReportGroupId] = useState(null); // Group ID for the comparison report modal
  const [stackReportData, setStackReportData] = useState(null); // { sessions, itemsPerSession }
  const [stackReportLoading, setStackReportLoading] = useState(false);
  const [setupPickerModal, setSetupPickerModal] = useState(null); // { template, parsMissing, scheduleMissing, isOrder, isEOP }
  const [directOrderConfirm, setDirectOrderConfirm] = useState(null); // { template } — confirm turning "count straight to order" ON
  const [templateLifecycleModal, setTemplateLifecycleModal] = useState(null); // { template, info } — Archive/Delete dialog from the template detail view
  const [templateArchiveConfirmText, setTemplateArchiveConfirmText] = useState(''); // typed "confirm" gate for archiving templates with items/history
  const [templateLifecycleBusy, setTemplateLifecycleBusy] = useState(false);
  const [tplOnboardingAcked, setTplOnboardingAcked] = useState(() => readTemplateOnboardingAck(getCurrentStoredUserId()));
  const [parsWizardModal, setParsWizardModal] = useState(null); // null | { template, items, parsOnly }
  const parsWizardResolverRef = useRef(null); // resolves the showParsWizard promise
  const markTemplateOnboardingSeen = useCallback(async (source = 'dismissed') => {
    const userId = getCurrentStoredUserId();
    writeTemplateOnboardingAck(userId);
    setTplOnboardingAcked(true);

    if (!userId) return;

    const column = source === 'dismissed'
      ? 'template_onboarding_dismissed_at'
      : 'template_onboarding_interacted_at';
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ [column]: new Date().toISOString() })
        .eq('id', userId);
      if (error) {
        console.warn('[TemplateOnboarding] Could not persist dismissal:', error.message || error);
      }
    } catch (err) {
      console.warn('[TemplateOnboarding] Persist dismissal threw:', err);
    }
  }, []);

  useEffect(() => {
    const userId = getCurrentStoredUserId();
    if (readTemplateOnboardingAck(userId)) {
      setTplOnboardingAcked(true);
    }
    if (!userId) return;

    let cancelled = false;
    supabase
      .from('user_profiles')
      .select('template_onboarding_dismissed_at, template_onboarding_interacted_at')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn('[TemplateOnboarding] Could not load persisted dismissal:', error.message || error);
          return;
        }
        if (data?.template_onboarding_dismissed_at || data?.template_onboarding_interacted_at) {
          writeTemplateOnboardingAck(userId);
          setTplOnboardingAcked(true);
        }
      })
      .catch((err) => {
        if (!cancelled) console.warn('[TemplateOnboarding] Load persisted dismissal threw:', err);
      });

    return () => { cancelled = true; };
  }, []);
  const [storePeriodConfig, setStorePeriodConfig] = useState(null); // store.inventory_period_config; fallback for legacy templates without ordering_cycle_config
  const [sortBy, setSortBy] = useState('location'); // 'location', 'vendor', 'category', 'alphabetical'
  const [selectedLocation, setSelectedLocation] = useState(null); // reuse as selected group (location/vendor/category)
  const keypadCardRef = useRef(null);
  // Mobile swipe-on-keypad navigation: left = next item, right = previous.
  // Only the numpad div binds these handlers, so the action row and the
  // bottom tape are unaffected.
  const keypadSwipeRef = useRef({ x: 0, y: 0, active: false });
  // Task #746 — keypad slide animation on swipe navigation.
  // 'exit-left' | 'exit-right' | 'enter-from-right' | 'enter-from-left' | ''
  const [keypadAnimClass, setKeypadAnimClass] = useState('');
  const swipeNavTimerRef = useRef(null);
  // Task #743 — one-time visual hint that the keypad supports swipe-to-navigate.
  // 'idle' (not shown), 'visible' (animating in/showing), 'leaving' (fading out).
  // Persisted in localStorage so each user only sees it once per device.
  const KEYPAD_SWIPE_HINT_KEY = 'countertop:keypadSwipeHintSeen';
  const [keypadSwipeHintState, setKeypadSwipeHintState] = useState('idle');
  const keypadSwipeHintTimerRef = useRef(null);
  const dismissKeypadSwipeHint = () => {
    setKeypadSwipeHintState((prev) => {
      if (prev !== 'visible') return prev;
      try { localStorage.setItem(KEYPAD_SWIPE_HINT_KEY, '1'); } catch (_) {}
      if (keypadSwipeHintTimerRef.current) {
        clearTimeout(keypadSwipeHintTimerRef.current);
        keypadSwipeHintTimerRef.current = null;
      }
      // Schedule removal after the fade-out finishes.
      setTimeout(() => setKeypadSwipeHintState('idle'), 320);
      return 'leaving';
    });
  };
  useEffect(() => {
    if (!mobileTool) return undefined;
    let seen = false;
    try { seen = localStorage.getItem(KEYPAD_SWIPE_HINT_KEY) === '1'; } catch (_) {}
    if (seen) return undefined;
    // Small delay so the hint appears after the screen has settled.
    const showT = setTimeout(() => setKeypadSwipeHintState('visible'), 450);
    keypadSwipeHintTimerRef.current = setTimeout(() => {
      dismissKeypadSwipeHint();
    }, 450 + 2500);
    return () => {
      clearTimeout(showT);
      if (keypadSwipeHintTimerRef.current) {
        clearTimeout(keypadSwipeHintTimerRef.current);
        keypadSwipeHintTimerRef.current = null;
      }
    };
  }, [mobileTool]);
  const [paneHeightPx, setPaneHeightPx] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // Task #515 — width-only breakpoints for mobileTool layout:
  //   tablet 768-1024 → 2-column (sidebar + items, no keypad)
  //   aboveTablet ≥1025 → 3-column (sidebar + items + keypad)
  const tabletMediaQuery = '(min-width: 768px) and (max-width: 1024px)';
  const aboveTabletMediaQuery = '(min-width: 1025px)';
  // iPad landscape: mobileTool on a tablet-sized screen in landscape orientation.
  // The min-height:600px excludes iPhones in landscape (max ~430px tall) while
  // including all iPads (768px+ tall in landscape). Portrait on the same device
  // falls back to the regular phone layout. iPhone experience is unchanged.
  const landscapeMobileMediaQuery = '(min-width: 768px) and (min-height: 600px) and (orientation: landscape)';
  const supportsMM = typeof window !== 'undefined' && !!window.matchMedia;
  const [isTabletLayout, setIsTabletLayout] = useState(
    () => mobileTool && supportsMM ? window.matchMedia(tabletMediaQuery).matches : false
  );
  const [isAboveTabletLayout, setIsAboveTabletLayout] = useState(
    () => mobileTool && supportsMM ? window.matchMedia(aboveTabletMediaQuery).matches : false
  );
  const [isIpadLandscape, setIsIpadLandscape] = useState(
    () => mobileTool && supportsMM ? window.matchMedia(landscapeMobileMediaQuery).matches : false
  );
  useEffect(() => {
    if (!mobileTool || !supportsMM) return undefined;
    const tabletMql = window.matchMedia(tabletMediaQuery);
    const aboveMql = window.matchMedia(aboveTabletMediaQuery);
    const landscapeMql = window.matchMedia(landscapeMobileMediaQuery);
    const tabletHandler = (e) => setIsTabletLayout(e.matches);
    const aboveHandler = (e) => setIsAboveTabletLayout(e.matches);
    const landscapeHandler = (e) => setIsIpadLandscape(e.matches);
    setIsTabletLayout(tabletMql.matches);
    setIsAboveTabletLayout(aboveMql.matches);
    setIsIpadLandscape(landscapeMql.matches);
    const add = (mql, h) => mql.addEventListener ? mql.addEventListener('change', h) : mql.addListener(h);
    const rm = (mql, h) => mql.removeEventListener ? mql.removeEventListener('change', h) : mql.removeListener(h);
    add(tabletMql, tabletHandler);
    add(aboveMql, aboveHandler);
    add(landscapeMql, landscapeHandler);
    return () => { rm(tabletMql, tabletHandler); rm(aboveMql, aboveHandler); rm(landscapeMql, landscapeHandler); };
  }, [mobileTool]);
  // mobileTool always uses the phone (keypad-centric) layout.
  // iPad landscape adds a side rail but keeps useFullItemRows false.
  // Portrait iPad previously showed the hybrid "full rows" view; it now
  // falls back to the regular phone layout (no keypad-less tablet hybrid).
  const useFullItemRows = !mobileTool;
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(null);
  // Mobile-only: search query for the in-count item finder, and the index
  // to return to after the user counts a searched item ("peek mode").
  const [mobileSearchText, setMobileSearchText] = useState('');
  const [peekReturnIndex, setPeekReturnIndex] = useState(null);
  // Mobile-only: lets a counter tap a clamped/truncated item name on the
  // keypad card to reveal its full text. `nameExpanded` is the toggle;
  // `nameTruncated` records whether the (clamped) heading actually overflows
  // so we only show the tap affordance for names that are really cut off.
  const [nameExpanded, setNameExpanded] = useState(false);
  const [nameTruncated, setNameTruncated] = useState(false);
  const itemNameRef = useRef(null);
  // Mobile rooms strip: tracks which room (location) is active. Synced to
  // the selected item's location on navigation, and updated when the user taps
  // a room chip. Used to scope the tape strip to a single room.
  const [mobileActiveRoom, setMobileActiveRoom] = useState(null);
  const mobileRoomsScrollRef = useRef(null);
  const mobileRoomChipRefsMap = useRef({});
  // Item-title transition animation (both mobileTool and web tool).
  // titleAnimIndexRef rotates through the four entrance animation classes;
  // titleAnimPrevIndexRef tracks the previous selectedItemIndex so we only
  // animate on actual item changes (not initial mount, not unrelated
  // re-renders). Keyframes live in count-mobile.html (mobile) and
  // count-session.html (web), both behind a prefers-reduced-motion gate.
  const titleAnimIndexRef = useRef(0);
  const titleAnimPrevIndexRef = useRef(null);
  // Tracks the item index that should display a blank count input because the
  // user arrived there via "Count / Next ▶" (or its Enter-key equivalent).
  // Cleared as soon as the user modifies the count for that item.
  // Manual navigation (card tap, search, Previous) never sets this ref.
  const blankOnNextIndexRef = useRef(null);

  // Mobile-only: when the selected item changes, collapse any expanded name and
  // re-measure whether the (clamped) heading actually overflows its 2-line box.
  // `nameTruncated` drives the "tap to read full name" affordance so we only
  // show it for names that are really cut off. Runs after paint via the index +
  // name deps so the measurement reflects the freshly rendered heading.
  const selectedItemNameForClamp =
    countItems[selectedItemIndex]?.item_snapshot?.description ||
    countItems[selectedItemIndex]?.item_snapshot?.name ||
    '';
  useEffect(() => {
    setNameExpanded(false);
  }, [selectedItemIndex, selectedItemNameForClamp]);
  useEffect(() => {
    if (!mobileTool) return undefined;
    const measure = () => {
      const el = itemNameRef.current;
      if (!el) {
        setNameTruncated(false);
        return;
      }
      if (nameExpanded) return; // can't measure clamp overflow while expanded
      // scrollHeight exceeds clientHeight when -webkit-line-clamp hides text.
      setNameTruncated(el.scrollHeight - el.clientHeight > 1);
    };
    measure();
    // Re-measure on viewport/orientation changes — clamp overflow depends on the
    // card width, which changes on rotate or responsive resize.
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [mobileTool, nameExpanded, selectedItemIndex, selectedItemNameForClamp]);

  // ── Offline-first sync state (mobileTool only) ────────────────────────────
  const [snapshotId, setSnapshotId] = useState(null);
  const [snapshotTimestamp, setSnapshotTimestamp] = useState(null);
  const [offlineSyncToast, setOfflineSyncToast] = useState(null);
  // Brief, non-blocking confirmation shown when an in-count PAR edit
  // successfully propagates to the parent template / inventory master.
  // See updateItemFieldsGlobal() and Task #289.
  const [templateSyncToast, setTemplateSyncToast] = useState(null);
  // Mobile tape edit mode (long-press to wiggle): state for the iOS-style
  // edit-screen interaction on the bottom item strip. tapeEditMode toggles
  // the wiggle animation + delete badges; tapeDrag tracks the in-flight drag
  // so the dragged card can be visually translated and the drop indicator
  // shown in the gap between cards. tapeDeleteConfirm holds a pending
  // deletion until the user confirms in the styled modal.
  const [tapeEditMode, setTapeEditMode] = useState(false);
  const [tapeDrag, setTapeDrag] = useState(null);
  // ^ { fromIndex, toIndex, pointerX, pointerY, startX, dx, cardWidth, gap }
  const [tapeDeleteConfirm, setTapeDeleteConfirm] = useState(null);
  const [tapeDeletePending, setTapeDeletePending] = useState(false);
  const [tapeTemplateName, setTapeTemplateName] = useState(null);
  const tapeLongPressTimerRef = useRef(null);
  const tapeLongPressStartRef = useRef(null);
  const tapeContainerRef = useRef(null);
  const tapeAutoScrollRafRef = useRef(null);
  const tapeAutoScrollDirRef = useRef(0);
  const [offlineCompletionMode, setOfflineCompletionMode] = useState(false);
  // ── Offline Done-block sub-state ───────────────────────────────────────────
  // Toggled true by the "Try again" button on the offline-blocked modal when
  // the fresh connectivity probe still returns offline. The render layer swaps
  // the modal copy to "Countertop is not detecting internet, check your device
  // settings." and the next dismissal returns the user to the primary
  // "Return to internet" message.
  const [offlineBlockedNoInternet, setOfflineBlockedNoInternet] = useState(false);
  const [offlineBlockedRetrying, setOfflineBlockedRetrying] = useState(false);
  // The completion workflow has exactly two user-facing states: a review
  // before the status changes, then a completed screen with the optional
  // ordering next step. Purchase orders are never created by completion.
  const [completionWorkflow, setCompletionWorkflow] = useState({
    summary: null,
    nextAction: COUNT_COMPLETION_NEXT_ACTION.NONE,
    savingsReviewId: null,
    error: null,
    isBusy: false,
  });
  const completionInFlightRef = useRef(false);
  const draftCreationInFlightRef = useRef(false);
  // A completed count with an open Savings Review must never render as a dead
  // end. Whoever lands here — back button, bookmark, a crash mid-completion —
  // is told the review is waiting and given the link, because the alternative
  // is a finished count page with no way to reach its own order.
  const [pendingSavingsReview, setPendingSavingsReview] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setPendingSavingsReview(null);
    if (!selectedSession?.id || selectedSession?.status !== 'completed') return undefined;
    if (!selectedSession?.order_calculations_enabled) return undefined;
    const storeIdForPlan = selectedSession.store_id
      || (typeof window !== 'undefined' ? parseInt(localStorage.getItem('selectedStoreId'), 10) : null);
    if (!storeIdForPlan) return undefined;
    import('../services/purchasingPlanService')
      .then(({ fetchOpenPlanForCount }) => fetchOpenPlanForCount(storeIdForPlan, selectedSession.id))
      .then((plan) => { if (!cancelled && plan) setPendingSavingsReview(plan); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedSession?.id, selectedSession?.status, selectedSession?.order_calculations_enabled, selectedSession?.store_id]);
  const offlineSyncToastTimerRef = useRef(null);
  const templateSyncToastTimerRef = useRef(null);

  const handleSyncToast = useCallback((msg) => {
    setOfflineSyncToast(msg);
    clearTimeout(offlineSyncToastTimerRef.current);
    offlineSyncToastTimerRef.current = setTimeout(() => setOfflineSyncToast(null), 4000);
  }, []);

  const showTemplateSyncToast = useCallback((msg) => {
    if (!msg) return;
    setTemplateSyncToast(msg);
    clearTimeout(templateSyncToastTimerRef.current);
    templateSyncToastTimerRef.current = setTimeout(() => setTemplateSyncToast(null), 3000);
  }, []);

  useEffect(() => () => clearTimeout(templateSyncToastTimerRef.current), []);

  // Syncing an offline completion only finishes the count. Creating orders is
  // always an explicit next action after completion, never a sync side effect.
  const handleOfflineSessionCompleted = useCallback(async () => null, []);

  const offlineSync = useOfflineCountSync({
    enabled: mobileTool,
    sessionId: selectedSession?.id,
    snapshotId,
    snapshotTimestamp,
    supabase,
    onSyncToast: handleSyncToast,
    onSessionCompleted: handleOfflineSessionCompleted,
  });

  // Warn before navigating away when there are pending changes (mobile).
  useEffect(() => {
    if (!mobileTool) return;
    const handler = (e) => {
      const status = offlineSync.getSessionSyncStatus();
      if (status === 'pending' || status === 'syncing' || status === 'failed') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [mobileTool, offlineSync]);


  // Show a one-time alert when the hook detects pending changes from a prior
  // session that were never synced.
  const pendingOtherAlertFiredRef = useRef(false);
  useEffect(() => {
    if (!mobileTool || !offlineSync.pendingOtherSession || pendingOtherAlertFiredRef.current) return;
    pendingOtherAlertFiredRef.current = true;
    // Non-blocking: show info via setTimeout so the UI is mounted first.
    setTimeout(() => {
      if (offlineSync.isOnline) {
        handleSyncToast('Syncing saved count from this phone…');
      } else {
        handleSyncToast('Pending count data saved on this phone — connect to sync.');
      }
    }, 800);
  }, [mobileTool, offlineSync.pendingOtherSession, offlineSync.isOnline]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Mobile rooms strip: sync active room with selected item ────────────────
  // When the user navigates to a new item (via ▶ Next, card tap, or search),
  // update the active room to match the item's location so the room chip
  // stays highlighted and scrolled into view.
  useEffect(() => {
    if (!mobileTool || countItems.length === 0) return;
    const item = countItems[selectedItemIndex];
    // Use the same resolver as mobileRooms so the active-room state cannot
    // diverge from the chip labels (e.g. when item.location is real but
    // snapshot.location is the legacy 'Individual Items' placeholder).
    setMobileActiveRoom(resolveCountItemLocation(item));
  }, [selectedItemIndex, countItems, mobileTool]);

  // Auto-scroll the rooms strip so the active chip stays in view.
  useEffect(() => {
    if (!mobileTool || !mobileActiveRoom || !mobileRoomsScrollRef.current) return;
    const chipEl = mobileRoomChipRefsMap.current[mobileActiveRoom];
    if (!chipEl) return;
    const container = mobileRoomsScrollRef.current;
    const chipLeft = chipEl.offsetLeft;
    const scrollTarget = chipLeft - 48;
    container.scrollTo({ left: Math.max(0, scrollTarget), behavior: 'smooth' });
  }, [mobileActiveRoom, mobileTool]);

  // Item-title transition animation: when selectedItemIndex changes, rotate
  // through a set of cinematic entrance animations on the title element.
  // Runs on both /count-mobile.html and /count-session.html.
  // Skips initial mount (no animation on first render) and skips no-op
  // re-renders where the index didn't actually change. The keyframes
  // live in count-mobile.html (gated by [data-mobile-tool="1"]) and in
  // count-session.html (no ancestor restriction), both inside a
  // prefers-reduced-motion gate, so motion-averse users get no animation.
  useEffect(() => {
    if (titleAnimPrevIndexRef.current === null) {
      // First render after mount — record the index but don't animate.
      titleAnimPrevIndexRef.current = selectedItemIndex;
      return;
    }
    if (titleAnimPrevIndexRef.current === selectedItemIndex) return;
    titleAnimPrevIndexRef.current = selectedItemIndex;

    const el = typeof document !== 'undefined'
      ? document.querySelector('[data-count-header-title="1"]')
      : null;
    if (!el) return;

    const classes = [
      'title-swipe-change',
      'title-fade-change',
      'title-dissolve-change',
      'title-dematerialize-change',
    ];
    const cls = classes[titleAnimIndexRef.current % classes.length];
    titleAnimIndexRef.current += 1;

    el.classList.remove(...classes);
    // Force a reflow so re-adding the same class would still restart the
    // animation. Cheap, scoped to a single small element.
    void el.offsetWidth;
    el.classList.add(cls);

    // 580ms covers the longest animation (520ms dematerialize) plus a small
    // buffer so the class is removed cleanly after the animation finishes.
    const t = window.setTimeout(() => {
      el.classList.remove(cls);
    }, 580);
    return () => window.clearTimeout(t);
  }, [selectedItemIndex]);

  // Hydrate viewed-run IDs from localStorage. Keyed by store so switching
  // stores shows a fresh "NEW" treatment.
  useEffect(() => {
    try {
      const storeId = localStorage.getItem('selectedStoreId');
      if (!storeId) return;
      const raw = localStorage.getItem(`marshmallow:viewedRunIds:${storeId}`);
      const arr = raw ? JSON.parse(raw) : [];
      setViewedRunIds(new Set(Array.isArray(arr) ? arr : []));
    } catch (_) {}
  }, []);

  // The `mm_setup_nudge_acked` / `mm_pars_nudge_acked` maps and the migration
  // between them are gone with the pulsing ⚙ Setup badge they suppressed.
  // Nothing nudges for setup any more, so there is nothing to acknowledge.
  // Any keys left in a browser from before are inert.

  // ── Mobile rooms strip: derive room list from session items ───────────────
  // Each entry carries the first uncounted item index so tapping a room jumps
  // to where the user left off, mirroring the web room-tile behavior.
  // Resolve a count_item's effective location. Order count sessions historically
  // wrote 'Individual Items' as a placeholder when the inventory item had no
  // location; treat that as non-meaningful so we fall back to the snapshot's
  // real inventory location and the rooms strip can show actual rooms instead
  // of one collapsed "Individual Items" chip. Mirrors the native fix from #638.
  const resolveCountItemLocation = (item) => {
    const isPlaceholder = (v) => {
      if (!v || typeof v !== 'string') return true;
      const t = v.trim().toLowerCase();
      return t === '' || t === 'individual items';
    };
    const raw = item?.location;
    if (!isPlaceholder(raw)) return raw.trim();
    const snap = item?.item_snapshot?.location;
    if (!isPlaceholder(snap)) return snap.trim();
    return 'Unknown';
  };

  const mobileRooms = useMemo(() => {
    if (!mobileTool || countItems.length === 0) return [];
    const seen = new Map();
    countItems.forEach((item, idx) => {
      const label = resolveCountItemLocation(item);
      if (!seen.has(label)) {
        seen.set(label, { label, count: 0, uncountedCount: 0, firstIndex: idx, firstUncountedIndex: -1 });
      }
      const r = seen.get(label);
      r.count += 1;
      if (!item.is_counted) {
        r.uncountedCount += 1;
        if (r.firstUncountedIndex === -1) {
          r.firstUncountedIndex = idx;
        }
      }
    });
    return Array.from(seen.values());
  }, [mobileTool, countItems]);

  const markRunViewed = (sessionId) => {
    if (!sessionId) return;
    setViewedRunIds(prev => {
      if (prev.has(sessionId)) return prev;
      const next = new Set(prev);
      next.add(sessionId);
      try {
        const storeId = localStorage.getItem('selectedStoreId');
        if (storeId) localStorage.setItem(`marshmallow:viewedRunIds:${storeId}`, JSON.stringify([...next]));
      } catch (_) {}
      return next;
    });
  };

  // Session list sort and filter state
  const [sessionSortBy, setSessionSortBy] = useState('accessed'); // 'name', 'created', 'accessed'
  const [filterByOrder, setFilterByOrder] = useState('all'); // 'all', 'order', 'no-order'
  const [filterByStatus, setFilterByStatus] = useState('all'); // 'all', 'draft', 'in-process', 'complete'
  const [searchText, setSearchText] = useState('');
  const [filterByStack, setFilterByStack] = useState('all'); // 'all', 'stacked', 'single'
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [isMobileOverview, setIsMobileOverview] = useState(window.innerWidth <= 768);
  // Redesigned overview: which template group's detail screen is open.
  // null = the template list. The detail screen holds run history, stats,
  // setup and Edit Template; the list cards stay calm (title + status +
  // one primary button). Applies to both mobile and desktop overviews.
  const [detailGroupId, setDetailGroupId] = useState(null);
  // Inline template details: the one summary card currently expanded in place
  // (accordion — opening a second card closes the first), and the expanded
  // card whose full inline run history is revealed. The separate template
  // detail screen stays reachable via "View full history" for long histories.
  const [inlineDetailsGroupId, setInlineDetailsGroupId] = useState(null);
  const [inlineHistoryGroupId, setInlineHistoryGroupId] = useState(null);
  const overviewScrollPosRef = useRef(0);
  // The mobile overview scrolls inside #mobile-overview-all-cards (the shell
  // re-parents the React container there); desktop scrolls the document.
  const getOverviewScroller = () => (
    document.getElementById('mobile-overview-all-cards')
    || document.scrollingElement
    || document.documentElement
  );
  const openTemplateDetail = (groupId) => {
    const scroller = getOverviewScroller();
    overviewScrollPosRef.current = scroller ? scroller.scrollTop : 0;
    setDetailGroupId(groupId);
    // Push a history entry so the Android/iOS back gesture closes the
    // detail screen instead of leaving the page.
    try { window.history.pushState({ mmTemplateDetail: String(groupId) }, ''); } catch (_) {}
    requestAnimationFrame(() => { const s = getOverviewScroller(); if (s) s.scrollTop = 0; });
  };
  const closeTemplateDetail = () => {
    let viaHistory = false;
    try { viaHistory = !!(window.history.state && window.history.state.mmTemplateDetail); } catch (_) {}
    if (viaHistory) { window.history.back(); return; } // popstate listener finishes the close
    setDetailGroupId(null);
    requestAnimationFrame(() => { const s = getOverviewScroller(); if (s) s.scrollTop = overviewScrollPosRef.current || 0; });
  };
  useEffect(() => {
    const onPop = () => {
      setDetailGroupId((cur) => {
        if (cur == null) return cur;
        requestAnimationFrame(() => { const s = getOverviewScroller(); if (s) s.scrollTop = overviewScrollPosRef.current || 0; });
        return null;
      });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  // Count-card "Available Reports" pills: when set, mounts the Reports
  // component in embedded mode so the report opens in a frame on this page.
  // Shape: { type: 'cogs'|'detail'|'value'|'order', sessionIds: string[] }
  const [embeddedReport, setEmbeddedReport] = useState(null);
  const openSessionReport = (type, session) => {
    setEmbeddedReport({ type, sessionIds: session ? [String(session.id)] : [] });
  };
  const activeStoreId = Number.parseInt(localStorage.getItem('selectedStoreId') || '', 10) || null;

  // Stacked cards state (already declared above)

  // Modal state
  const [completeFlowStep, setCompleteFlowStep] = useState(null);
  const [appModalConfig, setAppModalConfig] = useState(null);
  const modalPromiseRef = useRef({ resolve: null, confirmed: false });
  // End-of-room modal: null = hidden; { nextRoom: groupData | null } = showing
  const [endOfRoomModal, setEndOfRoomModal] = useState(null);
  // Close-progress overlay: null = hidden; { subStep, error } = visible.
  // error=null means "saving" pulse; error=string means failed state w/ Retry.
  const [closeProgress, setCloseProgress] = useState(null);
  // Choice modal shown after all pre-checks pass on "New Count / New Order"
  // null = closed; { session } = showing
  const [countStartChoiceModal, setCountStartChoiceModal] = useState(null);
  // Mobile instructions panel shown after session is created with 'mobile' mode
  // null = hidden; { id, name } = showing
  const [mobileInstructionSession, setMobileInstructionSession] = useState(null);

  // Block page unload while the count close-out is in flight.
  useEffect(() => {
    if (!closeProgress || closeProgress.error) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [closeProgress]);

  const formatCountLabel = (count, singular, plural) => (
    `${count} ${count === 1 ? singular : plural}`
  );

  const formatVendorSendSummary = (summary = {}) => {
    const pieces = [];
    if (summary.sentByEmail > 0) pieces.push(`${formatCountLabel(summary.sentByEmail, 'vendor order', 'vendor orders')} sent by email`);
    if (summary.pendingApproval > 0) pieces.push(`${formatCountLabel(summary.pendingApproval, 'vendor order', 'vendor orders')} pending approval`);
    if (summary.sentOutsideCountertop > 0) pieces.push(`${formatCountLabel(summary.sentOutsideCountertop, 'vendor order', 'vendor orders')} marked as sent outside Countertop`);
    if (summary.notSentNoVendor > 0) pieces.push(`${formatCountLabel(summary.notSentNoVendor, 'item', 'items')} not sent — no vendor assigned`);
    if (summary.notSentMissingEmail > 0) pieces.push(`${formatCountLabel(summary.notSentMissingEmail, 'item', 'items')} not sent — vendor has no contact email`);
    if (summary.errored > 0) pieces.push(`${formatCountLabel(summary.errored, 'vendor order', 'vendor orders')} failed`);
    return pieces.length ? pieces.join(', ') : 'No vendor sends were completed.';
  };

  // Game strip state
  const [countStreak, setCountStreak] = useState(0);
  const [itemsThisSession, setItemsThisSession] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  // Timer tracking: starts only after first count
  const [firstCountCommitted, setFirstCountCommitted] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const [timerAccumulatedMs, setTimerAccumulatedMs] = useState(0);
  const timerStartRef = useRef(null); // when the current running segment started
  const lastCountTimeRef = useRef(null); // timestamp of most recent count commit
  const [encouragement, setEncouragement] = useState('');
  const [encouragementKey, setEncouragementKey] = useState(0);
  const prevEncouragementRef = useRef('');
  const prevLocationRef = useRef(null);

  // Reset all game-band / timer state when the active session changes so stale
  // metrics from a previous session don't carry over if the component stays mounted.
  useEffect(() => {
    setCountStreak(0);
    setItemsThisSession(0);
    setElapsedSeconds(0);
    setFirstCountCommitted(false);
    setTimerPaused(false);
    setTimerAccumulatedMs(0);
    timerStartRef.current = null;
    lastCountTimeRef.current = null;
    prevEncouragementRef.current = '';
    prevLocationRef.current = null;
    setEncouragement('');
    setEncouragementKey(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession?.id]);

  // Resolve current user name for "Go [Name]" header
  const currentUserName = useMemo(() => {
    const u = getStoredUser();
    if (!u) return null;
    const first = (u.user_metadata?.first_name || u.first_name || '').trim();
    if (first) return first;
    const display = u.displayName || '';
    if (display && display !== 'Unknown') return display.split(' ')[0];
    return null;
  }, []);

  // Pause timer when location changes (after first count)
  useEffect(() => {
    if (!firstCountCommitted) return;
    if (prevLocationRef.current !== null && prevLocationRef.current !== selectedLocation) {
      // Location changed — pause the timer
      if (!timerPaused && timerStartRef.current && typeof timerStartRef.current === 'number') {
        const segment = Date.now() - timerStartRef.current;
        if (segment > 0 && segment < 60000) { // Sanity check: max 60s per segment
          setTimerAccumulatedMs(prev => prev + segment);
        }
        timerStartRef.current = null;
        setTimerPaused(true);
      }
    }
    prevLocationRef.current = selectedLocation;
  }, [selectedLocation, firstCountCommitted, timerPaused]);

  // Inactivity check: pause after 60s with no count commit
  useEffect(() => {
    if (!firstCountCommitted || timerPaused) return;
    const interval = setInterval(() => {
      if (lastCountTimeRef.current && typeof lastCountTimeRef.current === 'number' && Date.now() - lastCountTimeRef.current >= 60000) {
        // 60s of inactivity — pause
        if (timerStartRef.current && typeof timerStartRef.current === 'number') {
          const segment = Date.now() - timerStartRef.current;
          if (segment > 0 && segment < 60000) { // Sanity check
            setTimerAccumulatedMs(prev => prev + segment);
          }
          timerStartRef.current = null;
        }
        setTimerPaused(true);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [firstCountCommitted, timerPaused]);

  // Update elapsed seconds every second while timer is running
  useEffect(() => {
    if (!firstCountCommitted || timerPaused) return;
    const interval = setInterval(() => {
      // Defensive: ensure timerStartRef is a valid number and not a timestamp
      let segmentMs = 0;
      if (timerStartRef.current && typeof timerStartRef.current === 'number' && timerStartRef.current > 1700000000000) {
        // Valid: timerStartRef is a recent timestamp (after 2023)
        segmentMs = Date.now() - timerStartRef.current;
      }
      // Sanity check: segment should be 0-60s (we update every 1s)
      if (segmentMs < 0 || segmentMs > 60000) segmentMs = 0;
      // Sanity check: accumulated should also be reasonable (< 24 hours in ms)
      const accumulated = (timerAccumulatedMs && timerAccumulatedMs > 0 && timerAccumulatedMs < 86400000) ? timerAccumulatedMs : 0;
      setElapsedSeconds(Math.floor((accumulated + segmentMs) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [firstCountCommitted, timerPaused, timerAccumulatedMs]);

  // Encouragement text logic
  useEffect(() => {
    const totalItems = countItems.length;
    const countedItems = countItems.filter(i => i.is_counted).length;
    const remaining = totalItems - countedItems;
    const progressFrac = totalItems > 0 ? countedItems / totalItems : 0;

    let next = '';
    if (!firstCountCommitted) {
      next = 'Ready to count';
    } else if (progressFrac >= 1) {
      next = 'Section clear!';
    } else if (remaining <= 3 && remaining > 0) {
      next = 'Finish strong!';
    } else if (progressFrac >= 0.8) {
      next = 'Home stretch!';
    } else if (progressFrac >= 0.5) {
      next = 'Halfway there!';
    } else if (countStreak >= 8) {
      next = 'Nice streak!';
    } else if (countStreak >= 5) {
      next = 'Locked in!';
    } else if (itemsThisSession <= 3 && firstCountCommitted) {
      next = 'Good start!';
    } else {
      next = 'Keep going!';
    }

    if (next !== prevEncouragementRef.current) {
      prevEncouragementRef.current = next;
      setEncouragement(next);
      setEncouragementKey(k => k + 1);
    }
  }, [firstCountCommitted, countStreak, itemsThisSession, countItems]);

  // Refs so the Enter-key handler (useEffect) always gets the latest values
  // without needing to re-register on every render.
  const firstCountCommittedRef = useRef(firstCountCommitted);
  const timerPausedRef = useRef(timerPaused);
  useEffect(() => { firstCountCommittedRef.current = firstCountCommitted; }, [firstCountCommitted]);
  useEffect(() => { timerPausedRef.current = timerPaused; }, [timerPaused]);

  // startTimerIfNeeded — kick off the timer on FIRST count interaction.
  // Called from any count-modifying action (+/-, keypad, direct input)
  // so the clock starts as soon as the user touches something, not only
  // when they press "Count Next ▶".
  const startTimerIfNeededRef = useRef(null);
  startTimerIfNeededRef.current = () => {
    const now = Date.now();
    // Validate: now should be a reasonable timestamp (after Jan 1, 2024)
    if (!now || typeof now !== 'number' || now < 1700000000000) {
      console.warn('[Timer] Invalid timestamp from Date.now():', now);
      return;
    }
    lastCountTimeRef.current = now;
    if (!firstCountCommittedRef.current) {
      setFirstCountCommitted(true);
      timerStartRef.current = now;
      setTimerPaused(false);
    } else if (timerPausedRef.current) {
      // Resume from a location-change pause when the user starts counting again.
      timerStartRef.current = now;
      setTimerPaused(false);
    }
  };
  const startTimerIfNeeded = () => startTimerIfNeededRef.current();

  // onCountCommit — call when the user CONFIRMS a counted item (Count Next / Enter).
  // Increments session stats in addition to starting/resuming the timer.
  const onCountCommitRef = useRef(null);
  onCountCommitRef.current = () => {
    startTimerIfNeededRef.current();
    setItemsThisSession(prev => prev + 1);
    setCountStreak(prev => prev + 1);
  };
  const onCountCommit = () => onCountCommitRef.current();

  // Returns the count value to use for display, commit, and keypad operations.
  // On mobile, if the user arrived at this item via Count/Next and hasn't typed
  // anything yet, we treat the stored count as blank so the previously-saved
  // value cannot be accidentally committed again.
  const getEffectiveCount = (index) => {
    if (mobileTool && blankOnNextIndexRef.current === index) return '';
    const item = countItems[index];
    // The typed draft is what the counter saw themselves enter, and it is
    // never silently re-derived. The package rung resolves from data that
    // moves under a live count — purchase options, count formats and ladders
    // all load and refresh on their own clocks — and re-reading the stored
    // quantity through a NEW rung visibly rewrote counts mid-session (a
    // typed 5.5 became 4.75). A draft dies only by explicit action: an
    // explicit "Count as" change or a package-sheet save. (rungForLine and
    // its dependencies are declared later in this component; this function
    // only runs from events and JSX, after the whole body has evaluated.)
    const draft = packageCountDraftByItemId[String(item?.id)];
    if (draft) return draft.value;
    // No draft: read the stored quantity back through the line's rung — the
    // same number, expressed in the package on the shelf.
    const rung = rungForLine(item);
    if (!rung) return item?.current_count ?? '';
    const normalized = parseOptionalNumber(item?.current_count);
    if (item?.is_counted && normalized !== null) {
      const packages = packageQuantityFromNormalized(normalized, rung);
      return packages === null ? String(normalized) : String(packages);
    }
    return '';
  };

  // True when a session/template runs in "count straight to order" mode: the
  // count tool records the entered value as the order quantity instead of the
  // on-hand quantity. Only meaningful for order sessions, so we require both
  // flags — a stray direct_order_mode on a non-order session is inert.
  const isDirectOrderSession = (s) =>
    !!(s && s.order_calculations_enabled && s.direct_order_mode);

  // Schedules the next visible item's input to appear blank on arrival (mobile
  // only). Must be called immediately before nextItem() so the ref is in place
  // when the re-render triggered by nextItem fires. Does nothing if navigation
  // would clamp in place (no actual advance) or when not on mobile.
  const flagNextItemBlank = () => {
    if (!mobileTool) return;
    const visibleIndexes = getVisibleItemIndexes();
    const currentVisiblePosition = visibleIndexes.indexOf(selectedItemIndex);
    const nextVisiblePosition = currentVisiblePosition < 0
      ? 0
      : Math.min(currentVisiblePosition + 1, visibleIndexes.length - 1);
    const nextIndex = visibleIndexes[nextVisiblePosition];
    // Only blank items that haven't been counted yet in this session.
    // Already-counted items should show their real stored value on arrival.
    if (nextIndex !== selectedItemIndex && !countItems[nextIndex]?.is_counted) {
      blankOnNextIndexRef.current = nextIndex;
    }
  };

  const countAndNext = () => {
    if (!countItems[selectedItemIndex]) return;
    const effectiveCount = getEffectiveCount(selectedItemIndex);
    const val = effectiveCount !== '' && effectiveCount !== null && effectiveCount !== undefined ? effectiveCount : '0';
    updateItemCount(selectedItemIndex, val);
    onCountCommit();
    if (isAtLastItemInRoom() && isRoomAboutToBeComplete()) {
      setEndOfRoomModal({ nextRoom: getNextRoomWithUncounted() });
      return;
    }
    if (selectedItemIndex < countItems.length - 1) {
      flagNextItemBlank();
      nextItem();
    }
  };

  const formatElapsed = (seconds) => {
    let total = Math.max(0, Math.floor(seconds));
    // Sanity check: elapsed time shouldn't exceed 24 hours (86400 seconds = 24h)
    // If it does, it's likely a Unix timestamp being misinterpreted
    if (total > 86400) {
      console.warn('[Timer] Elapsed seconds exceeds 24h:', total, 'Displaying 00:00:00');
      total = 0;
    }
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const itemsPerHour = elapsedSeconds > 0 ? Math.round((itemsThisSession / elapsedSeconds) * 3600) : 0;

  const parseOptionalNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const hasCountEntry = (value) => value !== null && value !== undefined && value !== '';

  const getLocationOrderValue = (item) => {
    const locationPosition = parseOptionalNumber(item?.location_position);
    return locationPosition && locationPosition > 0 ? locationPosition : Number.POSITIVE_INFINITY;
  };

  const getItemOrderValue = (item) => {
    const countPosition = parseOptionalNumber(item?.count_position);
    return countPosition && countPosition > 0 ? countPosition : Number.POSITIVE_INFINITY;
  };

  const getGroupValueForItem = (ci, groupType = sortBy) => {
    if (!ci) return 'Unknown';
    switch (groupType) {
      case 'vendor': {
        const rawVendor = ci.item_snapshot?.vendor || ci.vendor || '';
        return resolveVendor(rawVendor) || 'Unknown';
      }
      case 'category':
        return ci.item_snapshot?.category || ci.category || 'Unknown';
      case 'location':
        return ci.location || ci.item_snapshot?.location || 'Unknown';
      default:
        return 'All Items';
    }
  };

  const getVisibleItemIndexes = () => {
    if (sortBy === 'alphabetical' || !selectedLocation) {
      return countItems.map((_, index) => index);
    }
    const visible = [];
    countItems.forEach((item, index) => {
      if (getGroupValueForItem(item) === selectedLocation) {
        visible.push(index);
      }
    });
    return visible;
  };

  // Returns all groups in the same display order as the group nav, each entry
  // carrying uncountedCount and firstUncountedIndex. Used by end-of-room logic.
  const getAllGroupsOrdered = () => {
    if (sortBy === 'alphabetical') return [];
    const groupCounts = {};
    const groupUncounted = {};
    const groupOrderMeta = {};
    const groupFirstIndex = {};
    const groupFirstUncounted = {};
    countItems.forEach((ci, idx) => {
      const g = getGroupValueForItem(ci);
      groupCounts[g] = (groupCounts[g] || 0) + 1;
      if (groupFirstIndex[g] === undefined) groupFirstIndex[g] = idx;
      if (!ci.is_counted) {
        groupUncounted[g] = (groupUncounted[g] || 0) + 1;
        if (groupFirstUncounted[g] === undefined) groupFirstUncounted[g] = idx;
      }
      const locationOrder = getLocationOrderValue(ci);
      const itemOrder = getItemOrderValue(ci);
      const currentMeta = groupOrderMeta[g];
      if (!currentMeta || locationOrder < currentMeta.locationOrder ||
          (locationOrder === currentMeta.locationOrder && itemOrder < currentMeta.itemOrder)) {
        groupOrderMeta[g] = { locationOrder, itemOrder };
      }
    });
    const labels = Object.keys(groupCounts).sort((left, right) => {
      if (sortBy === 'location') {
        const leftMeta = groupOrderMeta[left] || { locationOrder: Number.POSITIVE_INFINITY, itemOrder: Number.POSITIVE_INFINITY };
        const rightMeta = groupOrderMeta[right] || { locationOrder: Number.POSITIVE_INFINITY, itemOrder: Number.POSITIVE_INFINITY };
        if (leftMeta.locationOrder !== rightMeta.locationOrder) return leftMeta.locationOrder - rightMeta.locationOrder;
        if (leftMeta.itemOrder !== rightMeta.itemOrder) return leftMeta.itemOrder - rightMeta.itemOrder;
      }
      return left.localeCompare(right);
    });
    return labels.map(label => ({
      label,
      count: groupCounts[label],
      uncountedCount: groupUncounted[label] || 0,
      firstIndex: groupFirstIndex[label] ?? -1,
      firstUncountedIndex: groupFirstUncounted[label] ?? -1,
    }));
  };

  // True when the current item is the LAST item by position in the active room/group.
  // Only returns true when a room/group is selected (not in "all items" / alphabetical mode).
  const isAtLastItemInRoom = () => {
    if (sortBy === 'alphabetical' || !selectedLocation) return false;
    const visibleIndexes = getVisibleItemIndexes();
    if (visibleIndexes.length === 0) return false;
    const currentPos = visibleIndexes.indexOf(selectedItemIndex);
    return currentPos === visibleIndexes.length - 1;
  };

  // True when committing the current item will complete the active room/group.
  // Checks that no other uncounted items remain in the room's visible list
  // (since the current item is being committed by the caller). Only returns true
  // when a room/group is selected (not in "all items" / alphabetical mode).
  const isRoomAboutToBeComplete = () => {
    if (sortBy === 'alphabetical' || !selectedLocation) return false;
    const visibleIndexes = getVisibleItemIndexes();
    if (visibleIndexes.length === 0) return false;
    // After committing the current item, the room is complete if every other
    // item in the visible list is already counted.
    return visibleIndexes.every(
      idx => idx === selectedItemIndex || !!countItems[idx]?.is_counted
    );
  };

  // Returns the next group/room that has uncounted items, or null if all done.
  const getNextRoomWithUncounted = () => {
    const groups = mobileTool ? mobileRooms : getAllGroupsOrdered();
    const currentRoomLabel = mobileTool ? mobileActiveRoom : selectedLocation;
    if (!currentRoomLabel || groups.length < 2) return null;
    const currentIdx = groups.findIndex(g => g.label === currentRoomLabel);
    if (currentIdx === -1) return null;
    for (let i = currentIdx + 1; i < groups.length; i++) {
      if (groups[i].uncountedCount > 0) return groups[i];
    }
    for (let i = 0; i < currentIdx; i++) {
      if (groups[i].uncountedCount > 0) return groups[i];
    }
    return null;
  };

  // Returns the groups/rooms that have no entries at all (every item uncounted).
  // Used by the Done flow: an untouched room is a strong hint the user meant
  // "done with this location", not "done with the whole count".
  const getUntouchedRooms = () => {
    const groups = mobileTool ? mobileRooms : getAllGroupsOrdered();
    return groups.filter((group) => group.count > 0 && group.uncountedCount === group.count);
  };

  // Jump the active selection to the given room/group, landing on its first
  // uncounted item (or its first item when all are counted).
  const advanceToRoom = (room) => {
    if (!room) return;
    if (mobileTool) setMobileActiveRoom(room.label);
    setSelectedLocation(room.label);
    const targetIdx = room.firstUncountedIndex >= 0 ? room.firstUncountedIndex : room.firstIndex;
    if (targetIdx >= 0) selectItem(targetIdx);
  };

  // "Advance to next room" action from the end-of-room modal.
  const handleEndOfRoomAdvance = () => {
    if (!endOfRoomModal) return;
    const { nextRoom } = endOfRoomModal;
    setEndOfRoomModal(null);
    if (!nextRoom) return;
    advanceToRoom(nextRoom);
  };

  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [editItemSaving, setEditItemSaving] = useState(false);
  const [applyFutureSaving, setApplyFutureSaving] = useState(false);
  const [editItemDraft, setEditItemDraft] = useState({
    description: '',
    vendor: '',
    category: '',
    unit: '',
    location: '',
    case_price: '',
    unit_price: '',
    par_level: '',
  });

  // Flash state for visual feedback
  const [flashPar, setFlashPar] = useState(false);
  // ── Focused PAR setup card ────────────────────────────────────────────────
  // This modal is now opened deliberately: either by the compact SET PAR flag
  // on a newly defaulted item, or by tapping a genuinely missing PAR. It never
  // interrupts count entry. `reviewingAutoDefault` distinguishes the durable
  // one-unit creation default from a legacy row that truly has no PAR.
  // null = closed; open = { itemName, countItemId, inventoryItemId,
  //                         reviewingAutoDefault }
  const [parWarningModal, setParWarningModal] = useState(null);
  const [parSetupSaving, setParSetupSaving] = useState(false);
  const [flashOrder, setFlashOrder] = useState(false);
  const [showOrderEditModal, setShowOrderEditModal] = useState(false);
  const [orderEditValue, setOrderEditValue] = useState('');

  // Edit mode state for session editing
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const closeAppModal = () => {
    const pending = modalPromiseRef.current;
    setAppModalConfig((current) => {
      if (current?.type === 'confirm' && pending.resolve && !pending.confirmed) {
        pending.resolve(false);
      }
      if (current?.type === 'alert' && typeof current.onAcknowledge === 'function') {
        try {
          current.onAcknowledge();
        } catch (error) {
          console.error('Error running modal acknowledge callback:', error);
        }
      }
      return null;
    });
    modalPromiseRef.current = { resolve: null, confirmed: false };
  };

  const handleAppModalConfirm = () => {
    const pending = modalPromiseRef.current;
    if (pending.resolve) {
      pending.confirmed = true;
      pending.resolve(true);
    }
    setAppModalConfig(null);
    modalPromiseRef.current = { resolve: null, confirmed: false };
  };

  const showAppAlert = ({ title = 'Notice', message, confirmButtonStyle = 'primary', onAcknowledge = null }) => {
    setAppModalConfig({
      type: 'alert',
      title,
      message,
      confirmButtonStyle,
      onAcknowledge
    });
  };

  const showAppConfirm = ({
    title = 'Please confirm',
    message,
    confirmText = 'Yes',
    cancelText = 'No',
    confirmButtonStyle = 'warning'
  }) => {
    return new Promise((resolve) => {
      modalPromiseRef.current = { resolve, confirmed: false };
      setAppModalConfig({
        type: 'confirm',
        title,
        message,
        confirmText,
        cancelText,
        confirmButtonStyle
      });
    });
  };

  // Function to enhance sessions with user information. Batches a single
  // user_profiles lookup for every non-current user referenced by any session
  // (created_by + last_accessed_by) so cards show real names instead of a
  // truncated UUID like "2bb7ed10…".
  const enhanceSessionsWithUserInfo = async (sessions) => {
    if (!sessions || sessions.length === 0) return sessions;

    try {
      const currentUserInfo = getStoredUser();
      const currentId = currentUserInfo?.id || null;

      // Look up every referenced user (including the current user) in
      // user_profiles — the localStorage blob is often stale and lacks
      // first/last name, so the DB row is authoritative.
      const allIds = [...new Set(
        sessions.flatMap(s => [s.created_by, s.last_accessed_by]).filter(Boolean)
      )];

      const nameMap = {};
      if (allIds.length > 0) {
        try {
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('id, display_name, first_name, last_name, email')
            .in('id', allIds);
          (profiles || []).forEach(p => {
            const compact = formatUserCompact(p);
            const display = (compact && compact !== 'Unknown') ? compact : (p.email || '').trim();
            // Only map ids that resolve to a real, human-readable label. A
            // readable-but-empty profile (no name, no email) is left unmapped so
            // resolveId below falls through to the "Countertop App" label rather
            // than storing a raw UUID.
            if (display) nameMap[p.id] = display;
          });
        } catch (_) {
          // Non-critical; fall back to the current-user / outside-user label below.
        }
      }

      // Records the super admin created/touched in a store they don't own show
      // as "Countertop App" instead of the admin's name (or a bare UUID).
      const adminOwnedStores = await fetchSuperAdminOwnedStoreIds(
        supabase,
        sessions.map(s => s.store_id)
      );

      // The signed-in user, resolved once for reuse across every session below.
      const currentUser = currentId
        ? { id: currentId, compactName: currentUserInfo?.compactName, displayName: currentUserInfo?.displayName }
        : null;

      return sessions.map(session => {
        // Resolve created_by / last_accessed_by to a real name, falling back to
        // the neutral "Countertop App" label for outside users (e.g. accounts
        // imported from the older app whose profile row isn't readable in this
        // store) instead of exposing a raw Supabase UUID like "19b9b3ea…".
        const resolveId = (id) => resolveCreatorLabel(id, {
          nameMap,
          currentUser,
          override: creatorOverrideLabel(id, session.store_id, adminOwnedStores),
        });

        return {
          ...session,
          created_by_name: resolveId(session.created_by) || 'Unknown',
          last_accessed_by_name: resolveId(session.last_accessed_by) || 'Not yet'
        };
      });
    } catch (error) {
      console.error('Error enhancing sessions with user info:', error);
      return sessions;
    }
  };

  // The count workflow owns the exact NEED — PAR minus what is on hand, in the
  // item's own count unit. The purchasing workflow applies vendor package, MOQ,
  // and increment rules after selecting an order-ready purchase option; nothing
  // on this screen may round a need toward a package minimum.
  const calculateNeedQuantity = (shortage) => {
    const need = Number(shortage);
    return Number.isFinite(need) ? Math.round(Math.max(0, need) * 1e6) / 1e6 : 0;
  };

  // Flash animation helpers
  const triggerFlash = (setFlashFn) => {
    setFlashFn(true);
    setTimeout(() => setFlashFn(false), 600);
  };

  // Load available count sessions OR specific session if sessionId provided
  useEffect(() => {
    if (startWithSession && sessionId) {
      // Load specific session directly and go to counting
      loadSpecificSession(sessionId);
    } else {
      // Load all sessions for selection
      loadCountSessions();
    }
  }, []);

  // Listen for refresh events to reload sessions
  useEffect(() => {
    const handleRefresh = (event) => {
      console.log('🔄 [CountInterface] Received refresh event, reloading sessions...', event.detail);
      // If the event identifies a specific session/template, evict only that
      // entry from the icon cache so the next scan repopulates it without
      // blowing away icons for every other card.
      const affectedId = event.detail?.sessionId ?? event.detail?.evictSessionId;
      if (affectedId != null) {
        setSessionCategoryIconCache(prev => {
          if (!prev[affectedId]) return prev;
          const next = { ...prev };
          delete next[affectedId];
          return next;
        });
        // loadCountSessions re-reads count_sessions only, and the count_items
        // prefetch below skips any template already in sessionItemsCache — so
        // without this the card's item/category/PAR metadata keeps describing
        // whatever the page saw at load time, however many edits later.
        void refreshTemplateItemsCache(affectedId);
      }
      loadCountSessions();
    };

    document.addEventListener('refreshCountInterface', handleRefresh);

    // Expose refresh function globally for direct access
    window.refreshCountSessions = loadCountSessions;
    console.log('🔄 [CountInterface] Exposed global refresh function');

    return () => {
      document.removeEventListener('refreshCountInterface', handleRefresh);
      delete window.refreshCountSessions;
    };
  }, []);

  // Keep the count_items cache honest about PAR edits made in the template
  // editor. The editor and this overview share one page — count.html only
  // toggles which row is display:none — so this component stays mounted with
  // the rows it fetched at page load. ItemLocationFolders announces each
  // successful per-item write; patching the matching rows in place is both
  // exact and cheaper than re-reading the whole template on every keystroke.
  useEffect(() => {
    const handleFieldsChanged = (event) => {
      const { sessionId: changedId, inventoryItemId, fields } = event.detail || {};
      if (changedId == null || inventoryItemId == null || !fields) return;
      const patch = {};
      CACHED_COUNT_ITEM_FIELDS.forEach(key => { if (key in fields) patch[key] = fields[key]; });
      if (Object.keys(patch).length === 0) return;
      setSessionItemsCache(prev => {
        const rows = prev[changedId];
        if (!rows) return prev;
        let matched = false;
        const next = rows.map(row => {
          if (String(row.inventory_item_id ?? '') !== String(inventoryItemId)) return row;
          matched = true;
          return { ...row, ...patch };
        });
        return matched ? { ...prev, [changedId]: next } : prev;
      });
    };
    document.addEventListener('countItemFieldsChanged', handleFieldsChanged);
    return () => document.removeEventListener('countItemFieldsChanged', handleFieldsChanged);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileOverview(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Category → emoji mapping lives in utils/categoryIcon.js (shared with the
  // template-offer success preview so both surfaces show the same icon).

  // Pre-fetch category icons for all sessions (including templates) whenever
  // sessions list changes. For template sessions, items are sourced from
  // count_items first (the lazy-materialized path) and fall back to
  // count_template_items + inventory_items for older group-editor templates.
  // Icons are updated incrementally per session so existing cards stay
  // visible while the async scan runs. Stale entries for sessions removed
  // from the list are evicted upfront; sessions with no recognized categories
  // are explicitly cleared once their scan finishes.
  useEffect(() => {
    if (!countSessions || countSessions.length === 0) return;

    // Evict icons for sessions that are no longer in the list so deleted /
    // archived cards don't leave ghost entries in the cache indefinitely.
    setSessionCategoryIconCache(prev => {
      const currentIds = new Set(countSessions.map(s => s.id));
      const next = {};
      Object.entries(prev).forEach(([id, icons]) => {
        if (currentIds.has(Number(id)) || currentIds.has(id)) next[id] = icons;
      });
      return next;
    });

    // Scan each session independently and update its cache slot as soon as
    // its query resolves — no full blank-out while waiting for all results.
    countSessions.forEach(async (session) => {
      try {
        let items = null;

        if (session.is_template) {
          // Primary: count_items seeded by the lazy-materialize path
          const { data: tmplItems, error: tmplErr } = await supabase
            .from('count_items')
            .select('item_snapshot')
            .eq('count_session_id', session.id)
            .limit(100);
          if (!tmplErr && tmplItems && tmplItems.length > 0) {
            items = tmplItems;
          } else if (session.template_id) {
            // Fallback: count_template_items (older group-editor flow)
            const { data: ctiRows, error: ctiErr } = await supabase
              .from('count_template_items')
              .select('inventory_item_id')
              .eq('template_id', session.template_id)
              .limit(100);
            if (!ctiErr && ctiRows && ctiRows.length > 0) {
              const invIds = ctiRows.map(r => r.inventory_item_id).filter(Boolean);
              if (invIds.length > 0) {
                const { data: invRows } = await supabase
                  .from('inventory_items')
                  .select('category')
                  .in('id', invIds);
                items = (invRows || []).map(inv => ({ item_snapshot: { category: inv.category } }));
              }
            }
          }
        } else {
          const { data, error } = await supabase
            .from('count_items')
            .select('item_snapshot')
            .eq('count_session_id', session.id)
            .limit(100);
          if (!error && data && data.length > 0) items = data;
        }

        if (!items || items.length === 0) {
          // Explicitly clear any stale icon entry for this session so a
          // category removal doesn't leave an old icon row showing.
          setSessionCategoryIconCache(prev => {
            if (!(session.id in prev)) return prev;
            const next = { ...prev };
            delete next[session.id];
            return next;
          });
          return;
        }

        const catCounts = {};
        items.forEach(i => {
          const snap = i.item_snapshot || {};
          const cat = snap.category || snap.item_category || i.category || '';
          if (cat) catCounts[cat] = (catCounts[cat] || 0) + 1;
        });
        const topIcons = [...new Set(
          Object.entries(catCounts).sort((a, b) => b[1] - a[1]).map(([cat]) => getCategoryIcon(cat)).filter(Boolean)
        )].slice(0, 5);

        setSessionCategoryIconCache(prev => {
          if (topIcons.length === 0) {
            if (!(session.id in prev)) return prev;
            const next = { ...prev };
            delete next[session.id];
            return next;
          }
          return { ...prev, [session.id]: topIcons };
        });
      } catch (e) {
        console.warn('Category icon scan failed for session', session.id, e);
      }
    });
  }, [countSessions]);

  // Prefetch Template count_items for ALL visible templates on load so the
  // stack meta panel (categories / vendors / item counts) shows real numbers
  // immediately — even for stacks the user hasn't expanded yet.
  //
  // This pass only FILLS empty cache slots; it never refreshes one it already
  // has. Anything that changes a template's items after load must say so —
  // refreshTemplateItemsCache for structural edits, the countItemFieldsChanged
  // patch for PAR — or the card will keep describing the page-load state.
  useEffect(() => {
    if (!countSessions || countSessions.length === 0) return;
    const templates = countSessions.filter(s => s.is_template && s.count_group_id);
    for (const tmpl of templates) {
      const tid = tmpl.id;
      if (sessionItemsCache[tid] || cardPreviewStatus[tid] === 'loading') continue;
      (async () => {
        setCardPreviewStatus(p => ({ ...p, [tid]: 'loading' }));
        try {
          const { data, error } = await supabase
            .from('count_items')
            .select(CACHED_COUNT_ITEM_SELECT)
            .eq('count_session_id', tid);
          if (error) { setCardPreviewStatus(p => ({ ...p, [tid]: 'error' })); return; }
          // Deduped for display: a template this bug exploded reported "1,000
          // items" (the server row cap) on its card. One row per item+location
          // is what the card's counts and category chips should describe.
          setSessionItemsCache(prev => ({ ...prev, [tid]: dedupeCountItemRows(data) }));
          setCardPreviewStatus(p => ({ ...p, [tid]: 'success' }));
        } catch {
          setCardPreviewStatus(p => ({ ...p, [tid]: 'error' }));
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countSessions]);

  // Strip trailing run/date suffix so a Template can carry the bare recurring name.
  // Kept in sync with getGroupedSessions' nameClean pattern.
  const stripRunSuffix = (name) =>
    (name || '').replace(/\s*[-–]\s*(Run\s*#?\d+|Copy\s*\d*|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec).*$/i, '').trim() || (name || '');

  // ── Synthetic (stand-in) template parent ───────────────────────────────────
  // A recurring series (children share a count_group_id) normally owns an
  // is_template "parent" row, lazily created by ensureTemplatesForGroups. When
  // that row is missing at render time — e.g. the async lazy-create hasn't
  // landed in this browser/session — the overview would otherwise fall back to a
  // flat single card, visually detaching the count from its template. To keep
  // the parent → child relationship consistent across browsers, we synthesize an
  // in-memory template parent from the newest child. It is display-only: it is
  // never written to the DB nor added to countSessions. The real row is
  // materialized on demand the moment the user acts on it (edit / setup) and by
  // ensureTemplatesForGroups on the next reload.
  const SYNTHETIC_TEMPLATE_ID_PREFIX = '__synthetic_template__';
  const makeSyntheticTemplateId = (groupId) => `${SYNTHETIC_TEMPLATE_ID_PREFIX}${groupId}`;
  const isSyntheticTemplate = (t) => !!t && t._synthesized === true;
  const buildSyntheticTemplate = (newestChild, groupId) => {
    if (!newestChild) return null;
    // Equal created/updated timestamps so the "Updated" badge never falsely
    // fires for a stand-in (it compares template.updated_at vs child created_at).
    const baseTs = newestChild.created_at || newestChild.session_date || null;
    return {
      id: makeSyntheticTemplateId(groupId),
      _synthesized: true,
      is_template: true,
      is_copy: false,
      store_id: newestChild.store_id ?? null,
      name: stripRunSuffix(newestChild.name),
      session_date: newestChild.session_date ?? null,
      status: 'draft',
      count_group_id: newestChild.count_group_id ?? groupId,
      template_id: newestChild.template_id ?? null,
      order_calculations_enabled: newestChild.order_calculations_enabled ?? null,
      direct_order_mode: newestChild.direct_order_mode ?? false,
      count_purpose: newestChild.count_purpose ?? null,
      inventory_mode: newestChild.inventory_mode ?? null,
      vendor_id: newestChild.vendor_id ?? null,
      ordering_cycle_config: newestChild.ordering_cycle_config ?? null,
      total_items: newestChild.total_items ?? null,
      created_at: baseTs,
      updated_at: baseTs,
      created_by: newestChild.created_by ?? null,
      // Inherit the child's already-resolved creator label (a real name, or the
      // "Countertop App" outside-user fallback) so the stand-in never surfaces a
      // raw Supabase UUID on its "Created By" line.
      created_by_name: newestChild.created_by_name ?? null,
    };
  };

  // Create the real is_template row for a recurring group on demand. Used when a
  // user acts on a synthesized stand-in (edit / setup) so the action operates on
  // a persisted template instead of the in-memory placeholder. Returns the real
  // row (existing or newly inserted), or null on failure. Mirrors
  // ensureTemplatesForGroups' insert shape and seeds items from the newest child.
  const materializeTemplateForGroup = async (groupId) => {
    if (!groupId) return null;
    const existing = countSessions.find(s => s.is_template && s.count_group_id === groupId);
    if (existing) return existing;
    const newest = countSessions
      .filter(s => !s.is_template && s.count_group_id === groupId)
      .sort((a, b) => new Date(b.created_at || b.session_date || 0) - new Date(a.created_at || a.session_date || 0))[0];
    if (!newest) return null;
    let storeId = null;
    let userId = null;
    try { storeId = Number(localStorage.getItem('selectedStoreId')) || null; } catch (_) {}
    try { userId = JSON.parse(localStorage.getItem('currentUser') || 'null')?.id ?? null; } catch (_) {}
    try {
      const { data: inserted, error } = await supabase
        .from('count_sessions')
        .insert({
          store_id: storeId,
          name: stripRunSuffix(newest.name),
          session_date: new Date().toISOString().split('T')[0],
          status: 'draft',
          template_id: newest.template_id ?? null,
          template_name_snapshot: newest.template_name_snapshot ?? null,
          template_type_snapshot: newest.template_type_snapshot ?? null,
          created_by: userId,
          count_group_id: groupId,
          is_template: true,
          is_copy: false,
          notes: 'Recurring count template',
          order_calculations_enabled: newest.order_calculations_enabled ?? null,
          direct_order_mode: newest.direct_order_mode ?? false,
          count_purpose: newest.count_purpose ?? null,
          inventory_mode: newest.inventory_mode ?? null,
          vendor_id: newest.vendor_id ?? null,
          ordering_cycle_config: newest.ordering_cycle_config ?? null,
        })
        .select()
        .single();
      if (error || !inserted) {
        console.warn('[Template] on-demand materialize failed:', error);
        return null;
      }
      // Make the real row available to subsequent renders (synth → real upgrade),
      // resolving created_by → a display name first so it never shows a raw UUID.
      const [enhancedInserted] = await enhanceSessionsWithUserInfo([inserted]);
      setCountSessions(prev => [...prev, enhancedInserted || inserted]);
      // Seed the new template's items from the newest child so the editor opens populated.
      try { await seedEmptyExistingTemplates([...countSessions, inserted]); } catch (_) {}
      return inserted;
    } catch (err) {
      console.warn('[Template] materializeTemplateForGroup threw:', err);
      return null;
    }
  };


  // Derive a human-readable recurrence from the session_date pattern of
  // a group's children. Pure heuristic — returns null if no signal.
  const deriveFrequency = (childSessions) => {
    const DOW_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const dates = (childSessions || [])
      .map(s => s.session_date)
      .filter(Boolean)
      .map(d => new Date(d + 'T00:00:00'))
      .filter(d => !Number.isNaN(d.getTime()))
      .sort((a, b) => a - b);
    if (dates.length === 0) return null;
    if (dates.length === 1) return 'One-time';
    const dows = dates.map(d => d.getDay());
    if (dows.every(d => d === dows[0])) return `Every ${DOW_NAMES[dows[0]]}`;
    const isLastDom = (d) => d.getDate() === new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    if (dates.every(isLastDom)) return 'Last day of the month';
    const doms = dates.map(d => d.getDate());
    if (doms.every(d => d === doms[0])) {
      const n = doms[0];
      const suf = (n === 1) ? 'st' : (n === 2) ? 'nd' : (n === 3) ? 'rd' : 'th';
      return `${n}${suf} of the month`;
    }
    const gaps = [];
    for (let i = 1; i < dates.length; i++) gaps.push(Math.round((dates[i] - dates[i-1]) / 86400000));
    const avg = Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length);
    if (avg > 0 && gaps.every(g => Math.abs(g - avg) <= 2)) return `Every ~${avg} day${avg === 1 ? '' : 's'}`;
    return 'Irregular';
  };

  // Lazy-materialize a Template parent row (is_template=true) for every group
  // that has a real count_group_id and doesn't already own one. Seed metadata
  // + item list from the newest child so the Template opens as the current
  // recurring recipe (not an empty shell). Legacy name-grouped sessions
  // (count_group_id IS NULL) are skipped on purpose.
  const ensureTemplatesForGroups = async (sessions, storeId, userId) => {
    if (!Array.isArray(sessions) || sessions.length === 0) return sessions;
    const byGroup = new Map();
    for (const s of sessions) {
      if (!s.count_group_id) continue;
      if (!byGroup.has(s.count_group_id)) byGroup.set(s.count_group_id, []);
      byGroup.get(s.count_group_id).push(s);
    }
    const toInsert = [];
    const seedByGroup = new Map(); // count_group_id -> newest child session (for item copy)
    const todayIso = new Date().toISOString().split('T')[0];
    for (const [groupId, members] of byGroup.entries()) {
      if (members.some(m => m.is_template)) continue;
      const newest = [...members].sort((a, b) =>
        new Date(b.created_at || b.session_date || 0) - new Date(a.created_at || a.session_date || 0))[0];
      if (!newest) continue;
      seedByGroup.set(groupId, newest);
      toInsert.push({
        store_id: storeId,
        name: stripRunSuffix(newest.name),
        session_date: todayIso,
        status: 'draft',
        template_id: newest.template_id ?? null,
        template_name_snapshot: newest.template_name_snapshot ?? null,
        template_type_snapshot: newest.template_type_snapshot ?? null,
        created_by: userId,
        count_group_id: groupId,
        is_template: true,
        is_copy: false,
        notes: 'Recurring count template',
        order_calculations_enabled: newest.order_calculations_enabled ?? null,
        direct_order_mode: newest.direct_order_mode ?? false,
        count_purpose: newest.count_purpose ?? null,
        inventory_mode: newest.inventory_mode ?? null,
        vendor_id: newest.vendor_id ?? null,
        ordering_cycle_config: newest.ordering_cycle_config ?? null,
      });
    }
    if (toInsert.length === 0) return sessions;
    try {
      const { data: inserted, error } = await supabase
        .from('count_sessions')
        .insert(toInsert)
        .select();
      if (error) {
        console.warn('[Template] lazy-create failed:', error);
        return sessions;
      }
      console.log(`🧩 [Template] Materialized ${inserted?.length || 0} template row(s) for existing groups`);

      // Seed each new template's count_items. Prefer expanding from the
      // count_template_items rows (which carry per-location pars) when the
      // seed session has a template_id. Fall back to copying the seed session's
      // count_items directly if no template rows are found (ad-hoc sessions).
      for (const tmpl of inserted || []) {
        const seed = seedByGroup.get(tmpl.count_group_id);
        if (!seed) continue;
        // Claim the template id so the back-fill pass (which runs right after
        // with this id in its list) cannot seed it a second time this load.
        if (!templateSeedRegistry.begin(tmpl.id)) continue;
        try {
          let templateItems = null;

          // Prefer template-derived expansion (preserves multi-location pars)
          const sourceTemplateId = seed.template_id ?? tmpl.template_id ?? null;
          if (sourceTemplateId) {
            const { data: ctiRows, error: ctiErr } = await fetchTemplateItemsWithPar(sourceTemplateId);

            if (!ctiErr && ctiRows && ctiRows.length > 0) {
              // Resolve effective locations via inventory fallback
              const invIds = [...new Set(ctiRows.map(r => r.inventory_item_id))];
              const { data: invItems } = await supabase
                .from('inventory_items')
                .select('*')
                .in('id', invIds);
              const invLookup = {};
              (invItems || []).forEach(it => { invLookup[it.id] = it; });

              // Dedupe by (inventory_item_id, effectiveLoc)
              const seen = new Set();
              const deduped = ctiRows.filter(row => {
                const inv = invLookup[row.inventory_item_id];
                const loc = row.location || inv?.location || null;
                const key = `${row.inventory_item_id}::${loc || ''}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              });

              templateItems = deduped.map((row, index) => {
                const inv = invLookup[row.inventory_item_id] || {};
                const effectiveLoc = row.location || inv.location || null;
                const copiedParDefaulted = row.par_defaulted ?? inv.par_defaulted ?? false;
                const copiedParUnit = copiedParDefaulted
                  ? (inv.unit || row.par_unit || 'each')
                  : (row.par_unit || inv.unit || 'each');
                return {
                  count_session_id: tmpl.id,
                  inventory_item_id: row.inventory_item_id,
                  item_snapshot: { ...inv, location: effectiveLoc, par_level: row.par_level ?? inv.par_level ?? null, par_unit: copiedParUnit, par_defaulted: copiedParDefaulted },
                  expected_quantity: '',
                  actual_quantity: '',
                  variance_quantity: 0,
                  count_notes: '',
                  is_counted: false,
                  counted_at: null,
                  par_level: row.par_level ?? null,
                  par_unit: copiedParUnit,
                  par_defaulted: copiedParDefaulted,
                  // The durable PAR travels with the legacy pair. Without it
                  // "1 case" arrives with no package and resolves to nothing.
                  ...parNormalizationFrom(row),
                  // min_order_qty / min_order_unit are deliberately NOT seeded
                  // from inventory_items.moq: a vendor minimum is not an item
                  // fact, and copying one onto a count row is how a single
                  // supplier's minimum came to look like the item's own.
                  location: effectiveLoc,
                  count_position: (index + 1) * 10,
                };
              });
            }
          }

          // Fallback: copy seed session's count_items (ad-hoc sessions).
          // Paginated + deduped: the seed may be a session the duplication bug
          // exploded before the fix, and a capped verbatim copy would carry an
          // arbitrary 1,000 of its clones into the new template.
          if (!templateItems) {
            const { rows: seedRows, error: seedErr } = await fetchAllCountItems(
              supabase,
              seed.id,
              'id, inventory_item_id, item_snapshot, par_level, par_unit, par_defaulted, min_order_qty, min_order_unit, location, count_position, count_notes',
            );
            const seedItems = dedupeCountItemRows(seedRows);
            if (seedErr || seedItems.length === 0) { templateSeedRegistry.fail(tmpl.id); continue; }
            templateItems = seedItems.map((item, index) => ({
              count_session_id: tmpl.id,
              inventory_item_id: item.inventory_item_id,
              item_snapshot: item.item_snapshot,
              expected_quantity: '',
              actual_quantity: '',
              variance_quantity: 0,
              count_notes: item.count_notes || '',
              is_counted: false,
              counted_at: null,
              par_level: item.par_level ?? null,
              par_unit: item.par_defaulted === true
                ? (item.item_snapshot?.unit || item.par_unit || 'each')
                : (item.par_unit || item.item_snapshot?.unit || 'each'),
              par_defaulted: item.par_defaulted
                ?? item.item_snapshot?.par_defaulted
                ?? false,
              min_order_qty: item.min_order_qty ?? null,
              min_order_unit: item.min_order_unit || 'each',
              location: item.location || item.item_snapshot?.location || null,
              count_position: item.count_position ?? (index + 1) * 10,
            }));
          }

          // The template was inserted moments ago and should be empty — but a
          // second tab or device may have seeded it between our insert and now.
          const recheck = await isSessionEmpty(supabase, tmpl.id);
          if (!recheck.empty) {
            templateSeedRegistry.finish(tmpl.id);
            continue;
          }

          const { error: insErr } = await supabase.from('count_items').insert(templateItems);
          if (insErr) {
            console.warn('[Template] seed items insert failed for', tmpl.id, insErr);
            templateSeedRegistry.fail(tmpl.id);
          } else {
            templateSeedRegistry.finish(tmpl.id);
          }
        } catch (seedCopyErr) {
          console.warn('[Template] seed items copy threw for', tmpl.id, seedCopyErr);
          templateSeedRegistry.fail(tmpl.id);
        }
      }

      // Resolve created_by → a display name on the freshly-inserted template
      // rows (they were created by the current user) so their "Created By" line
      // shows a real name rather than "Unknown" or a raw UUID.
      const enhancedInserted = await enhanceSessionsWithUserInfo(inserted || []);
      return [...sessions, ...enhancedInserted];
    } catch (err) {
      console.warn('[Template] lazy-create threw:', err);
      return sessions;
    }
  };

  // Back-fill item lists for existing Template rows that were created empty
  // (before the seed-on-create logic landed). Picks the newest non-template
  // sibling's items and copies them with counts blanked.
  //
  // THE RUNAWAY THIS REPLACED: emptiness used to be decided by fetching raw
  // count_items rows across ALL template sessions in one query. PostgREST
  // silently caps that read at 1,000 rows, so once a store's template sessions
  // collectively held more, whichever templates' rows fell past the cap were
  // judged empty and re-seeded with their full item list — on every visit to
  // this screen, growing the table, pushing more templates past the cap the
  // next time. One store accumulated 1.97M junk rows this way. Emptiness is
  // now a per-session head COUNT (immune to the cap), each template is seeded
  // at most once per page load (templateSeedRegistry), and emptiness is
  // re-checked immediately before the insert so two concurrent loads cannot
  // both seed the same template.
  const seedEmptyExistingTemplates = async (sessions) => {
    if (!Array.isArray(sessions) || sessions.length === 0) return;
    const templates = sessions.filter(s => s.is_template && s.count_group_id);
    if (templates.length === 0) return;
    try {
      const emptyTemplates = await findEmptyTemplateSessions(supabase, templates);
      if (emptyTemplates.length === 0) return;
      for (const tmpl of emptyTemplates) {
        // Claim before any async work: a second caller (double-mount, the
        // create-path call site) is refused even mid-flight.
        if (!templateSeedRegistry.begin(tmpl.id)) continue;
        const siblings = sessions
          .filter(s => !s.is_template && s.count_group_id === tmpl.count_group_id)
          .sort((a, b) => new Date(b.created_at || b.session_date || 0) - new Date(a.created_at || a.session_date || 0));
        const seed = siblings[0];
        // No sibling to copy from yet — release the claim so a later load can
        // seed once one exists.
        if (!seed) { templateSeedRegistry.fail(tmpl.id); continue; }
        try {
          let templateItems = null;

          // Prefer expanding from count_template_items to preserve multi-location pars
          const sourceTemplateId = seed.template_id ?? tmpl.template_id ?? null;
          if (sourceTemplateId) {
            const { data: ctiRows, error: ctiErr } = await fetchTemplateItemsWithPar(sourceTemplateId);

            if (!ctiErr && ctiRows && ctiRows.length > 0) {
              const invIds = [...new Set(ctiRows.map(r => r.inventory_item_id))];
              const { data: invItems } = await supabase
                .from('inventory_items')
                .select('*')
                .in('id', invIds);
              const invLookup = {};
              (invItems || []).forEach(it => { invLookup[it.id] = it; });

              const seen = new Set();
              const deduped = ctiRows.filter(row => {
                const loc = row.location || invLookup[row.inventory_item_id]?.location || null;
                const key = `${row.inventory_item_id}::${loc || ''}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              });

              templateItems = deduped.map((row, index) => {
                const inv = invLookup[row.inventory_item_id] || {};
                const effectiveLoc = row.location || inv.location || null;
                const copiedParDefaulted = row.par_defaulted ?? inv.par_defaulted ?? false;
                const copiedParUnit = copiedParDefaulted
                  ? (inv.unit || row.par_unit || 'each')
                  : (row.par_unit || inv.unit || 'each');
                return {
                  count_session_id: tmpl.id,
                  inventory_item_id: row.inventory_item_id,
                  item_snapshot: { ...inv, location: effectiveLoc, par_level: row.par_level ?? inv.par_level ?? null, par_unit: copiedParUnit, par_defaulted: copiedParDefaulted },
                  expected_quantity: '',
                  actual_quantity: '',
                  variance_quantity: 0,
                  count_notes: '',
                  is_counted: false,
                  counted_at: null,
                  par_level: row.par_level ?? null,
                  par_unit: copiedParUnit,
                  par_defaulted: copiedParDefaulted,
                  // The durable PAR travels with the legacy pair. Without it
                  // "1 case" arrives with no package and resolves to nothing.
                  ...parNormalizationFrom(row),
                  // min_order_qty / min_order_unit are deliberately NOT seeded
                  // from inventory_items.moq: a vendor minimum is not an item
                  // fact, and copying one onto a count row is how a single
                  // supplier's minimum came to look like the item's own.
                  location: effectiveLoc,
                  count_position: (index + 1) * 10,
                };
              });
            }
          }

          // Fallback: copy seed session's count_items (ad-hoc sessions).
          // Paginated read + dedupe: the seed sibling may itself be a session
          // this bug exploded before the fix — copying it verbatim would carry
          // hundreds of clones (and a capped read would carry an arbitrary
          // 1,000 of them) into the template. Deduping here makes seeding from
          // corrupted history self-healing instead of contagious.
          if (!templateItems) {
            const { rows: seedRows, error: seedErr } = await fetchAllCountItems(
              supabase,
              seed.id,
              'id, inventory_item_id, item_snapshot, par_level, par_unit, par_defaulted, min_order_qty, min_order_unit, location, count_position, count_notes',
            );
            const seedItems = dedupeCountItemRows(seedRows);
            if (seedErr || seedItems.length === 0) { templateSeedRegistry.fail(tmpl.id); continue; }
            templateItems = seedItems.map((item, index) => ({
              count_session_id: tmpl.id,
              inventory_item_id: item.inventory_item_id,
              item_snapshot: item.item_snapshot,
              expected_quantity: '',
              actual_quantity: '',
              variance_quantity: 0,
              count_notes: item.count_notes || '',
              is_counted: false,
              counted_at: null,
              par_level: item.par_level ?? null,
              par_unit: item.par_defaulted === true
                ? (item.item_snapshot?.unit || item.par_unit || 'each')
                : (item.par_unit || item.item_snapshot?.unit || 'each'),
              par_defaulted: item.par_defaulted
                ?? item.item_snapshot?.par_defaulted
                ?? false,
              min_order_qty: item.min_order_qty ?? null,
              min_order_unit: item.min_order_unit || 'each',
              location: item.location || item.item_snapshot?.location || null,
              count_position: item.count_position ?? (index + 1) * 10,
            }));
          }

          // Emptiness can have changed since the screen loaded — another tab,
          // another device, or the concurrent create-path call. Re-check at
          // the last moment; inserting into a non-empty template is exactly
          // the duplication this function used to cause.
          const recheck = await isSessionEmpty(supabase, tmpl.id);
          if (!recheck.empty) {
            templateSeedRegistry.finish(tmpl.id);
            continue;
          }

          const { error: insErr } = await supabase.from('count_items').insert(templateItems);
          if (insErr) {
            console.warn('[Template] back-fill insert failed for', tmpl.id, insErr);
            templateSeedRegistry.fail(tmpl.id);
          } else {
            templateSeedRegistry.finish(tmpl.id);
            console.log(`🧩 [Template] Back-filled ${templateItems.length} items into empty template ${tmpl.id}`);
          }
        } catch (e) {
          console.warn('[Template] back-fill threw for', tmpl.id, e);
          templateSeedRegistry.fail(tmpl.id);
        }
      }
    } catch (err) {
      console.warn('[Template] seedEmptyExistingTemplates threw:', err);
    }
  };

  const loadCountSessions = async () => {
    setLoading(true);
    try {
      // Get current user and store - try Supabase auth first, fallback to localStorage
      let user = null;
      let storeId = null;
      
      // Skip hanging Supabase auth calls, use localStorage directly
      console.log('📋 [CountInterface] Getting user and store from localStorage...');
      const storedUser = localStorage.getItem('currentUser');
      const storedStoreId = localStorage.getItem('selectedStoreId');

      if (storedUser && storedStoreId) {
        user = JSON.parse(storedUser);
        storeId = parseInt(storedStoreId);
        console.log('✅ [CountInterface] Found user in localStorage:', user.email, 'store:', storeId);
      } else {
        console.error('❌ [CountInterface] No user found in localStorage');
        setLoading(false);
        return;
      }

      // Load count sessions for this store - USE DIRECT FETCH
      console.log('🔍 [DB-CALL-3] Starting count_sessions query for CountInterface, store:', storeId);

      // Fire off the store's inventory_period_config in parallel so legacy
      // templates (created before ordering_cycle_config was stamped on the
      // template row) can still render the schedule label they inherit from
      // store setup.
      supabase
        .from('stores')
        .select('inventory_period_config')
        .eq('id', storeId)
        .single()
        .then(({ data: storeRow, error: storeErr }) => {
          if (storeErr) {
            console.warn('[CountInterface] Could not load store inventory_period_config:', storeErr.message);
            return;
          }
          setStorePeriodConfig(storeRow?.inventory_period_config || null);
        })
        .catch((err) => console.warn('[CountInterface] store inventory_period_config fetch threw:', err));

      const { data, error } = await queryCountSessionsActive(storeId);

      if (error) {
        console.error('Error loading count sessions:', error);
        return;
      }

      console.log(`✅ [CountInterface] Sessions loaded: ${data?.length || 0} sessions`);
      console.log('🔍 [CountInterface] Sample session:', data?.[0]);
      console.log('🔍 [CountInterface] Sessions with is_copy=true:', data?.filter(s => s.is_copy)?.length || 0);
      console.log('🔍 [CountInterface] Session statuses:', data?.map(s => s.status).filter((v, i, a) => a.indexOf(v) === i));
      console.log('🔍 [CountInterface] Store IDs:', data?.map(s => s.store_id).filter((v, i, a) => a.indexOf(v) === i));

      // Enhance sessions with user information
      const enhancedSessions = await enhanceSessionsWithUserInfo(data || []);

      // Lazy-materialize Template parent rows for any group missing one.
      // One-time cost per group; subsequent loads find the template and skip.
      const storedUser2 = localStorage.getItem('currentUser');
      const userId2 = storedUser2 ? JSON.parse(storedUser2).id : null;
      const withTemplates = await ensureTemplatesForGroups(enhancedSessions, storeId, userId2);
      setCountSessions(withTemplates);
      // Fire-and-forget: back-fill items for any pre-existing empty template rows.
      seedEmptyExistingTemplates(withTemplates).catch(() => {});

      // Auto-expand any stack that has an active (draft/in_progress) child so
      // the user can see the next countable run without clicking to expand.
      try {
        const activeGroupIds = new Set();
        for (const s of withTemplates) {
          if (s.is_template) continue;
          if (!s.count_group_id) continue;
          if (s.status === 'draft' || s.status === 'in_progress' || s.status === 'paused') activeGroupIds.add(s.count_group_id);
        }
        if (activeGroupIds.size > 0) {
          setExpandedStacks(prev => {
            const next = new Set(prev);
            for (const id of activeGroupIds) next.add(id);
            return next;
          });
        }
      } catch (e) {
        console.warn('[CountInterface] auto-expand stacks failed:', e);
      }

      // Skip loading session items upfront - load on-demand for better performance
      console.log('✅ [CountInterface] Sessions loaded, tooltip data will load on hover');
    } catch (error) {
      console.error('Error loading count sessions:', error);
    } finally {
      setLoading(false);
      // Every exit lands here, including the no-user-in-localStorage early
      // return and a failed query — the overview must never keep shimmering
      // over an answer it already has, however empty or broken that answer is.
      setSessionsLoaded(true);
    }
  };

  // Load specific session by ID for direct count session page
  const loadSpecificSession = async (sessionId) => {
    // ── Offline gate: block switching to a different session with unsynced work ──
    // Includes failed items — a sync failure is still unsynced work.
    if (mobileTool && selectedSession && selectedSession.id !== sessionId) {
      const sessionSyncStatus = offlineSync.getSessionSyncStatus();
      if (
        sessionSyncStatus === 'pending' ||
        sessionSyncStatus === 'syncing' ||
        sessionSyncStatus === 'failed'
      ) {
        showAppAlert({
          title: 'Unsynced changes',
          message:
            'This count has changes saved on this phone that have not synced yet. Connect to the internet before starting another count.',
          confirmButtonStyle: 'warning',
        });
        return;
      }
    }

    setLoading(true);
    try {
      console.log('📋 [CountInterface] Getting user and store from localStorage for specific session...');
      const storedUser = localStorage.getItem('currentUser');
      const storedStoreId = localStorage.getItem('selectedStoreId');

      if (!storedUser || !storedStoreId) {
        console.error('❌ [CountInterface] No user found in localStorage');
        setLoading(false);
        return;
      }

      const user = JSON.parse(storedUser);
      const storeId = parseInt(storedStoreId);
      console.log('✅ [CountInterface] Found user in localStorage:', user.email, 'store:', storeId);

      // Load the specific session from Supabase; fall back to IDB draft if offline
      let session = null;
      try {
        const { data, error: sessionError } = await supabase
          .from('count_sessions')
          .select('*')
          .eq('id', sessionId)
          .eq('store_id', storeId)
          .single();
        if (sessionError) throw sessionError;
        session = data;
      } catch (fetchErr) {
        if (mobileTool) {
          // If offline, try to restore from the locally saved session draft
          const draft = await offlineSync.loadSessionDraft(sessionId);
          if (draft && draft.items && draft.items.length > 0) {
            console.warn('[OfflineSync] Supabase unreachable — restoring session from local draft');
            // Build a minimal session stub from the draft (fields needed by counting UI)
            const stubSession = { id: sessionId, store_id: storeId, status: 'active', _fromDraft: true };
            setSelectedSession(stubSession);
            setCountItems(draft.items);
            sortItems(draft.items, sortBy);
            // Restore snapshot context so queueChange uses correct timestamps
            // (prevents epoch-fallback from marking all subsequent changes as sync_problem)
            if (draft.snapshotId) setSnapshotId(draft.snapshotId);
            if (draft.snapshotTimestamp) setSnapshotTimestamp(draft.snapshotTimestamp);
            setStep('counting');
            setLoading(false);
            return;
          }
        }
        console.error('Error loading session:', fetchErr);
        return;
      }

      if (!session) {
        console.error('Session not found or not accessible');
        return;
      }

      console.log('✅ Loaded session:', session);
      setSelectedSession(session);

      // Load count items for this session — pass session directly so
      // order_calculations_enabled is available without a join query
      await loadCountItems(sessionId, session);

    } catch (error) {
      console.error('Error loading specific session:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Restore offline Done modal on session reopen ──────────────────────────
  // If the user pressed Done while offline and then closed the app, the
  // session has a pending `__completion` record in IDB. When they reopen,
  // we land them on the count screen but immediately re-show the appropriate
  // offline modal so they can't bypass the block — and so the count is
  // preserved as already-completed-on-device until sync runs.
  // Every session restores the same local-save modal. Order creation waits for
  // the completed screen after connectivity returns.
  useEffect(() => {
    if (!mobileTool || !selectedSession?.id || !offlineSync.getPendingCompletion) return;
    let cancelled = false;
    (async () => {
      try {
        const pending = await offlineSync.getPendingCompletion(selectedSession.id);
        if (cancelled || !pending) return;
        setCompleteFlowStep((prev) => (prev ? prev : 'offline-blocked'));
        setOfflineBlockedNoInternet(false);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [mobileTool, selectedSession?.id, offlineSync.getPendingCompletion]);

  // Note: Tooltip items are now loaded on-demand in handleTooltipMouseEnter

  // Filter and sort sessions (simplified for now)
  const getFilteredAndSortedSessions = () => {
    // Template parent rows are rendered inside their group card, never as standalone entries.
    let filtered = [...countSessions].filter(s => !s.is_template);
    console.log('🔍 [FILTER] Starting with sessions:', filtered.length);
    console.log('🔍 [FILTER] Filter settings:', { filterByOrder, filterByStatus, filterByStack, searchText, sessionSortBy });

    // Apply order filter
    if (filterByOrder !== 'all') {
      const beforeCount = filtered.length;
      filtered = filtered.filter(session => {
        if (filterByOrder === 'order') {
          return session.order_calculations_enabled === true;
        } else if (filterByOrder === 'no-order') {
          return session.order_calculations_enabled !== true;
        }
        return true;
      });
      console.log(`🔍 [FILTER] After order filter (${filterByOrder}): ${beforeCount} → ${filtered.length}`);
    }

    // Apply status filter
    if (filterByStatus !== 'all') {
      const beforeCount = filtered.length;
      filtered = filtered.filter(session => {
        const status = session.status || 'draft';
        return status === filterByStatus;
      });
      console.log(`🔍 [FILTER] After status filter (${filterByStatus}): ${beforeCount} → ${filtered.length}`);
    }

    // Apply text search
    if (searchText.trim()) {
      const beforeCount = filtered.length;
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(session =>
        (session.name || '').toLowerCase().includes(searchLower)
      );
      console.log(`🔍 [FILTER] After text search (${searchText}): ${beforeCount} → ${filtered.length}`);
    }

    console.log('🔍 [FILTER] Final filtered sessions:', filtered.length);
    console.log('🔍 [FILTER] Sample filtered session:', filtered[0]);

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sessionSortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'created':
          return new Date(b.created_at || b.session_date) - new Date(a.created_at || a.session_date);
        case 'accessed':
          return new Date(b.last_accessed_at || b.updated_at || b.created_at) - new Date(a.last_accessed_at || a.updated_at || a.created_at);
        default:
          return (a.name || '').localeCompare(b.name || '');
      }
    });

    return filtered;
  };

  // Group sessions by count_group_id for stacking.
  // Falls back to name-based grouping when count_group_id is null (e.g. column not yet backfilled).
  const getGroupedSessions = () => {
    const sessions = [...countSessions];
    const grouped = {};
    const nameClean = (name) =>
      (name || '').replace(/\s*[-–]\s*(Run\s*#?\d+|Copy\s*\d*|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec).*$/i, '').trim().toLowerCase();

    // Pass 1: build name → groupId map from sessions that already have count_group_id
    const nameToGroupId = {};
    sessions.forEach(session => {
      if (session.count_group_id) {
        const cn = nameClean(session.name);
        if (cn && !nameToGroupId[cn]) nameToGroupId[cn] = session.count_group_id;
      }
    });

    // Pass 2: assign each session to a group
    const nameToFirstId = {};
    sessions.forEach(session => {
      let groupId;
      if (session.count_group_id) {
        groupId = session.count_group_id;
      } else {
        const cn = nameClean(session.name);
        if (cn && nameToGroupId[cn]) {
          groupId = nameToGroupId[cn];
        } else if (cn) {
          if (!nameToFirstId[cn]) nameToFirstId[cn] = session.id;
          groupId = nameToFirstId[cn];
        } else {
          groupId = session.id;
        }
      }
      if (!grouped[groupId]) grouped[groupId] = [];
      grouped[groupId].push(session);
    });

    // Split each bucket into { template, children } and sort children newest-first.
    // Templates are recurring definitions (is_template=true); children are dated count runs.
    const partitioned = {};
    Object.keys(grouped).forEach(groupId => {
      let template = grouped[groupId].find(s => s.is_template) || null;
      const children = grouped[groupId]
        .filter(s => !s.is_template)
        .sort((a, b) => new Date(b.created_at || b.session_date) - new Date(a.created_at || a.session_date));
      // When a recurring series (children share a real count_group_id) has no
      // materialized template row yet, synthesize a display-only parent so the
      // count still nests under its template — consistently across browsers,
      // not dependent on the async lazy-create having landed this session.
      // Ad-hoc single counts (no count_group_id) keep their standalone card.
      if (!template && children.length > 0 && children[0].count_group_id) {
        template = buildSyntheticTemplate(children[0], children[0].count_group_id);
      }
      partitioned[groupId] = { template, children };
    });

    // Apply group-level filters using the representative (template if present, else newest child).
    const filteredGrouped = {};
    Object.keys(partitioned).forEach(groupId => {
      const { template, children } = partitioned[groupId];
      if (children.length === 0 && !template) return;
      // Template-only groups (no children yet) are shown so the user can
      // click "Start" to materialize the first run from the recurring recipe.
      const rep = template || children[0];

      // Order filter
      if (filterByOrder !== 'all') {
        if (filterByOrder === 'order' && rep.order_calculations_enabled !== true) return;
        if (filterByOrder === 'no-order' && rep.order_calculations_enabled === true) return;
      }

      // Status filter — evaluate against newest child so Template parents don't
      // trigger "draft" status filtering just because their placeholder status is 'draft'.
      if (filterByStatus !== 'all') {
        const repForStatus = children[0] || rep;
        const status = repForStatus.status || 'draft';
        if (status !== filterByStatus) return;
      }

      // Text search (by representative name — Template name is the recurring name)
      if (searchText.trim()) {
        const searchLower = searchText.toLowerCase();
        const name = (rep.name || '').toLowerCase();
        if (!name.includes(searchLower)) return;
      }

      // Stack filter — "stacked" means more than one child session under a parent.
      if (filterByStack === 'stacked' && children.length <= 1) return;
      if (filterByStack === 'single' && children.length > 1) return;

      filteredGrouped[groupId] = { template, children };
    });

    return filteredGrouped;
  };

  // Toggle the per-template "Show more runs" section that reveals children
  // beyond the default top 2. Independent of the metadata chevron above.
  const toggleStackChildrenExpansion = (groupId) => {
    setExpandedStackChildren(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  // Toggle stack expansion
  const toggleStackExpansion = (groupId) => {
    const newExpanded = new Set(expandedStacks);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedStacks(newExpanded);
  };

  const openStackReport = async (groupId, sessionsInGroup) => {
    setStackReportGroupId(groupId);
    setStackReportData(null);
    setStackReportLoading(true);
    try {
      const sessionIds = sessionsInGroup.map(s => s.id);
      const { data: allItems, error } = await supabase
        .from('count_items')
        .select('count_session_id, inventory_item_id, item_snapshot, actual_quantity, is_counted, location')
        .in('count_session_id', sessionIds);
      if (error) throw error;

      // Group items by inventory_item_id, build a map: itemKey -> { name, rows: { sessionId -> qty } }
      const itemMap = {};
      (allItems || []).forEach(item => {
        const itemId = item.inventory_item_id;
        const name = (item.item_snapshot && (item.item_snapshot.name || item.item_snapshot.item_name)) || `Item #${itemId}`;
        const key = `${itemId}`;
        if (!itemMap[key]) {
          itemMap[key] = { name, location: item.location || '', rows: {} };
        }
        itemMap[key].rows[item.count_session_id] = item.is_counted ? (item.actual_quantity ?? '—') : '—';
      });

      setStackReportData({ sessions: sessionsInGroup, itemMap });
    } catch (err) {
      console.error('Stack report error:', err);
    } finally {
      setStackReportLoading(false);
    }
  };

  const closeStackReport = () => {
    setStackReportGroupId(null);
    setStackReportData(null);
  };

  // Custom tooltip handlers
  const handleTooltipMouseEnter = async (event, session) => {
    // Clear any existing hide timeout
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }

    // Clear any existing hover timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }

    // Capture the position immediately to avoid null reference
    if (!event || !event.currentTarget) {
      console.warn('🚨 [TOOLTIP] Invalid event or currentTarget');
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();

    // Check if we already have data for this session
    let sessionItems = sessionItemsCache[session.id] || [];
    const totalItems = session.total_items || 0;

    console.log('🔍 [TOOLTIP] Session:', session.id, 'Items in cache:', sessionItems.length, 'Total items:', totalItems);

    // If no cached data, load it on-demand
    if (sessionItems.length === 0 && totalItems > 0) {
      console.log('🔄 [TOOLTIP] Loading items on-demand for session:', session.id);
      try {
        const { data: items, error } = await supabase
          .from('count_items')
          .select('*')
          .eq('count_session_id', session.id)
          .order('id');

        console.log('🔍 [TOOLTIP] Database query result:', { items, error, itemsLength: items?.length });

        if (error) {
          console.error('❌ [TOOLTIP] Database query error:', error);
        }

        if (!error && items && items.length > 0) {
          // Apply the same category-based selection logic
          const itemsByCategory = {};
          items.forEach(item => {
            const category = item.item_snapshot?.category || 'Uncategorized';
            if (!itemsByCategory[category]) {
              itemsByCategory[category] = [];
            }
            itemsByCategory[category].push(item);
          });

          const categories = Object.keys(itemsByCategory);
          const selectedItems = [];

          if (categories.length === 0) {
            selectedItems.push(...items.slice(0, 6));
          } else if (categories.length <= 6) {
            categories.forEach(category => {
              if (selectedItems.length < 6) {
                selectedItems.push(itemsByCategory[category][0]);
              }
            });
            while (selectedItems.length < 6) {
              let added = false;
              for (const category of categories) {
                if (selectedItems.length >= 6) break;
                const categoryItems = itemsByCategory[category];
                const unselectedItems = categoryItems.filter(item => !selectedItems.includes(item));
                if (unselectedItems.length > 0) {
                  selectedItems.push(unselectedItems[Math.floor(Math.random() * unselectedItems.length)]);
                  added = true;
                }
              }
              if (!added) break;
            }
          } else {
            const shuffledCategories = categories.sort(() => 0.5 - Math.random()).slice(0, 6);
            shuffledCategories.forEach(category => {
              const categoryItems = itemsByCategory[category];
              selectedItems.push(categoryItems[Math.floor(Math.random() * categoryItems.length)]);
            });
          }

          // Update cache
          const newCache = { ...sessionItemsCache };
          newCache[session.id] = selectedItems;
          setSessionItemsCache(newCache);
          sessionItems = selectedItems;

          console.log(`✅ [TOOLTIP] Loaded ${items.length} items from ${categories.length} categories, selected ${selectedItems.length} for tooltip`);
          console.log('🔍 [TOOLTIP] Sample item structure:', selectedItems[0]);
          console.log('🔍 [TOOLTIP] Sample item_snapshot:', selectedItems[0]?.item_snapshot);
        } else {
          console.log(`⚠️ [TOOLTIP] No items found for session ${session.id}. Expected ${totalItems} items but got ${items?.length || 0}`);

          // Let's also check if there are any count_items for this session with a different query
          console.log('🔍 [TOOLTIP] Checking count_items table for session:', session.id);
          const { data: allItems, error: checkError } = await supabase
            .from('count_items')
            .select('id, count_session_id, inventory_item_id, item_snapshot')
            .eq('count_session_id', session.id);

          console.log('🔍 [TOOLTIP] All count_items check:', { allItems, checkError, count: allItems?.length });
        }
      } catch (error) {
        console.error(`❌ [TOOLTIP] Error loading items for session ${session.id}:`, error);
      }
    } else if (sessionItems.length === 0) {
      console.log(`⚠️ [TOOLTIP] Session ${session.id} has no total_items (${totalItems}), skipping item load`);
    }

    // Set new hover timeout
    const newHoverTimeout = setTimeout(() => {

      // Show tooltip even if no items (for debugging)
      const content = sessionItems.length > 0 ? (
        <div style={{
          backgroundColor: '#FEFCFA',
          color: '#5A4A42',
          border: '1px solid #D4C4B0',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(90, 74, 66, 0.15)',
          fontFamily: "'Open Sans', sans-serif",
          fontSize: '0.8rem',
          minWidth: '200px',
          maxWidth: '300px',
          textAlign: 'left'
        }}>
          <div style={{
            fontWeight: '600',
            marginBottom: '8px',
            fontSize: '0.85rem',
            color: '#5A4A42',
            borderBottom: '1px solid #E8E0D8',
            paddingBottom: '4px'
          }}>
            Some Items from Count
          </div>
          <div style={{
            fontSize: '0.75rem',
            color: '#5A4A42'
          }}>
            {sessionItems.map((item, index) => {
              const snapshot = item.item_snapshot || {};
              const itemName = snapshot.description || snapshot.name || item.description || item.name || 'Unknown Item';
              console.log(`🔍 [TOOLTIP] Item ${index}:`, { snapshot, itemName, item });

              return (
                <div key={index} style={{ marginBottom: '3px', lineHeight: '1.3' }}>
                  • {itemName}
                  {snapshot.brand && (
                    <span style={{ color: '#8A7A6B', fontSize: '0.7rem', fontWeight: 'bold' }}>
                      {' '}- {snapshot.brand}
                    </span>
                  )}
                  {snapshot.category && (
                    <span style={{ color: '#8A7A6B', fontSize: '0.7rem', fontStyle: 'italic' }}>
                      {' '}[{snapshot.category}]
                    </span>
                  )}
                  {snapshot.location && (
                    <span style={{ color: '#8A7A6B', fontSize: '0.7rem' }}>
                      {' '}({snapshot.location})
                    </span>
                  )}
                </div>
              );
            })}
            {totalItems > sessionItems.length && (
              <div style={{
                fontStyle: 'italic',
                color: '#8A7A6B',
                marginTop: '6px',
                textAlign: 'center',
                fontSize: '0.7rem'
              }}>
                ...and {totalItems - sessionItems.length} more items
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{
          backgroundColor: '#FEFCFA',
          color: '#5A4A42',
          border: '1px solid #D4C4B0',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(90, 74, 66, 0.15)',
          fontFamily: "'Open Sans', sans-serif",
          fontSize: '0.8rem',
          minWidth: '200px',
          textAlign: 'left'
        }}>
          <div style={{
            fontWeight: '600',
            fontSize: '0.85rem',
            color: '#5A4A42',
            textAlign: 'center'
          }}>
            Empty
          </div>
          <div style={{
            fontSize: '0.75rem',
            textAlign: 'center',
            color: '#8A7A6B',
            marginTop: '4px'
          }}>
            {totalItems > 0
              ? `Data issue: Shows ${totalItems} items but none found`
              : 'No items in this session'
            }
          </div>
        </div>
      );

      const tooltipX = rect.left + rect.width / 2;
      const tooltipY = rect.top - 10;

      setTooltipData({
        show: true,
        content: content,
        x: tooltipX,
        y: tooltipY
      });
    }, 800);

    setHoverTimeout(newHoverTimeout);
  };

  const handleTooltipMouseLeave = () => {
    // Clear any existing hover timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }

    // Set hide timeout
    const newHideTimeout = setTimeout(() => {
      setTooltipData({ show: false, content: '', x: 0, y: 0 });
    }, 100);

    setHideTimeout(newHideTimeout);
  };

  const loadCountItems = async (sessionId, session = null) => {
    setLoading(true);
    try {
      // For open sessions, refresh item_snapshot from current inventory data
      // so prices / descriptions are current. Completed sessions are excluded by the RPC.
      // This is non-fatal: if it fails the existing snapshots are still shown.
      supabase.rpc('refresh_open_count_item_snapshots', {
        p_store_id: null,
        p_session_id: sessionId,
      }).then(({ error: rpcErr }) => {
        if (rpcErr) console.warn('refresh_open_count_item_snapshots (load):', rpcErr.message);
      });

      // Get count items — plain * to avoid 400 errors from missing columns or
      // unsupported !inner join. order_calculations_enabled comes from the
      // already-loaded session object passed in (or falls back gracefully).
      //
      // Paginated, then deduped. The pagination matters twice over: the
      // capped read is what showed "1,000 items" on an 8-item count, and the
      // heal-on-open diff below compares template pairs against THIS list —
      // a pair whose rows fell past the cap looked "missing" and was
      // re-inserted, which is a duplication vector of its own. The dedupe
      // keeps a session this bug already exploded usable (one row per item
      // and location, human-touched rows always preferred) until the data is
      // repaired.
      const { rows: rawData, error } = await fetchAllCountItems(supabase, sessionId);

      if (error) {
        console.error('Error loading count items:', error);
        setLoading(false);
        return;
      }

      // Deliberately item-scoped (no groups): a live session's rows must all
      // survive to the count-group fold below, which captures hidden
      // siblings' typed counts as the group baseline. Group-keyed deduping
      // here would discard counted rows before the fold could read them.
      let data = dedupeCountItemRows(rawData);

      // The store's working-set groups (confirmed same_item +
      // purchasing_alternative), fetched once for the whole load: the
      // template reconciliations below use them to stop re-materializing
      // sibling rows, and the count-card fold uses them to show one card per
      // group. Null on any failure — everything downstream then behaves
      // exactly item-scoped.
      let storeWideGroups = null;
      try {
        const groupsStoreId = session?.store_id || parseInt(localStorage.getItem('selectedStoreId'), 10);
        storeWideGroups = await fetchWorkingSetGroupsForStore(groupsStoreId);
      } catch (groupsErr) {
        console.warn('[group-fold] relationships unavailable (non-fatal):', groupsErr?.message);
        storeWideGroups = null;
      }

      // A recurring draft can outlive an item-link approval. The approval
      // archives the old vendor-specific inventory row and adds the surviving
      // multi-vendor row to the recurring template, while the already-created
      // draft intentionally keeps its historical FK. Before the liveness pass
      // hides that old row, reconcile the untouched draft with its current
      // template parent so the replacement item is actually available to
      // count. Completed/in-progress sessions remain historical and are never
      // changed here.
      const draftTemplateSyncKey = String(sessionId);
      if (
        !session?.is_template
        && session?.status === 'draft'
        && session?.count_group_id
      ) {
        let syncPromise = draftTemplateSyncInFlight.get(draftTemplateSyncKey);
        if (!syncPromise) {
          syncPromise = reconcileDraftWithTemplateParent(sessionId, session, storeWideGroups);
          draftTemplateSyncInFlight.set(draftTemplateSyncKey, syncPromise);
        }
        try {
          const restoredCount = await syncPromise;
          const { rows: refreshedRows, error: refreshErr } = await fetchAllCountItems(supabase, sessionId);
          if (!refreshErr) data = dedupeCountItemRows(refreshedRows);
          if (restoredCount > 0) {
            console.info(
              `[draft-template-sync] Restored ${restoredCount} current template item row(s) to draft ${sessionId}`,
            );
          }
        } catch (templateSyncErr) {
          console.warn('[draft-template-sync] threw (non-fatal):', templateSyncErr?.message);
        } finally {
          if (draftTemplateSyncInFlight.get(draftTemplateSyncKey) === syncPromise) {
            draftTemplateSyncInFlight.delete(draftTemplateSyncKey);
          }
        }
      }

      // Heal-on-open: if this session was started from a template, check for
      // (inventory_item_id, location) pairs that the template defines but are
      // absent from count_items (e.g. sessions created before this fix). Insert
      // the missing rows now so users counting in-progress sessions also see
      // every location. Non-fatal: failures are logged and silently skipped.
      if (session?.template_id && (data.length > 0 || shouldHideArchivedCountItems(session))) {
        try {
          const { data: ctiRows, error: ctiErr } = await fetchTemplateItemsWithPar(session.template_id);

          if (!ctiErr && ctiRows && ctiRows.length > 0) {
            // Fetch inventory records for all template rows upfront so we can
            // resolve the effective location (row.location || inv.location) using
            // the same fallback the insert path uses. This avoids false "missing"
            // detections when the template row has a null location but the
            // inventory item has a location set (which would already be in
            // count_items under that location, not under the null key).
            const ctiInvIds = [...new Set(ctiRows.map(r => r.inventory_item_id))];
            const { data: ctiInvItems } = await supabase
              .from('inventory_items')
              .select('*')
              .in('id', ctiInvIds);
            const ctiInvLookup = {};
            (ctiInvItems || []).forEach(it => { ctiInvLookup[it.id] = it; });

            // Annotate each template row with its effective location
            const normalizedCtiRows = ctiRows.map(row => ({
              ...row,
              effectiveLoc: row.location || ctiInvLookup[row.inventory_item_id]?.location || null,
            }));

            const sourceRows = normalizedCtiRows.map((row) => {
              const inv = ctiInvLookup[row.inventory_item_id] || {};
              const copiedParDefaulted = row.par_defaulted ?? inv.par_defaulted ?? false;
              const copiedParUnit = copiedParDefaulted
                ? (inv.unit || row.par_unit || 'each')
                : (row.par_unit || inv.unit || 'each');
              return {
                inventory_item_id: row.inventory_item_id,
                item_snapshot: { ...inv, location: row.effectiveLoc || inv.location || null, par_level: row.par_level ?? inv.par_level ?? null, par_unit: copiedParUnit, par_defaulted: copiedParDefaulted },
                par_level: row.par_level ?? null,
                par_unit: copiedParUnit,
                par_defaulted: copiedParDefaulted,
                ...parNormalizationFrom(row),
                // No min_order_qty seed — see the template-materialization path.
                location: row.effectiveLoc,
              };
            });
            const toInsert = buildMissingCountItemsFromTemplate(sessionId, data, sourceRows, { groups: storeWideGroups });

            if (toInsert.length > 0) {
              const { data: insertedRows, error: insertErr } = await supabase
                .from('count_items')
                .insert(toInsert)
                .select();

              if (!insertErr && insertedRows?.length > 0) {
                console.log(`🩹 [heal-on-open] Inserted ${insertedRows.length} missing location row(s) for session ${sessionId}`);
                data = [...data, ...insertedRows];
              } else if (insertErr) {
                console.warn('[heal-on-open] insert failed:', insertErr);
              }
            }
          }
        } catch (healErr) {
          console.warn('[heal-on-open] threw (non-fatal):', healErr?.message);
        }
      }

      // Heal-on-open, recurring-group kind: a run "Created from template"
      // whose item copy never landed (creation interrupted between the two
      // inserts) renders "No items in this session" forever. The template
      // heal above cannot reach it — these groups have no count_template_items
      // rows; the template IS a count session (is_template=true) holding the
      // item list as count_items. Reseed an EMPTY child from its group's
      // template parent, falling back to the newest intact sibling — the same
      // sources creating the run again would copy. Only a session with ZERO
      // rows qualifies: a draft the user pruned by hand must stay pruned.
      // Non-fatal end to end.
      if (data.length === 0 && !session?.template_id && session?.count_group_id && !session?.is_template) {
        try {
          const { data: groupSessions, error: groupErr } = await supabase
            .from('count_sessions')
            .select('id, is_template, created_at')
            .eq('count_group_id', session.count_group_id)
            .neq('id', sessionId)
            .not('is_archived', 'is', true)
            .not('is_trashed', 'is', true);
          if (!groupErr && groupSessions?.length) {
            const sources = [
              ...groupSessions.filter((s) => s.is_template),
              ...groupSessions
                .filter((s) => !s.is_template)
                .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
            ];
            for (const source of sources) {
              const { rows: sourceRows, error: sourceErr } = await fetchAllCountItems(supabase, source.id);
              if (sourceErr || sourceRows.length === 0) continue;
              const toInsert = buildMissingCountItemsFromTemplate(
                sessionId, [], dedupeCountItemRows(sourceRows),
              );
              if (toInsert.length === 0) continue;
              const { data: seeded, error: seedErr } = await supabase
                .from('count_items')
                .insert(toInsert)
                .select();
              if (!seedErr && seeded?.length > 0) {
                console.log(`🩹 [heal-on-open] Reseeded empty group session ${sessionId} with ${seeded.length} row(s) from session ${source.id}`);
                data = seeded;
              } else if (seedErr) {
                console.warn('[heal-on-open] group reseed insert failed:', seedErr);
              }
              break;
            }
          }
        } catch (healErr) {
          console.warn('[heal-on-open] group reseed threw (non-fatal):', healErr?.message);
        }
      }

      // Get current catalog state (liveness + the PAR fallback). Historical
      // sessions retain archived rows by design, but templates and untouched
      // drafts are forward-looking: an archived item must not sit in front of
      // the newly imported canonical item and make the vendor/package controls
      // appear to have vanished. The legacy moq / moq_unit columns are not read
      // here — counting has no use for a minimum order quantity.
      let inventoryIds = [...new Set(data.map(item => item.inventory_item_id).filter(Boolean))];
      const { data: inventoryData, error: invError } = await supabase
        .from('inventory_items')
        .select('id, par_level, par_defaulted, is_active, deleted_at')
        .in('id', inventoryIds);

      if (invError) {
        console.error('Error loading inventory catalog data:', invError);
      } else if (shouldHideArchivedCountItems(session)) {
        const before = data.length;
        data = filterCountItemsToLiveInventory(data, inventoryData);
        inventoryIds = [...new Set(data.map(item => item.inventory_item_id).filter(Boolean))];
        if (data.length !== before) {
          console.info(`[count-liveness] Hid ${before - data.length} archived item row(s) from forward-looking session ${sessionId}`);
        }
      }

      // Create inventory lookup for fallback
      const inventoryLookup = {};
      (inventoryData || []).forEach(inv => {
        inventoryLookup[inv.id] = inv;
      });

      // Flexible-source detection (multi-vendor purchasing). Count math remains
      // exact NEED = PAR − on hand; these rows only need vendor/package metadata
      // for UI labels before Savings Review performs purchase rounding.
      let flexibleIds = new Set();
      let optionsByItemId = {};
      try {
        const flexStoreId = session?.store_id || parseInt(localStorage.getItem('selectedStoreId'), 10);
        flexibleIds = await fetchFlexibleSourceItemIds(flexStoreId, inventoryIds);
        // Full option rows power the Multi-vendor pill in the item previews.
        optionsByItemId = await fetchOptionsForItems(flexStoreId, inventoryIds);
      } catch (flexErr) {
        console.warn('[flexible-source] lookup failed (non-fatal):', flexErr?.message);
        flexibleIds = new Set();
        optionsByItemId = {};
      }
      setFlexibleSourceItemIds(flexibleIds);
      setPurchaseOptionsByItemId(optionsByItemId);

      // ── Related-group fold: one card per working-set group ──────────────
      // Imports link "same product from another vendor" instead of merging,
      // so several inventory items can be one real-world thing on the shelf.
      // The session shows the FAVORED member's card only (the member most
      // recently entered, counted, or ordered — utils/favoredItem); sibling
      // rows are hidden, never rewritten, and their typed counts and PARs
      // keep contributing through the stamps planCountGroupFold leaves on
      // the favored row. Non-fatal by contract: any failure leaves the
      // session exactly item-scoped. Direct-order sessions never fold — the
      // typed value there is an order, not an on-hand count.
      let groupFoldPlan = { visibleRows: data, hiddenRows: [], foldsByGroupId: new Map() };
      let sessionWorkingGroups = null;
      if (!session?.direct_order_mode && storeWideGroups?.membersByGroupId?.size) {
        try {
          const foldStoreId = session?.store_id || parseInt(localStorage.getItem('selectedStoreId'), 10);
          const restricted = restrictGroupsToItems(storeWideGroups, inventoryIds);
          if (restricted.membersByGroupId.size) {
            const favoredMembers = await resolveFavoredMembers({
              storeId: foldStoreId,
              groups: restricted,
              optionsByItemId,
            });
            // The STICKY group canonical anchors the group's stock basis —
            // the favored member only fronts the card when it shares that
            // unit, so a recency flip can never redefine the PAR unit.
            // Advisory like the favored fetch: an empty map falls back to
            // the lowest eligible member, which is equally stable.
            let stickyCanonicals = new Map();
            try {
              const groupComparisons = await fetchEquivalentGroupComparisons(foldStoreId);
              stickyCanonicals = new Map(
                Object.entries(groupComparisons || {})
                  .filter(([, row]) => row?.canonical_item_id != null)
                  .map(([groupKey, row]) => [String(groupKey), String(row.canonical_item_id)]),
              );
            } catch (canonErr) {
              console.warn('[group-fold] sticky canonical fetch failed (non-fatal):', canonErr?.message);
            }
            groupFoldPlan = planCountGroupFold({
              rows: data,
              groups: restricted,
              favoredByGroupId: favoredMembers,
              stickyCanonicalByGroupId: stickyCanonicals,
            });
            sessionWorkingGroups = restricted;
          }
        } catch (foldErr) {
          console.warn('[group-fold] fold unavailable (non-fatal):', foldErr?.message);
          groupFoldPlan = { visibleRows: data, hiddenRows: [], foldsByGroupId: new Map() };
          sessionWorkingGroups = null;
        }
      }
      data = groupFoldPlan.visibleRows;
      setCountGroupFold({
        foldsByGroupId: groupFoldPlan.foldsByGroupId,
        groups: sessionWorkingGroups,
        hiddenRowCount: groupFoldPlan.hiddenRows.length,
        // Kept so group-scoped questions (the PAR transition prompt) can read
        // a hidden sibling's PAR provenance. Read-only — hidden rows are
        // never rewritten from here.
        hiddenRows: groupFoldPlan.hiddenRows,
      });

      // Order-vendor choices already recorded on these lines (set in the
      // template editor or Savings Review — never from a count card). Read only
      // for vendor/package labels; it does not alter the counted NEED.
      let selections = {};
      try {
        selections = (await fetchSelectedOptionsForCountItems((data || []).map((row) => row.id))) || {};
      } catch (selErr) {
        console.warn('[vendor-selection] lookup failed (non-fatal):', selErr?.message);
        selections = {};
      }

      // Package display metadata for each flexible item. Empty when the option
      // lookup failed above; NEED remains exact either way.
      const flexConstraints = buildFlexibleConstraints(data || [], flexibleIds, optionsByItemId, selections);
      setFlexibleConstraintsByItemId(flexConstraints);

      // The package ladders for this session, fetched once so the counter can
      // enter cases and bottles offline afterwards, plus any package detail
      // already recorded. Each fetch is independently non-fatal — settled, not
      // all-or-nothing, because a missing count-format column must not also
      // wipe the ladders (that is how the whole package feature used to vanish
      // on one undeployed migration).
      {
        const ladderStoreId = session?.store_id || parseInt(localStorage.getItem('selectedStoreId'), 10);
        const countItemIds = (data || []).map((row) => row.id);
        const [laddersResult, entriesResult, formatsResult, lastUsedResult] = await Promise.allSettled([
          fetchCountLadders(ladderStoreId, inventoryIds),
          fetchEntriesForCountItems(countItemIds),
          fetchCountFormatsForCountItems(countItemIds),
          // What package each location was counted in last time — template-
          // scoped memory the resolver reads as its "last used here" rung.
          fetchLastUsedCountFormats(supabase, session?.template_id),
        ]);

        const laddersByItemId =
          laddersResult.status === 'fulfilled' ? { ...(laddersResult.value.laddersByItemId || {}) } : {};
        if (laddersResult.status === 'rejected') {
          console.warn('[count-ladder] lookup failed (non-fatal):', laddersResult.reason?.message);
        }

        // Items the server gave no ladder for — the edge function unreachable,
        // not deployed, or missing this item — fall back to a ladder built
        // from the option rows themselves. qty_per_package is already in the
        // count unit, so this is arithmetic, not conversion. A count unit is
        // taken from the item's own snapshot.
        const countUnitByInventoryId = {};
        (data || []).forEach((row) => {
          const invId = String(row.inventory_item_id);
          if (!(invId in countUnitByInventoryId)) {
            countUnitByInventoryId[invId] = row.item_snapshot?.unit || row.par_unit || null;
          }
        });
        Object.entries(optionsByItemId).forEach(([invId, opts]) => {
          const key = String(invId);
          if (laddersByItemId[key] || !(opts || []).length) return;
          const fallback = fallbackCountLadder({
            countUnit: countUnitByInventoryId[key],
            options: opts,
          });
          if (fallback.rungs.some((r) => r.level !== 'canonical') || fallback.unresolvedOptions.length > 0) {
            laddersByItemId[key] = {
              countUnit: fallback.countUnit,
              rungs: fallback.rungs,
              unresolvedOptions: fallback.unresolvedOptions,
              optionCount: fallback.optionCount,
              source: fallback.source,
            };
          }
        });
        setCountLaddersByItemId(laddersByItemId);

        if (entriesResult.status === 'fulfilled') {
          const storedEntries = entriesResult.value || {};
          setCountEntriesByCountItemId(storedEntries);
          // A line whose working is exactly one package entry re-seeds the
          // input with the number that was typed ("2" bags), not its
          // normalized total — the same number the counter left it showing.
          const drafts = {};
          Object.entries(storedEntries).forEach(([countItemId, list]) => {
            if ((list || []).length !== 1) return;
            const entry = list[0];
            if (entry.purchase_option_id == null) return;
            if (entry.entry_kind === 'full_package') {
              drafts[countItemId] = {
                value: String(entry.entered_quantity),
                rungKey: `${entry.purchase_option_id}:package`,
              };
            } else if (entry.entry_kind === 'loose_inner_pack') {
              drafts[countItemId] = {
                value: String(entry.entered_quantity),
                rungKey: `${entry.purchase_option_id}:inner`,
              };
            }
          });
          // Merge UNDER any live drafts: a reload (adding an item, linking a
          // vendor product) must not replace a number the counter has typed
          // since the fetch started with the value that was stored before it.
          setPackageCountDraftByItemId((prev) => ({ ...drafts, ...prev }));
        } else {
          console.warn('[count-entries] lookup failed (non-fatal):', entriesResult.reason?.message);
          setCountEntriesByCountItemId({});
          // Keep whatever drafts exist — wiping them re-derives every input
          // from stored state, which is exactly the rewrite this buffer exists
          // to prevent.
        }
        if (formatsResult.status === 'fulfilled') {
          setCountFormatsByCountItemId(formatsResult.value || {});
        } else {
          console.warn('[count-format] lookup failed (non-fatal):', formatsResult.reason?.message);
          setCountFormatsByCountItemId({});
        }
        setLastUsedFormatsByKey(
          lastUsedResult.status === 'fulfilled' ? (lastUsedResult.value || {}) : {},
        );
      }

      // Direct order mode: the count tool edits the order amount
      // (manual_order_quantity), never on-hand. The PAR is unused there — the
      // counter states the quantity outright — and no minimum applies either.
      const directMode = isDirectOrderSession(session);

      // Initialize count data with the PAR from count_items (session-level)
      const itemsWithCounts = (data || []).map(item => {
        const inventory = inventoryLookup[item.inventory_item_id] || {};
        // In direct order mode the entered/stored value lives in
        // manual_order_quantity, so it — not actual_quantity — seeds the input
        // buffer and the "counted" (i.e. order-entered) state.
        const directMqVal = parseOptionalNumber(item.manual_order_quantity);
        const isCounted = directMode
          ? (directMqVal !== null || item.is_counted === true)
          : hasCountEntry(item.actual_quantity);
        const currentCount = directMode
          ? (directMqVal ?? 0)
          : (isCounted ? (parseFloat(item.actual_quantity) || 0) : 0);
        // Use PAR from count_items table (session-level), fallback to
        // expected_quantity, then inventory default. Nullish selection is
        // deliberate: PAR 0 is an explicit stocking target, not a missing
        // value that may inherit the inventory item's automatic PAR 1.
        const parLevel = parseOptionalNumber(item.par_level)
          ?? parseOptionalNumber(item.expected_quantity)
          ?? parseOptionalNumber(inventory.par_level)
          ?? 0;

        // Only calculate shortage/order once the item has actually been counted.
        // Uncounted items should start at order = 0 even though the keypad placeholder is 0.
        const shortage = (directMode || !isCounted) ? 0 : Math.max(0, parLevel - currentCount);
        // The count page stores and displays the exact vendor-neutral NEED.
        // Vendor package, MOQ and increment rounding belongs to Savings Review.
        const calculatedOrderQuantity = (directMode || !isCounted) ? 0 : shortage;
        const effectiveOrderQuantity = directMode
          ? (directMqVal ?? 0)
          : (isCounted
            ? (parseOptionalNumber(item.manual_order_quantity) ?? calculatedOrderQuantity)
            : 0);
        const orderValue = 0; // Price columns not available in current schema

        return {
          ...item,
          current_count: directMode
            ? (directMqVal !== null ? String(directMqVal) : '')
            : (hasCountEntry(item.actual_quantity) ? item.actual_quantity : ''),
          is_counted: isCounted,
          // Use the session-level PAR from count_items. No moq / moq_unit is
          // hydrated onto the row: nothing on a counting surface reads one.
          par_level: item.par_level ?? parLevel,
          par_unit: item.par_unit || 'each',
          par_defaulted: item.par_defaulted
            ?? item.item_snapshot?.par_defaulted
            ?? inventory.par_defaulted
            ?? false,
          calculated_order_quantity: calculatedOrderQuantity,
          manual_order_quantity: parseOptionalNumber(item.manual_order_quantity),
          order_quantity: effectiveOrderQuantity,
          is_manually_overridden: parseOptionalNumber(item.manual_order_quantity) !== null,
          order_value: orderValue,
          shortage: shortage,
          order_calculations_enabled: session?.order_calculations_enabled ?? (item.count_sessions?.order_calculations_enabled ?? false),
          direct_order_mode: !!(session?.direct_order_mode)
        };
      });

      // Skip the PAR shared-order recompute in direct order mode — the order
      // amount is entered directly and current_count is NOT on-hand, so running
      // the par/on-hand math would clobber the user's order values.
      const hydratedItems = directMode ? itemsWithCounts : applySharedOrderMathBulk(itemsWithCounts, { flexibleSourceItemIds: flexibleIds, packageConstraintsByItemId: flexConstraints });

      setCountItems(hydratedItems);
      sortItems(hydratedItems, sortBy);

      // Compute a snapshotId + snapshotTimestamp that travel with offline queue
      // entries so diverged snapshots can be detected on sync.
      if (mobileTool && sessionId) {
        const ts = new Date().toISOString();
        setSnapshotTimestamp(ts);
        const sortedIds = hydratedItems.map(i => i.id).sort().join(':');
        const fingerprint = sortedIds.length > 120 ? sortedIds.slice(0, 120) : sortedIds;
        const newSnapshotId = `${sessionId}_n${hydratedItems.length}_${fingerprint}`;
        setSnapshotId(newSnapshotId);
        // Persist the full item list + snapshot context to IDB so the session
        // can be restored offline if the user closes and reopens the app.
        offlineSync.saveSessionDraft(sessionId, hydratedItems, newSnapshotId, ts);
      }

      // Backfill: if any sibling rows had divergent PARs in the DB, persist the
      // canonical (item-global) value back so future loads, exports, and
      // server-side reports see one PAR per item per session. min_order_qty is
      // deliberately left exactly as stored — counting no longer owns it, so
      // opening a count must not rewrite it.
      try {
        const updates = [];
        hydratedItems.forEach((row) => {
          const orig = (data || []).find(d => d.id === row.id);
          if (!orig) return;
          const newPar = parseFloat(row.par_level);
          const origPar = parseFloat(orig.par_level);
          if (Number.isFinite(newPar) && newPar > 0 && newPar !== origPar) {
            updates.push({ id: row.id, par_level: newPar });
          }
        });
        if (updates.length > 0) {
          await Promise.all(updates.map(u => {
            const { id, ...patch } = u;
            return supabase.from('count_items').update(patch).eq('id', id);
          }));
        }
      } catch (e) {
        console.warn('PAR backfill skipped:', e?.message);
      }
      return hydratedItems;
    } catch (error) {
      console.error('Error loading count items:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const sortItems = (items, sortType) => {
    let sorted = [...items];
    
    switch (sortType) {
      case 'location': {
        // Rank locations exactly the way the location list displays them
        // (see getAllGroupsOrdered / allGroups): lexicographic
        // (min location_position, min count_position) per location, then
        // label. The previous per-item comparator fell back to alphabetical
        // location names whenever location_position was missing, which made
        // the item order — and therefore the initially selected location —
        // disagree with the displayed location list.
        const locationMeta = {};
        sorted.forEach((item) => {
          const label = getGroupValueForItem(item, 'location');
          const locationOrder = getLocationOrderValue(item);
          const itemOrder = getItemOrderValue(item);
          const current = locationMeta[label];
          if (!current || locationOrder < current.locationOrder ||
              (locationOrder === current.locationOrder && itemOrder < current.itemOrder)) {
            locationMeta[label] = { locationOrder, itemOrder };
          }
        });
        const locationRank = {};
        Object.keys(locationMeta)
          .sort((left, right) => {
            const leftMeta = locationMeta[left];
            const rightMeta = locationMeta[right];
            if (leftMeta.locationOrder !== rightMeta.locationOrder) return leftMeta.locationOrder - rightMeta.locationOrder;
            if (leftMeta.itemOrder !== rightMeta.itemOrder) return leftMeta.itemOrder - rightMeta.itemOrder;
            return left.localeCompare(right);
          })
          .forEach((label, rank) => { locationRank[label] = rank; });

        sorted.sort((a, b) => {
          const rankA = locationRank[getGroupValueForItem(a, 'location')];
          const rankB = locationRank[getGroupValueForItem(b, 'location')];
          if (rankA !== rankB) {
            return rankA - rankB;
          }

          const positionA = getItemOrderValue(a);
          const positionB = getItemOrderValue(b);
          if (positionA !== positionB) {
            return positionA - positionB;
          }

          const fallbackPositionA = parseOptionalNumber(a.count_position) || 0;
          const fallbackPositionB = parseOptionalNumber(b.count_position) || 0;
          if (fallbackPositionA !== fallbackPositionB) {
            return fallbackPositionA - fallbackPositionB;
          }

          return (a.item_snapshot?.description || '').localeCompare(b.item_snapshot?.description || '');
        });
        break;
      }
      case 'vendor':
        sorted.sort((a, b) => {
          const vendorA = resolveVendor(a.item_snapshot?.vendor || '');
          const vendorB = resolveVendor(b.item_snapshot?.vendor || '');
          if (vendorA !== vendorB) {
            return vendorA.localeCompare(vendorB);
          }
          const locationPosA = getLocationOrderValue(a);
          const locationPosB = getLocationOrderValue(b);
          if (locationPosA !== locationPosB) return locationPosA - locationPosB;
          const itemPosA = getItemOrderValue(a);
          const itemPosB = getItemOrderValue(b);
          if (itemPosA !== itemPosB) return itemPosA - itemPosB;
          return (a.item_snapshot?.description || '').localeCompare(b.item_snapshot?.description || '');
        });
        break;
      case 'category':
        sorted.sort((a, b) => {
          const categoryA = a.item_snapshot?.category || '';
          const categoryB = b.item_snapshot?.category || '';
          if (categoryA !== categoryB) {
            return categoryA.localeCompare(categoryB);
          }
          const locationPosA = getLocationOrderValue(a);
          const locationPosB = getLocationOrderValue(b);
          if (locationPosA !== locationPosB) return locationPosA - locationPosB;
          const itemPosA = getItemOrderValue(a);
          const itemPosB = getItemOrderValue(b);
          if (itemPosA !== itemPosB) return itemPosA - itemPosB;
          return (a.item_snapshot?.description || '').localeCompare(b.item_snapshot?.description || '');
        });
        break;
      case 'alphabetical':
        sorted.sort((a, b) => 
          (a.item_snapshot?.description || '').localeCompare(b.item_snapshot?.description || '')
        );
        break;
    }
    
    setCountItems(sorted);
    setSelectedItemIndex(0); // Reset to first item when sorting
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setSelectedLocation(null); // will be initialized to first group for the new sort in render
    sortItems(countItems, newSortBy);
  };

  // Sync heights: left (groups) and middle (items) panes match the keypad card height
  useEffect(() => {
    const measure = () => {
      if (keypadCardRef.current) {
        const h = keypadCardRef.current.offsetHeight;
        if (h && h !== paneHeightPx) setPaneHeightPx(h);
      }
    };
    setTimeout(measure, 0);
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [selectedItemIndex, countItems.length]);

  // Subscription gate: check if user can count more items
  const [subGateChecked, setSubGateChecked] = useState(false);
  const [subStatus, setSubStatus] = useState(null);

  const checkSubscriptionGate = async (newlyCounted) => {
    if (typeof window === 'undefined' || !window.SubscriptionGate) return true;
    const storeId = parseInt(localStorage.getItem('selectedStoreId'), 10);
    if (!storeId) return true;
    try {
      const access = await window.SubscriptionGate.checkAccess(storeId, 'count');
      setSubStatus(access.status);
      if (!access.allowed) {
        if (access.reason === 'free_limit' || (access.status?.is_free_tier && access.status?.free_remaining <= 0)) {
          window.PaywallModal?.showTrialPrompt(storeId, {
            onTrialStarted: () => { setSubGateChecked(false); }
          });
        } else {
          window.PaywallModal?.showUpgradePrompt(access.reason);
        }
        return false;
      }
      // Free tier: increment counted items
      if (access.status?.is_free_tier && newlyCounted) {
        const updated = await window.SubscriptionGate.incrementCounted(storeId, 1);
        setSubStatus(updated);
        if (updated?.free_remaining <= 0) {
          window.PaywallModal?.showTrialPrompt(storeId, {
            onTrialStarted: () => { setSubGateChecked(false); }
          });
        }
      }
      return true;
    } catch (e) {
      console.warn('Sub gate check failed, allowing:', e.message);
      return true;
    }
  };

  /**
   * @param {object} [opts]
   * @param {boolean} [opts.normalized]  newCount is already in the count unit
   *                                     (the package sheet's total) — do not
   *                                     interpret it through the active rung.
   * @param {object}  [opts.rungOverride] interpret through this rung instead
   *                                     of the resolved one — used when a
   *                                     format change has not landed in state
   *                                     yet ("Change this entry").
   */
  const updateItemCount = (index, newCount, opts = {}) => {
    // If the user is entering a count for the item that was flagged to show
    // blank on arrival (via Count / Next), clear the flag so the real value
    // is used from this point on.
    if (blankOnNextIndexRef.current === index) {
      blankOnNextIndexRef.current = null;
    }
    const updatedItems = [...countItems];
    const currentItem = updatedItems[index];
    const wasUncounted = !currentItem.is_counted;
    const willBeCounted = hasCountEntry(newCount);

    // Start/resume the game-band timer on any count-entry path, including
    // direct text input. +/- and keypad call startTimerIfNeeded explicitly
    // too; the double-call is a no-op after the first commit.
    if (willBeCounted) startTimerIfNeededRef.current();

    // ── Direct order mode ────────────────────────────────────────────────────
    // The entered value IS the order amount, so it is recorded to
    // manual_order_quantity and on-hand (actual_quantity) is left untouched.
    // PAR and the multi-location shared-order math do not apply here — the
    // user is counting what they need to order, not what is on the shelf.
    if (isDirectOrderSession(selectedSession)) {
      const orderVal = parseOptionalNumber(newCount);
      updatedItems[index] = {
        ...currentItem,
        current_count: newCount, // display buffer mirrors the order amount
        is_counted: willBeCounted,
        manual_order_quantity: orderVal,
        calculated_order_quantity: 0,
        order_quantity: orderVal ?? 0,
        is_manually_overridden: orderVal !== null,
        shortage: 0,
      };
      setCountItems(updatedItems);
      if (willBeCounted && (orderVal ?? 0) > 0) triggerFlash(setFlashOrder);
      if (mobileTool) {
        // Queue only order-related columns — never actual_quantity — so on-hand
        // is preserved through the offline batch sync.
        offlineSync.queueChange(currentItem.id, {
          manual_order_quantity: orderVal,
          is_counted: willBeCounted,
          counted_at: willBeCounted ? new Date().toISOString() : null,
          calculated_order_quantity: 0,
        });
      }
      if (wasUncounted && willBeCounted) checkSubscriptionGate(true);
      return;
    }

    // ── Package interpretation ──────────────────────────────────────────────
    // With a package format active the typed number means PACKAGES: "2" is two
    // Costco 4 lb bags, stored as 8 lb. The literal text lives in the draft
    // buffer (so the input shows what was typed); everything stored and
    // computed from here down is in the item's count unit, exactly as before.
    //
    // The rung is PINNED with the draft at the first keystroke: while the
    // draft is alive, later keystrokes and the commit all normalize through
    // the rung the counter was looking at when they typed, never through a
    // resolution that shifted underneath them (a purchase-option refresh, a
    // late count-format load). An explicit "Count as" change or a
    // package-sheet save clears the draft and lets the line resolve live
    // again.
    const pinned = opts.normalized ? undefined : pinnedRungOf(currentItem);
    const rung = opts.normalized
      ? null
      : (opts.rungOverride || (pinned === undefined ? activeCountRung(currentItem) : pinned));
    let storedCount = newCount;
    if (rung && willBeCounted) {
      const normalized = normalizedFromInput(currentItem, rung, newCount);
      if (normalized !== null) storedCount = String(normalized);
    }
    if (!opts.normalized && currentItem.id != null) {
      // Every typed value gets a draft — including one typed in the item's
      // own unit (rung: null), which pins the canonical meaning so a package
      // format arriving a beat later cannot re-read "5.5 lb" as packages.
      const key = String(currentItem.id);
      setPackageCountDraftByItemId((prev) => ({
        ...prev,
        [key]: rung
          ? {
            value: newCount,
            rungKey: rung.key,
            rung: {
              key: rung.key,
              level: rung.level,
              optionId: rung.optionId,
              entryKind: rung.entryKind,
              unitLabel: rung.unitLabel,
              qtyInCountUnit: rung.qtyInCountUnit,
              allowsFractional: rung.allowsFractional,
            },
          }
          : { value: newCount, rungKey: null, rung: null },
      }));
    } else if (opts.normalized && currentItem.id != null) {
      // A sheet-computed total replaces whatever was typed; the input rereads
      // it through the rung on the next render.
      setPackageCountDraftByItemId((prev) => {
        if (!(String(currentItem.id) in prev)) return prev;
        const next = { ...prev };
        delete next[String(currentItem.id)];
        return next;
      });
    }
    // Mirror the typed package into the line's raw entries (debounced,
    // best-effort) so the package identity survives the session — an 8 lb
    // total that was two 4 lb bags stays auditable as two 4 lb bags.
    scheduleTypedEntryWrite(currentItem, rung, newCount, willBeCounted, opts);

    const currentCountNum = parseFloat(storedCount) || 0;
    const parLevel = parseFloat(currentItem.par_level ?? currentItem.expected_quantity) || 0;
    // Write the new count onto the current item first so computeSharedOrderData
    // can read it when summing across all locations for the same inventory item.
    updatedItems[index] = {
      ...currentItem,
      current_count: storedCount,
      actual_quantity: storedCount,
      is_counted: willBeCounted,
      order_value: 0,
    };

    // Resolve the inventory item ID for shared-PAR aggregation.
    const inventoryItemId =
      currentItem.inventory_item_id || currentItem.item_snapshot?.id || currentItem.id;

    // Compute the global (shared) order quantity using all location rows for this
    // inventory item in the session. The deficit is PAR minus the sum of every
    // counted location, so counting more in any location automatically reduces
    // the suggested order on every other location row too.
    //
    // We always run this — even when clearing the current row (willBeCounted=false)
    // — so siblings retain a correct shared calc based on their remaining totals.
    // When the current row is cleared, updatedItems[index].current_count = ''
    // which makes currentCountNum = 0 inside computeSharedOrderData, giving the
    // correct "siblings only" total without this row's contribution.
    // The legacy minimum-order and flexible-source arguments are retained at
    // this call boundary for signature stability; computeSharedOrderData always
    // returns the exact vendor-neutral NEED and reads neither. `null` is passed
    // for the minimum so no call site can look as though it supplies one.
    const currentIsFlexible = isFlexibleSource(currentItem, flexibleSourceItemIds);
    const { sharedOrderQty, otherLocations } = computeSharedOrderData(
      inventoryItemId, updatedItems, index, parLevel, null,
      currentIsFlexible ? (flexibleConstraintsByItemId[String(inventoryItemId)] || true) : false,
    );

    // Derive shortage from the shared perspective for flash triggers.
    const totalCounted = otherLocations.reduce((s, e) => s + e.value, 0) + currentCountNum;
    const sharedShortage = Math.max(0, parLevel - totalCounted);

    // Trigger visual feedback flashes. There is no MOQ flash any more: it fired
    // when the order quantity exceeded the shortage, which only ever happened
    // when a package minimum inflated the need — something counting no longer does.
    if (willBeCounted && sharedShortage > 0) triggerFlash(setFlashPar);
    if (willBeCounted && sharedOrderQty > 0) triggerFlash(setFlashOrder);

    // Apply the shared calculated order quantity to ALL rows for this inventory item.
    // Each row keeps its own manual override but their calculated_order_quantity now
    // reflects the combined count so the ORDER badge is consistent everywhere.
    updatedItems.forEach((ci, i) => {
      const ciInvId = ci.inventory_item_id || ci.item_snapshot?.id || ci.id;
      if (ciInvId !== inventoryItemId) return;
      const isCounted = i === index ? willBeCounted : ci.is_counted;
      updatedItems[i] = {
        ...updatedItems[i],
        shortage: isCounted ? sharedShortage : 0,
        calculated_order_quantity: isCounted ? sharedOrderQty : 0,
        order_quantity: isCounted
          ? (parseOptionalNumber(ci.manual_order_quantity) ?? sharedOrderQty)
          : 0,
        is_manually_overridden: isCounted && parseOptionalNumber(ci.manual_order_quantity) !== null,
      };
    });

    setCountItems(updatedItems);

    // ── Local-first offline write (mobileTool only) ───────────────────────
    // Queue this item's count change to IndexedDB immediately. The hook will
    // attempt a Supabase write right away; if the backend is unreachable the
    // record stays pending until the sync runner drains it when back online.
    if (mobileTool) {
      const changedItem = updatedItems[index];
      offlineSync.queueChange(changedItem.id, {
        actual_quantity: changedItem.current_count,
        is_counted: changedItem.is_counted,
        counted_at: changedItem.is_counted ? new Date().toISOString() : null,
        calculated_order_quantity: parseOptionalNumber(changedItem.calculated_order_quantity),
        manual_order_quantity: parseOptionalNumber(changedItem.manual_order_quantity),
      });
    }

    // Subscription gate: check after marking an item counted for the first time
    if (wasUncounted && willBeCounted) {
      checkSubscriptionGate(true);
    }
  };

  // Update PAR for an item across every sibling location row in the current
  // session, then persist the same value to all matching count_items rows in
  // the DB. A PAR is item-global within a session — see Task #70.
  const updateItemFieldsGlobal = async (
    index,
    updates /* { par_level?, par_defaulted? } */,
    { requireDurable = false } = {},
  ) => {
    const target = countItems[index];
    if (!target) {
      if (requireDurable) throw new Error('The count item is no longer available.');
      return;
    }
    const patch = {};
    if (updates && Number.isFinite(updates.par_level) && updates.par_level >= 0) {
      patch.par_level = updates.par_level;
    }
    if (updates && typeof updates.par_defaulted === 'boolean') {
      patch.par_defaulted = updates.par_defaulted;
    }
    if (Object.keys(patch).length === 0) {
      if (requireDurable) throw new Error('No valid PAR change was provided.');
      return;
    }
    // Use only the true inventory_item_id — never fall back to the count_items
    // row id (target.id) or a snapshot id because those shapes do not match
    // count_template_items.inventory_item_id and will silently match 0 rows.
    // Normalize to string so mixed number/string shapes compare correctly.
    const rawInvId = target.inventory_item_id;
    if (!rawInvId) {
      if (requireDurable) throw new Error('This count row is not linked to an inventory item.');
      return;
    }
    const invId = String(rawInvId);

    // Group fold: the visible canonical card speaks for its group, so its
    // PAR edit IS the group's PAR — but only for the SAME-UNIT members the
    // fold actually sums (group_fold.memberItemIds). Writing it to an
    // odd-unit member would stamp an 'each' PAR onto a row counted in lb —
    // a cross-dimension stomp. Odd-unit members keep their own PARs; their
    // visible legacy cards edit themselves.
    const foldEntry = [...(countGroupFold.foldsByGroupId?.values?.() ?? [])]
      .find((fold) => (fold.memberItemIds || []).includes(String(invId)));
    const foldMembers = foldEntry ? new Set(foldEntry.memberItemIds.map(String)) : null;

    // A count-page PAR is typed in the item's own count unit, so the stored
    // normalized PAR must move WITH it. Leaving par_normalized_quantity stale
    // would make the normalized readers (resolveRowPar everywhere) keep the
    // old target while the raw column holds the new one — two PAR truths on
    // one row. Written together, degraded together (see the retry below for
    // stores behind on the normalization migration).
    let normalizationPatch = null;
    if ('par_level' in patch) {
      // No par_unit fallback: with no item unit the quintet records a
      // unit-less value rather than borrowing the PAR's own unit label.
      normalizationPatch = buildParNormalizationPatch({
        parLevel: patch.par_level,
        parUnit: null,
        countUnit: target.item_snapshot?.unit ?? target.count_unit ?? null,
      });
    }

    // Update every in-memory sibling row for this item in one pass.
    const updatedItems = countItems.map((ci) => {
      const ciRawInvId = ci.inventory_item_id || ci.item_snapshot?.id;
      const ciInvId = ciRawInvId ? String(ciRawInvId) : null;
      if (ciInvId !== invId) return ci;
      const next = { ...ci, ...(normalizationPatch || {}) };
      if ('par_level' in patch) {
        next.par_level = patch.par_level;
        if (next.group_par_floor !== null && next.group_par_floor !== undefined) {
          next.group_par_floor = null;
        }
      }
      if ('par_defaulted' in patch) {
        next.par_defaulted = patch.par_defaulted;
        if (next.group_par_defaulted !== undefined) next.group_par_defaulted = false;
      }
      return next;
    });

    // Recompute shared shortage / order qty using the new value(s) so that
    // already-counted rows show updated order badges right away.
    const recomputed = applySharedOrderMathBulk(updatedItems, { flexibleSourceItemIds, packageConstraintsByItemId: flexibleConstraintsByItemId });
    // The focused SET PAR card is a confirmation workflow, so it keeps the
    // automatic marker visible until both the current count and its durable
    // parent have accepted the write. Existing inline PAR editors retain the
    // long-standing optimistic behavior.
    if (!requireDurable) setCountItems(recomputed);

    // Persist to DB on every sibling row so the change survives reload — and,
    // when the card is a folded group, on the hidden member rows of THIS
    // session too (they are not in countItems, so they are addressed by
    // (session, inventory_item_id) instead of by row id).
    if (Object.keys(patch).length > 0) {
      try {
        // Full patch first (legacy pair + normalization quintet); a store whose
        // count_items predates the normalization columns retries legacy-only,
        // the same degradation contract every PAR writer uses.
        const runParUpdate = async (applyFilters) => {
          const full = { ...patch, ...(normalizationPatch || {}) };
          const { data, error } = await applyFilters(
            supabase.from('count_items').update(full).select('id'),
          );
          if (error && normalizationPatch && isMissingParNormalizationColumnError(error)) {
            const { data: legacyData, error: legacyError } = await applyFilters(
              supabase.from('count_items').update(patch).select('id'),
            );
            if (legacyError) throw new Error(legacyError.message);
            if (requireDurable && (!Array.isArray(legacyData) || legacyData.length === 0)) {
              throw new Error('The PAR change did not match a count row.');
            }
          } else if (error) {
            throw new Error(error.message);
          } else if (requireDurable && (!Array.isArray(data) || data.length === 0)) {
            throw new Error('The PAR change did not match a count row.');
          }
        };
        if (selectedSession?.id && foldMembers) {
          await runParUpdate((query) => query
            .eq('count_session_id', selectedSession.id)
            .in('inventory_item_id', [...foldMembers]));
        } else {
          const ids = recomputed
            .filter(ci => {
              const rawId = ci.inventory_item_id || ci.item_snapshot?.id;
              return rawId && String(rawId) === invId && ci.id;
            })
            .map(ci => ci.id);
          if (ids.length > 0) {
            await runParUpdate((query) => query.in('id', ids));
          } else if (requireDurable) {
            throw new Error('The PAR change could not find a persisted count row.');
          }
        }
      } catch (e) {
        console.warn('[GlobalPAR] persist failed:', e?.message);
        if (requireDurable) throw e;
      }
    }

    // Propagate the PAR to the parent template so the next count from the same
    // template pre-fills it — for a folded card, to every group member's
    // template rows, so the next session's floor agrees with what the user
    // typed. Best-effort — failures are logged in the helper, never thrown.
    // We await so we can show a brief confirmation toast on success
    // (Task #289).
    try {
      if (selectedSession?.id && invId) {
        const propagationTargets = foldMembers ? [...foldMembers] : [invId];
        let toastShown = false;
        for (const targetInvId of propagationTargets) {
          const propagation = { sessionId: selectedSession.id, inventoryItemId: targetInvId };
          if ('par_level' in patch) {
            propagation.par_level = patch.par_level;
            // The same quintet just written to count_items goes to the
            // template, or the next session inherits the typed pair alone and
            // a package-shaped PAR arrives with no package.
            if (normalizationPatch) propagation.par_normalization = normalizationPatch;
          }
          if ('par_defaulted' in patch) propagation.par_defaulted = patch.par_defaulted;
          const result = await propagateGlobalCountFieldsToParents(propagation);
          if (
            requireDurable
            && (result?.parPropagated !== true || result?.inventoryParPropagated !== true)
          ) {
            throw new Error('The PAR was not saved to its template and inventory item.');
          }
          if (!toastShown) {
            const msg = formatPropagationToast(result);
            if (msg) { showTemplateSyncToast(msg); toastShown = true; }
          }
        }
      }
    } catch (e) {
      console.warn('[GlobalPAR] template propagation failed:', e?.message);
      if (requireDurable) throw e;
    }

    if (requireDurable) setCountItems(recomputed);
    return { countPersisted: true, parentPersisted: true };
  };

  // Save from the focused PAR setup card. PAR persists through the same path
  // every in-count PAR editor uses (sibling rows, shared order math,
  // count_items, and template propagation). MOQ is saved separately to the
  // selected vendor product's authoritative moq_packages field; it is never
  // written to legacy inventory_items.moq. When the line is being counted in
  // a package, the typed PAR converts through the same per-package quantity
  // the COUNT tile displays with.
  const saveParSetupFromCard = async ({ par, purchaseOptionId, moqPackages }) => {
    const modal = parWarningModal;
    if (!modal) return;
    const idx = countItems.findIndex((ci) => String(ci.id) === String(modal.countItemId));
    if (idx < 0) {
      setParWarningModal(null);
      return;
    }
    setParSetupSaving(true);
    try {
      const row = countItems[idx];
      const view = packageViewFor(row);
      const initialDisplayedPar = parDisplayFor(row).value;
      const countUnit = row.item_snapshot?.unit || row.par_unit || 'each';
      const originalResolution = resolveRowPar(row, countUnit);
      const storedPar = storedParFromFocusedSetup({
        enteredPar: par,
        unitsPerDisplayedUnit: view?.per,
        reviewingAutoDefault: modal.reviewingAutoDefault === true,
        initialDisplayedPar,
        originalNormalizedPar: originalResolution.status === PAR_STATUS.VALID
          ? originalResolution.normalized
          : null,
      });
      if (purchaseOptionId != null && Number.isFinite(moqPackages) && moqPackages > 0) {
        await updateOrderingRules(purchaseOptionId, { moqPackages });
      }
      await updateItemFieldsGlobal(
        idx,
        { par_level: storedPar, par_defaulted: false },
        { requireDurable: true },
      );
      const optionPool = pooledOptionsByItemId[row.inventory_item_id]
        || purchaseOptionsByItemId[row.inventory_item_id]
        || [];
      const changedOption = optionPool.find(
        (option) => String(option.id) === String(purchaseOptionId),
      );
      const refreshItemId = changedOption?.inventory_item_id || row.inventory_item_id;
      if (refreshItemId) {
        try {
          await refreshOptionsForItem(refreshItemId);
        } catch (refreshError) {
          console.warn('[SetPAR] vendor option refresh failed:', refreshError?.message);
        }
      }
      setParWarningModal(null);
    } catch (error) {
      console.error('[SetPAR] save failed:', error);
      showAppAlert({
        title: 'Could not save settings',
        message: error?.message || 'PAR and minimum order could not be saved. Please try again.',
        confirmButtonStyle: 'danger',
      });
    } finally {
      setParSetupSaving(false);
    }
  };

  const openFocusedParSetupFromCount = (index, { reviewingAutoDefault = false } = {}) => {
    const item = countItems[index];
    if (!item) return;
    setParWarningModal({
      itemName: item.description || item.item_snapshot?.description || item.name || 'this item',
      countItemId: item.id,
      inventoryItemId: item.inventory_item_id || null,
      reviewingAutoDefault,
    });
  };

  // Opens the multi-item pars wizard from the counting screen, anchored on the
  // item whose PAR badge was tapped (replaces the old single-item modal).
  // An item with no PAR yet opens the wizard filtered to just the items still
  // missing one — the batch the user most likely wants to fill in. Tapping the
  // badge on an item that already has a PAR means "edit existing pars", so the
  // filter is off and every item shows. The wizard sets PARs only; it asks
  // nothing about vendors or their minimums.
  const openParsWizardFromCount = (index) => {
    const item = countItems[index];
    if (!item) return;
    const hasPar = hasStoredParValue(item.par_level);
    // A no-PAR item on an order count gets the focused single-item setup card
    // (PAR + one selected vendor product's Minimum Order) instead of the whole
    // missing-PARs list — the badge was tapped to fix THIS item. An explicit
    // tap always opens it. Count entry itself never launches this modal.
    if (!hasPar && selectedSession?.order_calculations_enabled) {
      openFocusedParSetupFromCount(index);
      return;
    }
    setParsWizardModal({
      template: selectedSession,
      // Live in-memory rows, NOT a re-read: mid-count they are the source of
      // truth (mobile counts run offline-first). Hydration coerces a missing
      // PAR to 0 — normalize those back to null so their inputs open blank
      // exactly like the template-setup flow.
      items: countItems.map(ci => ({
        ...ci,
        par_level: hasStoredParValue(ci.par_level) ? ci.par_level : null,
      })),
      isFirstTimeSetup: false,
      parsOnly: false,
      countMode: true,
      anchorCountItemId: item.id,
      excludeItemsWithPar: !hasPar,
    });
  };

  // Apply a count-mode pars-wizard save to the active count: update the
  // in-memory rows, recompute the shared order math, persist the session's
  // count_items, and propagate the PAR to the parent template — the same
  // pipeline the single-item modal used, for N items at once. The wizard
  // carries a PAR and its unit and nothing else; no vendor minimum travels
  // with it.
  const applyCountParsWizardSave = async (changedRows) => {
    if (!Array.isArray(changedRows) || changedRows.length === 0) return;
    const reviewedRows = changedRows.map((row) => ({ ...row, par_defaulted: false }));
    const patchById = {};
    reviewedRows.forEach(r => { patchById[r.id] = r; });

    const updatedItems = countItems.map((ci) => {
      const patch = patchById[ci.id];
      if (!patch) return ci;
      return {
        ...ci,
        // par_level 0 is this component's "no PAR" representation (see the
        // session hydration), so a cleared wizard value maps back to 0.
        par_level: patch.par_level ?? 0,
        par_unit: patch.par_unit || ci.par_unit || 'each',
        par_defaulted: false,
        // Keep the stored normalized PAR in step with the edit — the same
        // quintet persistParsToTemplate writes, so the in-memory row and the
        // DB row resolve identically.
        ...wizardNormalizationPatch({ ...ci, par_level: patch.par_level ?? 0, par_unit: patch.par_unit || ci.par_unit || 'each' }),
      };
    });
    const recomputed = applySharedOrderMathBulk(updatedItems, { flexibleSourceItemIds, packageConstraintsByItemId: flexibleConstraintsByItemId });
    setCountItems(recomputed);

    // Persist every changed count_items row (the wizard already fanned the
    // values out to all sibling location rows). Best-effort like the old
    // modal — the in-memory state above is already what the user sees.
    try {
      await persistParsToTemplate(reviewedRows);
    } catch (e) {
      console.warn('[CountParsWizard] count_items persist failed:', e?.message || e);
    }

    // Propagate the PAR to the parent template, once per inventory item. Null
    // values are passed as undefined — "don't touch" — so a PAR left blank
    // doesn't clear the stored one.
    try {
      if (!selectedSession?.id) return;
      const patchByInventoryId = new Map();
      recomputed.forEach((ci) => {
        const patch = patchById[ci.id];
        if (!patch) return;
        const invId = ci.inventory_item_id ? String(ci.inventory_item_id) : null;
        if (!invId || patchByInventoryId.has(invId)) return;
        patchByInventoryId.set(invId, patch);
      });
      let lastResult = null;
      for (const [invId, patch] of patchByInventoryId) {
        lastResult = await propagateGlobalCountFieldsToParents({
          sessionId: selectedSession.id,
          inventoryItemId: invId,
          par_level: patch.par_level ?? undefined,
          par_unit: patch.par_unit,
          par_defaulted: false,
        });
      }
      const msg = patchByInventoryId.size === 1
        ? formatPropagationToast(lastResult)
        : `PARs updated for ${patchByInventoryId.size} items — synced to template`;
      if (msg) showTemplateSyncToast(msg);
    } catch (e) {
      console.warn('[CountParsWizard] template propagation failed:', e?.message || e);
    }
  };

  const applyManualOrderOverride = (index, rawValue) => {
    const parsed = parseOptionalNumber(rawValue);
    const updatedItems = [...countItems];
    const item = updatedItems[index];
    if (!item) return;
    const fallbackCalculated = calculateNeedQuantity(
      Math.max(0, (parseFloat(item.par_level ?? item.expected_quantity) || 0) - (parseFloat(item.current_count) || 0))
    );
    const calculatedOrderQuantity = parseOptionalNumber(item.calculated_order_quantity ?? fallbackCalculated) ?? fallbackCalculated;
    updatedItems[index] = {
      ...item,
      calculated_order_quantity: calculatedOrderQuantity,
      manual_order_quantity: parsed,
      order_quantity: parsed ?? calculatedOrderQuantity,
      is_manually_overridden: parsed !== null
    };
    setCountItems(updatedItems);
  };

  const clearManualOrderOverride = (index) => {
    applyManualOrderOverride(index, '');
  };

  const incrementCount = (index, amount = 1) => {
    const currentCount = parseFloat(getEffectiveCount(index)) || 0;
    const newCount = Math.max(0, currentCount + amount);
    updateItemCount(index, newCount.toString());
    // Start the timer on first +/− press so it begins the moment the user
    // starts entering counts, not only when they press "Count Next ▶".
    startTimerIfNeeded();
  };

  // Number pad functions
  const appendToCount = (digit) => {
    startTimerIfNeeded(); // First keypad digit starts the clock.
    const currentValue = getEffectiveCount(selectedItemIndex);
    let newValue = currentValue + digit;

    // Prevent multiple decimal points
    if (digit === '.' && currentValue.includes('.')) {
      return;
    }

    // Limit to reasonable length
    if (newValue.length > 10) {
      return;
    }

    updateItemCount(selectedItemIndex, newValue);
  };

  const setCount = (value) => {
    updateItemCount(selectedItemIndex, value);
  };

  const addFraction = (fraction) => {
    const currentNumber = parseFloat(getEffectiveCount(selectedItemIndex)) || 0;
    const fractionValue = parseFloat(fraction);
    const newValue = currentNumber + fractionValue;
    updateItemCount(selectedItemIndex, newValue.toString());
  };

  const clearCount = () => {
    updateItemCount(selectedItemIndex, '');
  };

  const toNullableText = (value) => {
    const t = (value ?? '').toString().trim();
    return t ? t : null;
  };

  const normalizeMoneyValue = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return null;
    const numeric = Number.parseFloat(raw);
    if (!Number.isFinite(numeric)) return null;
    return Math.round((numeric + Number.EPSILON) * 100) / 100;
  };

  const openEditItemModal = (itemOverride) => {
    // Called directly as an onClick handler (receives the click event) or from
    // the Multi-vendor badge with a specific (preview-list) item.
    const item = (itemOverride && !itemOverride.nativeEvent)
      ? itemOverride
      : countItems[selectedItemIndex];
    if (!item) return;
    const snap = normalizeSnapshotPrices(item.item_snapshot || {});
    const currentPar = item.par_level ?? item.expected_quantity;
    setEditItemDraft({
      description: snap.description || '',
      vendor: snap.vendor || '',
      category: snap.category || '',
      unit: snap.unit || item.par_unit || 'each',
      location: item.location || snap.location || '',
      case_price: snap.case_price != null && snap.case_price !== '' ? String(snap.case_price) : '',
      unit_price: (snap.unit_price ?? snap.each_price) != null && (snap.unit_price ?? snap.each_price) !== ''
        ? String(snap.unit_price ?? snap.each_price)
        : '',
      par_level: currentPar != null && currentPar !== '' ? String(currentPar) : '',
    });
    setShowEditItemModal(true);
  };

  // Refetch one item's purchase options into the badge map (after a preferred
  // change or an edit in the vendor-products modal). Non-fatal: the
  // purchase_options table may not exist in every environment yet.
  const refreshOptionsForItem = async (inventoryItemId) => {
    if (!inventoryItemId) return;
    try {
      const storeId = selectedSession?.store_id || parseInt(localStorage.getItem('selectedStoreId'), 10);
      const grouped = await fetchOptionsForItems(storeId, [inventoryItemId]);
      setPurchaseOptionsByItemId((prev) => ({ ...prev, [inventoryItemId]: grouped[inventoryItemId] || [] }));
    } catch (err) {
      console.warn('[purchase-options] refresh failed (non-fatal):', err?.message);
    }
  };

  // ── Counting by the package ────────────────────────────────────────────────
  // The count line still stores one number. These handlers let the counter
  // arrive at it by entering the things they are actually holding — a tub, two
  // cases, eight loose bottles — instead of doing the conversion in their head
  // in a walk-in.

  /**
   * The count unit of every inventory item in this session, hidden sibling
   * rows included — the fold hides an odd-unit member's row but its unit is
   * exactly what the operational anchor needs in order to bridge that
   * member's package into the group's own dimension.
   */
  // countItems is a NEW array on every keystroke (the typed value lands on a
  // row), so anything memoized directly on it recomputes while somebody is
  // counting. None of what the anchor needs from a row — its id, its item, its
  // location, its unit — can change mid-count, so these memos key on a
  // primitive signature of exactly those fields instead. Counting on a phone
  // in a walk-in is the one workflow that must not get slower.
  const countRowIdentitySignature = useMemo(() => [
    ...(countItems || []),
    ...(countGroupFold.hiddenRows || []),
  ].map((row) => (
    `${row?.id ?? ''}:${row?.inventory_item_id ?? row?.item_snapshot?.id ?? ''}`
    + `:${row?.location ?? ''}:${row?.item_snapshot?.unit ?? row?.count_unit ?? ''}`
  )).join('|'), [countItems, countGroupFold.hiddenRows]);

  const memberUnitsById = useMemo(() => {
    const units = {};
    const record = (row) => {
      const key = String(row?.inventory_item_id ?? row?.item_snapshot?.id ?? '');
      if (!key || units[key]) return;
      const unit = row?.item_snapshot?.unit || row?.count_unit || '';
      if (unit) units[key] = unit;
    };
    (countItems || []).forEach(record);
    (countGroupFold.hiddenRows || []).forEach(record);
    Object.entries(countLaddersByItemId || {}).forEach(([key, ladder]) => {
      if (!units[key] && ladder?.countUnit) units[key] = ladder.countUnit;
    });
    return units;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countRowIdentitySignature, countLaddersByItemId]);

  const memberItemsById = useMemo(() => {
    const items = {};
    Object.entries(memberUnitsById).forEach(([key, unit]) => {
      items[key] = { id: key, unit };
    });
    return items;
  }, [memberUnitsById]);

  /** The count row that fronts each inventory item, by identity alone — the
   *  id and location the anchor's pin lookup needs, with none of the fields
   *  a keystroke touches. */
  const frontingRowIdentityByItemId = useMemo(() => {
    const rows = {};
    (countItems || []).forEach((row) => {
      const key = String(row?.inventory_item_id ?? '');
      if (!key || rows[key]) return;
      rows[key] = { id: row.id, location: row.location ?? null, inventory_item_id: row.inventory_item_id };
    });
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countRowIdentitySignature]);

  /**
   * The count-format memory a line resolves against: the count row's own
   * columns over the template row's, exactly as the resolver expects them.
   * Shared by the single-item presentation and the group anchor so a pin
   * means the same thing to both.
   */
  const resolverRowFor = useCallback((item) => {
    const tpl = lastUsedFormatsByKey[lastUsedKeyFor(item?.inventory_item_id, item?.location)] || {};
    const rowFmt = countFormatsByCountItemId[String(item?.id)] || {};
    return {
      count_last_used_purchase_option_id: tpl.count_last_used_purchase_option_id ?? null,
      count_last_used_at: tpl.count_last_used_at ?? null,
      ...(rowFmt.count_display_purchase_option_id != null ? rowFmt : {
        ...rowFmt,
        count_display_purchase_option_id: tpl.count_display_purchase_option_id ?? null,
        count_display_unit: rowFmt.count_display_unit ?? tpl.count_display_unit ?? null,
        follow_latest_purchase: tpl.count_display_purchase_option_id != null
          ? tpl.follow_latest_purchase
          : rowFmt.follow_latest_purchase,
      }),
    };
  }, [countFormatsByCountItemId, lastUsedFormatsByKey]);

  /**
   * The OPERATIONAL ANCHOR of every folded group — the package the group is
   * actually counted in (utils/groupCountAnchor).
   *
   * Distinct from the fold's basis member, which owns identity and the unit
   * the quantities are STORED in. A store buying Diet Coke in 5 gal BIBs must
   * read PAR / COUNT / NEED in BIBs even when the sticky canonical is a
   * gallon-counted sibling, and the BIB package usually belongs to that
   * sibling rather than to the card that fronts the group. Pooling the
   * members' options here is what lets it lead.
   *
   * Non-fatal by the same contract as the fold itself: a failure leaves every
   * line resolving exactly as an ungrouped item does.
   */
  const groupAnchorByItemId = useMemo(() => {
    const anchors = new Map();
    const folds = countGroupFold.foldsByGroupId;
    if (!folds?.size) return anchors;
    folds.forEach((fold) => {
      try {
        // Keyed on the member that FRONTS the card — a hidden sibling never
        // renders one, and the anchor was resolved against that card's pins.
        const frontingId = String(fold.favoredItemId ?? fold.basisItemId ?? '');
        if (!frontingId) return;
        const frontingRow = frontingRowIdentityByItemId[frontingId] || null;
        const basisUnit = memberUnitsById[frontingId]
          || memberUnitsById[String(fold.basisItemId ?? '')]
          || '';
        if (!basisUnit) return;
        const members = fold.presentItemIds?.length
          ? fold.presentItemIds
          : [...new Set([...(fold.memberItemIds || []), frontingId])];
        anchors.set(frontingId, resolveGroupCountAnchor({
          basisItemId: frontingId,
          basisUnit,
          basisRow: frontingRow ? resolverRowFor(frontingRow) : null,
          memberItemIds: members,
          optionsByItemId: purchaseOptionsByItemId,
          laddersByItemId: countLaddersByItemId,
          itemsByItemId: memberItemsById,
          unitByItemId: memberUnitsById,
        }));
      } catch (err) {
        console.warn('[group-anchor] anchor unavailable (non-fatal):', err?.message || err);
      }
    });
    return anchors;
  }, [
    countGroupFold.foldsByGroupId, frontingRowIdentityByItemId, memberUnitsById,
    memberItemsById, purchaseOptionsByItemId, countLaddersByItemId, resolverRowFor,
  ]);

  /** The group anchor a line reads in, or null when the line is not the
   *  fronting card of a folded group. */
  const groupAnchorFor = useCallback(
    (item) => (item?.inventory_item_id == null
      ? null
      : groupAnchorByItemId.get(String(item.inventory_item_id)) || null),
    [groupAnchorByItemId],
  );

  /**
   * The ladder a line is COUNTED against, or null when packages are
   * unavailable for it.
   *
   * For the fronting card of a folded group this is the group's pooled,
   * anchored rung set — every member's package, each already expressed in the
   * group's stored unit — so the counter can enter "0.1 BIB" on a card whose
   * quantities are kept in gallons. For every other line it is the item's own
   * ladder, byte for byte as before.
   */
  const ladderForItem = useCallback(
    (item) => {
      const own = countLaddersByItemId[String(item?.inventory_item_id)] || null;
      const anchor = groupAnchorFor(item);
      if (!anchor?.rungs?.length) return own;
      return {
        countUnit: own?.countUnit || anchor.basisUnit || null,
        rungs: anchor.rungs,
        // The GROUP's unsized and unbridgeable packages, so the "needs
        // package review" affordance lists the same set the format menu
        // does — one of them is commonly the very product the store buys.
        unresolvedOptions: anchor.presentation?.unresolved || own?.unresolvedOptions || [],
        optionCount: anchor.presentation?.options?.length ?? own?.optionCount ?? 0,
        source: 'group_anchor',
      };
    },
    [countLaddersByItemId, groupAnchorFor],
  );

  /**
   * Whether the "Count by package" action is worth showing.
   *
   * Only when the item can actually be counted in something other than its own
   * unit — otherwise the sheet would open on a single row that duplicates the
   * field the counter is already looking at.
   */
  const canCountByPackage = useCallback((item) => {
    const ladder = ladderForItem(item);
    if (!ladder) return false;
    return ladder.rungs.some((rung) => rung.level !== 'canonical') || ladder.unresolvedOptions.length > 0;
  }, [ladderForItem]);

  const openPackageSheet = useCallback((item) => {
    if (!item?.id) return;
    setPackageSheetItem(item);
  }, []);

  /**
   * The physical package a line is counted in.
   *
   * Resolved rather than stored-and-read, because the answer depends on things
   * that move: a newer verified invoice, a vendor product being retired. The
   * stored columns are inputs to the decision (the pin, the toggle), not the
   * decision itself — see utils/countPresentation for the precedence.
   *
   * The fronting card of a folded group resolves at GROUP scope: the anchor
   * ran the same precedence over every member's packages (each re-expressed
   * in the group's stored unit) and hands back the presentation it produced.
   * A pin, a verified invoice or a fresh import on a SIBLING is evidence
   * about the same physical product, so it leads here exactly as the item's
   * own would.
   */
  const presentationForItem = useCallback(
    (item) => {
      if (!item) return null;
      const groupPresentation = groupAnchorFor(item)?.presentation ?? null;
      if (groupPresentation) return groupPresentation;
      const ladder = ladderForItem(item);
      // Merge the template row's memory under the count row's own columns.
      // A pin on the COUNT row is the user's in-count choice and wins
      // outright; otherwise the TEMPLATE's pin (made in the template editor)
      // applies, and the template's last-used package feeds the resolver's
      // "last counted here" rung. Session creation copies neither, so this
      // merge is what makes both reach a live count.
      return resolveCountPresentation({
        row: resolverRowFor(item),
        options: purchaseOptionsByItemId[item.inventory_item_id] || [],
        rungs: ladder?.rungs || [],
        unresolved: ladder?.unresolvedOptions || [],
      });
    },
    [ladderForItem, groupAnchorFor, resolverRowFor, purchaseOptionsByItemId],
  );

  /**
   * The package rung the main count input records in, or null when the line
   * counts in the item's own unit. When a rung is active the typed number
   * means PACKAGES — two Costco 4 lb bags, not two pounds — and every path
   * that reads or writes the input goes through this so the two meanings can
   * never mix. Direct order mode types an order amount and is exempt.
   */
  const activeCountRung = useCallback(
    (item) => {
      if (!item || isDirectOrderSession(selectedSession)) return null;
      const rung = presentationForItem(item)?.rung || null;
      return rung && rung.level !== 'canonical' ? rung : null;
    },
    [presentationForItem, selectedSession],
  );

  /**
   * The rung a line's typed draft was entered in, pinned at the keystroke.
   *
   * The live resolution moves — a purchase-option refresh, a count-format or
   * ladder load finishing after the counter already typed — and a number that
   * meant "5.5 of THAT package" must not be re-normalized as 5.5 of a
   * different one. Returns `undefined` when the line has no draft (resolve
   * live), `null` when the draft was typed in the item's own unit, or the
   * pinned rung. Seeded drafts (rebuilt from stored entries on load) carry
   * only a rungKey; their rung is recovered from the ladder, and `undefined`
   * (fall back to live) when it is gone.
   */
  const pinnedRungOf = useCallback(
    (item) => {
      const draft = packageCountDraftByItemId[String(item?.id)];
      if (!draft) return undefined;
      if (draft.rung) return draft.rung;
      if (draft.rungKey == null) return null;
      const rungs = ladderForItem(item)?.rungs || [];
      return rungs.find((r) => r.key === draft.rungKey) || undefined;
    },
    [packageCountDraftByItemId, ladderForItem],
  );

  /** The rung every read and write of a line's number goes through: the
   *  draft's pinned rung while a draft is alive, the live resolution after. */
  const rungForLine = useCallback(
    (item) => {
      const pinned = pinnedRungOf(item);
      return pinned === undefined ? activeCountRung(item) : pinned;
    },
    [pinnedRungOf, activeCountRung],
  );

  /**
   * What the typed value stores in the count unit, resolved through the same
   * ladder rules the entry table uses (including the whole-package floor), so
   * the input and a saved entry can never disagree about one number.
   */
  const normalizedFromInput = useCallback(
    (item, rung, typedValue) => {
      if (!rung) return null;
      const resolution = resolveEntry(
        {
          entry_kind: rung.entryKind,
          purchase_option_id: rung.optionId,
          entered_quantity: parseFloat(typedValue),
          entered_unit: rung.unitLabel,
        },
        ladderForItem(item)?.rungs || [],
      );
      return resolution.status === 'valid' ? resolution.normalized : null;
    },
    [ladderForItem],
  );

  /**
   * The package view for a line — the unit of the vendor being viewed, with
   * everything the card and badges need to show the line's numbers IN that
   * unit. Null when the line counts in the item's own unit.
   *
   * This is the display layer that keeps normalization out of sight: the
   * stored quantities, the shared order math, and every report stay in the
   * item's count unit exactly as before. Only what the counter READS converts.
   */
  const packageViewFor = useCallback((item) => {
    // Reads through the line's pinned rung while a typed draft is alive, so
    // the badges, chips and order display describe the same package the
    // number in the input was typed in.
    const rung = rungForLine(item);
    const per = Number(rung?.qtyInCountUnit);
    if (!rung || !Number.isFinite(per) || per <= 0) return null;
    // Pooled, not own-only: a folded group's anchor package commonly belongs
    // to a SIBLING member, and reading its ordering rules off an empty lookup
    // silently made every such line whole-package and non-fractional.
    const options = pooledOptionsByItemId[item?.inventory_item_id]
      || purchaseOptionsByItemId[item?.inventory_item_id]
      || [];
    const option = rung.optionId != null
      ? options.find((o) => String(o.id) === String(rung.optionId)) || null
      : null;
    const unitLabel = rung.unitLabel || option?.purchase_unit || 'package';
    // No minimum is read here. `purchase_options.moq_packages` is a real fact
    // about a real vendor package, but it is a PURCHASING fact: this view
    // describes the package the shelf is COUNTED in, and the two questions have
    // different answers whenever an item is bought from more than one supplier.
    const incRaw = Number(option?.order_increment);
    return {
      rung,
      per,
      option,
      unitLabel,
      // Fractional COUNTING (a divisible rung) never implies fractional
      // ORDERING — the order step comes only from the option's own fields.
      allowsFractional: option?.allows_fractional_packages === true,
      countIsDivisible: rung.allowsFractional === true || option?.allows_fractional_packages === true,
      orderIncrement: Number.isFinite(incRaw) && incRaw > 0 ? incRaw : null,
    };
  }, [rungForLine, pooledOptionsByItemId, purchaseOptionsByItemId]);

  /**
   * Whether this line gets the visual bottle-fraction slider. Liquor/wine
   * lines only, decided from the category text alone (never AI). Deliberately
   * hidden when the line counts in a MULTI-unit package (a tenth of a case is
   * not a bottle fill level), when the line is a prepared sub-recipe (no
   * bottle on a shelf), and in direct-order mode (nobody orders 0.4 of a
   * bottle).
   *
   * A package rung holding exactly ONE canonical unit is not a case: it is
   * the bottle itself. Liquor is nearly always sold by the bottle, so the
   * distributor option reads "1 bottle = 1 bottle" and the line resolves to a
   * package-level rung — the commonest liquor line there is. Hiding the tab
   * on every non-canonical rung took the slider off almost the whole bar,
   * which is exactly the shelf it was built for. Anything the ladder cannot
   * size (qtyInCountUnit null) still hides: an unknown package is not a
   * bottle we can promise a fill level for.
   */
  const bottleInfoForItem = useCallback((item) => {
    if (!item) return null;
    if (isDirectOrderSession(selectedSession)) return null;
    if (isPreparedSnapshot(item.item_snapshot, item)) return null;
    const rung = rungForLine(item);
    if (rung && Number(rung.qtyInCountUnit) !== 1) return null;
    const info = bottleEligibilityForItem(item);
    return info.eligible ? info : null;
  }, [selectedSession, rungForLine]);

  /** A normalized quantity read back in the view's package, rounded for
   *  badges and labels only — inputs use the unrounded read. */
  const packageDisplayQty = (normalized, view) => {
    const packages = packageQuantityFromNormalized(normalized, view?.rung);
    if (packages === null) return null;
    return Math.round(packages * 100) / 100;
  };

  /** A row's counted quantity as badges and tape chips show it: in the
   *  package being counted when one is active, else the stored number. */
  const displayCountForRow = useCallback((item) => {
    const stored = parseOptionalNumber(item?.current_count);
    const view = packageViewFor(item);
    if (!view) return stored ?? 0;
    if (stored === null) return 0;
    return packageDisplayQty(stored, view) ?? stored;
  }, [packageViewFor]);

  /**
   * The vendor-neutral need for a row, IN THE PACKAGE THE LINE IS COUNTED IN.
   *
   * A count format changes how on-hand is entered and read; it does not turn
   * the count card into a vendor order — package rounding, MOQ and increments
   * still belong to Savings Review. But the three tiles are one sentence, and
   * PAR and COUNT have always converted into the active package: leaving NEED
   * in the item's stored unit produced the card this correction exists to
   * kill — "PAR 1 BIB · COUNT 0.1 BIB · NEED 4.5 gal", three numbers that
   * cannot be compared and one that was silently relabelled rather than
   * converted. The stored shortage is untouched; only its expression moves.
   */
  const calculatedOrderDisplayFor = useCallback((item) => {
    const shortage = Math.max(0, parseOptionalNumber(item?.shortage) ?? 0);
    const view = packageViewFor(item);
    if (!view) return Math.round(shortage * 1e6) / 1e6;
    return packageDisplayQty(shortage, view) ?? Math.round(shortage * 1e6) / 1e6;
  }, [packageViewFor]);

  /** The ORDER / NEED badge value for a row: the manual override when one is
   *  set (converted into the viewed package), else the calculated order —
   *  both in the same package, so the badge never mixes the two meanings. */
  const displayOrderForRow = useCallback((item) => {
    if (!item?.order_calculations_enabled) return 0;
    const manual = parseOptionalNumber(item.manual_order_quantity);
    const view = packageViewFor(item);
    if (manual !== null) return view ? (packageDisplayQty(manual, view) ?? manual) : manual;
    if (!item.is_counted) return 0;
    return calculatedOrderDisplayFor(item);
  }, [packageViewFor, calculatedOrderDisplayFor]);

  /** The unit word every number on a line reads in: the counted package when
   *  one is active, else the item's own count unit. One source, so the PAR,
   *  COUNT and NEED tiles can never disagree about what they are counting. */
  const displayUnitForRow = useCallback((item) => (
    packageViewFor(item)?.unitLabel
    || item?.item_snapshot?.unit
    || item?.par_unit
    || 'each'
  ), [packageViewFor]);

  /* A counting surface shows PAR, COUNT and NEED and nothing else about how the
   * item is bought. The MOQ tile that used to live here is gone deliberately:
   * a PAR belongs to the ITEM, an MOQ belongs to the VENDOR PRODUCT, and one
   * item-level minimum is meaningless for an item bought from four vendors —
   * which is why the tile had grown a "Varies by vendor" state it could not
   * resolve. Vendor minimums, order increments and package rounding are read
   * and shown in Savings Review, where a vendor and a package have been chosen.
   *
   * `inventory_items.replenishment_minimum_qty` (the item's own floor, in count
   * units) is likewise NOT shown here: it is an advanced item-policy field that
   * belongs in item / purchasing settings and surfaces in Savings Review only
   * when it actually moves the purchase target. */

  /* ── PAR transition ("your usual package changed") ─────────────────────── */

  // Checks ONE item, once per session, when its card opens — the "first time
  // the item is counted after a newly verified invoice changed the default
  // package" moment. Detection is pure; the stored ledger read and the
  // (cached, deterministic-first) recommendation happen only when a
  // transition is actually due. Never fires per keystroke, never in direct
  // order mode, never for prepared items.
  useEffect(() => {
    const item = countItems[selectedItemIndex];
    if (!item?.inventory_item_id || isDirectOrderSession(selectedSession)) return;
    if (isPreparedSnapshot(item.item_snapshot)) return;
    const invKey = String(item.inventory_item_id);
    if (parTransitionCheckedRef.current.has(invKey)) return;
    parTransitionCheckedRef.current.add(invKey);

    const storeId = selectedSession?.store_id
      || parseInt(localStorage.getItem('selectedStoreId'), 10);
    if (!storeId) return;

    let cancelled = false;
    (async () => {
      try {
        // Group scope: a folded card's PAR is the GROUP's, and its provenance
        // may live on a hidden sibling row. Hand the detector every same-unit
        // member row so a favored-member switch across siblings still asks
        // its transition question — while the normalized group PAR stays put.
        const foldEntryForItem = [...(countGroupFold.foldsByGroupId?.values?.() ?? [])]
          .find((fold) => (fold.memberItemIds || []).map(String).includes(invKey)) || null;
        const groupRowsForItem = foldEntryForItem
          ? (row) => {
            const members = new Set((foldEntryForItem.memberItemIds || []).map(String));
            const hidden = (countGroupFold.hiddenRows || [])
              .filter((hiddenRow) => members.has(String(hiddenRow?.inventory_item_id)));
            return [row, ...hidden];
          }
          : undefined;
        const pending = await detectPendingParTransitions({
          storeId,
          items: [item],
          presentationFor: presentationForItem,
          parResolutionFor: (row) =>
            resolveRowPar(row, row?.item_snapshot?.unit || row?.par_unit || 'each'),
          optionsFor: (row) => purchaseOptionsByItemId[row.inventory_item_id] || [],
          groupRowsFor: groupRowsForItem,
        });
        const entry = pending[invKey];
        if (!entry || cancelled) return;
        if (entry.autoResolve) {
          // Exact whole-package equivalent: no stocking question, no prompt.
          // The normalized PAR stays put; only the friendly display moves to
          // the new package, and the ledger remembers so this never re-runs.
          const applied = await applyExactEquivalentRefresh({
            storeId,
            sessionId: selectedSession?.id ?? null,
            inventoryItemId: item.inventory_item_id,
            transition: entry.transition,
          });
          if (applied?.displayPatch && !cancelled) {
            setCountItems((prev) => prev.map((row) =>
              String(row.inventory_item_id) === invKey
                ? { ...row, ...applied.displayPatch }
                : row));
          }
          return;
        }
        const recommendation = await getParRecommendation({
          storeId,
          inventoryItemId: item.inventory_item_id,
          transition: entry.transition,
          cachedRow: entry.storedRow,
        });
        if (cancelled) return;
        setParTransitionPrompt({
          item,
          transition: entry.transition,
          storedRow: entry.storedRow,
          recommendation,
          busy: false,
        });
      } catch (err) {
        console.warn('[par-transition] check skipped:', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
    // presentationForItem/purchaseOptionsByItemId are stable enough per
    // session; the checked-set makes re-runs no-ops anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemIndex, countItems, selectedSession, purchaseOptionsByItemId]);

  const settleParTransition = useCallback(async (decision) => {
    const prompt = parTransitionPrompt;
    if (!prompt) return;
    const storeId = selectedSession?.store_id
      || parseInt(localStorage.getItem('selectedStoreId'), 10);
    setParTransitionPrompt({ ...prompt, busy: true });
    try {
      if (decision === 'dismissed') {
        // Asked, not answered: record the suggestion so the next ask reuses
        // it, and let the prompt quietly return next count.
        await recordParTransition({
          storeId,
          inventoryItemId: prompt.item.inventory_item_id,
          transition: prompt.transition,
          recommendation: prompt.recommendation,
        });
        setParTransitionPrompt(null);
        return;
      }
      const countUnit = prompt.item.item_snapshot?.unit || prompt.item.par_unit || 'each';
      // A folded card answers for its group: the decision writes to every
      // same-unit member (the fold's partition), exactly like a count-page
      // PAR edit — a sibling's old higher PAR must not silently outvote the
      // explicit answer.
      const promptInvKey = String(prompt.item.inventory_item_id);
      const foldEntryForPrompt = [...(countGroupFold.foldsByGroupId?.values?.() ?? [])]
        .find((fold) => (fold.memberItemIds || []).map(String).includes(promptInvKey)) || null;
      const promptMemberIds = foldEntryForPrompt
        ? foldEntryForPrompt.memberItemIds.map(String)
        : [promptInvKey];
      await applyParDecision({
        storeId,
        sessionId: selectedSession?.id ?? null,
        templateId: selectedSession?.template_id ?? null,
        inventoryItemId: prompt.item.inventory_item_id,
        memberItemIds: promptMemberIds,
        transition: prompt.transition,
        decision,
        recommendation: prompt.recommendation,
        countUnit,
      });
      if (decision === 'preserve_package_count') {
        // Mirror the new PAR into local state so badges and order math see it
        // immediately — same fields the PAR edit modal writes. Fanned to the
        // group members and their now-stale floor stamps cleared, so the old
        // group PAR cannot keep winning through a stamp.
        const newPar = prompt.transition.preserveNormalizedPar;
        const memberSet = new Set(promptMemberIds);
        setCountItems((prev) => {
          const next = prev.map((row) =>
            memberSet.has(String(row.inventory_item_id))
              ? {
                  ...row,
                  par_level: newPar,
                  par_unit: countUnit,
                  par_normalized_quantity: newPar,
                  par_normalized_unit: countUnit,
                  par_display_quantity: prompt.transition.preservePackages,
                  par_display_unit: prompt.transition.newPackageLabel,
                  par_purchase_option_id: prompt.transition.newOptionId,
                  // This transition is an explicit stocking decision. The DB
                  // trigger clears the creation-default marker with the PAR
                  // value change; mirror that immediately so SET PAR does not
                  // linger until the next reload.
                  par_defaulted: false,
                  ...(row.group_par_floor != null ? { group_par_floor: null } : {}),
                  ...(row.group_par_defaulted !== undefined ? { group_par_defaulted: false } : {}),
                }
              : row,
          );
          return applySharedOrderMathBulk(next, {
            flexibleSourceItemIds: flexibleSourceItemIds,
            packageConstraintsByItemId: flexibleConstraintsByItemId,
          });
        });
      }
      setParTransitionPrompt(null);
    } catch (err) {
      console.warn('[par-transition] decision failed:', err?.message || err);
      showAppAlert({
        title: 'Could not save the PAR decision',
        message: err?.message || 'Please try again.',
        confirmButtonStyle: 'danger',
      });
      setParTransitionPrompt({ ...prompt, busy: false });
    }
  }, [parTransitionPrompt, selectedSession, flexibleSourceItemIds, flexibleConstraintsByItemId, countGroupFold]);

  /**
   * The PAR with the unit it is expressed in.
   *
   * A bare "12" beside a count entered in cases is the ambiguity this whole
   * change exists to remove — twelve pounds and twelve packs are different
   * shelves. With a package view active the PAR reads in the same package the
   * count does ("0.25 case" beside a count of 2 cases), so the two are always
   * comparable at a glance; the stored PAR stays in the item's count unit.
   */
  const parDisplayFor = useCallback((item) => {
    if (!item) return { value: null, text: '—', unit: null };
    const view = packageViewFor(item);
    if (view) {
      const countUnit = item.item_snapshot?.unit || item.par_unit || null;
      const resolution = resolveRowPar(item, countUnit);
      if (resolution.status === PAR_STATUS.VALID && resolution.normalized != null) {
        // Through the SAME expression the COUNT and NEED tiles use, so the
        // three can never round or pluralize differently.
        const expressed = expressInCountFormat(resolution.normalized, view.rung, countUnit, { maxDecimals: 2 });
        if (expressed.value !== null) {
          return { value: expressed.value, text: expressed.text, unit: expressed.unit };
        }
      }
    }
    const value = parseOptionalNumber(item.par_display_quantity)
      ?? parseOptionalNumber(item.par_level)
      ?? parseOptionalNumber(item.expected_quantity);
    const unit = item.par_display_unit || item.par_unit || item.item_snapshot?.unit || null;
    // A PAR of zero is a real target ("we hold none of this"); only a missing
    // one renders as a dash.
    if (value === null) return { value: null, text: '—', unit };
    return { value, text: unit ? `${value} ${unit}` : String(value), unit };
  }, [packageViewFor]);

  /**
   * Records a count-format choice for ONE line.
   *
   * Deliberately not propagated to sibling locations the way the vendor choice
   * is: the walk-in may hold tubs while the line holds jars. The optimistic
   * local update comes first so the control responds at counting speed, and a
   * failed write is a warning rather than an interruption — the format is a
   * display preference, and losing it must not cost somebody their count.
   */
  const applyCountFormat = useCallback(async (item, patch) => {
    if (!item?.id) return;
    const key = String(item.id);
    setCountFormatsByCountItemId((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...(patch || {}) },
    }));
    // An explicit format change is the one moment a typed draft (and the rung
    // pinned with it) is allowed to die: the counter asked for the line to be
    // read in a different package, so the input rereads the stored quantity
    // through the new resolution. Callers that carry the typed number across
    // on purpose ("Change this entry") recreate the draft right after.
    setPackageCountDraftByItemId((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    try {
      await setCountFormatForLines([item.id], patch);
    } catch (e) {
      console.warn('[count-format] persist failed (non-fatal):', e?.message);
    }
  }, []);

  // Trigger A: an explicit count-format change is a natural moment to ask
  // about the PAR — immediately, with the exact consequence stated. Exact
  // whole-package equivalents resolve silently (display refresh only); a
  // transition this store already answered never asks again.
  const maybePromptParTransitionForChoice = useCallback(async (item, choice) => {
    try {
      if (!item?.inventory_item_id || !choice) return;
      if (choice.level === 'canonical' || choice.optionId == null) return;
      if (isDirectOrderSession(selectedSession) || isPreparedSnapshot(item.item_snapshot)) return;
      const countUnit = item.item_snapshot?.unit || item.par_unit || 'each';
      const par = resolveRowPar(item, countUnit);
      if (!par || par.normalized == null) return;
      const storeId = selectedSession?.store_id
        || parseInt(localStorage.getItem('selectedStoreId'), 10);
      if (!storeId) return;
      const detection = detectParTransition({
        normalizedPar: par.normalized,
        parDisplay: par.display,
        parQtyPerPackage: par.snapshot?.quantity_per_package ?? null,
        newOptionId: choice.optionId,
        newQtyPerPackage: choice.qtyInCountUnit ?? null,
        newPackageLabel: choice.label || choice.unitLabel || null,
        oldPackageLabel: par.display?.unit ?? null,
        countUnit,
      });
      if (detection.autoResolve && detection.transition) {
        const applied = await applyExactEquivalentRefresh({
          storeId,
          sessionId: selectedSession?.id ?? null,
          inventoryItemId: item.inventory_item_id,
          transition: detection.transition,
        });
        if (applied?.displayPatch) {
          setCountItems((prev) => prev.map((row) =>
            String(row.inventory_item_id) === String(item.inventory_item_id)
              ? { ...row, ...applied.displayPatch }
              : row));
        }
        return;
      }
      if (!detection.required || !detection.transition) return;
      const stored = await fetchParTransitions(storeId, [item.inventory_item_id]);
      const row = stored[parTransitionKey(
        item.inventory_item_id, detection.transition.oldOptionId, detection.transition.newOptionId,
      )] || null;
      if (row?.decision && row.decision !== 'dismissed') return;
      const recommendation = await getParRecommendation({
        storeId,
        inventoryItemId: item.inventory_item_id,
        transition: detection.transition,
        cachedRow: row,
      });
      setParTransitionPrompt({
        item,
        transition: detection.transition,
        storedRow: row,
        recommendation,
        busy: false,
      });
    } catch (err) {
      console.warn('[par-transition] format-change check skipped:', err?.message || err);
    }
  }, [selectedSession]);

  /**
   * A format choice from the "Count as" menu.
   *
   * The number in the box counts a SPECIFIC package: "2" meaning two 4 lb bags
   * is 8 lb, and carrying that 2 across to a 10 lb case would silently make it
   * 20 lb. So switching between two packages with a nonzero count never
   * reinterprets on its own — it stops and offers adding a second entry
   * (mixed packaging is the normal state of a shelf mid-transition) or an
   * explicit change that states both readings. Switches from or to the item's
   * own unit preserve the stored quantity exactly and pass straight through.
   */
  const handleCountFormatSelect = (item, patch, choice) => {
    if (!item?.id || !patch) return;
    const index = countItems.findIndex((ci) => ci?.id === item.id);
    // The typed number's CURRENT meaning is the draft's pinned rung when one
    // is alive — the live resolution may already have moved on.
    const currentRung = rungForLine(item);
    const nextIsPackage = choice && choice.level && choice.level !== 'canonical';
    const typedText = index >= 0 ? getEffectiveCount(index) : '';
    const typed = parseFloat(typedText);
    const sameRung = currentRung && choice && String(currentRung.key) === String(choice.key);

    if (!currentRung || !nextIsPackage || sameRung || !(Number.isFinite(typed) && typed > 0)) {
      applyCountFormat(item, patch);
      if (!sameRung) maybePromptParTransitionForChoice(item, choice);
      return;
    }

    const presentation = presentationForItem(item);
    const plan = planPackageSwitch({
      currentQuantity: typed,
      currentChoice: {
        vendorLabel: presentation?.label || null,
        label: presentation?.label || null,
        unitLabel: currentRung.unitLabel,
      },
      nextChoice: choice,
    });
    if (plan.action !== SWITCH_ACTIONS.OFFER_CHOICE) {
      applyCountFormat(item, patch);
      maybePromptParTransitionForChoice(item, choice);
      return;
    }
    setPackageSwitchPrompt({ item, patch, choice, plan, typedText, index });
  };

  /** "Add as another count entry": keep the typed package on record, switch the
   *  format, and open the sheet so the new package gets its own line. */
  const confirmSwitchAddEntry = async () => {
    const prompt = packageSwitchPrompt;
    if (!prompt) return;
    setPackageSwitchPrompt(null);
    await flushTypedEntryWrite(prompt.item);
    applyCountFormat(prompt.item, prompt.patch);
    openPackageSheet(prompt.item);
    maybePromptParTransitionForChoice(prompt.item, prompt.choice);
  };

  /** "Change this entry": the typed number now counts the NEW package — the
   *  reinterpretation the user was just warned about, done on purpose. */
  const confirmSwitchChangeEntry = () => {
    const prompt = packageSwitchPrompt;
    if (!prompt) return;
    setPackageSwitchPrompt(null);
    applyCountFormat(prompt.item, prompt.patch);
    if (prompt.index >= 0) {
      updateItemCount(prompt.index, prompt.typedText, {
        rungOverride: {
          key: prompt.choice.key,
          level: prompt.choice.level,
          optionId: prompt.choice.optionId,
          entryKind: prompt.choice.entryKind,
          unitLabel: prompt.choice.unitLabel,
          qtyInCountUnit: prompt.choice.qtyInCountUnit,
          allowsFractional: prompt.choice.allowsFractional,
        },
      });
    }
    maybePromptParTransitionForChoice(prompt.item, prompt.choice);
  };

  /**
   * A measure in a unit the browser has no factor for ("2.5 gal" on an item
   * counted in fl oz). The browser owns no conversion table, so this is the one
   * thing the sheet asks the server for. A failure returns null, which the
   * sheet shows as unconvertible — it never becomes a zero in the total.
   */
  const resolvePackageMeasure = useCallback(async ({ quantity, unit }) => {
    const item = packageSheetItem;
    if (!item?.id) return null;
    const storeId = selectedSession?.store_id || parseInt(localStorage.getItem('selectedStoreId'), 10);
    const { ok, totals } = await normalizeEntries({
      storeId,
      countItemId: item.id,
      entries: [{ entry_kind: 'manual_measure', entered_quantity: quantity, entered_unit: unit }],
    });
    if (!ok || !totals || totals.total == null) return null;
    return totals.total;
  }, [packageSheetItem, selectedSession]);

  /**
   * Stores the entries and the total they add up to.
   *
   * The on-screen count follows the saved total so the two can never disagree.
   * A total of null — nothing in the sheet could be converted — leaves the
   * line's number alone rather than writing a zero over a real count.
   */
  const savePackageEntries = useCallback(async (entries, total) => {
    const item = packageSheetItem;
    if (!item?.id) return;
    // A pending typed-entry mirror firing after this save would flatten the
    // sheet's mixed entries back into one synthetic package row.
    const timerKey = String(item.id);
    if (entryWriteTimersRef.current[timerKey]) {
      clearTimeout(entryWriteTimersRef.current[timerKey]);
      delete entryWriteTimersRef.current[timerKey];
    }
    const storeId = selectedSession?.store_id || parseInt(localStorage.getItem('selectedStoreId'), 10);
    const ladder = ladderForItem(item);

    setPackageSheetSaving(true);
    try {
      const result = await saveEntriesForCountItem({
        countItemId: item.id,
        storeId,
        countSessionId: item.count_session_id ?? selectedSession?.id ?? null,
        inventoryItemId: item.inventory_item_id ?? null,
        countUnit: ladder?.countUnit ?? item.item_snapshot?.unit ?? null,
        location: item.location ?? null,
        userId: getCurrentStoredUserId(),
        entries,
        rungs: ladder?.rungs || [],
      });

      setCountEntriesByCountItemId((prev) => ({ ...prev, [String(item.id)]: result.entries || [] }));

      if (result.total != null) {
        const index = countItems.findIndex((ci) => ci?.id === item.id);
        // The sheet's total is already in the count unit — it must not be
        // re-read as a package quantity by the active format.
        if (index >= 0) updateItemCount(index, String(result.total), { normalized: true });
      }
      if (result.hasUnresolved) {
        showTemplateSyncToast('Saved. Some packages could not be converted and are not in the total.');
      }
      setPackageSheetItem(null);
    } catch (err) {
      console.warn('[count-entries] Could not save the package count:', err?.message);
      showTemplateSyncToast(`Could not save the package count: ${err?.message || 'unknown error'}`);
    } finally {
      setPackageSheetSaving(false);
    }
  }, [packageSheetItem, selectedSession, ladderForItem, countItems, showTemplateSyncToast]);

  /**
   * Mirrors the main input into the line's raw entries, so a count typed as
   * "2 Costco bags" is stored as that fact and not only as its 8 lb total.
   *
   * Debounced per line (typing "24" must not write "2" first) and entirely
   * best-effort: entries are the working behind the number, and losing the
   * working must never cost somebody their count. The normalized total is
   * already persisted through the ordinary count path.
   */
  const scheduleTypedEntryWrite = (item, rung, typedValue, willBeCounted, opts = {}) => {
    if (!item?.id) return;
    // The package sheet writes its own entries; re-mirroring its total here
    // would flatten a mixed count into one synthetic package row.
    if (opts.normalized) return;
    const key = String(item.id);
    if (entryWriteTimersRef.current[key]) clearTimeout(entryWriteTimersRef.current[key]);
    entryWriteTimersRef.current[key] = setTimeout(() => {
      delete entryWriteTimersRef.current[key];
      persistTypedEntry(item, rung, typedValue, willBeCounted);
    }, 900);
  };

  const persistTypedEntry = async (item, rung, typedValue, willBeCounted) => {
    const key = String(item.id);
    try {
      if (!rung || !willBeCounted) {
        // Counting in the item's own unit (or clearing the line) makes any
        // stored package detail stale — the typed number is the new truth.
        if ((countEntriesByCountItemId[key] || []).length > 0) {
          await clearEntriesForCountItem(item.id);
          setCountEntriesByCountItemId((prev) => ({ ...prev, [key]: [] }));
        }
        return;
      }
      const quantity = parseFloat(typedValue);
      if (!Number.isFinite(quantity) || quantity < 0) return;
      const storeId = selectedSession?.store_id || parseInt(localStorage.getItem('selectedStoreId'), 10);
      const ladder = ladderForItem(item);
      const result = await saveEntriesForCountItem({
        countItemId: item.id,
        storeId,
        countSessionId: item.count_session_id ?? selectedSession?.id ?? null,
        inventoryItemId: item.inventory_item_id ?? null,
        countUnit: ladder?.countUnit ?? item.item_snapshot?.unit ?? null,
        location: item.location ?? null,
        userId: getCurrentStoredUserId(),
        entries: [
          {
            purchase_option_id: rung.optionId,
            entry_kind: rung.entryKind,
            entered_quantity: quantity,
            entered_unit: rung.unitLabel,
          },
        ],
        rungs: ladder?.rungs || [],
        // The mirror records the WORKING (which package the number meant);
        // the count itself was already persisted through the ordinary count
        // path. Recomputing it here through a fresh ladder read rewrote
        // settled counts a second after they were typed.
        updateActualQuantity: false,
      });
      if (result.ok) {
        setCountEntriesByCountItemId((prev) => ({ ...prev, [key]: result.entries || [] }));
      }
    } catch (err) {
      console.warn('[count-entries] typed package mirror failed (non-fatal):', err?.message);
    }
  };

  /**
   * Makes sure the line's current count exists as a stored entry NOW — called
   * before the package sheet opens on a switch, where the sheet's delete-then-
   * insert save would otherwise replace an unrecorded count instead of adding
   * beside it. Covers both a pending debounce and a line counted before
   * entries existed at all.
   */
  const flushTypedEntryWrite = async (item) => {
    const key = String(item?.id ?? '');
    const hasPendingTimer = !!entryWriteTimersRef.current[key];
    if (hasPendingTimer) {
      clearTimeout(entryWriteTimersRef.current[key]);
      delete entryWriteTimersRef.current[key];
    }
    const index = countItems.findIndex((ci) => ci?.id === item.id);
    if (index < 0) return;
    const hasStoredEntries = (countEntriesByCountItemId[key] || []).length > 0;
    if (!hasPendingTimer && hasStoredEntries) return;
    // The typed number is recorded in the rung it was TYPED in, not whatever
    // the resolver says now — the two can differ while a draft is alive.
    const rung = rungForLine(item);
    if (!rung) return;
    const typed = getEffectiveCount(index);
    if (!hasCountEntry(typed)) return;
    await persistTypedEntry(item, rung, typed, true);
  };

  const openVendorProductsModal = (item, snap, name) => {
    if (!item?.inventory_item_id) return;
    const storeId = previewSession?.store_id || selectedSession?.store_id
      || parseInt(localStorage.getItem('selectedStoreId'), 10);
    if (!storeId) return;
    setVendorProductsItem({
      inventoryItemId: item.inventory_item_id,
      name: name || 'Item',
      countUnit: snap?.unit || item?.par_unit || 'each',
      storeId,
      // Full item/snapshot so the editor can mirror legacy pricing into a
      // heal option when the item has no purchase options yet.
      item: { ...(item || {}), ...(snap || {}) },
    });
  };

  // Canonical-item flag for the count lists: rendered when the item has ≥2
  // active purchase options — several vendors, or one vendor with several
  // packs — or when it represents a working-set relationship group, where
  // the other vendors' products live on sibling items (link-don't-merge).
  // The badge gets the group-POOLED option list so its panel names every
  // vendor the group can buy from, exactly the pool the Savings Review
  // shops. Otherwise the caller keeps its plain single-vendor text.
  // Informational chrome — the badge stops propagation on its own clicks.
  const renderVendorPurchaseBadge = (item, snap, name) => {
    const invId = item?.inventory_item_id;
    const options = pooledOptionsByItemId[invId] || purchaseOptionsByItemId[invId] || [];
    const groupMembers = groupMembersFor(countGroupFold.groups, invId);
    const groupMemberCount = groupMembers ? groupMembers.size : 0;
    if (!canonicalLinkage(options, { groupMemberCount }).isCanonical) return null;
    return (
      <MultiVendorBadge
        options={options}
        itemName={name}
        countUnit={snap?.unit || item?.par_unit || 'each'}
        mobileTool={mobileTool}
        groupMemberCount={groupMemberCount}
        onViewDetails={() => openEditItemModal(item)}
        onManage={() => openVendorProductsModal(item, snap, name)}
        onPreferredChanged={() => refreshOptionsForItem(item.inventory_item_id)}
      />
    );
  };

  // "Related Products" modal — hosts PurchaseOptionsEditor for the item
  // whose Multi-vendor pill was used. Referenced by both shells below.
  const vendorProductsModal = vendorProductsItem ? (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(90,74,66,0.45)', zIndex: 10050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={() => setVendorProductsItem(null)}>
      <div style={{ background: '#FEFCFA', borderRadius: '14px', maxWidth: '560px', width: '100%', maxHeight: '80vh', overflowY: 'auto', padding: '14px 16px', border: '1px solid #E8DDD0', boxShadow: '0 12px 40px rgba(0,0,0,0.22)' }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', gap: '8px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#5A4A42' }}>
              Related Products
            </h3>
            <div style={{ fontSize: '0.8rem', color: '#8B7B73', marginTop: '0.15rem' }}>
              {vendorProductsItem.name}
              {vendorProductsItem.countUnit ? ` · counted in ${vendorProductsItem.countUnit}` : ''}
            </div>
          </div>
          <button type="button" style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#8B7B73', lineHeight: 1 }} onClick={() => setVendorProductsItem(null)}>&times;</button>
        </div>
        <PurchaseOptionsEditor
          storeId={vendorProductsItem.storeId}
          inventoryItemId={vendorProductsItem.inventoryItemId}
          countUnit={vendorProductsItem.countUnit}
          item={vendorProductsItem.item}
          onChanged={() => refreshOptionsForItem(vendorProductsItem.inventoryItemId)}
        />
      </div>
    </div>
  ) : null;

  // A package switch with a count already in the box. Never reinterprets on
  // its own: adding a second entry is offered first (mixed packaging is the
  // normal state of a shelf mid-transition), and changing in place carries the
  // warning that states both readings. Rendered in both shells below.
  const packageSwitchModal = packageSwitchPrompt ? (
    <div
      data-package-switch-modal="1"
      style={{ position: 'fixed', inset: 0, background: 'rgba(90,74,66,0.45)', zIndex: 10060, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={() => setPackageSwitchPrompt(null)}
    >
      <div
        style={{ background: '#FEFCFA', borderRadius: '14px', maxWidth: '420px', width: '100%', padding: '16px 18px', border: '1px solid #E8DDD0', boxShadow: '0 12px 40px rgba(0,0,0,0.22)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#5A4A42' }}>
          You already counted this line
        </h3>
        <p style={{ margin: '8px 0 12px', fontSize: '0.82rem', color: '#6D5A4E', lineHeight: 1.45 }}>
          {packageSwitchPrompt.plan.warning}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            type="button"
            onClick={confirmSwitchAddEntry}
            style={{ background: '#7B4B2A', color: '#FFF7EE', border: 'none', borderRadius: '10px', padding: '10px 12px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', textAlign: 'left' }}
          >
            {packageSwitchPrompt.plan.addLabel}
            <span style={{ display: 'block', fontWeight: 500, fontSize: '0.72rem', opacity: 0.85 }}>
              Keeps what you counted and adds the new package beside it.
            </span>
          </button>
          <button
            type="button"
            onClick={confirmSwitchChangeEntry}
            style={{ background: '#FFFFFF', color: '#5A4A42', border: '1px solid #C8B5A0', borderRadius: '10px', padding: '10px 12px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', textAlign: 'left' }}
          >
            {packageSwitchPrompt.plan.changeLabel}
            <span style={{ display: 'block', fontWeight: 500, fontSize: '0.72rem', color: '#8B7B73' }}>
              The number you typed will now count the new package.
            </span>
          </button>
          <button
            type="button"
            onClick={() => setPackageSwitchPrompt(null)}
            style={{ background: 'transparent', color: '#8B7B73', border: 'none', padding: '6px 4px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // "Your usual package changed" — rendered in both shells beside the
  // package sheet. The user still chooses; the suggestion is only shown.
  const parTransitionModal = parTransitionPrompt ? (
    <ParTransitionPrompt
      itemName={parTransitionPrompt.item.item_snapshot?.description
        || parTransitionPrompt.item.item_snapshot?.name
        || parTransitionPrompt.item.description
        || 'Item'}
      transition={parTransitionPrompt.transition}
      recommendation={parTransitionPrompt.recommendation}
      busy={parTransitionPrompt.busy}
      onKeep={() => settleParTransition('keep_quantity')}
      onPreservePackageCount={() => settleParTransition('preserve_package_count')}
      onDismiss={() => settleParTransition('dismissed')}
    />
  ) : null;

  // "Count by package" — the entries behind a count line's number. Rendered in
  // both shells below alongside vendorProductsModal.
  const packageCountSheet = packageSheetItem ? (
    <PackageCountSheet
      open
      mobile={mobileTool}
      saving={packageSheetSaving}
      itemName={packageSheetItem.item_snapshot?.description || packageSheetItem.item_snapshot?.name || 'Item'}
      countUnit={ladderForItem(packageSheetItem)?.countUnit || packageSheetItem.item_snapshot?.unit || 'each'}
      rungs={ladderForItem(packageSheetItem)?.rungs || []}
      unresolvedOptions={ladderForItem(packageSheetItem)?.unresolvedOptions || []}
      entries={countEntriesByCountItemId[String(packageSheetItem.id)] || []}
      onResolveMeasure={resolvePackageMeasure}
      onSave={savePackageEntries}
      onClose={() => setPackageSheetItem(null)}
    />
  ) : null;

  const saveEditItemFromModal = async () => {
    const item = countItems[selectedItemIndex];
    if (!item?.inventory_item_id) {
      showAppAlert({
        title: 'Cannot edit item',
        message: 'Missing inventory item for this line.',
        confirmButtonStyle: 'danger'
      });
      return;
    }
    const desc = (editItemDraft.description || '').trim();
    if (!desc) {
      showAppAlert({
        title: 'Description required',
        message: 'Please provide a description before saving.',
        confirmButtonStyle: 'warning'
      });
      return;
    }
    const storeId = parseInt(localStorage.getItem('selectedStoreId'), 10);
    if (!storeId) {
      showAppAlert({
        title: 'Store missing',
        message: 'No store selected.',
        confirmButtonStyle: 'danger'
      });
      return;
    }

    // Disable the Save button immediately (before any async work) so the user
    // cannot double-submit and re-enter this flow while PAR persistence
    // is in flight. The matching setEditItemSaving(false) lives in the finally
    // block of the snapshot save below.
    setEditItemSaving(true);

    // Apply the PAR update via the global helper. The helper:
    //   - writes the new PAR to every sibling location row for this
    //     inventory item in the session in ONE setCountItems pass,
    //   - recomputes the shared shortage and need (so already-counted
    //     rows see their NEED badge update immediately),
    //   - persists the PAR to count_items in a single Supabase call.
    // Vendor-product MOQ is edited only by the focused SET PAR modal, which
    // identifies the purchase option before writing its ordering rule.
    const draftParRaw = String(editItemDraft.par_level ?? '').trim();
    const draftPar = draftParRaw === '' ? null : parseFloat(draftParRaw);
    const currentParNum = parseFloat(item.par_level ?? item.expected_quantity);
    const parChanged = draftPar !== null && Number.isFinite(draftPar) && draftPar >= 0
      && (!Number.isFinite(currentParNum) || draftPar !== currentParNum);
    if (parChanged) {
      await updateItemFieldsGlobal(selectedItemIndex, {
        par_level: draftPar,
        par_defaulted: false,
      });
    }

    const casePrice = normalizeMoneyValue(editItemDraft.case_price);
    const unitPrice = normalizeMoneyValue(editItemDraft.unit_price);
    const prevSnap = item.item_snapshot && typeof item.item_snapshot === 'object' ? { ...item.item_snapshot } : {};
    const normalizedDesc = (editItemDraft.description || '').trim();
    const normalizedVendor = (editItemDraft.vendor || '').trim();
    const normalizedUnit = (editItemDraft.unit || '').trim() || 'each';
    const previousDesc = (prevSnap.description || prevSnap.name || '').trim();
    const previousVendor = (prevSnap.vendor || '').trim();
    const previousUnit = (prevSnap.unit || item.par_unit || 'each').trim() || 'each';
    const identityChanged = (
      previousDesc.toLowerCase() !== normalizedDesc.toLowerCase()
      || previousVendor.toLowerCase() !== normalizedVendor.toLowerCase()
      || previousUnit.toLowerCase() !== normalizedUnit.toLowerCase()
    );

    setEditItemSaving(true);
    try {
      const u = unitPrice != null ? unitPrice : (parseFloat(prevSnap.unit_price ?? prevSnap.each_price) || 0);
      const c = casePrice != null ? casePrice : (parseFloat(prevSnap.case_price) || 0);

      const vendorAssignment = await assignVendorToItem({
        supabaseClient: supabase,
        storeId,
        vendorName: normalizedVendor,
        stubNote: 'Auto-created from count item edit'
      });

      // Stamp the resolved vendor_id into the snapshot alongside the
      // existing `vendor` name so freshly-edited items don't depend on
      // the SQL `refresh_open_count_item_snapshots` RPC firing later
      // (it only runs on open sessions). Falls back to any existing
      // value rather than overwriting a real id with null.
      const resolvedVendorId = vendorAssignment?.vendorId ?? prevSnap.vendor_id ?? null;
      const nextSnap = normalizeSnapshotPrices({
        ...prevSnap,
        description: normalizedDesc,
        vendor: normalizedVendor || null,
        vendor_id: resolvedVendorId,
        category: (editItemDraft.category || '').trim() || null,
        unit: normalizedUnit,
        case_price: c,
        unit_price: u,
        each_price: u,
      });

      const loc = (editItemDraft.location || '').trim() || item.location;
      let nextInventoryItemId = item.inventory_item_id;

      if (identityChanged) {
        const createNewItem = await showAppConfirm({
          title: 'This looks like a different item',
          message: 'Name, vendor, or unit changed. Create and count a new item for this session? Choose "No" to permanently edit this item across open orders and EOMs.',
          confirmText: 'Create new item',
          cancelText: 'Permanently edit this item',
          confirmButtonStyle: 'warning'
        });

        if (createNewItem) {
          const { data: baseInventoryItem, error: baseItemError } = await supabase
            .from('inventory_items')
            .select('*')
            .eq('id', item.inventory_item_id)
            .eq('store_id', storeId)
            .single();

          if (baseItemError || !baseInventoryItem) {
            throw baseItemError || new Error('Could not load the original inventory item.');
          }

          const {
            id: _oldId,
            created_at: _createdAt,
            updated_at: _updatedAt,
            par_level: baseParLevel,
            par_defaulted: baseParDefaulted,
            moq: _legacyMoq,
            moq_unit: _legacyMoqUnit,
            ...basePayload
          } = baseInventoryItem;

          const newInventoryPayload = {
            ...basePayload,
            description: normalizedDesc,
            vendor: toNullableText(vendorAssignment.vendorDisplayName || normalizedVendor),
            category: toNullableText(editItemDraft.category),
            unit: toNullableText(normalizedUnit) || 'each',
            location: toNullableText(editItemDraft.location) || baseInventoryItem.location || item.location || null,
            case_price: casePrice ?? 0,
            unit_price: unitPrice ?? 0,
          };
          // A genuinely new identity gets the same creation semantics as every
          // other channel. Leave an unreviewed automatic PAR omitted so the DB
          // assigns one of the NEW item's unit and keeps SET PAR visible. Carry
          // only a reviewed/explicit PAR (including zero). Legacy item-level
          // MOQ is intentionally not cloned; the new vendor product defaults
          // to one package in purchase_options.
          const sourceParDefaulted = item.par_defaulted ?? baseParDefaulted ?? false;
          const explicitParForNew = parChanged
            ? draftPar
            : (sourceParDefaulted ? null : (Number.isFinite(currentParNum) ? currentParNum : baseParLevel));
          if (explicitParForNew !== null && explicitParForNew !== undefined) {
            newInventoryPayload.par_level = explicitParForNew;
          }
          if (vendorAssignment.vendorId) newInventoryPayload.vendor_id = vendorAssignment.vendorId;

          const { data: returnedItem, error: createItemError } = await supabase
            .from('inventory_items')
            .insert(newInventoryPayload)
            .select('*')
            .maybeSingle();

          if (createItemError) throw createItemError;
          const createdItem = returnedItem || await findInventoryItemAfterDuplicateMerge(
            storeId,
            newInventoryPayload,
            {},
            supabase,
          );
          if (!createdItem?.id) throw new Error('Failed to resolve the new inventory item.');
          nextInventoryItemId = createdItem.id;

          // Mirror the new item into the multi-vendor purchase_options model
          // (its own way-to-buy) and surface fuzzy-duplicate cross-reference
          // suggestions. Best-effort: awaited inside a try/catch that only
          // warns, so the counting UX never fails or blocks on this (offline
          // or not-yet-deployed tables are fine to swallow).
          try {
            const seenCatalogIds = new Set();
            const countCatalog = [];
            countItems.forEach((ci) => {
              const invId = ci.inventory_item_id;
              if (!invId || invId === createdItem.id || seenCatalogIds.has(invId)) return;
              seenCatalogIds.add(invId);
              const snap = ci.item_snapshot || {};
              countCatalog.push({
                id: invId,
                description: snap.description,
                brand: snap.brand,
                unit: snap.unit,
                pack_size: snap.pack_size,
                vendor: snap.vendor,
                strict_specification: snap.strict_specification,
              });
            });
            await createOptionForItem(
              storeId,
              { ...newInventoryPayload, id: createdItem.id },
              { channel: 'counting', catalog: countCatalog }
            );
          } catch (optionErr) {
            console.warn('[purchase-options] createOptionForItem failed (non-fatal):', optionErr?.message);
          }
        } else {
          const invUpdate = {
            description: normalizedDesc,
            vendor: toNullableText(vendorAssignment.vendorDisplayName || normalizedVendor),
            category: toNullableText(editItemDraft.category),
            unit: toNullableText(normalizedUnit) || 'each',
            location: toNullableText(editItemDraft.location),
            case_price: casePrice ?? 0,
            unit_price: unitPrice ?? 0,
          };
          if (vendorAssignment.vendorId) invUpdate.vendor_id = vendorAssignment.vendorId;

          const { error: invErr } = await supabase
            .from('inventory_items')
            .update(invUpdate)
            .eq('id', item.inventory_item_id)
            .eq('store_id', storeId);

          if (invErr) throw invErr;

          await recordPriceHistory({
            inventoryItemId: item.inventory_item_id,
            storeId,
            oldName: prevSnap.description || null,
            newName: normalizedDesc,
            oldCasePrice: normalizeMoneyValue(prevSnap.case_price),
            newCasePrice: casePrice,
            oldUnitPrice: normalizeMoneyValue(prevSnap.unit_price ?? prevSnap.each_price),
            newUnitPrice: unitPrice,
            source: 'web_count',
          });

          // After updating the inventory item, refresh snapshots on ALL open sessions
          // for this item so every open count reflects the current item data.
          // This preserves count-specific fields (location override, count_position, etc.)
          // while syncing item-level fields (price, description, vendor, etc.).
          await supabase.rpc('refresh_open_count_item_snapshots', {
            p_store_id: storeId,
            p_session_id: null,
          }).then(({ error: rpcErr }) => {
            if (rpcErr) console.warn('refresh_open_count_item_snapshots failed (non-fatal):', rpcErr.message);
          });
        }
      } else {
        const invUpdate = {
          description: normalizedDesc,
          vendor: toNullableText(vendorAssignment.vendorDisplayName || normalizedVendor),
          category: toNullableText(editItemDraft.category),
          unit: toNullableText(normalizedUnit) || 'each',
          location: toNullableText(editItemDraft.location),
          case_price: casePrice ?? 0,
          unit_price: unitPrice ?? 0,
        };
        if (vendorAssignment.vendorId) invUpdate.vendor_id = vendorAssignment.vendorId;

        const { error: invErr } = await supabase
          .from('inventory_items')
          .update(invUpdate)
          .eq('id', item.inventory_item_id)
          .eq('store_id', storeId);

        if (invErr) throw invErr;

        await recordPriceHistory({
          inventoryItemId: item.inventory_item_id,
          storeId,
          oldName: prevSnap.description || null,
          newName: normalizedDesc,
          oldCasePrice: normalizeMoneyValue(prevSnap.case_price),
          newCasePrice: casePrice,
          oldUnitPrice: normalizeMoneyValue(prevSnap.unit_price ?? prevSnap.each_price),
          newUnitPrice: unitPrice,
          source: 'web_count',
        });

        await supabase.rpc('refresh_open_count_item_snapshots', {
          p_store_id: storeId,
          p_session_id: null,
        }).then(({ error: rpcErr }) => {
          if (rpcErr) console.warn('refresh_open_count_item_snapshots failed (non-fatal):', rpcErr.message);
        });
      }

      const { error: ciErr } = await supabase
        .from('count_items')
        .update({
          inventory_item_id: nextInventoryItemId,
          location: loc,
          item_snapshot: nextSnap,
        })
        .eq('id', item.id);

      if (ciErr) throw ciErr;

      setCountItems((prev) => {
        const next = [...prev];
        const idx = selectedItemIndex;
        if (next[idx]) {
          next[idx] = {
            ...next[idx],
            inventory_item_id: nextInventoryItemId,
            location: loc,
            item_snapshot: nextSnap,
          };
        }
        return next;
      });
      setShowEditItemModal(false);
    } catch (e) {
      console.error('Edit item save failed:', e);
      showAppAlert({
        title: 'Save failed',
        message: e?.message || 'Failed to save item.',
        confirmButtonStyle: 'danger'
      });
    } finally {
      setEditItemSaving(false);
    }
  };

  const backspaceCount = () => {
    const currentValue = getEffectiveCount(selectedItemIndex);
    const newValue = currentValue.slice(0, -1);
    updateItemCount(selectedItemIndex, newValue);
  };

  // ── Mobile tape edit mode ─────────────────────────────────────────────────
  // Long-press a tape card to enter edit mode (iOS Home-screen style):
  //   • All cards wiggle.
  //   • A small × badge appears in the upper-right of every card.
  //   • Press-and-hold any card again to drag it horizontally; release to drop
  //     in the new position. Vertical movement is ignored. Reaching the
  //     left/right edge of the strip auto-scrolls when more items are
  //     off-screen.
  //   • Tapping outside the strip exits edit mode.
  //   • Tap × → styled confirmation modal → Yes removes the item from the
  //     current count AND the parent template + sibling template session.
  // The reorder/delete persistence mirrors the PAR propagation pattern
  // (see updateItemFieldsGlobal): write the per-count change, then best-effort
  // propagate to count_template_items and the is_template count_session.
  const exitTapeEditMode = useCallback(() => {
    if (tapeAutoScrollRafRef.current) {
      cancelAnimationFrame(tapeAutoScrollRafRef.current);
      tapeAutoScrollRafRef.current = null;
    }
    tapeAutoScrollDirRef.current = 0;
    if (tapeLongPressTimerRef.current) {
      clearTimeout(tapeLongPressTimerRef.current);
      tapeLongPressTimerRef.current = null;
    }
    tapeLongPressStartRef.current = null;
    setTapeDrag(null);
    setTapeEditMode(false);
  }, []);

  // Lazily resolve the template name for the delete-confirm modal copy.
  // Prefers the snapshot already on the session, falls back to a fetch.
  useEffect(() => {
    if (!tapeEditMode) return;
    if (!selectedSession) return;
    const snap = selectedSession.template_name_snapshot;
    if (snap && tapeTemplateName !== snap) {
      setTapeTemplateName(snap);
      return;
    }
    if (snap) return;
    if (!selectedSession.template_id) {
      if (tapeTemplateName !== null) setTapeTemplateName(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const tpl = await fetchTemplateMeta(supabase, selectedSession.template_id);
        if (!cancelled) setTapeTemplateName(tpl?.name || null);
      } catch (_) {
        if (!cancelled) setTapeTemplateName(null);
      }
    })();
    return () => { cancelled = true; };
  }, [tapeEditMode, selectedSession?.id, selectedSession?.template_id, selectedSession?.template_name_snapshot]);

  // Auto-scroll the tape container while dragging near its left/right edge.
  const startTapeAutoScroll = useCallback((direction) => {
    if (tapeAutoScrollDirRef.current === direction) return;
    tapeAutoScrollDirRef.current = direction;
    if (direction === 0) {
      if (tapeAutoScrollRafRef.current) {
        cancelAnimationFrame(tapeAutoScrollRafRef.current);
        tapeAutoScrollRafRef.current = null;
      }
      return;
    }
    const tick = () => {
      const container = tapeContainerRef.current;
      const dir = tapeAutoScrollDirRef.current;
      if (!container || dir === 0) {
        tapeAutoScrollRafRef.current = null;
        return;
      }
      container.scrollLeft += dir * 12;
      tapeAutoScrollRafRef.current = requestAnimationFrame(tick);
    };
    tapeAutoScrollRafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => () => {
    if (tapeAutoScrollRafRef.current) cancelAnimationFrame(tapeAutoScrollRafRef.current);
    if (tapeLongPressTimerRef.current) clearTimeout(tapeLongPressTimerRef.current);
  }, []);

  // Persist a reorder produced by the tape: optimistic in-memory swap, then
  // a best-effort write to count_items + parent template + sibling template
  // session (mirrors the PAR-level propagation pattern).
  const commitTapeReorder = useCallback(async (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex == null || toIndex == null) return;
    const next = [...countItems];
    if (fromIndex < 0 || fromIndex >= next.length) return;
    if (toIndex < 0 || toIndex > next.length) return;
    const [moved] = next.splice(fromIndex, 1);
    const insertAt = toIndex > fromIndex ? toIndex - 1 : toIndex;
    next.splice(Math.max(0, Math.min(insertAt, next.length)), 0, moved);
    const renumbered = next.map((ci, idx) => ({ ...ci, count_position: (idx + 1) * 10 }));
    setCountItems(renumbered);
    // Keep the moved item visually selected so the user's focus follows it.
    const newSelectedIndex = renumbered.findIndex((ci) => ci.id === moved.id);
    if (newSelectedIndex >= 0) setSelectedItemIndex(newSelectedIndex);

    if (!selectedSession?.id) return;
    try {
      await persistTapeReorder({
        sessionId: selectedSession.id,
        templateId: selectedSession.template_id ?? null,
        countGroupId: selectedSession.count_group_id ?? null,
        orderedRows: renumbered.map((ci) => ({
          id: ci.id,
          inventory_item_id: ci.inventory_item_id,
          location: ci.location || ci.item_snapshot?.location || null,
        })),
      });
      showTemplateSyncToast('Order updated in template');
    } catch (e) {
      console.warn('[tape-reorder] persist failed:', e?.message);
    }
  }, [countItems, selectedSession, showTemplateSyncToast]);

  // Open the styled confirmation modal for a tape-initiated delete. Actual
  // deletion runs in confirmTapeDelete after the user taps Yes.
  const requestTapeDelete = useCallback((index) => {
    const item = countItems[index];
    if (!item) return;
    const itemName = item.item_snapshot?.description || item.item_snapshot?.name || 'this item';
    setTapeDeleteConfirm({
      countItemId: item.id,
      inventoryItemId: item.inventory_item_id,
      itemName,
      itemIndex: index,
    });
  }, [countItems]);

  const cancelTapeDelete = useCallback(() => {
    if (tapeDeletePending) return;
    setTapeDeleteConfirm(null);
  }, [tapeDeletePending]);

  const confirmTapeDelete = useCallback(async () => {
    const target = tapeDeleteConfirm;
    if (!target) return;
    if (!selectedSession?.id) return;
    setTapeDeletePending(true);
    try {
      // Optimistic local removal so the wiggle layout updates immediately.
      const next = countItems.filter((ci) => ci.id !== target.countItemId);
      const renumbered = next.map((ci, idx) => ({ ...ci, count_position: (idx + 1) * 10 }));
      setCountItems(renumbered);
      // Keep the selection valid: clamp to the new length.
      setSelectedItemIndex((prev) => {
        if (renumbered.length === 0) return 0;
        return Math.min(prev, renumbered.length - 1);
      });

      const result = await persistTapeDelete({
        sessionId: selectedSession.id,
        templateId: selectedSession.template_id ?? null,
        countGroupId: selectedSession.count_group_id ?? null,
        countItemId: target.countItemId,
        inventoryItemId: target.inventoryItemId,
      });

      // Renumber count_position on remaining rows so subsequent reorder math
      // and the next session creation see a contiguous order.
      if (renumbered.length > 0) {
        try {
          await Promise.all(
            renumbered.map((ci) =>
              supabase
                .from('count_items')
                .update({ count_position: ci.count_position })
                .eq('id', ci.id)
            )
          );
        } catch (e) {
          console.warn('[tape-delete] renumber after delete failed:', e?.message);
        }
      }

      if (result?.templateDeleted) {
        showTemplateSyncToast('Removed from template');
      } else if (result?.ok) {
        showTemplateSyncToast('Removed from this count');
      }
    } catch (err) {
      console.warn('[tape-delete] failed:', err?.message);
    } finally {
      setTapeDeletePending(false);
      setTapeDeleteConfirm(null);
    }
  }, [tapeDeleteConfirm, selectedSession, countItems, showTemplateSyncToast]);

  const selectItem = (index, { fromNext = false } = {}) => {
    // Any navigation that is NOT a Count/Next advance should clear the
    // blank-on-arrival flag so the item shows its real stored value.
    if (!fromNext) {
      blankOnNextIndexRef.current = null;
    }
    setSelectedItemIndex(index);
    setShowOrderEditModal(false);
    // Keep left selector aligned with the active group mode (location/vendor/category/A-Z)
    const item = countItems[index];
    const groupValue = sortBy === 'alphabetical' ? 'All Items' : getGroupValueForItem(item);
    setSelectedLocation(groupValue);
    // Scroll to item and focus its input
    setTimeout(() => {
      const element = document.getElementById(`count-item-${index}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = element.querySelector('input[data-count-input]');
        if (input) {
          input.focus();
          input.select();
        }
      }
    }, 50);
  };

  // ── Bottle fraction slider ─────────────────────────────────────────────────
  // On request only: the count card's small bottle tab is the one way in. A
  // typed decimal is taken at face value and never interrupts the count.
  const openBottleSheet = (itemIndex) => {
    if (!bottleInfoForItem(countItems[itemIndex])) return;
    const current = getEffectiveCount(itemIndex);
    setBottleSheet({
      itemIndex,
      initialTenths: tenthsFromCountValue(current),
      wholePart: Math.floor(parseFloat(current) || 0),
    });
  };

  // Done. The slider owns the DECIMAL only — the whole bottles already
  // entered are kept — and the write goes through updateItemCount so drafts,
  // offline sync and the subscription gate all behave exactly as if the
  // number had been typed.
  const handleBottleDone = (tenths) => {
    const sheet = bottleSheet;
    setBottleSheet(null);
    if (!sheet) return;
    const current = getEffectiveCount(sheet.itemIndex);
    updateItemCount(sheet.itemIndex, applyBottleTenths(current, tenths));
  };

  // Cancel — close without touching the count.
  const handleBottleCancel = () => {
    setBottleSheet(null);
  };

  // Rendered in both shells beside packageCountSheet.
  const bottleCountOverlay = bottleSheet ? (
    <BottleCountOverlay
      mobile={mobileTool}
      itemName={countItems[bottleSheet.itemIndex]?.item_snapshot?.description
        || countItems[bottleSheet.itemIndex]?.item_snapshot?.name
        || 'Item'}
      unitLabel={countItems[bottleSheet.itemIndex]?.item_snapshot?.unit
        || countItems[bottleSheet.itemIndex]?.par_unit
        || 'bottle'}
      wholePart={bottleSheet.wholePart}
      initialTenths={bottleSheet.initialTenths}
      onDone={handleBottleDone}
      onCancel={handleBottleCancel}
    />
  ) : null;

  // Mobile rooms strip: tap a room to jump to its first uncounted item,
  // matching the web room-tile behavior.
  const handleMobileRoomSelect = useCallback((room) => {
    if (!room) return;
    setMobileActiveRoom(room.label);
    const target = room.firstUncountedIndex >= 0 ? room.firstUncountedIndex : room.firstIndex;
    if (target >= 0) selectItem(target);
  // selectItem is defined in the same render scope; deps are stable.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countItems]);

  const selectItemFromListInput = (index) => {
    if (index === selectedItemIndex) return;
    blankOnNextIndexRef.current = null; // Manual navigation clears the blank flag
    setSelectedItemIndex(index);
    const item = countItems[index];
    const groupValue = sortBy === 'alphabetical' ? 'All Items' : getGroupValueForItem(item);
    setSelectedLocation(groupValue);
  };

  const nextItem = () => {
    // Peek mode: if the user jumped here from a search, return to the
    // pre-jump position instead of advancing to the next visible item.
    if (peekReturnIndex !== null && peekReturnIndex !== selectedItemIndex) {
      const target = peekReturnIndex;
      setPeekReturnIndex(null);
      selectItem(target);
      return;
    }
    if (peekReturnIndex !== null) setPeekReturnIndex(null);
    const visibleIndexes = getVisibleItemIndexes();
    if (visibleIndexes.length === 0) return;
    const currentVisiblePosition = visibleIndexes.indexOf(selectedItemIndex);
    const nextVisiblePosition = currentVisiblePosition < 0
      ? 0
      : Math.min(currentVisiblePosition + 1, visibleIndexes.length - 1);
    selectItem(visibleIndexes[nextVisiblePosition], { fromNext: true });
  };

  // Jump to an item via search and remember where we were so the next
  // count-and-advance returns the user to their original position.
  const selectItemWithPeek = (targetIndex) => {
    if (targetIndex === selectedItemIndex) return;
    setPeekReturnIndex(selectedItemIndex);
    selectItem(targetIndex);
  };

  const previousItem = () => {
    const visibleIndexes = getVisibleItemIndexes();
    if (visibleIndexes.length === 0) return;
    const currentVisiblePosition = visibleIndexes.indexOf(selectedItemIndex);
    const previousVisiblePosition = currentVisiblePosition < 0
      ? 0
      : Math.max(currentVisiblePosition - 1, 0);
    selectItem(visibleIndexes[previousVisiblePosition]);
  };

  // Task #746 — animate the keypad sliding in the swipe direction.
  // dir: 'left' (next item) or 'right' (prev item).
  // navFn: the navigation function to call mid-animation.
  // Respects prefers-reduced-motion: skip animation and navigate instantly.
  // Skips animation (but still navigates) if already at a list boundary so
  // a "page turn" is never shown when no item change will occur.
  const KEYPAD_ANIM_DURATION = 220;
  const triggerSwipeNav = (dir, navFn) => {
    // Boundary check: skip animation if the swipe won't actually move to a
    // different item (e.g. already at first/last visible item).
    // peekReturnIndex is the jump-back case for nextItem — that always moves.
    const willNavigate = (() => {
      const vis = getVisibleItemIndexes();
      if (vis.length === 0) return false;
      const pos = vis.indexOf(selectedItemIndex);
      if (dir === 'left') {
        // nextItem: moves unless already at last position (and no peek-return pending)
        return peekReturnIndex !== null || pos < vis.length - 1;
      }
      // previousItem: moves unless already at first position
      return pos > 0;
    })();
    if (!willNavigate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      navFn();
      return;
    }
    // Cancel any in-flight animation so rapid swipes restart cleanly.
    if (swipeNavTimerRef.current) {
      clearTimeout(swipeNavTimerRef.current);
      swipeNavTimerRef.current = null;
      setKeypadAnimClass('');
    }
    const exitClass = dir === 'left' ? 'keypad-slide-exit-left' : 'keypad-slide-exit-right';
    const enterClass = dir === 'left' ? 'keypad-slide-enter-from-right' : 'keypad-slide-enter-from-left';
    setKeypadAnimClass(exitClass);
    swipeNavTimerRef.current = setTimeout(() => {
      navFn();
      setKeypadAnimClass(enterClass);
      swipeNavTimerRef.current = setTimeout(() => {
        setKeypadAnimClass('');
        swipeNavTimerRef.current = null;
      }, KEYPAD_ANIM_DURATION);
    }, KEYPAD_ANIM_DURATION);
  };

  // Unmount cleanup: clear any pending swipe-nav animation timer so the
  // component does not attempt state updates after it has been torn down.
  useEffect(() => {
    return () => {
      if (swipeNavTimerRef.current) {
        clearTimeout(swipeNavTimerRef.current);
      }
    };
  }, []);

  // Enter key: commit a typed value (including an explicit "0") and advance.
  // If nothing was typed, just advance without marking the item counted.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && step === 'counting') {
        // The bottle sheet owns Enter/Escape while it is open — committing
        // under it would fire a navigation the sheet is there to intercept.
        if (bottleSheet) return;
        // Mobile search input handles its own Enter (jump to result). Skip the
        // global commit-and-advance so we don't double-act on the same key.
        const active = document.activeElement;
        if (active && active.dataset && active.dataset.mobileSearch === '1') return;
        e.preventDefault();
        const item = countItems[selectedItemIndex];
        const effectiveCount = getEffectiveCount(selectedItemIndex);
        const didCommit = item && !item.is_counted && hasCountEntry(effectiveCount);
        if (didCommit) {
          updateItemCount(selectedItemIndex, effectiveCount);
          onCountCommit();
        }
        if (didCommit && isAtLastItemInRoom() && isRoomAboutToBeComplete()) {
          setEndOfRoomModal({ nextRoom: getNextRoomWithUncounted() });
        } else if (selectedItemIndex < countItems.length - 1) {
          flagNextItemBlank();
          nextItem();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedItemIndex, countItems, step, bottleSheet]);

  // Mobile tape auto-recenter: keeps the selected card centered in the
  // horizontal scroll strip whenever the selection changes. Only runs when
  // mobileTool is on and the tape is in the DOM. Suspended in edit mode so
  // the auto-scroll doesn't fight the user's drag gesture.
  useEffect(() => {
    if (!mobileTool || step !== 'counting') return;
    if (tapeEditMode) return;
    const el = document.getElementById(`count-mobile-tape-item-${selectedItemIndex}`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [mobileTool, step, selectedItemIndex, tapeEditMode]);

  // Tap outside the tape (or press Escape) exits tape edit mode. The strip
  // itself catches clicks via its handlers, so this only fires when the user
  // commits to leaving edit mode.
  useEffect(() => {
    if (!tapeEditMode) return undefined;
    const onDocPointer = (e) => {
      const container = tapeContainerRef.current;
      if (!container) return;
      if (container.contains(e.target)) return;
      // Ignore clicks inside the delete-confirm modal (or its backdrop) so
      // dismissing the confirm doesn't also drop edit mode — the user may
      // want to cancel the delete and keep editing.
      if (tapeDeleteConfirm) return;
      if (e.target.closest && e.target.closest('[data-tape-delete-modal="1"]')) return;
      exitTapeEditMode();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') exitTapeEditMode();
    };
    document.addEventListener('pointerdown', onDocPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [tapeEditMode, tapeDeleteConfirm, exitTapeEditMode]);

  // Window-level pointer-up safety net: if the user lifts their finger off
  // a non-card element while dragging (e.g. between cards or off the strip
  // entirely), the per-card pointer-up never fires. Commit the drag here
  // instead so the reorder isn't silently dropped. Mirrors the pointer-up
  // path on the card.
  useEffect(() => {
    if (!tapeDrag) return undefined;
    const onWindowUp = () => {
      const drag = tapeDrag;
      startTapeAutoScroll(0);
      setTapeDrag(null);
      if (drag && drag.visibleStartPos !== drag.toIndex) {
        // Reuse the same master-index translation used by the in-card
        // pointer-up handler.
        const visibleEntries = (mobileRooms.length > 1 && mobileActiveRoom
          ? countItems
              .map((ti, i) => ({ ti, i }))
              .filter(({ ti }) => resolveCountItemLocation(ti) === mobileActiveRoom)
          : countItems.map((ti, i) => ({ ti, i })));
        const fromMasterIdx = drag.fromIndex;
        let toMasterIdx;
        if (drag.toIndex >= visibleEntries.length) {
          const lastVisible = visibleEntries[visibleEntries.length - 1];
          toMasterIdx = lastVisible ? lastVisible.i + 1 : countItems.length;
        } else {
          toMasterIdx = visibleEntries[drag.toIndex]?.i ?? fromMasterIdx;
        }
        commitTapeReorder(fromMasterIdx, toMasterIdx);
      }
    };
    window.addEventListener('pointerup', onWindowUp);
    window.addEventListener('pointercancel', onWindowUp);
    return () => {
      window.removeEventListener('pointerup', onWindowUp);
      window.removeEventListener('pointercancel', onWindowUp);
    };
  }, [tapeDrag, mobileRooms.length, mobileActiveRoom, countItems, commitTapeReorder, startTapeAutoScroll]);

  useEffect(() => {
    if (step !== 'counting' || countItems.length === 0) return;

    if (sortBy === 'alphabetical') {
      if (selectedLocation !== 'All Items') {
        setSelectedLocation('All Items');
      }
      return;
    }

    // Order groups exactly as the on-screen group list displays them (see
    // getAllGroupsOrdered) so the initial selection lands on the FIRST
    // displayed group. The previous ad-hoc sort here ignored count_position
    // and fell back to alphabetical labels, which could select a different
    // location than the one listed first.
    const groups = getAllGroupsOrdered().map((group) => group.label);
    if (groups.length === 0) return;

    const activeGroup = selectedLocation && groups.includes(selectedLocation) ? selectedLocation : groups[0];
    if (selectedLocation !== activeGroup) {
      setSelectedLocation(activeGroup);
    }

    const selectedItem = countItems[selectedItemIndex];
    const selectedItemGroup = getGroupValueForItem(selectedItem, sortBy);
    if (selectedItemGroup !== activeGroup) {
      const firstItemInGroupIndex = countItems.findIndex((item) => getGroupValueForItem(item, sortBy) === activeGroup);
      if (firstItemInGroupIndex >= 0 && firstItemInGroupIndex !== selectedItemIndex) {
        setSelectedItemIndex(firstItemInGroupIndex);
      }
    }
  }, [step, countItems, sortBy, selectedLocation, selectedItemIndex]);


  const saveProgress = async (sessionStatus = null, options = {}) => {
    const { silent = false, itemsOverride = null, onStep = null, suppressAlert = false } = options;
    if (!selectedSession) return { ok: false, error: new Error('No selected session.') };

    const itemsToPersist = Array.isArray(itemsOverride) ? itemsOverride : countItems;
    const directMode = isDirectOrderSession(selectedSession);
    setLoading(true);
    try {
      // Get current user for order events from localStorage
      const storedUser = localStorage.getItem('currentUser');
      const userAccount = storedUser ? JSON.parse(storedUser).email : 'unknown';

      onStep?.('Updating items');

      // Update count items in database
      // Note: count_items has calculated_order_quantity and manual_order_quantity.
      // There is no order_quantity column — omit it from all writes.
      // Direct order mode persists the entered value as the order amount
      // (manual_order_quantity) and never writes actual_quantity, so on-hand is
      // left untouched.
      const updates = itemsToPersist.map(item => ({
        id: item.id,
        actual_quantity: directMode ? undefined : item.current_count,
        is_counted: item.is_counted,
        counted_at: item.is_counted ? new Date().toISOString() : null,
        calculated_order_quantity: directMode ? 0 : parseOptionalNumber(item.calculated_order_quantity),
        manual_order_quantity: directMode
          ? parseOptionalNumber(item.current_count)
          : parseOptionalNumber(item.manual_order_quantity)
      }));

      // Resolve the canonical PAR per inventory_item_id so every sibling row is
      // persisted with the same item-global value (a PAR is session-item-global,
      // see Task #70). No MOQ is resolved or written: saving a count must not
      // rewrite a vendor-product fact it does not own.
      const canonicalParByInv = new Map();
      itemsToPersist.forEach((it) => {
        const invId = it.inventory_item_id;
        if (!invId) return;
        const p = parseFloat(it.par_level);
        if (Number.isFinite(p) && p > (canonicalParByInv.get(invId) || 0)) canonicalParByInv.set(invId, p);
      });

      for (const update of updates) {
        const row = itemsToPersist.find(it => it.id === update.id) || {};
        const invId = row.inventory_item_id;
        const canonicalPar = invId ? canonicalParByInv.get(invId) : null;
        const patch = {
          is_counted: update.is_counted,
          counted_at: update.counted_at,
          calculated_order_quantity: update.calculated_order_quantity,
          manual_order_quantity: update.manual_order_quantity,
        };
        // Only write on-hand outside direct order mode (where it must stay put).
        if (!directMode) patch.actual_quantity = update.actual_quantity;
        if (Number.isFinite(canonicalPar) && canonicalPar > 0) patch.par_level = canonicalPar;
        const { error } = await supabase
          .from('count_items')
          .update(patch)
          .eq('id', update.id);

        if (error) {
          console.error('Error updating count item:', error);
          throw error;
        }
      }

      // Create order events for items with shortages (if order calculations are enabled)
      if (selectedSession.order_calculations_enabled) {
        onStep?.('Generating order info');
        // De-duplicate by inventory_item_id so multi-location items produce one event.
        // The first row encountered determines suggested_order_qty and shortage.
        // If sibling rows have conflicting manual overrides, only the first counted
        // row's effective order qty is recorded; this is intentional — order events
        // represent item-level demand, not per-location overrides.
        //
        // `order_events.moq` below is a legacy column on a legacy table that no
        // reader in this app consults. Counting no longer resolves a minimum, so
        // it now records the column's historical default of 1. It is kept only so
        // the insert keeps satisfying whatever shape the column was created with;
        // see docs/moq-ownership-audit.md.
        const eventsByInvId = new Map();
        const eventsNoInvId = [];

        itemsToPersist.forEach(item => {
          if (!item.is_counted || !(item.shortage > 0) || !(item.order_quantity > 0)) return;
          const invId = item.inventory_item_id;
          const countedQty = parseFloat(item.current_count) || 0;
          const loc = item.location || 'Unknown';

          if (!invId) {
            eventsNoInvId.push({
              account: userAccount,
              template_id: selectedSession.template_id,
              inventory_item_id: null,
              location: loc,
              counted_quantity: countedQty,
              par_level: parseFloat(item.expected_quantity) || 0,
              shortage: item.shortage,
              suggested_order_qty: item.order_quantity,
              moq: item.moq || 1,
              count_session_id: selectedSession.id,
              status: 'pending',
            });
            return;
          }

          if (eventsByInvId.has(invId)) {
            const e = eventsByInvId.get(invId);
            e.counted_quantity += countedQty;
            if (!e.location.split(', ').includes(loc)) e.location += `, ${loc}`;
          } else {
            eventsByInvId.set(invId, {
              account: userAccount,
              template_id: selectedSession.template_id,
              inventory_item_id: invId,
              location: loc,
              counted_quantity: countedQty,
              par_level: parseFloat(item.expected_quantity) || 0,
              shortage: item.shortage,
              suggested_order_qty: item.order_quantity,
              moq: item.moq || 1,
              count_session_id: selectedSession.id,
              status: 'pending',
            });
          }
        });

        const orderEvents = [...eventsByInvId.values(), ...eventsNoInvId];

        if (orderEvents.length > 0) {
          console.log(`🛒 Creating ${orderEvents.length} order events for shortages...`);
          const { error: orderError } = await supabase
            .from('order_events')
            .insert(orderEvents);

          if (orderError) {
            console.error('Error creating order events:', orderError);
            throw orderError;
          } else {
            console.log(`✅ Created ${orderEvents.length} order events successfully!`);
          }
        }
      }

      // Update session status with new session_status tracking
      const countedItems = itemsToPersist.filter(item => item.is_counted).length;
      const totalItems = itemsToPersist.length;

      let finalStatus;
      let finalSessionStatus;

      if (sessionStatus === 'paused') {
        finalStatus = 'paused';
        finalSessionStatus = 'paused';
        console.log('📋 Session PAUSED - user will resume later');
      } else if (sessionStatus === 'completed') {
        finalStatus = 'completed';
        finalSessionStatus = 'completed';
        console.log('✅ Session COMPLETED - user marked as done');
      } else {
        finalStatus = countedItems === totalItems ? 'completed' : 'in_progress';
        finalSessionStatus = 'active';
        console.log('💾 Regular save - session remains active');
      }

      onStep?.('Finalizing session');

      const sessionUpdate = {
        status: finalStatus,
        items_counted: countedItems,
        total_items: totalItems,
        session_status: finalSessionStatus,
        last_saved_at: new Date().toISOString()
      };

      if (finalStatus === 'completed') {
        sessionUpdate.completed_at = new Date().toISOString();
        // Persist pace-based score so the report session selector can surface
        // Top Counter stats without recomputing from raw timing data.
        // Keep the established 5-second target/rabbit thresholds used by reports.
        try {
          const scoredCounted = countItems ? countItems.filter(i => i.is_counted).length : countedItems;
          const scoredSecs = elapsedSeconds || 0;
          const hasScore = scoredCounted > 0 && scoredSecs > 0;
          const rawPct = hasScore ? Math.round((scoredCounted * 5 / scoredSecs) * 100) : 0;
          const pct = Math.min(150, rawPct);
          let rabbits = 0;
          if (hasScore) {
            if (pct >= 100) rabbits = 3;
            else if (pct >= 75) rabbits = 2;
            else if (pct >= 50) rabbits = 1;
          }
          sessionUpdate.score_percent = pct;
          sessionUpdate.rabbit_count = rabbits;
        } catch (scoreErr) {
          console.warn('[saveProgress] Score persistence failed (non-blocking):', scoreErr);
        }
      }

      if (sessionStatus === 'paused') {
        sessionUpdate.paused_at = new Date().toISOString();
      } else if (sessionStatus === 'completed') {
        sessionUpdate.completed_at = new Date().toISOString();
      }

      if (finalStatus === 'completed' && !selectedSession.template_name_snapshot && selectedSession.template_id) {
        try {
          const tpl = await fetchTemplateMeta(supabase, selectedSession.template_id);
          if (tpl) {
            sessionUpdate.template_name_snapshot = tpl.name ?? null;
            sessionUpdate.template_type_snapshot = tpl.category ?? null;
          }
        } catch (snapErr) {
          console.warn('[saveProgress] template snapshot backfill failed:', snapErr);
        }
      }

      const { error: sessionError } = await supabase
        .from('count_sessions')
        .update(sessionUpdate)
        .eq('id', selectedSession.id);

      if (sessionError) {
        console.error('Error updating session:', sessionError);
        throw sessionError;
      }

      if (finalStatus === 'completed') {
        onStep?.('Locking in prices');
        await freezeSnapshotsForCompletedSession(supabase, selectedSession.id);
        // Inferred scheduling: the template's schedule follows what the store
        // actually does, so completing a run is what sets (or corrects) it.
        // Best-effort by design — see inferredScheduleService.
        await applyInferredScheduleOnComplete(supabase, selectedSession, {
          completedAt: sessionUpdate.completed_at,
          storePeriodConfig,
        });
        // Remember what package each location was counted in, so the next
        // count opens in it. Best-effort — see countFormatMemoryService.
        await persistLastUsedCountFormats(supabase, selectedSession, {
          completedAt: sessionUpdate.completed_at,
        });
      }

      if (!silent) {
        if (sessionStatus === 'paused') {
          console.log('✅ Progress saved and session PAUSED');
          showAppAlert({
            title: 'Progress saved',
            message: `${countedItems} items counted. You can resume this count later from the main screen.`,
            confirmButtonStyle: 'warning'
          });
        } else if (sessionStatus === 'completed') {
          console.log('✅ Count session COMPLETED');
          showAppAlert({
            title: 'Count completed',
            message: `${countedItems} items counted. Session marked as complete.`,
            confirmButtonStyle: 'success'
          });
        } else {
          console.log('✅ Progress saved successfully!');
          showAppAlert({
            title: 'Progress saved',
            message: `${countedItems} items counted.`,
            confirmButtonStyle: 'primary'
          });
        }
      }

      return { ok: true, countedItems, totalItems, finalStatus };
    } catch (error) {
      console.error('❌ Error saving progress:', error);
      if (!suppressAlert) {
        showAppAlert({
          title: 'Save failed',
          message: `Failed to save: ${error.message}`,
          confirmButtonStyle: 'danger'
        });
      }
      return { ok: false, error };
    } finally {
      setLoading(false);
    }
  };

  const handleEditSession = async (session) => {
    try {
      setLoading(true);
      console.log('✏️ Reopening saved session for editing:', session?.id);
      // Edit Count reopens this exact session so revisions stay scoped to
      // the dated record instead of creating a new run.
      window.location.href = `count-session.html?session=${session.id}`;

    } catch (error) {
      console.error('Error setting up edit session:', error);
      showAppAlert({
        title: 'Unable to edit session',
        message: 'Error loading session for editing: ' + error.message,
        confirmButtonStyle: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  // Edit the Template parent row. Opens the count-creation editor (same tool
  // used for "+ New") so the user gets the add-items/sorting/date controls.
  // Changes only affect future children created from the template; existing
  // children remain untouched.
  const persistScheduleToTemplate = async (templateId, cycle) => {
    const { error } = await supabase
      .from('count_sessions')
      .update({ ordering_cycle_config: cycle })
      .eq('id', templateId);
    if (error) throw error;
    setCountSessions(prev => prev.map(s =>
      s.id === templateId ? { ...s, ordering_cycle_config: cycle } : s
    ));
  };

  // Persist the "count straight to order" (direct order) flag on a template row
  // (count_sessions.is_template=true) and mirror it into local state so cards and
  // the setup modal reflect the change immediately. New runs created from the
  // template inherit the value via the insert paths (see handleCountAgain etc.).
  const persistDirectOrderMode = async (templateId, enabled) => {
    const { error } = await supabase
      .from('count_sessions')
      .update({ direct_order_mode: !!enabled })
      .eq('id', templateId);
    if (error) throw error;
    setCountSessions(prev => prev.map(s =>
      s.id === templateId ? { ...s, direct_order_mode: !!enabled } : s
    ));
    // Keep an open setup picker for this template in sync (the picker renders
    // from its own snapshot of the template row). Clearing parsMissing when
    // enabling reflects that PARs no longer gate setup in direct order mode.
    setSetupPickerModal(prev => (prev && prev.template?.id === templateId)
      ? { ...prev, template: { ...prev.template, direct_order_mode: !!enabled }, parsMissing: enabled ? false : prev.parsMissing }
      : prev);
  };

  // Persist the "par setup skipped" sentinel on a template row and mirror it
  // into local state. The pars counterpart of the schedule skip sentinel
  // (ordering_cycle_config = {preset:'none', skipped:true}): the setup step
  // reads done, the ⚙ Setup gate releases the Start button, and the Start
  // Order pars wizard stops re-prompting. Saving real pars later makes it inert.
  const persistParsSetupSkipped = async (templateId) => {
    const { error } = await supabase
      .from('count_sessions')
      .update({ pars_setup_skipped: true })
      .eq('id', templateId);
    if (error) throw error;
    setCountSessions(prev => prev.map(s =>
      s.id === templateId ? { ...s, pars_setup_skipped: true } : s
    ));
    setSetupPickerModal(prev => (prev && prev.template?.id === templateId)
      ? { ...prev, template: { ...prev.template, pars_setup_skipped: true }, parsMissing: false }
      : prev);
  };

  // Flip the "count straight to order" switch. Turning it ON changes how every
  // future count from this template behaves, so we confirm first; turning it OFF
  // simply restores PAR-based ordering and needs no confirmation.
  const handleDirectOrderToggle = (template, nextOn) => {
    if (!template?.id) return;
    if (nextOn) {
      setDirectOrderConfirm({ template });
      return;
    }
    persistDirectOrderMode(template.id, false).catch((e) => {
      console.error('[DirectOrder] disable failed:', e);
      showAppAlert({ title: 'Could not update', message: e.message || 'Please try again.', confirmButtonStyle: 'danger' });
    });
  };

  const confirmDirectOrderOn = async () => {
    const t = directOrderConfirm?.template;
    setDirectOrderConfirm(null);
    if (!t?.id) return;
    try {
      await persistDirectOrderMode(t.id, true);
    } catch (e) {
      console.error('[DirectOrder] enable failed:', e);
      showAppAlert({ title: 'Could not update', message: e.message || 'Please try again.', confirmButtonStyle: 'danger' });
    }
  };

  // Reusable "count straight to order" row with an iOS-style switch. Shared by
  // the Template Setup modal and the template card so the control looks and
  // behaves identically wherever it appears.
  const renderDirectOrderToggle = (template, opts = {}) => {
    const on = !!template?.direct_order_mode;
    const { compact = false } = opts;
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: compact ? '10px 12px' : '12px 14px', borderRadius: '10px',
        border: `1px solid ${on ? '#C9A882' : '#D9CEC2'}`,
        backgroundColor: on ? '#FBF3E8' : '#FAF6F2',
      }}>
        <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>🎯</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#3E2723' }}>Count straight to order</div>
          <div style={{ fontSize: '0.76rem', color: '#8B7B73', marginTop: '2px', lineHeight: 1.35 }}>
            Skip PARs — count what you need to order, not what's on hand.
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label="Count straight to order"
          title={on ? 'Turn off — go back to counting on-hand against PARs' : 'Turn on — count the amount to order directly'}
          onClick={(e) => { e.stopPropagation(); handleDirectOrderToggle(template, !on); }}
          style={{
            position: 'relative', flexShrink: 0, width: '44px', height: '26px',
            borderRadius: '13px', border: 'none', cursor: 'pointer', padding: 0,
            backgroundColor: on ? '#8A7868' : '#D9CEC2', transition: 'background-color 0.2s',
          }}
        >
          <span style={{
            position: 'absolute', top: '3px', left: on ? '21px' : '3px',
            width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left 0.2s',
          }} />
        </button>
      </div>
    );
  };

  // Returns true when NOT ONE distinct item has a stored PAR (including an
  // explicit zero) — i.e. par setup has never been done for this template.
  // has never been done for this template. A partially-filled template counts as
  // set up, so the wizard never reopens over work already in progress.
  // Items are deduplicated by item_id (or normalized name|vendor) so multi-location
  // items don't skew the result. An item "has par" if any of its location
  // rows already has a finite, non-negative par_level. Used both in the Start Order pars wizard
  // gate and in the parsMissing badge flag so both stay in sync from a single source
  // of truth.
  //
  // Feed this FRESH rows. Judging it from sessionItemsCache alone reports "needs
  // PARs" for a template whose pars were filled in via the editor on this same
  // page load — see getTemplateItemsForParGate.
  const isParsSetupNeeded = (items) => {
    if (!items || items.length === 0) return false;
    // Group by dedup key; track whether any row for that key has a par.
    const parByKey = new Map();
    (items || []).forEach(item => {
      const name = (item.item_snapshot?.description || item.item_snapshot?.name || '').trim().toLowerCase();
      const vendor = (item.item_snapshot?.vendor_name || item.item_snapshot?.vendor || item.vendor || '').trim().toLowerCase();
      const key = item.item_id ? `id:${item.item_id}` : `nv:${name}|${vendor}`;
      const hasPar = hasStoredParValue(item.par_level);
      parByKey.set(key, (parByKey.get(key) || false) || hasPar);
    });
    const withPar = Array.from(parByKey.values()).filter(Boolean).length;
    return withPar === 0;
  };

  // Persist pars for a list of { id, par_level, par_unit } rows. Throws if any
  // row fails so the caller can abort session creation instead of producing a
  // count with stale/empty pars. min_order_qty / min_order_unit are deliberately
  // NOT written: a PAR is an item fact, a minimum is a vendor-product fact, and
  // setting a PAR must work with zero vendor knowledge.
  // The stored normalized PAR must move with a wizard edit, or the stale
  // stored value keeps winning in every resolveRowPar reader. One shared
  // patch builder (countUnitModel.buildParNormalizationPatch) so every PAR
  // writer — here, the session item editor, the dashboard wizard — refreshes
  // or clears the quintet identically.
  const wizardNormalizationPatch = (row) => buildParNormalizationPatch({
    parLevel: row.par_level,
    parUnit: row.par_unit,
    countUnit: row.item_snapshot?.unit ?? row.count_unit ?? null,
  });

  const persistParsToTemplate = async (updatedRows) => {
    const results = await Promise.all(
      updatedRows.map(async row => {
        const legacyPatch = {
          par_level: row.par_level,
          par_unit: row.par_unit || 'each',
          par_defaulted: row.par_defaulted === true,
        };
        let { error } = await supabase
          .from('count_items')
          .update({ ...legacyPatch, ...wizardNormalizationPatch(row) })
          .eq('id', row.id);
        if (error && isMissingParNormalizationColumnError(error)) {
          ({ error } = await supabase
            .from('count_items')
            .update(legacyPatch)
            .eq('id', row.id));
        }
        if (error) {
          console.warn('[ParsWizard] Failed to update item', row.id, error);
          return false;
        }
        return true;
      })
    );
    const failCount = results.filter(r => !r).length;
    if (failCount > 0) {
      throw new Error(`Failed to save ${failCount} of ${updatedRows.length} par value${updatedRows.length !== 1 ? 's' : ''}`);
    }
  };

  // Re-read one template's count_items and replace its cache entry. The prefetch
  // effect only fills empty slots, so this is how a card learns about edits made
  // after page load without a hard refresh. Existing rows keep rendering while
  // the read is in flight (the card's loading state needs an EMPTY list), so
  // there's no flash back to placeholder numbers.
  const refreshTemplateItemsCache = async (templateId) => {
    if (!templateId) return;
    setCardPreviewStatus(p => ({ ...p, [templateId]: 'loading' }));
    try {
      const { data, error } = await supabase
        .from('count_items')
        .select(CACHED_COUNT_ITEM_SELECT)
        .eq('count_session_id', templateId);
      if (error) throw error;
      setSessionItemsCache(prev => ({ ...prev, [templateId]: dedupeCountItemRows(data || []) }));
      setCardPreviewStatus(p => ({ ...p, [templateId]: 'success' }));
    } catch (error) {
      console.warn('[CountInterface] Could not refresh template items for', templateId, error?.message || error);
      setCardPreviewStatus(p => ({ ...p, [templateId]: 'error' }));
    }
  };

  // Rows for the pars wizard. ALWAYS re-reads: the wizard is opened by an
  // explicit click (one query is cheap) and the cache it used to trust can
  // predate PAR edits made in the template editor without leaving this page —
  // which is exactly how the wizard came to open blank over a template whose
  // pars were already set.
  //
  // Returns the raw rows: the wizard collapses duplicates itself and fans each
  // save out to every sibling count_item id, so handing it a deduped list would
  // silently drop a location's row from the write. The cache gets the deduped
  // view the cards expect.
  const fetchTemplateItemsForPars = async (templateId) => {
    if (!templateId) return [];
    setCardPreviewStatus(p => ({ ...p, [templateId]: 'loading' }));
    try {
      const { data, error } = await supabase
        .from('count_items')
        .select('*')
        .eq('count_session_id', templateId)
        .order('id');
      if (error) throw error;
      const items = data || [];
      setSessionItemsCache(prev => ({ ...prev, [templateId]: dedupeCountItemRows(items) }));
      setCardPreviewStatus(p => ({ ...p, [templateId]: 'success' }));
      return items;
    } catch (error) {
      setCardPreviewStatus(p => ({ ...p, [templateId]: 'error' }));
      throw error;
    }
  };

  // Items to judge par setup against, for the gates that must not block on a
  // failed read (the setup checklist, and the pars step of starting a new run).
  // Prefers fresh rows; falls back to whatever is cached so a transient network
  // error degrades to today's behavior instead of stopping the user.
  const getTemplateItemsForParGate = async (templateId) => {
    try {
      return await fetchTemplateItemsForPars(templateId);
    } catch (error) {
      console.warn('[CountInterface] Par gate falling back to cached items:', error?.message || error);
      return sessionItemsCache[templateId] || [];
    }
  };

  const applyParsWizardRowsToCache = (templateId, allRows) => {
    if (!templateId || !allRows || allRows.length === 0) return;
    const parById = {};
    allRows.forEach(r => { parById[r.id] = r; });
    setSessionItemsCache(prev => {
      const updated = { ...prev };
      if (updated[templateId]) {
        updated[templateId] = updated[templateId].map(item =>
          parById[item.id]
            ? { ...item,
                par_level: parById[item.id].par_level,
                par_unit: parById[item.id].par_unit,
                par_defaulted: parById[item.id].par_defaulted === true,
              }
            : item
        );
      }
      return updated;
    });
  };

  const saveParsWizardResult = async (templateId, result) => {
    if (!result || result.type !== 'save') return false;
    await persistParsToTemplate(result.rows || []);
    applyParsWizardRowsToCache(templateId, result.allRows || []);
    return true;
  };

  // Opens the pars wizard modal and returns a Promise that resolves with:
  //   'cancel'               — user closed with X or backdrop (abort Start Order)
  //   { type: 'skip' }       — user clicked Skip for now (proceed, no save)
  //   { type: 'save', rows } — user saved (rows = array of updated row objects)
  const showParsWizard = (template, items, isFirstTimeSetup = false, options = {}) => {
    return new Promise((resolve) => {
      parsWizardResolverRef.current = resolve;
      setParsWizardModal({
        template,
        items,
        isFirstTimeSetup,
        parsOnly: !!options.parsOnly,
      });
    });
  };

  const openTemplateSetupPicker = async ({ template, parsMissing, scheduleMissing, isOrder, isEOP }) => {
    if (!template) return;
    // Setup writes (schedule / pars) need a persisted template — materialize a
    // synthesized stand-in first.
    if (isSyntheticTemplate(template)) {
      const real = await materializeTemplateForGroup(template.count_group_id);
      if (!real) return;
      template = real;
    }
    // The caller's parsMissing was derived from the cached rows. Confirm it
    // against the database before showing the PAR row as unticked — otherwise a
    // template whose pars were filled in from the editor (or mid-count) on this
    // same page load reads as untouched, and the step it offers opens blank.
    // Re-reading also refreshes the cache, so the card behind the modal
    // corrects itself at the same time.
    let resolvedParsMissing = parsMissing;
    if (isOrder && parsMissing && !template.direct_order_mode && !template.pars_setup_skipped) {
      const freshItems = await getTemplateItemsForParGate(template.id);
      if (freshItems.length > 0) resolvedParsMissing = isParsSetupNeeded(freshItems);
    } else if (template.pars_setup_skipped) {
      resolvedParsMissing = false;
    }
    setSetupPickerModal({ template, parsMissing: resolvedParsMissing, scheduleMissing, isOrder, isEOP });
  };

  const handleEditTemplate = async (template) => {
    if (!template) return;
    // A synthesized stand-in has no DB row — create the real template first so
    // the editor opens the persisted template (not the in-memory placeholder).
    if (isSyntheticTemplate(template)) {
      setLoading(true);
      const real = await materializeTemplateForGroup(template.count_group_id);
      setLoading(false);
      if (!real) {
        showAppAlert({
          title: 'Template not ready yet',
          message: 'This recurring count\'s template is still being set up. Please try again in a moment.',
          confirmButtonStyle: 'warning',
        });
        return;
      }
      template = real;
    }
    if (!template?.id) return;
    try {
      setLoading(true);
      // Stash the template row so count-creation's edit-mode init (reads
      // `editSessionData` from localStorage, see count.html) can pre-fill the
      // form and load items. Force is_template:true so the dedicated template
      // edit header in count.html activates even if the source row didn't have
      // the flag explicitly set.
      localStorage.setItem('editSessionData', JSON.stringify({ ...template, is_template: true }));
      const type = template.order_calculations_enabled ? 'order' : 'end-of-period';
      const params = new URLSearchParams({
        view: 'count-creation',
        edit: 'true',
        sessionName: template.name || '',
        type,
      });
      window.location.href = `count.html?${params.toString()}`;
    } catch (error) {
      console.error('Error opening template for edit:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyCurrentSessionItemsToFutureOrders = async () => {
    const groupAnchor = selectedSession?.count_group_id || selectedSession?.id;
    if (!groupAnchor) {
      showAppAlert({
        title: 'No recurring card linked',
        message: 'This session is not linked to a recurring card, so there are no future defaults to update.',
        confirmButtonStyle: 'warning'
      });
      return;
    }
    if (!Array.isArray(countItems) || countItems.length === 0) {
      showAppAlert({
        title: 'No items to apply',
        message: 'This session has no items to save as recurring defaults.',
        confirmButtonStyle: 'warning'
      });
      return;
    }

    const confirmed = await showAppConfirm({
      title: 'Apply to future orders?',
      message: 'This updates the Template defaults. Future counts created from this template will use the new item list. Past sessions will not be changed.',
      confirmText: 'Apply to future orders',
      cancelText: 'Keep session only',
      confirmButtonStyle: 'primary'
    });
    closeAppModal();
    if (!confirmed) return;

    const storeId = parseInt(localStorage.getItem('selectedStoreId'), 10) || selectedSession.store_id;
    if (!storeId) {
      showAppAlert({
        title: 'Store missing',
        message: 'No store selected.',
        confirmButtonStyle: 'danger'
      });
      return;
    }

    setApplyFutureSaving(true);
    try {
      // Resolve the Template row for this session's group (lazy-create if missing).
      const { data: existingTemplate } = await supabase
        .from('count_sessions')
        .select('id')
        .eq('count_group_id', groupAnchor)
        .eq('is_template', true)
        .limit(1)
        .maybeSingle();

      let templateSessionId = existingTemplate?.id || null;
      if (!templateSessionId) {
        const storedUser = localStorage.getItem('currentUser');
        const userId = storedUser ? JSON.parse(storedUser).id : null;
        const { data: inserted, error: insErr } = await supabase
          .from('count_sessions')
          .insert([{
            store_id: storeId,
            name: stripRunSuffix(selectedSession.name),
            session_date: new Date().toISOString().split('T')[0],
            status: 'draft',
            template_id: selectedSession.template_id ?? null,
            template_name_snapshot: selectedSession.template_name_snapshot ?? null,
            template_type_snapshot: selectedSession.template_type_snapshot ?? null,
            created_by: userId,
            count_group_id: groupAnchor,
            is_template: true,
            is_copy: false,
            notes: 'Recurring count template',
            order_calculations_enabled: selectedSession.order_calculations_enabled ?? null,
            direct_order_mode: selectedSession.direct_order_mode ?? false,
            count_purpose: selectedSession.count_purpose ?? null,
            inventory_mode: selectedSession.inventory_mode ?? null,
            vendor_id: selectedSession.vendor_id ?? null,
            ordering_cycle_config: selectedSession.ordering_cycle_config ?? null,
          }])
          .select('id')
          .single();
        if (insErr) throw insErr;
        templateSessionId = inserted.id;
      }

      const orderedItems = [...countItems].sort((a, b) => (Number(a.count_position) || 0) - (Number(b.count_position) || 0));
      const templateCountItems = orderedItems.map((item, index) => ({
        count_session_id: templateSessionId,
        inventory_item_id: item.inventory_item_id,
        item_snapshot: item.item_snapshot,
        expected_quantity: item.expected_quantity ?? '',
        actual_quantity: '',
        variance_quantity: 0,
        count_notes: '',
        is_counted: false,
        counted_at: null,
        par_level: item.par_level === '' || item.par_level == null ? null : item.par_level,
        par_unit: item.par_defaulted === true
          ? (item.item_snapshot?.unit || item.par_unit || 'each')
          : (item.par_unit || item.item_snapshot?.unit || 'each'),
        par_defaulted: item.par_defaulted
          ?? item.item_snapshot?.par_defaulted
          ?? false,
        min_order_qty: item.min_order_qty ?? null,
        min_order_unit: item.min_order_unit || 'each',
        location: item.location || item.item_snapshot?.location || null,
        count_position: (index + 1) * 10,
      }));

      // Replace the Template row's count_items list (single source of truth for future defaults).
      const { error: deleteError } = await supabase
        .from('count_items')
        .delete()
        .eq('count_session_id', templateSessionId);
      if (deleteError) throw deleteError;

      if (templateCountItems.length > 0) {
        const { error: insertError } = await supabase
          .from('count_items')
          .insert(templateCountItems);
        if (insertError) throw insertError;
      }

      showAppAlert({
        title: 'Template updated',
        message: 'Applied to future orders. Past and in-progress sessions were not changed.',
        confirmButtonStyle: 'success'
      });
    } catch (error) {
      console.error('Failed to apply defaults to future orders:', error);
      showAppAlert({
        title: 'Update failed',
        message: error.message || 'Could not update recurring defaults.',
        confirmButtonStyle: 'danger'
      });
    } finally {
      setApplyFutureSaving(false);
    }
  };

  const getNextOrderDayForVendor = async (vendorId) => {
    if (!vendorId) return null;
    try {
      const { data: vendor } = await supabase
        .from('vendors')
        .select('ordering_days')
        .eq('id', vendorId)
        .single();
      if (!vendor?.ordering_days) return null;
      const dayBits = [64, 1, 2, 4, 8, 16, 32];
      const now = new Date();
      for (let offset = 1; offset <= 7; offset++) {
        const d = new Date(now);
        d.setDate(d.getDate() + offset);
        if (vendor.ordering_days & dayBits[d.getDay()]) return d.toISOString().split('T')[0];
      }
    } catch (e) { console.warn('Could not fetch vendor ordering days:', e); }
    return null;
  };

  // A double-tap on "Count" used to create two identical sessions two seconds
  // apart — setLoading(true) does not stop the second invocation that is
  // already past the button. A ref is synchronous where state is not.
  const countAgainInFlightRef = useRef(false);

  const handleCountAgain = async (session, mode = 'web') => {
    if (countAgainInFlightRef.current) return;
    countAgainInFlightRef.current = true;
    try {
      await runCountAgain(session, mode);
    } finally {
      countAgainInFlightRef.current = false;
    }
  };

  const runCountAgain = async (session, mode = 'web') => {
    // A synthesized stand-in template has no DB row; operate on the real latest
    // child of its group so the item-copy / new-run logic has a real source.
    if (isSyntheticTemplate(session)) {
      const realChild = countSessions
        .filter(s => !s.is_template && s.count_group_id === session.count_group_id)
        .sort((a, b) => new Date(b.created_at || b.session_date || 0) - new Date(a.created_at || a.session_date || 0))[0];
      if (!realChild) {
        showAppAlert({
          title: 'Count not ready yet',
          message: 'This recurring count is still being set up. Please try again in a moment.',
          confirmButtonStyle: 'warning',
        });
        return;
      }
      session = realChild;
    }
    try {
      setLoading(true);
      const storeId = parseInt(localStorage.getItem('selectedStoreId'), 10);
      const storedUser = localStorage.getItem('currentUser');
      const userId = storedUser ? JSON.parse(storedUser).id : null;

      const groupId = session.count_group_id || session.id;
      const isFromTemplate = !!session.is_template;

      // Schedule + PAR gates are enforced on Save (in the template editor),
      // never on Start/Create. Opening a count should never prompt — the
      // user has already committed to the configured template.

      // Source of truth for item list: the session clicked on. If it's a
      // Template that has no items yet (never had "Apply to future orders"
      // run against it), fall back to the newest completed child in the group
      // so "New" still produces a usable count.
      // Paginated + deduped: a source session the duplication bug exploded
      // holds hundreds of clones per item, and a capped select('*') copied an
      // arbitrary 1,000 of them into the new run (a real production session
      // was born with 1,000 rows for 6 items this way). Deduping here means
      // counting again from corrupted history produces a clean run — the app
      // self-heals instead of propagating the junk.
      // A failed read here must ABORT, not proceed: ignoring it once turned a
      // transient error into a permanently empty draft — the session row was
      // created, the item copy was "skipped" because the list read as empty,
      // and the run dead-ended at "No items in this session".
      const { rows: origRows, error: origReadErr } = await fetchAllCountItems(supabase, session.id);
      if (origReadErr) {
        throw new Error(`Could not read the source items: ${origReadErr.message || origReadErr}`);
      }
      let origItems = dedupeCountItemRows(origRows);

      if (isFromTemplate && origItems.length === 0) {
        const { data: siblings } = await supabase
          .from('count_sessions')
          .select('id')
          .eq('count_group_id', groupId)
          .eq('is_template', false)
          .not('is_archived', 'is', true)
          .not('is_trashed', 'is', true)
          .order('created_at', { ascending: false })
          .limit(1);
        if (siblings && siblings[0]) {
          const { rows: childRows, error: childReadErr } = await fetchAllCountItems(supabase, siblings[0].id);
          if (childReadErr) {
            throw new Error(`Could not read the source items: ${childReadErr.message || childReadErr}`);
          }
          origItems = dedupeCountItemRows(childRows);
        }
      }

      // A new run is forward-looking even when its source is historical. The
      // catalog row stays for reports, but an archived item is not countable
      // inventory and must not be copied into the new tape. Preserve the source
      // list if the lookup itself fails; a transient read error must never erase
      // an entire template.
      const sourceInventoryIds = [...new Set(
        origItems.map((item) => item.inventory_item_id).filter(Boolean)
      )];
      if (sourceInventoryIds.length > 0) {
        const { data: sourceInventory, error: sourceInventoryError } = await supabase
          .from('inventory_items')
          .select('id, is_active, deleted_at')
          .in('id', sourceInventoryIds);
        if (sourceInventoryError) {
          console.warn('[CountAgain] inventory liveness lookup failed; preserving source rows:', sourceInventoryError.message);
        } else {
          origItems = filterCountItemsToLiveInventory(origItems, sourceInventory);
        }
      }

      let sessionDate = new Date().toISOString().split('T')[0];
      // Prefer the full cycle snapshot on the template/session so monthly,
      // ordinal and 4-5-4/5-4-4 schedules land on the correct future date.
      // Fall back to the vendor's weekly ordering_days bitmask, then tomorrow.
      const sessionCycle = session.ordering_cycle_config || null;
      if (sessionCycle && sessionCycle.preset) {
        try {
          const nxt = getNextOccurrence(sessionCycle, sessionDate);
          if (nxt) sessionDate = nxt;
        } catch (e) {
          console.warn('[CountAgain] getNextOccurrence failed, falling back:', e);
        }
      } else if (session.order_calculations_enabled) {
        let nextDate = null;
        if (session.vendor_id) {
          nextDate = await getNextOrderDayForVendor(session.vendor_id);
        }
        if (nextDate) {
          sessionDate = nextDate;
        } else {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          sessionDate = tomorrow.toISOString().split('T')[0];
        }
      }

      // When creating from the Template parent, use the bare template name.
      // No date suffix — it reflected the schedule's next occurrence, not the
      // actual count date, and lists already show real timestamps alongside.
      const childName = isFromTemplate
        ? stripRunSuffix(session.name)
        : session.name;
      const childNotes = isFromTemplate
        ? 'Created from template'
        : `Recount of session from ${new Date(session.session_date || session.created_at).toLocaleDateString()}`;

      let liveTemplateName = session.template_name_snapshot ?? null;
      let liveTemplateType = session.template_type_snapshot ?? null;
      if (session.template_id) {
        try {
          const tpl = await fetchTemplateMeta(supabase, session.template_id);
          if (tpl) {
            liveTemplateName = tpl.name ?? liveTemplateName;
            liveTemplateType = tpl.category ?? liveTemplateType;
          }
        } catch (snapErr) {
          console.warn('[countAgain] template snapshot lookup failed:', snapErr);
        }
      }

      const { data: newSession, error } = await supabase
        .from('count_sessions')
        .insert([{
          store_id: storeId,
          name: childName,
          session_date: sessionDate,
          status: 'draft',
          template_id: session.template_id,
          template_name_snapshot: liveTemplateName,
          template_type_snapshot: liveTemplateType,
          created_by: userId,
          count_group_id: groupId,
          is_copy: true,
          notes: childNotes,
          order_calculations_enabled: session.order_calculations_enabled ?? null,
          direct_order_mode: session.direct_order_mode ?? false,
          count_purpose: session.count_purpose ?? null,
          inventory_mode: session.inventory_mode ?? null,
          vendor_id: session.vendor_id ?? null,
          ordering_cycle_config: sessionCycle || null,
          total_items: origItems?.length || 0,
        }])
        .select()
        .single();

      if (error) throw error;

      // Also ensure the original session has count_group_id set (backfill)
      if (!session.count_group_id) {
        await supabase
          .from('count_sessions')
          .update({ count_group_id: session.id })
          .eq('id', session.id);
      }

      if (origItems && origItems.length > 0) {
        const newItems = origItems.map((item, index) => ({
          count_session_id: newSession.id,
          inventory_item_id: item.inventory_item_id,
          item_snapshot: item.item_snapshot,
          expected_quantity: item.expected_quantity ?? '',
          actual_quantity: '',
          variance_quantity: 0,
          count_notes: item.count_notes || '',
          is_counted: false,
          counted_at: null,
          par_level: item.par_level ?? null,
          par_unit: item.par_defaulted === true
            ? (item.item_snapshot?.unit || item.par_unit || 'each')
            : (item.par_unit || item.item_snapshot?.unit || 'each'),
          par_defaulted: item.par_defaulted
            ?? item.item_snapshot?.par_defaulted
            ?? false,
          min_order_qty: item.min_order_qty ?? null,
          min_order_unit: item.min_order_unit || 'each',
          location: item.location || item.item_snapshot?.location || null,
          count_position: item.count_position ?? (index + 1) * 10
        }));
        const { error: itemsInsertError } = await supabase.from('count_items').insert(newItems);
        if (itemsInsertError) {
          // The half-born run would strand as an empty draft nobody can open;
          // remove it before surfacing the failure (best-effort — the throw
          // stands either way).
          await supabase.from('count_sessions').delete().eq('id', newSession.id);
          throw itemsInsertError;
        }
      }

      if (mode === 'later') {
        await loadCountSessions();
      } else if (mode === 'mobile') {
        setMobileInstructionSession({ id: newSession.id, name: newSession.name });
        await loadCountSessions();
      } else {
        window.location.href = `count-session.html?session=${newSession.id}`;
      }
      return;
    } catch (error) {
      console.error('Error creating recount:', error);
      showAppAlert({
        title: 'Unable to create next run',
        message: 'Error creating next run: ' + error.message,
        confirmButtonStyle: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async (session) => {
    try {
      const completionUpdate = { status: 'completed', completed_at: new Date().toISOString() };
      // Backfill total_items / items_counted from the live count_items rows so
      // sessions force-completed from the overview always have a correct denominator.
      try {
        const { data: ciRows } = await supabase
          .from('count_items')
          .select('is_counted')
          .eq('count_session_id', session.id);
        if (ciRows && ciRows.length > 0) {
          completionUpdate.total_items = ciRows.length;
          completionUpdate.items_counted = ciRows.filter(r => r.is_counted).length;
        }
      } catch (ciErr) {
        console.warn('[handleMarkComplete] count_items lookup failed (non-blocking):', ciErr);
      }

      if (!session.template_name_snapshot && session.template_id) {
        try {
          const tpl = await fetchTemplateMeta(supabase, session.template_id);
          if (tpl) {
            completionUpdate.template_name_snapshot = tpl.name ?? null;
            completionUpdate.template_type_snapshot = tpl.category ?? null;
          }
        } catch (snapErr) {
          console.warn('[handleMarkComplete] template snapshot backfill failed:', snapErr);
        }
      }

      const { data, error } = await supabase
        .from('count_sessions')
        .update(completionUpdate)
        .eq('id', session.id)
        .select();
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('No rows updated — you may not have permission to modify this session.');
      }
      try {
        await freezeSnapshotsForCompletedSession(supabase, session.id);
      } catch (freezeErr) {
        console.warn('Snapshot freeze warning (non-blocking):', freezeErr);
      }
      // Same inferred-schedule pass the in-count Done flow runs — completing
      // from the overview is still the user telling us when they count.
      await applyInferredScheduleOnComplete(supabase, session, {
        completedAt: completionUpdate.completed_at,
        storePeriodConfig,
      });
      await persistLastUsedCountFormats(supabase, session, {
        completedAt: completionUpdate.completed_at,
      });
      if (session.order_calculations_enabled) {
        await openVendorSendStepForSession(session, { completedVia: 'overview-complete' });
      }
      await loadCountSessions();
    } catch (error) {
      console.error('Error marking complete:', error);
      showAppAlert({
        title: 'Unable to mark complete',
        message: 'Error marking complete: ' + error.message,
        confirmButtonStyle: 'danger'
      });
    }
  };

  // Helper: get all sessions in the same stack as this session
  const getStackSessions = (session) => {
    const groupId = session.count_group_id || session.id;
    return countSessions.filter(s => (s.count_group_id || s.id) === groupId);
  };

  const handleDeleteSession = async (session) => {
    // Three-way prompt: Archive / Delete to Trash / Cancel
    const stackSessions = getStackSessions(session);
    const isTopOfStack = stackSessions.length > 1 && stackSessions[0].id === session.id;
    const sessionName = session.name || 'this count';
    const sessionDate = session.session_date ? new Date(session.session_date).toLocaleDateString() : '';

    const archiveChoice = await showAppConfirm({
      title: 'Remove this count?',
      message: `This will remove the counted information from reports.\n\nWas this count a mistake, or do you wish to archive "${sessionName}"${sessionDate ? ` (${sessionDate})` : ''}?`,
      confirmText: '📦 Archive',
      cancelText: 'Cancel',
      confirmButtonStyle: 'warning'
    });
    closeAppModal();

    if (archiveChoice) {
      // User chose to archive instead
      await handleArchiveSession(session);
      return;
    }

    // Ask to confirm trash
    const trashConfirm = await showAppConfirm({
      title: 'Move to Trash?',
      message: `Move "${sessionName}" to trash? This removes it from reports. You can recover it from the Trash later, or permanently delete it there.`,
      confirmText: '🗑️ Move to Trash',
      cancelText: 'Cancel',
      confirmButtonStyle: 'danger'
    });
    closeAppModal();
    if (!trashConfirm) return;

    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('count_sessions')
        .update({ is_trashed: true, trashed_at: now })
        .eq('id', session.id);
      if (error) throw error;
      await loadCountSessions();
    } catch (error) {
      console.error('Error trashing session:', error);
      showAppAlert({
        title: 'Trash failed',
        message: 'Error moving to trash: ' + error.message,
        confirmButtonStyle: 'danger'
      });
    }
  };

  const handleArchiveSession = async (session) => {
    const stackSessions = getStackSessions(session);
    const isTopOfStack = stackSessions.length > 1;
    const sessionName = session.name || 'this count';
    const sessionDate = session.session_date ? new Date(session.session_date).toLocaleDateString() : '';

    let sessionIds = [session.id];

    // If this session is part of a stack, ask whether to archive the whole stack
    if (isTopOfStack) {
      const stackChoice = await showAppConfirm({
        title: 'Archive stack or single count?',
        message: `"${sessionName}" is part of a stack of ${stackSessions.length} counts.\n\nArchive the entire stack, or only "${sessionName}${sessionDate ? ` (${sessionDate})` : ''}"?`,
        confirmText: `📦 Archive Stack (${stackSessions.length})`,
        cancelText: `Archive only this count`,
        confirmButtonStyle: 'warning'
      });
      closeAppModal();

      if (stackChoice) {
        // Archive entire stack
        sessionIds = stackSessions.map(s => s.id);
      }
      // else: archive just this one session (sessionIds already set)
    }

    try {
      const now = new Date().toISOString();
      for (const id of sessionIds) {
        const { data, error } = await supabase
          .from('count_sessions')
          .update({ is_archived: true, archived_at: now })
          .eq('id', id)
          .select();
        if (error) throw error;
        if (!data || data.length === 0) {
          throw new Error('No rows updated — you may not have permission to archive this session.');
        }
      }
      await loadCountSessions();
      showAppAlert({
        title: '📦 Archived',
        message: `"${sessionName}" has been archived.\n\nIf the count values are not correct and you wish to delete this count permanently, you can do so from the Archive page.`,
        confirmButtonStyle: 'primary'
      });
    } catch (error) {
      console.error('Error archiving:', error);
      showAppAlert({
        title: 'Archive failed',
        message: 'Error: ' + error.message,
        confirmButtonStyle: 'danger'
      });
    }
  };

  // ── Template Archive / Delete (from the template detail view) ─────────────
  // Templates here are count_sessions rows with is_template=true. Archiving
  // sets is_archived so the overview query (queryCountSessionsActive) drops the
  // card; past runs are separate rows and stay untouched. Hard delete is only
  // offered for templates with no items and no runs, so removing the row (and
  // its count_items) cannot orphan any history.
  const resolveLifecycleTemplateRow = async (template) => {
    if (!template) return null;
    if (isSyntheticTemplate(template)) {
      return await materializeTemplateForGroup(template.count_group_id);
    }
    return template;
  };

  const closeTemplateLifecycleModal = () => {
    setTemplateLifecycleModal(null);
    setTemplateArchiveConfirmText('');
    setTemplateLifecycleBusy(false);
  };

  const handleTemplateLifecycleDelete = async () => {
    const m = templateLifecycleModal;
    if (!m || templateLifecycleBusy) return;
    setTemplateLifecycleBusy(true);
    try {
      const row = await resolveLifecycleTemplateRow(m.template);
      if (!row?.id) throw new Error('Template row is not available yet. Please try again in a moment.');

      const { error: itemsError } = await supabase
        .from('count_items')
        .delete()
        .eq('count_session_id', row.id);
      if (itemsError) throw itemsError;

      // .select() so a silent RLS block (0 rows deleted, no error) is caught.
      const { data: deletedRows, error } = await supabase
        .from('count_sessions')
        .delete()
        .eq('id', row.id)
        .select();
      if (error) throw error;
      if (!deletedRows || deletedRows.length === 0) {
        throw new Error('No rows deleted — you may not have permission to delete this template.');
      }

      console.log(`✅ Permanently deleted template ${row.id}`);
      closeTemplateLifecycleModal();
      // Close whichever surface the dialog was launched from — the full
      // detail screen or the inline expanded card.
      if (detailGroupId) closeTemplateDetail();
      if (inlineDetailsGroupId) setInlineDetailsGroupId(null);
      await loadCountSessions();
    } catch (error) {
      console.error('Error deleting template:', error);
      setTemplateLifecycleBusy(false);
      showAppAlert({
        title: 'Delete failed',
        message: 'Error deleting template: ' + error.message,
        confirmButtonStyle: 'danger'
      });
    }
  };

  const handleTemplateLifecycleArchive = async () => {
    const m = templateLifecycleModal;
    if (!m || templateLifecycleBusy) return;
    if (templateArchiveConfirmText.trim().toLowerCase() !== 'confirm') return;
    setTemplateLifecycleBusy(true);
    try {
      const row = await resolveLifecycleTemplateRow(m.template);
      if (!row?.id) throw new Error('Template row is not available yet. Please try again in a moment.');

      const now = new Date().toISOString();
      // Archive the whole group — template row plus every run in it. Archiving
      // only the template row lets ensureTemplatesForGroups materialize a fresh
      // template for the still-active runs on next load, so the card comes
      // straight back. Archived runs stay in reports and are recoverable from
      // the Session Archive (same as "Archive Stack" on a count).
      const groupId = row.count_group_id || null;
      let archiveQuery = supabase
        .from('count_sessions')
        .update({ is_archived: true, archived_at: now })
        .select();
      archiveQuery = groupId ? archiveQuery.eq('count_group_id', groupId) : archiveQuery.eq('id', row.id);
      const { data, error } = await archiveQuery;
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('No rows updated — you may not have permission to archive this template.');
      }

      const name = m.info?.seriesName || row.name || 'This template';
      console.log(`✅ Archived template ${row.id}`);
      closeTemplateLifecycleModal();
      // Close whichever surface the dialog was launched from — the full
      // detail screen or the inline expanded card.
      if (detailGroupId) closeTemplateDetail();
      if (inlineDetailsGroupId) setInlineDetailsGroupId(null);
      await loadCountSessions();
      showAppAlert({
        title: '🗄️ Template archived',
        message: `"${name}" was moved to the Archive. It will remain there in case you need it — you can restore it or permanently delete it from the Archive; nothing is deleted automatically.`,
        confirmButtonStyle: 'primary'
      });
    } catch (error) {
      console.error('Error archiving template:', error);
      setTemplateLifecycleBusy(false);
      showAppAlert({
        title: 'Archive failed',
        message: 'Error: ' + error.message,
        confirmButtonStyle: 'danger'
      });
    }
  };

  const [openMenuId, setOpenMenuId] = useState(null);
  useEffect(() => {
    if (!openMenuId) return;
    const handler = (e) => {
      if (!e.target.closest('[data-card-menu]')) setOpenMenuId(null);
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [openMenuId]);

  // ── Super-admin test tooling: backdated copy of a count session ────────────
  // Hidden inside the card overflow menu and only for super admins, so it adds
  // no permanent UI for merchants. Duplicates a session (items included) with
  // every date shifted to a chosen day — used to fabricate realistic historical
  // data when testing end-of-period reports and notifications.
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  useEffect(() => {
    let cancelled = false;
    // Visibility-only gate (RLS still governs the actual writes). Accept either
    // super-admin signal: the sidebar's cached user_profiles.app_role, or the
    // canonical super_admins table via the is_super_admin() RPC — the two are
    // not guaranteed to be in sync (same dual check as system-admin.html).
    try {
      if (localStorage.getItem('app_role') === 'super_admin') setIsSuperAdmin(true);
    } catch (_) { /* localStorage unavailable */ }
    (async () => {
      try {
        const { data, error } = await supabase.rpc('is_super_admin');
        if (!cancelled && !error && data === true) setIsSuperAdmin(true);
      } catch (_) { /* treated as not super admin */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // { session, date: 'YYYY-MM-DD', busy, error } | null
  const [backdateCopyModal, setBackdateCopyModal] = useState(null);

  const openBackdateCopy = (session) => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    const pad = (n) => String(n).padStart(2, '0');
    setBackdateCopyModal({
      session,
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      randomize: true,
      busy: false,
      error: null
    });
  };

  const handleBackdateCopyConfirm = async () => {
    const modal = backdateCopyModal;
    if (!modal?.session || !modal.date || modal.busy) return;
    setBackdateCopyModal((m) => ({ ...m, busy: true, error: null }));

    let newSessionId = null;
    try {
      const { data: orig, error: fetchErr } = await supabase
        .from('count_sessions')
        .select('*')
        .eq('id', modal.session.id)
        .single();
      if (fetchErr || !orig) throw new Error(fetchErr?.message || 'Could not load the original count.');

      // Whole-day shift that lands the count's real timeline on the chosen
      // date. Anchored on completed_at (what the EOP wizard and reports read),
      // not session_date: EOP counts carry a future period-end session_date,
      // so anchoring there would push completed_at weeks off the chosen day.
      // Time-of-day is preserved on every timestamp so the copy reads naturally
      // (a count finished at 4:12 PM still finishes at 4:12 PM on the new day).
      const deltaMs = computeDayShiftMs(orig.completed_at || orig.created_at, modal.date);
      const shiftTs = (value) => shiftTimestamp(value, deltaMs);

      const copy = { ...orig };
      delete copy.id;
      copy.session_date = modal.date;
      copy.created_at = shiftTs(orig.created_at);
      copy.updated_at = shiftTs(orig.updated_at);
      copy.started_at = shiftTs(orig.started_at);
      copy.completed_at = shiftTs(orig.completed_at);
      copy.last_accessed_at = shiftTs(orig.last_accessed_at);
      copy.archived_at = shiftTs(orig.archived_at);
      copy.voided_at = shiftTs(orig.voided_at);
      copy.notes = `${orig.notes ? `${orig.notes}\n` : ''}[Backdated test copy of count #${orig.id}]`;

      const { data: inserted, error: insertErr } = await supabase
        .from('count_sessions')
        .insert(copy)
        .select('id')
        .single();
      if (insertErr || !inserted) throw new Error(insertErr?.message || 'Could not create the copy.');
      newSessionId = inserted.id;

      const { data: items, error: itemsErr } = await supabase
        .from('count_items')
        .select('*')
        .eq('count_session_id', orig.id);
      if (itemsErr) throw new Error(itemsErr.message);

      // With "Give items new random counts" checked, every counted quantity is
      // re-rolled (scaled 1–50) so the copy's inventory value differs from the
      // original — exact clones make period-over-period comparisons useless.
      const roll = modal.randomize !== false ? makeRandomCountRoller() : null;
      const itemCopies = (items || []).map((item) => {
        const itemCopy = { ...item };
        delete itemCopy.id;
        itemCopy.count_session_id = newSessionId;
        itemCopy.created_at = shiftTs(item.created_at);
        itemCopy.updated_at = shiftTs(item.updated_at);
        itemCopy.counted_at = shiftTs(item.counted_at);
        const wasCounted = item.is_counted
          || (item.actual_quantity !== null && item.actual_quantity !== undefined && item.actual_quantity !== '');
        if (roll && wasCounted) {
          itemCopy.actual_quantity = roll();
        }
        return itemCopy;
      });
      for (let i = 0; i < itemCopies.length; i += 200) {
        const { error: chunkErr } = await supabase
          .from('count_items')
          .insert(itemCopies.slice(i, i + 200));
        if (chunkErr) throw new Error(chunkErr.message);
      }

      setBackdateCopyModal(null);
      await loadCountSessions();
      showAppAlert({
        title: '🧪 Backdated copy created',
        message: `"${orig.name}" was duplicated as count #${newSessionId} dated ${modal.date} (${itemCopies.length} items, all timestamps shifted${modal.randomize !== false ? ', counted quantities re-randomized' : ''}).`,
        confirmButtonStyle: 'primary'
      });
    } catch (error) {
      // Remove the half-created session so a failed run leaves nothing behind
      // (count_items cascade-delete with the session).
      if (newSessionId) {
        try { await supabase.from('count_sessions').delete().eq('id', newSessionId); } catch (_) {}
      }
      console.error('Backdated copy failed:', error);
      setBackdateCopyModal((m) => (m ? { ...m, busy: false, error: error.message } : m));
    }
  };

  // Extra overflow-menu entries that only super admins see.
  const superAdminCardMenuItems = (session) => (
    isSuperAdmin
      ? [{ icon: '🧪', label: 'Backdated copy…', action: () => { setOpenMenuId(null); openBackdateCopy(session); } }]
      : []
  );

  const [previewSession, setPreviewSession] = useState(null);
  const [previewItems, setPreviewItems] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSort, setPreviewSort] = useState('default');

  const sortPreviewItems = (items, sortKey) => {
    if (sortKey === 'default') return items;
    const cmp = (a, b) => {
      const snapA = a.item_snapshot || {};
      const snapB = b.item_snapshot || {};
      if (sortKey === 'az') {
        const nameA = (snapA.description || snapA.name || a.description || a.name || '').toLowerCase();
        const nameB = (snapB.description || snapB.name || b.description || b.name || '').toLowerCase();
        if (!nameA && nameB) return 1;
        if (nameA && !nameB) return -1;
        return nameA.localeCompare(nameB);
      }
      if (sortKey === 'vendor') {
        const vA = (snapA.vendor || a.vendor || '').toLowerCase();
        const vB = (snapB.vendor || b.vendor || '').toLowerCase();
        if (!vA && vB) return 1;
        if (vA && !vB) return -1;
        return vA.localeCompare(vB);
      }
      if (sortKey === 'location') {
        const lA = (snapA.location || a.location || '').toLowerCase();
        const lB = (snapB.location || b.location || '').toLowerCase();
        if (!lA && lB) return 1;
        if (lA && !lB) return -1;
        return lA.localeCompare(lB);
      }
      if (sortKey === 'counted') {
        const cA = a.is_counted || (a.counted_quantity != null && a.counted_quantity > 0);
        const cB = b.is_counted || (b.counted_quantity != null && b.counted_quantity > 0);
        if (cA === cB) {
          const nameA = (snapA.description || snapA.name || a.description || a.name || '').toLowerCase();
          const nameB = (snapB.description || snapB.name || b.description || b.name || '').toLowerCase();
          if (!nameA && nameB) return 1;
          if (nameA && !nameB) return -1;
          return nameA.localeCompare(nameB);
        }
        return cA ? 1 : -1;
      }
      return 0;
    };
    return [...items].sort(cmp);
  };

  const handlePreviewItems = async (session) => {
    setPreviewSession(session);
    setPreviewSort('default');
    setPreviewLoading(true);
    setOpenMenuId(null);

    try {
      const cached = sessionItemsCache[session.id];
      if (cached && cached.length > 0) {
        setPreviewItems(cached);
        setPreviewLoading(false);
        return;
      }

      const { data: items, error } = await supabase
        .from('count_items')
        .select('*')
        .eq('count_session_id', session.id)
        .order('id');

      if (!error && items) {
        const newCache = { ...sessionItemsCache };
        newCache[session.id] = items;
        setSessionItemsCache(newCache);
        setPreviewItems(items);
      } else {
        setPreviewItems([]);
      }
    } catch (err) {
      console.error('Error loading preview items:', err);
      setPreviewItems([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadVendorDirectoryForStore = async (storeId) => {
    if (!storeId) return { vendorsById: new Map(), vendorsByKey: new Map() };
    return loadVendorDirectory(supabase, storeId);
  };

  const buildOrderSessionVendorData = async ({ countSession, countItemRows, storeId }) => {
    if (!countSession || !countSession.order_calculations_enabled) return [];
    const rows = Array.isArray(countItemRows) ? countItemRows : [];
    if (!rows.length) return [];
    const vendorDirectory = await loadVendorDirectoryForStore(storeId);
    const buckets = new Map();

    // Pre-compute shared count total and PAR per inventory_item_id.
    // Only counted rows contribute to the on-hand total so an uncounted sibling
    // does not silently read as zero. No package minimum is read here: the need
    // this builds is the vendor-neutral PAR − on hand, and which vendor's
    // minimum applies is decided in Savings Review, not by counting.
    const sharedCountTotalsMap = new Map();
    const sharedParMap = new Map();
    rows.forEach((row) => {
      const invId = row.inventory_item_id;
      if (!invId) return;
      const isCounted = Boolean(row.is_counted);
      const qty = isCounted ? (parseFloat(row.actual_quantity) || 0) : 0;
      if (isCounted) {
        sharedCountTotalsMap.set(invId, (sharedCountTotalsMap.get(invId) || 0) + qty);
      }
      // Resolved read: stored normalized PAR first, legacy only in the row's
      // own count unit — an ambiguous "2 cases" never enters this max as 2
      // (hence no par_unit fallback, which would compare it against itself).
      const resolution = resolveRowPar(row, row?.item_snapshot?.unit ?? row?.count_unit ?? '');
      const p = resolution.status === PAR_STATUS.VALID ? resolution.normalized : null;
      if (Number.isFinite(p) && p > (sharedParMap.get(invId) || 0)) sharedParMap.set(invId, p);
    });

    // Pre-aggregate sibling rows by inventory_item_id. manual_order_quantity
    // from ANY sibling takes precedence over PAR-calculated quantities.
    // manual_order_quantity = 0 is a valid override (suppresses the order line).
    const invIdAgg = new Map(); // invId → { repRow, bestManualQty, hasManualOverride }
    rows.forEach((itemRow) => {
      const invId = itemRow.inventory_item_id;
      const manualQty = parseOptionalNumber(itemRow.manual_order_quantity);
      const hasManual = manualQty !== null;
      const isCounted = Boolean(itemRow.is_counted);
      if (!isCounted && !hasManual) return;

      const key = invId || `__no_invid_${itemRow.id}`;
      if (!invIdAgg.has(key)) {
        invIdAgg.set(key, { repRow: itemRow, bestManualQty: hasManual ? manualQty : null, hasManualOverride: hasManual });
      } else {
        const agg = invIdAgg.get(key);
        if (hasManual && !agg.hasManualOverride) {
          agg.repRow = itemRow;
          agg.bestManualQty = manualQty;
          agg.hasManualOverride = true;
        }
      }
    });

    invIdAgg.forEach(({ repRow: itemRow, bestManualQty }) => {
      const invIdForLookup = itemRow.inventory_item_id;
      const snap = itemRow?.snapshot_at_close && typeof itemRow.snapshot_at_close === 'object'
        ? itemRow.snapshot_at_close
        : (itemRow?.item_snapshot || {});
      const rawVendorLabel = String(
        snap.vendor_name || snap.vendor_display_snapshot || snap.vendor || ''
      ).trim();
      const hasVendorLabel = Boolean(rawVendorLabel);
      const vendorLabel = hasVendorLabel ? normalizeVendorLabel(rawVendorLabel) : '';
      // No resolvable PAR = no calculated order (a manual quantity still
      // wins below); the raw column is never a fallback quantity here.
      const parLevel = sharedParMap.get(invIdForLookup) ?? 0;
      const quantity = parseFloat(itemRow?.actual_quantity) || 0;
      const totalCounted = sharedCountTotalsMap.get(invIdForLookup) ?? quantity;
      const shortage = Math.max(0, parLevel - totalCounted);
      const calculatedQty = calculateNeedQuantity(shortage);
      const orderQuantity = bestManualQty !== null ? bestManualQty : calculatedQty;
      if (orderQuantity <= 0) return;
      // Order quantity is in per-unit terms (matches the `$X/unit` price
      // shown in the editor), so pick the per-unit price first and only
      // fall back to case_price when no per-unit price is available.
      const unitPrice = parseFloat(snap.unit_price ?? snap.each_price ?? snap.case_price);
      const cleanUnitPrice = Number.isFinite(unitPrice) ? unitPrice : 0;
      const orderValue = cleanUnitPrice * orderQuantity;
      const resolvedVendor = hasVendorLabel ? resolveVendorForName(vendorLabel, vendorDirectory) : null;
      const contact = buildVendorContactInfo(resolvedVendor);
      const hasVendorAssignment = Boolean(resolvedVendor?.id || hasVendorLabel);
      const key = hasVendorAssignment ? (resolvedVendor?.id || vendorLabel.toLowerCase()) : '__no_vendor_assigned__';
      const normalizedVendorName = hasVendorAssignment ? vendorLabel : 'No vendor assigned';
      if (!buckets.has(key)) {
        buckets.set(key, {
          vendorAssigned: hasVendorAssignment,
          vendorId: resolvedVendor?.id || null,
          vendorName: normalizedVendorName,
          contact,
          items: [],
          totalOrderValue: 0,
          totalOrderQty: 0,
        });
      }
      const bucket = buckets.get(key);
      bucket.items.push({
        description: snap.description || snap.name || 'Unnamed Item',
        brand: snap.brand || '',
        location: itemRow?.location || snap.location || '-',
        quantity,
        parLevel,
        shortage,
        orderQuantity,
        unitPrice: cleanUnitPrice,
        orderValue,
      });
      bucket.totalOrderValue += orderValue;
      bucket.totalOrderQty += orderQuantity;
    });

    return [...buckets.values()]
      .map((bucket) => {
        const sendReadiness = getVendorSendReadiness(bucket.contact, { vendorAssigned: bucket.vendorAssigned });
        return {
          ...bucket,
          canSend: sendReadiness.canSend,
          sendReadinessCode: sendReadiness.code,
          sendReadinessReason: sendReadiness.reason,
          items: [...bucket.items].sort((a, b) => String(a.description || '').localeCompare(String(b.description || ''))),
        };
      })
      .sort((a, b) => {
        if (a.vendorAssigned !== b.vendorAssigned) return a.vendorAssigned ? -1 : 1;
        return a.vendorName.localeCompare(b.vendorName);
      });
  };

  // Helper function to format dates and times
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }) + ' ' + date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Helper function to format time strings (like "3:00 PM")
  const formatTimeString = (timeString) => {
    if (!timeString) return 'Not set';
    // If it's already a formatted time string, return as-is
    if (typeof timeString === 'string' && (timeString.includes('AM') || timeString.includes('PM'))) {
      return timeString;
    }
    // Otherwise try to format it
    return timeString;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Human relative date for the overview status sentences: "today",
  // "yesterday", "3 days ago", "2 weeks ago", then falls back to the
  // absolute date once it's over ~8 weeks old.
  const formatRelativeDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      const dayMs = 24 * 60 * 60 * 1000;
      const diffDays = Math.round((startOfDay(new Date()) - startOfDay(date)) / dayMs);
      if (diffDays <= 0) return 'today';
      if (diffDays === 1) return 'yesterday';
      if (diffDays < 14) return `${diffDays} days ago`;
      if (diffDays < 60) return `${Math.round(diffDays / 7)} weeks ago`;
      return formatDate(dateString);
    } catch (_) {
      return '';
    }
  };

  // Human relative time for the Recent activity tray: "3 min ago", "5 hours
  // ago", "Yesterday", "4 days ago", then an abbreviated date ("Jul 25")
  // once it's a week or more old. Finer-grained than formatRelativeDate,
  // which only knows whole days.
  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
      if (diffMin < 1) return 'Just now';
      if (diffMin < 60) return `${diffMin} min ago`;
      const diffHours = Math.floor(diffMin / 60);
      if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      const dayMs = 24 * 60 * 60 * 1000;
      const diffDays = Math.round((startOfDay(new Date()) - startOfDay(date)) / dayMs);
      if (diffDays <= 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch (_) {
      return '';
    }
  };

  // Compact numeric date "M/D/YY" used on the mobile count overview cards
  // where horizontal space is tight and "May 1, 2026" wraps onto 3 lines.
  const formatDateShort = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '—';
      const m = date.getMonth() + 1;
      const d = date.getDate();
      const y = String(date.getFullYear()).slice(-2);
      return `${m}/${d}/${y}`;
    } catch (error) {
      return '—';
    }
  };

  // "9:41 AM" — time-of-day only, for history rows and meta cells that
  // already carry the date elsewhere on the line.
  const formatTimeOnly = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch (_) {
      return '';
    }
  };

  // Short "weekday, mon day" label (e.g. "Sat, Feb 1") for schedule-preview
  // rows where the real cadence matters more than the calendar year.
  const formatDayAndDate = (ymd) => {
    if (!ymd) return '';
    const d = typeof ymd === 'string' ? new Date(ymd + 'T00:00:00') : new Date(ymd);
    if (isNaN(d.getTime())) return String(ymd);
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const handlePauseSession = async () => {
    const result = await saveProgress('paused', { silent: true });
    if (!result?.ok) return;
    showAppAlert({
      title: 'Progress saved',
      message: `${result.countedItems} items counted. You can resume this count later from the main screen.`,
      confirmButtonStyle: 'warning',
      onAcknowledge: () => {
        if (onCancel) onCancel();
      }
    });
  };

  // ── isNetworkError — distinguish transient network failures from server errors ─
  // Used by the Done flow to decide whether to fall back to offline-completion
  // mode. Server 4xx/5xx errors must NOT trigger the offline path — they have
  // their own error UI. Only genuine connectivity failures should.
  const isNetworkError = (err) => {
    if (!err) return false;
    if (err.name === 'AbortError') return true;
    const msg = (err.message || '').toLowerCase();
    const name = (err.name || '').toLowerCase();
    // Match specific network-failure messages from browsers and supabase-js.
    // Do NOT blanket-match all TypeError — only message-matched ones qualify,
    // so generic TypeErrors like "Cannot read property …" are not misclassified.
    if (
      msg.includes('failed to fetch') ||
      msg.includes('network request failed') ||
      msg.includes('load failed') ||
      msg.includes('networkerror') ||
      msg.includes('connection refused') ||
      msg.includes('etimedout') ||
      msg.includes('econnrefused')
    ) return true;
    return (
      name.includes('networkerror') ||
      name.includes('fetcherror')
    );
  };

  // ── runOfflineCompletionPath — queue all local state to IDB for later sync ──
  // Called when a real save attempt fails with a network error. Queues all
  // item changes + session completion to IndexedDB; the background sync runner
  // pushes them to Supabase when connectivity returns. Also sets the
  // appropriate modal step so the user knows what happened.
  const runOfflineCompletionPath = async ({ showNoInternetOnNetworkFail = false } = {}) => {
    try {
      const now = new Date().toISOString();
      const itemsToFinalize = countItems;

      for (const item of itemsToFinalize) {
        const hasEnteredCount = item.is_counted || hasCountEntry(item.current_count);
        await offlineSync.queueChange(item.id, {
          actual_quantity: hasEnteredCount
            ? (item.current_count ?? item.actual_quantity ?? '0')
            : null,
          is_counted: hasEnteredCount,
          counted_at: hasEnteredCount ? now : null,
          calculated_order_quantity: parseOptionalNumber(item.calculated_order_quantity),
          manual_order_quantity: parseOptionalNumber(item.manual_order_quantity),
        });
      }

      // Completion sync must never create purchase orders in the background.
      await offlineSync.queueSessionCompletion({ completedAt: now, skipDraftCreation: true });
      setOfflineCompletionMode(false);
      setOfflineBlockedNoInternet(false);
      if (showNoInternetOnNetworkFail) setOfflineBlockedNoInternet(true);
      setCompleteFlowStep('offline-blocked');
    } catch (error) {
      console.error('Error completing count session offline:', error);
      showAppAlert({
        title: 'Unable to save count',
        message: error.message || 'Failed to save count locally.',
        confirmButtonStyle: 'danger',
      });
      setCompleteFlowStep(null);
    }
  };

  const buildCompletionWorkflowSummary = () => {
    // Group-aware: a related group is accounted for when its visible favored
    // card was counted — hidden sibling rows must never read as unaccounted
    // items (the session shows one card, so completion counts one thing).
    const completionOpts = { groups: countGroupFold.groups };
    const eligibleCountItems = countItemsEligibleForCompletionOrdering(countItems || [], completionOpts);
    // Groups ride along so one Equivalent group stays one purchasing need even
    // if these rows ever arrive unfolded; a stated PAR the normalized
    // resolution refused is collected rather than silently ordering nothing.
    const unresolvedParItems = [];
    const needs = selectedSession?.order_calculations_enabled
      ? buildNeedsFromCountItems(eligibleCountItems, {
        groups: countGroupFold.groups,
        unresolved: unresolvedParItems,
      })
      : [];
    return {
      ...summarizeCountCompletion(countItems || [], completionOpts),
      orderingNeedCount: needs.length,
      eligibleCountItems,
      needs,
      unresolvedParItems,
    };
  };

  // EOP completed screen: category valuation for the summary card. Reads the
  // frozen close snapshots saveProgress('completed') just wrote — the same
  // select and per-row math as the Inventory Value report — so the totals
  // shown at completion equal the report the screen links to. Purely a bonus
  // on the completed screen: a failed load hides the card (valueByCategory
  // null) and never disturbs the completed state.
  const attachEopCompletionValueSummary = async (sessionId) => {
    let valueByCategory = null;
    try {
      const { data, error } = await supabase
        .from('count_items')
        .select('actual_quantity, item_snapshot, snapshot_at_close')
        .eq('count_session_id', sessionId);
      if (error) throw error;
      valueByCategory = summarizeCountValueByCategory(data || []);
    } catch (err) {
      console.warn('[completion] EOP value summary load failed:', err);
    }
    setCompletionWorkflow((previous) => (
      previous?.summary
        ? { ...previous, summary: { ...previous.summary, valueByCategory } }
        : previous
    ));
  };

  const finalizeCompleteCountSession = async ({ showNoInternetOnNetworkFail = false } = {}) => {
    if (completionInFlightRef.current) return;
    completionInFlightRef.current = true;
    setCompletionWorkflow((previous) => ({ ...previous, error: null, isBusy: true }));
    // ── Always attempt the real online save first ──────────────────────────────
    // The connectivity decision is driven by whether the actual save call
    // succeeds or fails — not by the standalone ping probe. This prevents a
    // transient blip in the ping from causing a false-offline completion.
    // Only genuine network/timeout errors fall through to the offline path.
    // Server 4xx/5xx errors surface their existing error UI unchanged.
    setCloseProgress({ subStep: null, error: null });
    try {
      const result = await saveProgress('completed', {
        silent: true,
        suppressAlert: true,
        onStep: (subStep) => setCloseProgress({ subStep, error: null }),
      });
      if (result?.ok) {
        // The session is now completed in Supabase — drop any queued
        // offline completion record so the background sync runner doesn't
        // redundantly re-issue the same status='completed' update and so
        // the offline-blocked modal isn't restored on the next reopen.
        if (mobileTool && offlineSync.clearPendingCompletion) {
          try { await offlineSync.clearPendingCompletion(); } catch (_) {}
        }
        const completionSummary = buildCompletionWorkflowSummary();
        let nextAction = COUNT_COMPLETION_NEXT_ACTION.NONE;
        let savingsReviewId = null;
        let orderingError = null;
        const hasOrderingNextStep = selectedSession?.order_calculations_enabled
          && completionSummary.needs.length > 0;

        // The count is genuinely complete now. Move to the completed screen
        // immediately while any vendor-route lookup finishes in-place.
        setCloseProgress(null);
        setCompletionWorkflow({
          summary: completionSummary,
          nextAction: hasOrderingNextStep
            ? COUNT_COMPLETION_NEXT_ACTION.PREPARING_ORDERING
            : COUNT_COMPLETION_NEXT_ACTION.NONE,
          savingsReviewId: null,
          error: null,
          isBusy: hasOrderingNextStep,
        });
        setCompleteFlowStep(COUNT_COMPLETION_STEP.COMPLETED);

        // Completion stops at the inventory/reporting boundary. We only
        // prepare the next route here; draft POs are created later, if the user
        // explicitly chooses that action on the completed screen.
        if (hasOrderingNextStep) {
          try {
            const planStoreId = selectedSession.store_id || activeStoreId;
            // Count identity stays Equivalent-only, while Shopping Review also
            // loads each direct confirmed Alternative through its OWN
            // Equivalent closure. A graph fetch failure safely degrades to the
            // existing Equivalent-only route.
            const shoppingGraph = await fetchShoppingCandidateGraphForStore(planStoreId);
            const sameItemGroups = shoppingGraph?.equivalentGroups
              || await fetchWorkingSetGroupsForStore(planStoreId);
            const needItemIds = completionSummary.needs.map((need) => need.inventory_item_id);
            const optionItemIds = [...new Set(needItemIds.flatMap((itemId) => {
              if (shoppingGraph) return shoppingCandidateItemIdsForItem(itemId, shoppingGraph);
              const members = groupMembersFor(sameItemGroups, itemId);
              return members ? [...members] : [itemId];
            }))];
            const [optionsByItemId, candidateItemsById] = await Promise.all([
              fetchOptionsForItems(planStoreId, optionItemIds),
              fetchComparisonItems(planStoreId, optionItemIds),
            ]);
            const worthReviewing = hasVendorChoices(
              completionSummary.needs.map((need) => ({ id: null, inventory_item_id: need.inventory_item_id })),
              optionsByItemId,
              {
                storeId: planStoreId,
                groups: sameItemGroups,
                shoppingCandidateGraph: shoppingGraph,
                itemsById: candidateItemsById,
              },
            );

            if (worthReviewing) {
              const { plan } = await createOrOpenPlanForCount({
                storeId: planStoreId,
                countSessionId: selectedSession.id,
                countItems: completionSummary.eligibleCountItems,
                optionsByItemId,
                shoppingCandidateGraph: shoppingGraph,
              });
              savingsReviewId = plan.id;
              nextAction = COUNT_COMPLETION_NEXT_ACTION.SAVINGS_REVIEW;
            } else {
              nextAction = COUNT_COMPLETION_NEXT_ACTION.CREATE_DRAFTS;
            }
          } catch (routeErr) {
            const errMessage = routeErr?.message || 'Could not prepare the ordering next step.';
            nextAction = COUNT_COMPLETION_NEXT_ACTION.ORDERING_ERROR;
            orderingError = `Your count is complete, but Countertop could not prepare ordering: ${errMessage}`;
            // Persist a durable breadcrumb so the Ordering Hub's
            // CountOrderReview can surface the underlying reason. Best-effort:
            // logging must never block the completed screen.
            try {
              await recordOrderingHubEvent({
                action: 'completion_order_route_failed',
                countSessionId: selectedSession.id,
                details: { error: errMessage, source: 'count_complete' },
              });
            } catch (_) {}
          }
        }
        setCompletionWorkflow({
          summary: completionSummary,
          nextAction,
          savingsReviewId,
          error: orderingError,
          isBusy: false,
        });
        // EOP endings add the category valuation + report links to the
        // completed screen; the totals load in place from the frozen rows.
        if (!selectedSession?.order_calculations_enabled && isEOPSession(selectedSession)) {
          void attachEopCompletionValueSummary(selectedSession.id);
        }
      } else {
        // Save returned a failure. On mobileTool, check if it's a network
        // error — if so, queue locally and show the offline modal. For server
        // errors (RLS, validation, etc.) surface the existing error UI.
        const err = result?.error;
        if (mobileTool && isNetworkError(err)) {
          setCloseProgress(null);
          await runOfflineCompletionPath({ showNoInternetOnNetworkFail });
        } else {
          const errMsg = err?.message || 'Failed to save count.';
          setCloseProgress(null);
          setCompletionWorkflow((previous) => ({
            ...previous,
            error: `We couldn't complete this count. ${errMsg} Your entered quantities are still here; try again.`,
            isBusy: false,
          }));
          setCompleteFlowStep(COUNT_COMPLETION_STEP.REVIEW);
        }
      }
    } catch (error) {
      // Apply the same network vs server distinction to uncaught failures.
      if (mobileTool && isNetworkError(error)) {
        setCloseProgress(null);
        await runOfflineCompletionPath({ showNoInternetOnNetworkFail });
      } else {
        console.error('Error completing count session:', error);
        setCloseProgress(null);
        setCompletionWorkflow((previous) => ({
          ...previous,
          error: `We couldn't complete this count. ${error.message || 'Please try again.'} Your entered quantities are still here.`,
          isBusy: false,
        }));
        setCompleteFlowStep(COUNT_COMPLETION_STEP.REVIEW);
      }
    } finally {
      completionInFlightRef.current = false;
      setCompletionWorkflow((previous) => ({ ...previous, isBusy: false }));
    }
  };

  // ── Offline-blocked Done modal: "Try again" handler (non-order sessions) ───
  // Attempts the real save directly — no standalone ping probe. If the save
  // succeeds we're online and the normal completion flow runs. If it fails
  // with a network error we're still offline: shows the secondary "No internet
  // detected" copy so the user knows to check their settings. If it fails
  // with a server error the existing error UI appears.
  const handleOfflineBlockedRetry = async () => {
    if (offlineBlockedRetrying) return;
    setOfflineBlockedRetrying(true);
    try {
      // Drop the queued completion so we don't double-sync if the save below
      // succeeds, and re-run the finalize which will attempt the real save.
      // showNoInternetOnNetworkFail=true surfaces the secondary "No internet
      // detected" sub-state when the attempt still fails.
      try { await offlineSync.clearPendingCompletion(); } catch (_) {}
      setOfflineBlockedNoInternet(false);
      setOfflineCompletionMode(false);
      setCompleteFlowStep(null);
      await finalizeCompleteCountSession({ showNoInternetOnNetworkFail: true });
    } finally {
      setOfflineBlockedRetrying(false);
    }
  };

  const openCompletionReview = () => {
    if (completionInFlightRef.current) return;
    const { eligibleCountItems: _eligible, needs: _needs, ...summary } = buildCompletionWorkflowSummary();
    setCompletionWorkflow({
      summary,
      nextAction: COUNT_COMPLETION_NEXT_ACTION.NONE,
      savingsReviewId: null,
      error: null,
      isBusy: false,
    });
    setCompleteFlowStep(COUNT_COMPLETION_STEP.REVIEW);
  };

  // Testing helper offered from the Done options sheet (super admins only):
  // gives every uncounted item a random count so a full count can be
  // exercised without keying a value into every item. Uses the shared scaled
  // roller so each run's total inventory value differs meaningfully —
  // unscaled 1–50 rolls average out to nearly the same total every time.
  const fillUncountedWithRandomCounts = () => {
    const wasUncounted = countItems.map((item) => !hasCountEntry(item.current_count));
    const filledCount = wasUncounted.filter(Boolean).length;
    if (filledCount === 0) return;
    startTimerIfNeededRef.current();
    const timestamp = new Date().toISOString();
    const roll = makeRandomCountRoller();
    const filled = countItems.map((item, i) => {
      if (!wasUncounted[i]) return item;
      const randomCount = roll();
      return { ...item, current_count: randomCount, actual_quantity: randomCount, is_counted: true };
    });
    const hydrated = applySharedOrderMathBulk(filled, { flexibleSourceItemIds, packageConstraintsByItemId: flexibleConstraintsByItemId });
    setCountItems(hydrated);
    // Mirror updateItemCount's local-first write so the filled values survive
    // offline and sync like hand-entered counts. Desktop persists on save/Done.
    if (mobileTool) {
      hydrated.forEach((item, i) => {
        if (!wasUncounted[i]) return;
        offlineSync.queueChange(item.id, {
          actual_quantity: item.current_count,
          is_counted: true,
          counted_at: timestamp,
          calculated_order_quantity: parseOptionalNumber(item.calculated_order_quantity),
          manual_order_quantity: parseOptionalNumber(item.manual_order_quantity),
        });
      });
    }
    showTemplateSyncToast(`Random counts added to ${filledCount} item${filledCount === 1 ? '' : 's'}`);
  };

  const handleDoneClick = () => {
    // Connectivity is not pre-checked — the offline/online decision is driven
    // by whether the real save attempt inside finalizeCompleteCountSession
    // succeeds or fails, not by a standalone ping probe.
    //
    // All versions: when at least one location has no entries at all, the
    // user probably meant "done with this location" rather than "done with
    // the whole count" (the Done button sits right below the last item of a
    // location). Offer the choice before any completion flow starts.
    if (getUntouchedRooms().length > 0 && getNextRoomWithUncounted()) {
      setCompleteFlowStep(COUNT_COMPLETION_STEP.LOCATION_CHOICE);
      return;
    }
    openCompletionReview();
  };

  const dismissCompletedCount = () => {
    setCompleteFlowStep(null);
    setCompletionWorkflow({
      summary: null,
      nextAction: COUNT_COMPLETION_NEXT_ACTION.NONE,
      savingsReviewId: null,
      error: null,
      isBusy: false,
    });
    if (onCancel) onCancel();
  };

  const handleCreateDraftOrdersAfterCompletion = async () => {
    if (draftCreationInFlightRef.current || !selectedSession?.id) return;
    draftCreationInFlightRef.current = true;
    setCompletionWorkflow((previous) => ({ ...previous, isBusy: true, error: null }));
    try {
      const result = await createDraftPurchaseOrdersFromCount({
        countSessionId: selectedSession.id,
      });
      // A clean success goes straight to the PO page. Partial and empty
      // results stay put so the buyer sees what needs attention instead of
      // being dropped onto a page that cannot explain the missing lines.
      // The shared helper keeps setup skips (no vendor assigned) distinct from
      // server-side failures — an insert failure must surface its real error,
      // never read as "items need ordering setup".
      const outcome = describeDraftCreationOutcome({
        drafts: result?.data?.drafts || [],
        skipped: result?.data?.skipped || [],
      });
      if (outcome.status === 'created') {
        window.location.href = completionOrderingDestination();
        return;
      }
      setCompletionWorkflow((previous) => ({ ...previous, error: outcome.message }));
    } catch (draftErr) {
      const errMessage = draftErr?.message || 'Draft order creation failed.';
      setCompletionWorkflow((previous) => ({
        ...previous,
        error: `Your count is complete, but draft orders could not be created: ${errMessage}`,
      }));
      try {
        await recordOrderingHubEvent({
          action: 'draft_create_failed',
          countSessionId: selectedSession.id,
          details: { error: errMessage, source: 'count_complete_next_step' },
        });
      } catch (_) {}
    } finally {
      draftCreationInFlightRef.current = false;
      setCompletionWorkflow((previous) => ({ ...previous, isBusy: false }));
    }
  };

  const handleCountAgainWithStaleGuard = async (session, isNewest) => {
    if (!isNewest) {
      const confirmed = await showAppConfirm({
        title: 'Use older run as template?',
        message: 'This is not the newest run. Copy this older version?',
        confirmText: 'Copy older run',
        cancelText: 'Cancel',
        confirmButtonStyle: 'warning'
      });
      closeAppModal();
      if (!confirmed) return;
    }
    await handleCountAgain(session);
  };

  // Pars wizard — opened from Start Order / setup flows on the overview,
  // and (countMode) from the counting screen's PAR badge, anchored on the
  // item that was tapped. The overview and the counting screen are SEPARATE
  // component returns, so each branch must render this element itself —
  // defined once here so the two cannot drift.
  const renderParsWizardModal = () => {
    if (!parsWizardModal) return null;
    return (
      <ParsWizardModal
        template={parsWizardModal.template}
        items={parsWizardModal.items}
        isFirstTimeSetup={!!parsWizardModal.isFirstTimeSetup}
        excludeItemsWithPar={!!parsWizardModal.excludeItemsWithPar}
        anchorCountItemId={parsWizardModal.anchorCountItemId ?? null}
        hideSkip={!!parsWizardModal.countMode}
        confirmMissingPars={!parsWizardModal.countMode}
        onSave={(changedRows, allRows) => {
          const isCountMode = !!parsWizardModal.countMode;
          setParsWizardModal(null);
          if (isCountMode) {
            // Fire-and-forget like the old single-item modal: the in-memory
            // update inside is immediate, persistence is best-effort.
            void applyCountParsWizardSave(changedRows);
            return;
          }
          const resolve = parsWizardResolverRef.current;
          parsWizardResolverRef.current = null;
          if (resolve) resolve({ type: 'save', rows: changedRows, allRows });
        }}
        onSkip={() => {
          const isCountMode = !!parsWizardModal.countMode;
          setParsWizardModal(null);
          if (isCountMode) return;
          const resolve = parsWizardResolverRef.current;
          parsWizardResolverRef.current = null;
          if (resolve) resolve({ type: 'skip' });
        }}
        onCancel={() => {
          const isCountMode = !!parsWizardModal.countMode;
          setParsWizardModal(null);
          if (isCountMode) return;
          const resolve = parsWizardResolverRef.current;
          parsWizardResolverRef.current = null;
          if (resolve) resolve('cancel');
        }}
      />
    );
  };

  // Main component return
  if (step === 'counting' && selectedSession && countItems.length > 0) {
    const selectedItem = countItems[selectedItemIndex];

    // Offline sync indicator: use the item-specific status if available,
    // falling back to the overall session status. Only rendered in mobileTool.
    const currentItemSyncStatus = mobileTool
      ? (offlineSync.itemSyncStatuses[selectedItem?.id] || offlineSync.getSessionSyncStatus())
      : 'synced';

    // Direct order mode: the center tile becomes the order field, PAR and
    // the separate ORDER tile are hidden, and the entered value is the order qty.
    const directOrderMode = isDirectOrderSession(selectedSession);

    const selectedManualOverride = selectedItem.order_calculations_enabled
      ? parseOptionalNumber(selectedItem.manual_order_quantity)
      : null;
    const selectedOrderHasOverride = selectedItem.order_calculations_enabled
      && selectedManualOverride !== null;
    const selectedShowsNeed = !selectedOrderHasOverride;
    // The package every number on this card reads in — null when the line
    // counts in the item's own unit. The order value is already expressed in
    // it by displayOrderForRow.
    const selectedPackageView = packageViewFor(selectedItem);
    const selectedOrderValue = displayOrderForRow(selectedItem);
    // The one unit word the PAR / COUNT / NEED tiles share. Reading NEED in
    // the item's stored unit while PAR and COUNT read in the counted package
    // is the "PAR 1 BIB · NEED 4.5 gal" card — same shelf, three units.
    const selectedDisplayUnit = displayUnitForRow(selectedItem);

    // Multi-location support: find other count_items in this session that
    // reference the same inventory item (same item in another location). When
    // the user counts one location, the label above the count box surfaces
    // the other location's counted value; once this one is entered too, it
    // flips to the global total so the user can see the running combined
    // inventory (used for pars/reports).
    const selectedInvId = selectedItem.inventory_item_id
      || selectedItem.item_snapshot?.id
      || selectedItem.id;
    const multiLocSiblings = countItems.filter((ci, i) => (
      i !== selectedItemIndex
      && (ci.inventory_item_id || ci.item_snapshot?.id || ci.id) === selectedInvId
    ));
    const siblingCountedEntries = multiLocSiblings
      .filter(ci => ci.is_counted || (ci.current_count !== '' && ci.current_count != null))
      .map(ci => ({
        location: ci.location || ci.item_snapshot?.location || 'Other',
        abbr: locationCode(ci.location || ci.item_snapshot?.location || 'Other'),
        // The chip shows what was counted THERE, in the package that line
        // counts in; the normalized value stays behind it for the total,
        // which cannot be summed across differently-packaged rows otherwise.
        value: displayCountForRow(ci),
        normalized: parseFloat(ci.current_count) || 0,
      }));
    const selectedHasValue = selectedItem.is_counted
      || (selectedItem.current_count !== '' && selectedItem.current_count != null && selectedItem.current_count !== '0');
    const multiLocGlobalTotalNormalized = siblingCountedEntries.reduce((s, e) => s + e.normalized, 0)
      + (parseFloat(selectedItem.current_count) || 0);
    // The combined total reads in the selected line's package, like every
    // other number on the card. Falls back to the raw total when no package
    // view is active.
    const multiLocGlobalTotal = (() => {
      const view = packageViewFor(selectedItem);
      if (!view) return multiLocGlobalTotalNormalized;
      const packages = packageDisplayQty(multiLocGlobalTotalNormalized, view);
      return packages === null ? multiLocGlobalTotalNormalized : packages;
    })();
    // multiLocLabel: shown as a text badge when the user has their own entry (shows combined total).
    // siblingChips: shown as individual chips when the user hasn't entered yet (shows per-location).
    let multiLocLabel = null;
    let siblingChips = null;
    if (!directOrderMode && multiLocSiblings.length > 0) {
      if (selectedHasValue) {
        multiLocLabel = `Total all Locations: ${multiLocGlobalTotal}`;
      } else if (siblingCountedEntries.length > 0) {
        siblingChips = siblingCountedEntries;
      }
    }
    const truncateMeta = (value) => {
      const s = value == null ? '' : String(value);
      return s.length > 25 ? `${s.slice(0, 24)}…` : s;
    };

    // Canonical-item flag for the count card. An item with several linked
    // vendor products has no single vendor and no single price to show here,
    // so the Vendor cell becomes the flag that opens them for review and the
    // Cost cell shows the range across them instead of one snapshot number.
    const countCardName = selectedItem?.item_snapshot?.description
      || selectedItem?.item_snapshot?.name
      || 'Item';
    const countCardUnit = selectedItem?.item_snapshot?.unit || selectedItem?.par_unit || 'each';
    // Group-pooled: a folded card speaks for its whole working-set group, so
    // the vendor/cost cells read the pooled options — the same pool the
    // Savings Review shops — and group membership alone flags the card.
    const countCardInvId = selectedItem?.inventory_item_id;
    const countCardOptions = pooledOptionsByItemId[countCardInvId]
      || purchaseOptionsByItemId[countCardInvId] || [];
    const countCardMembers = groupMembersFor(countGroupFold.groups, countCardInvId);
    const countCardLinkage = canonicalLinkage(countCardOptions, {
      groupMemberCount: countCardMembers ? countCardMembers.size : 0,
    });
    const countCardSummary = countCardLinkage.isCanonical
      ? purchaseOptionsSummary(countCardOptions)
      : null;
    const countCardPresentation = presentationForItem(selectedItem);
    const countCardDisplayUnit = countCardPresentation?.rung?.unitLabel || countCardUnit;
    const countCardPriceLabel = (() => {
      // The counting vendor's own unit price ("$6.40/lb" for the Shamrock
      // case being counted) — never the "$6.40–$119.50/lb" range across all
      // vendors: the counter is holding ONE vendor's package, and the range
      // reads as noise. The multi-vendor range and the snapshot price remain
      // as fallbacks when no vendor package is resolved.
      const countAsOption = countCardPresentation?.option || null;
      if (countAsOption) {
        const unitCost = comparableUnitCost(countAsOption);
        if (unitCost !== null && unitCost !== undefined) {
          return formatUnitPrice(unitCost, countCardUnit);
        }
      }
      if (countCardSummary) {
        return costCellForSummary(countCardSummary, countCardUnit)?.text || '—';
      }
      const raw = selectedItem?.item_snapshot?.unit_price || selectedItem?.item_snapshot?.each_price;
      const parsed = parseFloat(raw);
      return Number.isFinite(parsed) && parsed > 0
        ? parsed.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
        : '—';
    })();
    // The vendor for this line, as a link. Tapping it lists the connected
    // vendor products and lets the counter switch — the moment they are
    // holding the item is the moment they know which supplier it came from.
    // A prepared item has no vendor, and "Vendor: N/A" reads as a gap in the
    // data rather than the truth. The same slot names where it actually comes
    // from — the prep kitchen — so the count card never implies a missing
    // supplier or invents one.
    const countCardIsPrepared = isPreparedSnapshot(selectedItem?.item_snapshot, selectedItem);
    const countCardSourceLabel = countCardIsPrepared ? 'Source' : (countCardLinkage.isCanonical ? 'Vendors' : 'Vendor');
    const countCardSourceValue = countCardIsPrepared
      ? 'Prepared in house'
      : (resolveVendor(selectedItem?.item_snapshot?.vendor || '') || 'N/A');
    // Count as is THE selector on the count card: it chooses the package this
    // line is counted in ("Sysco case — 12 bags") and pins it. It never sets
    // or preselects the vendor the item is ordered from — that decision lives
    // in Savings Review / the template editor, and the directional rule runs
    // both ways: a Savings Review vendor choice doesn't rewrite this format
    // either. Vendor renders as passive context beside it.
    // The live count expressed back in the item's own unit — "2 bags" beside
    // "= 8 lb" is what lets a counter check the conversion instead of trusting
    // it. Only rendered while a package format is active.
    const countCardEquivalence = (() => {
      if (directOrderMode) return null;
      const rung = rungForLine(selectedItem);
      if (!rung) return null;
      const normalized = parseOptionalNumber(selectedItem.current_count);
      if (!selectedItem.is_counted || normalized === null) return null;
      const packages = packageQuantityFromNormalized(normalized, rung);
      if (packages === null) return null;
      return `${packages} ${rung.unitLabel}${packages === 1 ? '' : 's'} = ${formatQuantity(normalized, countCardUnit)}`;
    })();
    // Mixed stock: one old Sysco case + half a US Foods case + two loose bags
    // is the normal state of a shelf mid-transition. The entry sheet keeps
    // those auditable as separate entries while the line stores one
    // normalized total; the link renders only where a package ladder exists.
    const countCardCanCountSeveral =
      !directOrderMode && !countCardIsPrepared && canCountByPackage(selectedItem);
    const countCardEntryCount =
      (countEntriesByCountItemId[String(selectedItem.id)] || []).length;
    // The mixed-count action named in the shelf's own words — "Count cases +
    // loose bottles" — when the counted package has a discrete rung below it;
    // null keeps the generic "Count several packages" wording.
    const countCardMixedLabel = countCardCanCountSeveral
      ? describeMixedCountAction(ladderForItem(selectedItem)?.rungs, rungForLine(selectedItem))
      : null;
    // The "Count as" control: counting language, pins the format on explicit
    // selection, and writes ONLY the count_display_* columns (see
    // countItemSelectionService — nothing in this path can touch
    // selected_purchase_option_id).
    const countCardCountAs = (!countCardIsPrepared && !directOrderMode && countCardPresentation) ? (
      <CountFormatControl
        presentation={countCardPresentation}
        countUnit={countCardUnit}
        compact
        hideLabel
        onSelectFormat={(patch, choice) => handleCountFormatSelect(selectedItem, patch, choice)}
        onToggleFollowLatest={(next) => applyCountFormat(selectedItem, { follow_latest_purchase: next !== false })}
        onOpenPackageSheet={countCardCanCountSeveral ? () => openPackageSheet(selectedItem) : undefined}
        entryCount={countCardEntryCount}
      />
    ) : null;
    // The bottle-fraction tab (liquor/wine lines only): a small button pinned
    // to the far right of the count row that flips the card to the bottle
    // slider. Rendered once here so the mobile and desktop rows share it.
    const countCardBottleInfo = bottleInfoForItem(selectedItem);
    const countCardBottleButton = countCardBottleInfo ? (
      <button
        type="button"
        data-bottle-count-trigger="1"
        onClick={() => openBottleSheet(selectedItemIndex)}
        aria-label="Count the open bottle by tenths"
        title="Count the open bottle by tenths"
        style={{
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2px',
          width: mobileTool ? '2.1rem' : '2.3rem',
          height: mobileTool ? '4.2rem' : '3.3rem',
          padding: 0,
          backgroundColor: '#FFFFFF',
          border: '1.5px solid #C8B5A0',
          borderRadius: '10px',
          color: '#6D5648',
          cursor: 'pointer',
        }}
      >
        <BottleGlyph size={mobileTool ? 17 : 16} />
        <span style={{ fontSize: '0.42rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em', lineHeight: 1 }}>
          Bottle
        </span>
      </button>
    ) : null;
    // Always show the ORDER badge for order-enabled sessions so the user can
    // tap it to set an override even when shortage = 0. In direct order mode the
    // center tile already IS the order field, so the separate ORDER tile is hidden.
    const selectedShowOrder = !!selectedItem.order_calculations_enabled && !directOrderMode;
    const groupCounts = {};
    const groupUncounted = {};
    const groupOrderMeta = {};
    countItems.forEach(ci => {
      const g = getGroupValueForItem(ci);
      groupCounts[g] = (groupCounts[g] || 0) + 1;
      if (!ci.is_counted) groupUncounted[g] = (groupUncounted[g] || 0) + 1;
      const locationOrder = getLocationOrderValue(ci);
      const itemOrder = getItemOrderValue(ci);
      const currentMeta = groupOrderMeta[g];
      if (!currentMeta || locationOrder < currentMeta.locationOrder || (locationOrder === currentMeta.locationOrder && itemOrder < currentMeta.itemOrder)) {
        groupOrderMeta[g] = { locationOrder, itemOrder };
      }
    });
    const allGroups = Object.keys(groupCounts).sort((left, right) => {
      if (sortBy === 'location') {
        const leftMeta = groupOrderMeta[left] || { locationOrder: Number.POSITIVE_INFINITY, itemOrder: Number.POSITIVE_INFINITY };
        const rightMeta = groupOrderMeta[right] || { locationOrder: Number.POSITIVE_INFINITY, itemOrder: Number.POSITIVE_INFINITY };
        if (leftMeta.locationOrder !== rightMeta.locationOrder) return leftMeta.locationOrder - rightMeta.locationOrder;
        if (leftMeta.itemOrder !== rightMeta.itemOrder) return leftMeta.itemOrder - rightMeta.itemOrder;
      }
      return left.localeCompare(right);
    });
    const countedItems = countItems.filter(item => item.is_counted).length;
    const totalItems = countItems.length;
    const progressPercent = Math.round((countedItems / totalItems) * 100);

    // Compute locations remaining (groups with at least one uncounted item).
    // Only shown when sorted by location to match the "locations to go" concept.
    const locationsToGo = sortBy === 'location'
      ? allGroups.filter(g => (groupUncounted[g] || 0) > 0).length
      : null;

    const selectedGroupName = sortBy === 'alphabetical' ? 'All Items' : (selectedLocation || allGroups[0] || 'Group');
    const groupItems = sortBy === 'alphabetical'
      ? countItems
      : countItems.filter(ci => {
          const g = getGroupValueForItem(ci);
          return g === selectedGroupName;
        });
    const groupTotal = groupItems.length;
    const groupCounted = groupItems.filter(ci => ci.is_counted).length;
    const groupPercent = groupTotal > 0 ? Math.round((groupCounted / groupTotal) * 100) : 0;
    const visibleItems = sortBy === 'alphabetical'
      ? countItems
      : countItems.filter(ci => getGroupValueForItem(ci) === selectedGroupName);

    return (
      <div
        className={`container-fluid p-2${!mobileTool && isTabletLayout ? ' count-tablet' : ''}${!mobileTool && isAboveTabletLayout ? ' count-desktop-fallback' : ''}${isIpadLandscape ? ' count-ipad-landscape' : ''}`}
        data-mobile-tool={mobileTool ? '1' : undefined}
        data-count-tablet={!mobileTool && isTabletLayout ? '1' : undefined}
        data-count-desktop-fallback={!mobileTool && isAboveTabletLayout ? '1' : undefined}
        data-ipad-landscape={isIpadLandscape ? '1' : undefined}
        style={{ maxWidth: '100%', backgroundColor: '#FEFCFA' }}
      >
        {pendingSavingsReview && (
          <div
            data-testid="count-pending-savings-review"
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#FDF6EC', border: '1px solid #EAD9BE', borderRadius: 10, padding: '10px 14px', margin: '8px 0 12px', fontFamily: 'Montserrat, sans-serif' }}
          >
            <span style={{ fontSize: 13, color: '#7b5a2a', fontWeight: 600 }}>
              💡 This count has a purchase plan waiting — compare vendors and create the purchase orders there.
            </span>
            <button
              type="button"
              onClick={() => { window.location.href = `ordering.html?savings=${encodeURIComponent(pendingSavingsReview.id)}`; }}
              style={{ borderRadius: 999, padding: '8px 16px', border: '2px solid #5A3A28', background: '#3A2418', color: '#fff', fontFamily: 'inherit', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
            >
              Open Purchase Planner
            </button>
          </div>
        )}
        <BarcodeScanAndAddModal
          open={scanModalOpen}
          onClose={() => setScanModalOpen(false)}
          storeId={selectedSession?.store_id || (typeof window !== 'undefined' ? parseInt(localStorage.getItem('selectedStoreId'), 10) : null)}
          session={selectedSession}
          hideLocationField={mobileTool}
          currentLocation={
            mobileTool && mobileActiveRoom && mobileActiveRoom !== 'Unknown'
              ? mobileActiveRoom
              : null
          }
          countSessionId={selectedSession?.id || null}
          countItems={countItems}
          onJumpToItem={(idx) => { try { selectItemWithPeek(idx); } catch { selectItem(idx); } }}
          onJumpToInventoryItem={async (invId) => {
            if (!selectedSession?.id || !invId) return;
            const fresh = await loadCountItems(selectedSession.id, selectedSession);
            const list = Array.isArray(fresh) ? fresh : [];
            const idx = list.findIndex(ci =>
              String(ci.inventory_item_id || ci.item_snapshot?.id) === String(invId)
            );
            if (idx >= 0) {
              try { selectItemWithPeek(idx); } catch { try { selectItem(idx); } catch {} }
            }
          }}
          onItemAdded={() => { if (selectedSession?.id) { loadCountItems(selectedSession.id, selectedSession); } }}
          onItemLinked={() => { if (selectedSession?.id) { loadCountItems(selectedSession.id, selectedSession); } }}
        />
        <BarcodeScanAndAddModal
          open={addItemModalOpen}
          onClose={() => setAddItemModalOpen(false)}
          noScanMode
          storeId={selectedSession?.store_id || (typeof window !== 'undefined' ? parseInt(localStorage.getItem('selectedStoreId'), 10) : null)}
          session={selectedSession}
          hideLocationField={mobileTool}
          currentLocation={
            mobileTool && mobileActiveRoom && mobileActiveRoom !== 'Unknown'
              ? mobileActiveRoom
              : null
          }
          countSessionId={selectedSession?.id || null}
          countItems={countItems}
          onJumpToItem={(idx) => { try { selectItemWithPeek(idx); } catch { selectItem(idx); } }}
          onJumpToInventoryItem={async (invId) => {
            if (!selectedSession?.id || !invId) return;
            const fresh = await loadCountItems(selectedSession.id, selectedSession);
            const list = Array.isArray(fresh) ? fresh : [];
            const idx = list.findIndex(ci =>
              String(ci.inventory_item_id || ci.item_snapshot?.id) === String(invId)
            );
            if (idx >= 0) {
              try { selectItemWithPeek(idx); } catch { try { selectItem(idx); } catch {} }
            }
          }}
          onItemAdded={() => { if (selectedSession?.id) { loadCountItems(selectedSession.id, selectedSession); } }}
          onItemLinked={() => { if (selectedSession?.id) { loadCountItems(selectedSession.id, selectedSession); } }}
        />
        {/* ── Offline sync toast (mobileTool only) ─────────────────────────────
            Shows "Synced." (or a pending-changes notice) when the queue drains.
            Fixed at the bottom of the viewport so it doesn't shift any layout. */}
        {mobileTool && offlineSyncToast && (
          <div
            style={{
              position: 'fixed',
              bottom: 'calc(env(safe-area-inset-bottom, 0px) + 70px)',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: '#323232',
              color: '#fff',
              padding: '8px 18px',
              borderRadius: '20px',
              fontSize: '0.82rem',
              fontWeight: 600,
              zIndex: 9999,
              pointerEvents: 'none',
              maxWidth: '85vw',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
            }}
          >
            {offlineSyncToast}
          </div>
        )}

        {/* ── Template-sync confirmation toast (Task #289) ───────────────────
            Shown briefly after an in-count PAR edit successfully
            propagates back to the parent template (or, in fallback cases,
            to the inventory master). Visible in both mobile and desktop. */}
        {templateSyncToast && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              bottom: mobileTool
                ? 'calc(env(safe-area-inset-bottom, 0px) + 110px)'
                : '24px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: '#2f6f3a',
              color: '#fff',
              padding: '8px 18px',
              borderRadius: '20px',
              fontSize: '0.82rem',
              fontWeight: 600,
              zIndex: 9999,
              pointerEvents: 'none',
              maxWidth: '85vw',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
            }}
          >
            {templateSyncToast}
          </div>
        )}

        {/* Mobile search bar lives under the item-details card now (search
            paired with Edit-item button); see [data-count-search-bar]. */}

        {/* New Layout: Left selector (with sort above), Right area with progress header above items+keypad */}
        {selectedItem && (
          <div className={isIpadLandscape ? 'count-ipad-landscape-wrap' : 'row'}>
            {/* iPad landscape: left side rail — location tape (top) + item tape (bottom).
                Only rendered in landscape; portrait falls back to the standard phone layout. */}
            {isIpadLandscape && (() => {
              const railItems = countItems
                .map((item, index) => ({ item, index }))
                .filter(({ item }) => !mobileActiveRoom || resolveCountItemLocation(item) === mobileActiveRoom);
              const multiLoc = mobileRooms.length > 1;
              const itemTape = (
                <div className="count-ipad-item-tape">
                  {railItems.length === 0 ? (
                    <div style={{ padding: '12px 8px', fontSize: '0.72rem', color: '#9B8B82', textAlign: 'center' }}>
                      No items
                    </div>
                  ) : railItems.map(({ item, index }) => {
                    const isActive = index === selectedItemIndex;
                    const isCounted = !!item.is_counted;
                    // parDisplayFor renders a missing PAR as '—' — "no PAR
                    // set" must not masquerade as "PAR 0" (a real target).
                    const rowPar = parDisplayFor(item).text;
                    const rowCount = displayCountForRow(item);
                    const rowOrder = displayOrderForRow(item);
                    const rowIsManual = item.order_calculations_enabled && isCounted
                      && parseOptionalNumber(item.manual_order_quantity) !== null;
                    return (
                      <div
                        key={item.id}
                        onClick={() => selectItem(index)}
                        className={`count-ipad-item-row${isActive ? ' count-ipad-item-row--active' : ''}`}
                      >
                        <div className="count-ipad-item-name">
                          {isCounted && <span style={{ color: '#4CAF50', marginRight: '3px' }}>✓</span>}
                          {item.item_snapshot?.description || item.item_snapshot?.name || '—'}
                        </div>
                        {/* Count as, switchable without leaving the tape —
                            counting language only; it never touches who the
                            line is ordered from. Only rendered where a
                            package ladder exists. */}
                        {canCountByPackage(item) && (
                          <div
                            className="count-ipad-item-vendor"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <CountFormatControl
                              presentation={presentationForItem(item)}
                              countUnit={item.item_snapshot?.unit || item.par_unit || 'each'}
                              compact
                              hideLabel
                              onSelectFormat={(patch, choice) => handleCountFormatSelect(item, patch, choice)}
                              onToggleFollowLatest={(next) => applyCountFormat(item, { follow_latest_purchase: next !== false })}
                              onOpenPackageSheet={() => openPackageSheet(item)}
                            />
                          </div>
                        )}
                        <div className="count-ipad-item-badges">
                          <span className="count-ipad-badge" style={{ backgroundColor: '#E8DDD0', color: '#5A4A42' }}>PAR {rowPar}</span>
                          <span className="count-ipad-badge" style={{ backgroundColor: '#F5E2D0', color: '#5A4A42' }}>{rowCount}</span>
                          {item.order_calculations_enabled && (
                            <span className="count-ipad-badge" style={{
                              backgroundColor: rowIsManual ? '#FF9800' : (rowOrder > 0 ? '#C8E6C9' : '#ECEFF1'),
                              color: rowIsManual ? '#fff' : (rowOrder > 0 ? '#2E7D32' : '#607D8B'),
                            }}>{rowIsManual ? 'ORD' : 'NEED'} {rowOrder}{!rowIsManual ? ` ${item.item_snapshot?.unit || item.par_unit || 'each'}` : ''}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
              return (
                <div
                  className={`count-ipad-rail${multiLoc ? ' count-ipad-rail--split' : ''}`}
                  style={{ paddingLeft: 'env(safe-area-inset-left, 0px)' }}
                >
                  {multiLoc ? (
                    <>
                      {/* Location column — left half of rail */}
                      <div className="count-ipad-col count-ipad-col--locations">
                        <div className="count-ipad-rail-header">Locations</div>
                        <div className="count-ipad-loc-tape">
                          {mobileRooms.map((room) => {
                            const isRoomActive = room.label === mobileActiveRoom;
                            const isDone = room.uncountedCount === 0;
                            const locIcon = (() => {
                              const l = (room.label || '').toLowerCase();
                              if (l.includes('bar')) return '🍸';
                              if (l.includes('kitchen')) return '🍳';
                              if (l.includes('storage') || l.includes('dry')) return '📦';
                              if (l.includes('cooler') || l.includes('fridge')) return '❄️';
                              if (l.includes('freezer')) return '🧊';
                              return '📍';
                            })();
                            return (
                              <button
                                key={room.label}
                                onClick={() => handleMobileRoomSelect(room)}
                                className={`count-ipad-loc-row${isRoomActive ? ' count-ipad-loc-row--active' : ''}${isDone ? ' count-ipad-loc-row--done' : ''}`}
                                aria-label={isDone
                                  ? `${room.label}, all ${room.count} counted`
                                  : `${room.label}, ${room.uncountedCount} of ${room.count} left`}
                              >
                                <span className="count-ipad-loc-icon">{locIcon}</span>
                                <span className="count-ipad-loc-label">{room.label}</span>
                                <span className="count-ipad-loc-badges">
                                  <span className="count-ipad-badge count-ipad-badge--uncounted" title="Uncounted">
                                    {isDone ? '✓' : room.uncountedCount}
                                  </span>
                                  <span className="count-ipad-badge count-ipad-badge--total" title="Total">
                                    {room.count}
                                  </span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {/* Item column — right half of rail */}
                      <div className="count-ipad-col count-ipad-col--items">
                        <div className="count-ipad-rail-header">
                          {mobileActiveRoom || 'All Items'}
                        </div>
                        {itemTape}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Single location: item tape fills the full rail (unchanged layout) */}
                      <div className="count-ipad-rail-header">
                        {mobileActiveRoom || 'All Items'}
                      </div>
                      {itemTape}
                    </>
                  )}
                </div>
              );
            })()}
            {/* Far Left Column: Sort controls above Group Selector */}
            <div className="col-md-3">
              {/* Sort Controls (same width as selector) */}
              <div className="btn-group w-100 mb-2" role="group">
                <button
                  className={`btn btn-sm ${sortBy === 'location' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => handleSortChange('location')}
                  style={sortBy === 'location' ? 
                    { backgroundColor: '#C8B5A0', borderColor: '#C8B5A0' } : 
                    { borderColor: '#D4C4B0', color: '#5A4A42' }}
                >
                  <i className="feather icon-map-pin me-1"></i>
                  Location
                </button>
                <button
                  className={`btn btn-sm ${sortBy === 'vendor' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => handleSortChange('vendor')}
                  style={sortBy === 'vendor' ? 
                    { backgroundColor: '#C8B5A0', borderColor: '#C8B5A0' } : 
                    { borderColor: '#D4C4B0', color: '#5A4A42' }}
                >
                  <i className="feather icon-truck me-1"></i>
                  Vendor
                </button>
                <button
                  className={`btn btn-sm ${sortBy === 'category' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => handleSortChange('category')}
                  style={sortBy === 'category' ? 
                    { backgroundColor: '#C8B5A0', borderColor: '#C8B5A0' } : 
                    { borderColor: '#D4C4B0', color: '#5A4A42' }}
                >
                  <i className="feather icon-tag me-1"></i>
                  Category
                </button>
                <button
                  className={`btn btn-sm ${sortBy === 'alphabetical' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => handleSortChange('alphabetical')}
                  style={sortBy === 'alphabetical' ? 
                    { backgroundColor: '#C8B5A0', borderColor: '#C8B5A0' } : 
                    { borderColor: '#D4C4B0', color: '#5A4A42' }}
                >
                  <i className="feather icon-type me-1"></i>
                  A-Z
                </button>
              </div>

              {/* Group Selector */}
              <div className="card" style={{ borderColor: '#C8B5A0', borderWidth: '2px', boxShadow: 'rgba(0,0,0,0.04) 1px 1px 3px', height: paneHeightPx ? `${paneHeightPx}px` : 'auto', display: 'flex', flexDirection: 'column' }}>
                <div className="card-body p-2" style={{ flex: 1, overflowY: 'auto' }}>
                  {sortBy === 'alphabetical' ? (
                    <div className="list-group">
                      <div
                        className={`list-group-item ci-location-tile d-flex align-items-center active`}
                        style={{ backgroundColor: '#7B5E45', color: 'white', borderColor: '#7B5E45' }}
                      >
                        <div className="me-2 ci-location-icon">📋</div>
                        <div className="d-flex justify-content-between align-items-center w-100">
                          <span className="fw-semibold ci-location-title">All Items</span>
                          <div className="d-flex align-items-center gap-1">
                            <span className="badge" style={{ backgroundColor: 'white', color: '#7B5E45', opacity: 0.85 }}>{countItems.filter(ci => !ci.is_counted).length}</span>
                            <span className="badge" style={{ backgroundColor: 'white', color: '#7B5E45' }}>{countItems.length}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="list-group">
                      {allGroups.map(group => {
                        const getLocationIcon = (location) => {
                          if (location.toLowerCase().includes('bar')) return '🍸';
                          if (location.toLowerCase().includes('kitchen')) return '🍳';
                          if (location.toLowerCase().includes('storage') || location.toLowerCase().includes('dry')) return '📦';
                          if (location.toLowerCase().includes('cooler') || location.toLowerCase().includes('fridge')) return '❄️';
                          if (location.toLowerCase().includes('freezer')) return '🧊';
                          return '📍';
                        };
                        
                        return (
                          <div
                            key={group}
                            className={`list-group-item ci-location-tile d-flex align-items-center ${group === selectedLocation ? 'active' : ''}`}
                            style={{
                              // Tablet selected row: cream per spec; else dark brown.
                              backgroundColor: group === selectedLocation
                                ? (isTabletLayout ? '#F5EBDD' : '#7B5E45')
                                : 'white',
                              color: group === selectedLocation
                                ? (isTabletLayout ? '#5A4A42' : 'white')
                                : '#5A4A42',
                              borderColor: group === selectedLocation
                                ? (isTabletLayout ? '#E8DDD0' : '#7B5E45')
                                : '#FAF6F2',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setSelectedLocation(group);
                              const index = countItems.findIndex(ci => getGroupValueForItem(ci) === group);
                              if (index >= 0) {
                                selectItem(index);
                                if (isTabletLayout) {
                                  // Tablet: scroll right column to TOP of group (overrides selectItem's center-scroll).
                                  requestAnimationFrame(() => {
                                    const row = document.getElementById(`count-item-${index}`);
                                    if (!row) return;
                                    const scroller = row.closest('.card-body');
                                    if (!scroller) return;
                                    scroller.scrollTop = Math.max(0, row.offsetTop - scroller.offsetTop);
                                  });
                                }
                              }
                            }}
                          >
                            <div className="me-2 ci-location-icon">{sortBy === 'location' ? getLocationIcon(group) : '📋'}</div>
                            <div className="d-flex justify-content-between align-items-center w-100">
                              <span className="fw-semibold ci-location-title">{group}</span>
                              <div className="d-flex align-items-center gap-1">
                                <span className="badge" title="Uncounted" style={{
                                  backgroundColor: group === selectedLocation
                                    ? (isTabletLayout ? '#7B5E45' : 'white')
                                    : '#7B5E45',
                                  color: group === selectedLocation
                                    ? (isTabletLayout ? '#FFFFFF' : '#7B5E45')
                                    : 'white',
                                  opacity: 0.85
                                }}>{groupUncounted[group] || 0}</span>
                                <span className="badge" title="Total" style={{
                                  backgroundColor: group === selectedLocation
                                    ? (isTabletLayout ? '#7B5E45' : 'white')
                                    : '#7B5E45',
                                  color: group === selectedLocation
                                    ? (isTabletLayout ? '#FFFFFF' : '#7B5E45')
                                    : 'white'
                                }}>{groupCounts[group]}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Side: Progress + Items + Keypad */}
            <div className={isIpadLandscape ? 'col-md-9 count-ipad-main' : 'col-md-9'}>
              {/* Progress Meters */}
              <div className="card mb-2" style={{ borderColor: '#C8B5A0', borderWidth: '2px', boxShadow: 'rgba(0,0,0,0.04) 1px 1px 3px' }}>
                <div className="card-body p-2">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center gap-2">
                        <small className="text-muted">Overall Progress</small>
                        <span className="badge" style={{ backgroundColor: '#5A4A42', color: 'white' }}>{countedItems}/{totalItems}</span>
                      </div>
                      <div className="progress" style={{ height: '8px', backgroundColor: '#FAF8F6' }}>
                        <div className="progress-bar" style={{ width: `${progressPercent}%`, backgroundColor: '#5A4A42' }}></div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center gap-2">
                        <small className="text-muted">{selectedGroupName} Progress</small>
                        <span className="badge" style={{ backgroundColor: '#7B5E45', color: 'white' }}>{groupCounted}/{groupTotal}</span>
                      </div>
                      <div className="progress" style={{ height: '8px', backgroundColor: '#FAF8F6' }}>
                        <div className="progress-bar" style={{ width: `${groupPercent}%`, backgroundColor: '#7B5E45' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Items + Keypad row */}
              <div className="row">
                {/* Middle Column: Items List */}
                <div className="col-md-7">
                  <div className="card" style={{ borderColor: '#C8B5A0', borderWidth: '2px', boxShadow: 'rgba(0,0,0,0.04) 1px 1px 3px', display: 'flex', flexDirection: 'column', height: paneHeightPx ? `${paneHeightPx}px` : 'auto' }}>
                    {/* Dynamic header showing what we're counting */}
                    <div style={{ padding: '6px 12px', backgroundColor: '#F5E2D0', borderBottom: '1px solid #E8DDD0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '700', fontSize: '0.85rem', color: '#5A4A42' }}>
                        Counting {selectedGroupName}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#8B7B73' }}>
                        {groupCounted}/{groupTotal} counted
                      </span>
                    </div>
                    <div className="card-body p-2" style={{ flex: 1, overflowY: mobileTool ? 'hidden' : 'auto', overflowX: 'hidden' }}>
                      <div>
                        {mobileTool && !isIpadLandscape && (() => {
                          // Mobile flow tape: horizontal strip of all countItems
                          // Hidden in iPad landscape (items shown in the left rail instead).
                          // centered on the current selection. Tap = jump (no
                          // peek-return; that's reserved for search jumps).
                          // The auto-scroll effect below recenters the strip
                          // whenever selectedItemIndex changes.
                          //
                          // Long-press a card (≥420 ms without horizontal travel) to
                          // enter edit mode: cards wiggle, a × badge appears, and
                          // any subsequent press-and-drag horizontally repositions
                          // the card. Releasing inserts at the gap; tapping outside
                          // the strip exits edit mode. Tapping × opens the styled
                          // delete-confirmation modal (see tapeDeleteConfirm).
                          const visibleEntries = (mobileRooms.length > 1 && mobileActiveRoom
                            ? countItems
                                .map((ti, i) => ({ ti, i }))
                                .filter(({ ti }) => resolveCountItemLocation(ti) === mobileActiveRoom)
                            : countItems.map((ti, i) => ({ ti, i })));

                          // Visible-position helpers for drag math: the drag
                          // operates on indexes within the currently rendered tape
                          // (which may be a single-room slice when rooms exist).
                          const visibleIndexes = visibleEntries.map((e) => e.i);
                          const visiblePositionOf = (countItemsIdx) => visibleIndexes.indexOf(countItemsIdx);

                          const CARD_W = 90;
                          const GAP = 6;

                          const beginLongPress = (countItemsIdx, clientX, clientY) => {
                            tapeLongPressStartRef.current = { x: clientX, y: clientY, idx: countItemsIdx };
                            if (tapeLongPressTimerRef.current) clearTimeout(tapeLongPressTimerRef.current);
                            tapeLongPressTimerRef.current = setTimeout(() => {
                              if (!tapeEditMode) {
                                setTapeEditMode(true);
                                try { if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(15); } catch (_) {}
                              }
                              // After entering edit mode, the same hold can begin a drag
                              // immediately so the user doesn't have to release and
                              // re-press to start moving the card.
                              setTapeDrag({
                                fromIndex: countItemsIdx,
                                toIndex: visiblePositionOf(countItemsIdx),
                                startX: clientX,
                                pointerX: clientX,
                                dx: 0,
                                visibleStartPos: visiblePositionOf(countItemsIdx),
                              });
                              tapeLongPressTimerRef.current = null;
                            }, 420);
                          };

                          const cancelLongPress = () => {
                            if (tapeLongPressTimerRef.current) {
                              clearTimeout(tapeLongPressTimerRef.current);
                              tapeLongPressTimerRef.current = null;
                            }
                            tapeLongPressStartRef.current = null;
                          };

                          const computeDropIndex = (visibleStartPos, dx) => {
                            const offsetSlots = Math.round(dx / (CARD_W + GAP));
                            return Math.max(0, Math.min(visibleEntries.length, visibleStartPos + offsetSlots));
                          };

                          const onCardPointerDown = (e, countItemsIdx) => {
                            if (e.pointerType === 'mouse' && e.button !== 0) return;
                            // In edit mode, pressing immediately starts a drag (no
                            // long-press required for re-grabs). Otherwise we arm
                            // the long-press timer.
                            if (tapeEditMode) {
                              try { e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
                              setTapeDrag({
                                fromIndex: countItemsIdx,
                                toIndex: visiblePositionOf(countItemsIdx),
                                startX: e.clientX,
                                pointerX: e.clientX,
                                dx: 0,
                                visibleStartPos: visiblePositionOf(countItemsIdx),
                              });
                            } else {
                              beginLongPress(countItemsIdx, e.clientX, e.clientY);
                            }
                          };

                          const onCardPointerMove = (e) => {
                            const start = tapeLongPressStartRef.current;
                            if (start) {
                              const dx = e.clientX - start.x;
                              const dy = e.clientY - start.y;
                              if (Math.abs(dx) > 8 || Math.abs(dy) > 8) cancelLongPress();
                            }
                            if (tapeDrag) {
                              const newDx = e.clientX - tapeDrag.startX;
                              const newDrop = computeDropIndex(tapeDrag.visibleStartPos, newDx);
                              setTapeDrag({ ...tapeDrag, pointerX: e.clientX, dx: newDx, toIndex: newDrop });
                              // Edge-driven auto-scroll while dragging.
                              const container = tapeContainerRef.current;
                              if (container) {
                                const rect = container.getBoundingClientRect();
                                const edge = 50;
                                if (e.clientX < rect.left + edge) startTapeAutoScroll(-1);
                                else if (e.clientX > rect.right - edge) startTapeAutoScroll(1);
                                else startTapeAutoScroll(0);
                              }
                              if (e.cancelable) e.preventDefault();
                            }
                          };

                          // Per-card pointer-up only handles the "tap" case
                          // (no drag, no long-press) — drag commits run from
                          // the window-level pointerup handler so a release
                          // off-card never silently drops the reorder.
                          const onCardPointerUp = (e, countItemsIdx) => {
                            cancelLongPress();
                            if (!tapeDrag && !tapeEditMode) {
                              selectItem(countItemsIdx);
                            }
                          };

                          const onCardPointerCancel = () => {
                            cancelLongPress();
                          };

                          return (
                            <div
                              ref={tapeContainerRef}
                              id="count-mobile-tape"
                              style={{
                                display: 'flex',
                                gap: `${GAP}px`,
                                padding: '4px 4px',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                WebkitOverflowScrolling: 'touch',
                                scrollSnapType: tapeEditMode ? 'none' : 'x mandatory',
                                touchAction: tapeEditMode ? 'none' : 'pan-x',
                                position: 'relative',
                              }}
                            >
                              {/* Wiggle keyframes — injected once when edit mode is
                                  active. Honors prefers-reduced-motion. */}
                              {tapeEditMode && (
                                <style>{`
                                  @keyframes count-tape-wiggle-a { 0% { transform: rotate(-1.1deg); } 50% { transform: rotate(1.1deg); } 100% { transform: rotate(-1.1deg); } }
                                  @keyframes count-tape-wiggle-b { 0% { transform: rotate(1.1deg); } 50% { transform: rotate(-1.1deg); } 100% { transform: rotate(1.1deg); } }
                                  .count-tape-wiggle-a { animation: count-tape-wiggle-a 0.34s ease-in-out infinite; transform-origin: 50% 50%; }
                                  .count-tape-wiggle-b { animation: count-tape-wiggle-b 0.34s ease-in-out infinite; transform-origin: 50% 50%; }
                                  @media (prefers-reduced-motion: reduce) {
                                    .count-tape-wiggle-a, .count-tape-wiggle-b { animation: none; }
                                  }
                                `}</style>
                              )}
                              {/* Tape items: scoped to the active room when multiple rooms
                                  exist and a room is selected; falls back to full list. */}
                              {visibleEntries.map(({ ti: tItem, i: tIdx }, visIdx) => {
                                const isCur = tIdx === selectedItemIndex;
                                const tDesc = tItem.item_snapshot?.description || tItem.item_snapshot?.name || 'Unknown';
                                const resolvedLoc = resolveCountItemLocation(tItem);
                                const tLoc = resolvedLoc === 'Unknown' ? '' : resolvedLoc;
                                const tCounted = !!tItem.is_counted;
                                const isDragging = tapeDrag && tapeDrag.fromIndex === tIdx;
                                // Lateral shift to open a gap at the current drop
                                // target. Cards before the gap stay; cards after
                                // it shift left/right by one slot.
                                let shiftX = 0;
                                if (tapeDrag && !isDragging) {
                                  const fromPos = tapeDrag.visibleStartPos;
                                  const toPos = tapeDrag.toIndex;
                                  if (fromPos < toPos && visIdx > fromPos && visIdx <= toPos - 1) shiftX = -(CARD_W + GAP);
                                  else if (fromPos > toPos && visIdx >= toPos && visIdx < fromPos) shiftX = (CARD_W + GAP);
                                }
                                const wiggleClass = tapeEditMode && !isDragging
                                  ? (visIdx % 2 === 0 ? 'count-tape-wiggle-a' : 'count-tape-wiggle-b')
                                  : '';
                                const cardTransform = isDragging
                                  ? `translateX(${tapeDrag.dx}px) scale(1.06)`
                                  : (shiftX !== 0 ? `translateX(${shiftX}px)` : 'none');
                                return (
                                  <div
                                    key={tItem.id}
                                    id={`count-mobile-tape-item-${tIdx}`}
                                    className={wiggleClass}
                                    onPointerDown={(e) => onCardPointerDown(e, tIdx)}
                                    onPointerMove={onCardPointerMove}
                                    onPointerUp={(e) => onCardPointerUp(e, tIdx)}
                                    onPointerCancel={onCardPointerCancel}
                                    onContextMenu={(e) => { if (tapeEditMode) e.preventDefault(); }}
                                    style={{
                                      flex: '0 0 90px',
                                      width: `${CARD_W}px`,
                                      height: '62px',
                                      padding: '4px 6px',
                                      borderRadius: '8px',
                                      cursor: tapeEditMode ? 'grab' : 'pointer',
                                      scrollSnapAlign: tapeEditMode ? 'none' : 'center',
                                      // Selected chip wears the card's cream + the action
                                      // terracotta so it visually "feeds" the card above it;
                                      // counted chips keep the pale green = done convention.
                                      backgroundColor: isCur ? '#FBF6EF' : (tCounted ? '#F1F8E9' : '#FFFFFF'),
                                      border: `2px solid ${isCur ? '#C0603F' : (tCounted ? '#C8E6C9' : '#E8DDD0')}`,
                                      color: isCur ? '#3E2723' : '#5A4A42',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'space-between',
                                      overflow: 'visible',
                                      position: 'relative',
                                      transform: cardTransform,
                                      transition: isDragging ? 'none' : 'transform 180ms ease',
                                      boxShadow: isDragging ? '0 6px 16px rgba(0,0,0,0.25)' : 'none',
                                      zIndex: isDragging ? 5 : 1,
                                      touchAction: tapeEditMode ? 'none' : undefined,
                                      userSelect: 'none',
                                      WebkitUserSelect: 'none',
                                    }}
                                  >
                                    <div style={{ fontSize: '0.65rem', fontWeight: 800, lineHeight: 1.15, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                      {tDesc}
                                    </div>
                                    {/* Single divider between item name and location row.
                                        Prior render had two stacked rules — kept just the
                                        borderTop on the location row so there's one clean line. */}
                                    <div style={{ borderTop: `1px solid ${isCur ? 'rgba(192,96,63,0.35)' : '#E8DDD0'}`, paddingTop: '2px', fontSize: '0.58rem', opacity: 0.85, display: 'flex', justifyContent: 'space-between', overflow: 'hidden' }}>
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
                                        {tLoc ? (tLoc.length > 6 ? tLoc.slice(0, 6) + '…' : tLoc) : '—'}
                                      </span>
                                      <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{tCounted ? `✓ ${displayCountForRow(tItem)}` : ''}</span>
                                    </div>
                                    {tapeEditMode && (
                                      <button
                                        type="button"
                                        aria-label={`Delete ${tDesc}`}
                                        onPointerDown={(e) => { e.stopPropagation(); }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          requestTapeDelete(tIdx);
                                        }}
                                        style={{
                                          position: 'absolute',
                                          top: '-7px',
                                          right: '-7px',
                                          width: '20px',
                                          height: '20px',
                                          padding: 0,
                                          border: '1.5px solid #fff',
                                          borderRadius: '50%',
                                          backgroundColor: '#D9534F',
                                          color: '#fff',
                                          fontSize: '12px',
                                          fontWeight: 800,
                                          lineHeight: '16px',
                                          cursor: 'pointer',
                                          boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          zIndex: 6,
                                        }}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                              {tapeEditMode && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); exitTapeEditMode(); }}
                                  onPointerDown={(e) => e.stopPropagation()}
                                  style={{
                                    flex: '0 0 auto',
                                    alignSelf: 'center',
                                    marginLeft: '4px',
                                    height: '26px',
                                    padding: '0 10px',
                                    borderRadius: '13px',
                                    border: '1px solid #C8B5A0',
                                    backgroundColor: '#F3EDE4',
                                    color: '#5A4A42',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                  }}
                                >
                                  Done
                                </button>
                              )}
                            </div>
                          );
                        })()}

                        {/* Mobile-only: location tape (rooms strip) — sits
                            directly under the item tape so all navigation
                            tools are grouped at the bottom of the page.
                            Only renders when the session has more than one
                            distinct location. Tapping a chip jumps to the
                            first uncounted item in that room. */}
                        {mobileTool && !isIpadLandscape && mobileRooms.length > 1 && (
                          <div
                            ref={mobileRoomsScrollRef}
                            data-rooms-strip="1"
                          >
                            {mobileRooms.map((room) => {
                              const isActive = room.label === mobileActiveRoom;
                              const isDone = room.uncountedCount === 0;
                              const icon = (() => {
                                const l = (room.label || '').toLowerCase();
                                if (l.includes('bar')) return '🍸';
                                if (l.includes('kitchen')) return '🍳';
                                if (l.includes('storage') || l.includes('dry')) return '📦';
                                if (l.includes('cooler') || l.includes('fridge')) return '❄️';
                                if (l.includes('freezer')) return '🧊';
                                return '📍';
                              })();
                              return (
                                <button
                                  key={room.label}
                                  ref={(el) => { mobileRoomChipRefsMap.current[room.label] = el; }}
                                  data-room-chip={isActive ? 'active' : '1'}
                                  data-room-done={isDone ? '1' : undefined}
                                  aria-label={isDone ? `${room.label}, all ${room.count} counted` : `${room.label}, ${room.uncountedCount} of ${room.count} left to count`}
                                  onClick={() => handleMobileRoomSelect(room)}
                                >
                                  <span>{icon}</span>
                                  <span>{room.label}</span>
                                  <span className="room-count-badge">
                                    {isDone ? '✓' : room.uncountedCount}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* Mobile-only: search + Edit item — sits directly
                            under the location tape so all navigation tools
                            (item tape / location tape / search) are grouped
                            at the bottom. Search left ("Item search"
                            placeholder), Edit-item button right. Search
                            results drop down upward to overlay the count
                            frame above. */}
                        {mobileTool && (() => {
                          const q = (mobileSearchText || '').trim().toLowerCase();
                          const results = q.length === 0 ? [] : countItems
                            .map((ci, idx) => ({ ci, idx }))
                            .filter(({ ci }) => {
                              const s = ci.item_snapshot || {};
                              const hay = `${s.description || ''} ${s.name || ''} ${s.vendor_name || ''} ${s.location || ''}`.toLowerCase();
                              return hay.includes(q);
                            })
                            .slice(0, 8);
                          const jumpToFirst = () => {
                            if (results.length === 0) return;
                            selectItemWithPeek(results[0].idx);
                            setMobileSearchText('');
                          };
                          return (
                            <div data-count-search-bar="1" style={{ margin: '6px auto 0', position: 'relative' }}>
                              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                {/* Search field with the barcode scanner docked inside
                                    (trailing icon) so find-an-item and scan-an-item read
                                    as one affordance. The duplicate "Edit item" button
                                    was removed \u2014 the same action lives in the keypad's
                                    action grid one thumb-length up. */}
                                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', border: '1.5px solid #D9CEC2', borderRadius: '999px', backgroundColor: '#FFFFFF', height: '2.6rem', paddingLeft: '12px', overflow: 'hidden' }}>
                                  <span aria-hidden="true" style={{ color: '#A8988D', fontSize: '0.85rem', marginRight: '6px', flexShrink: 0 }}>{'\u{1F50D}'}</span>
                                  <input
                                    type="text"
                                    data-mobile-search="1"
                                    placeholder="Find an item"
                                    value={mobileSearchText}
                                    onChange={(e) => setMobileSearchText(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') { e.preventDefault(); jumpToFirst(); }
                                      else if (e.key === 'Escape') { setMobileSearchText(''); }
                                    }}
                                    style={{ flex: 1, minWidth: 0, fontSize: '0.85rem', border: 'none', outline: 'none', backgroundColor: 'transparent', color: '#3E2723', padding: 0, height: '100%' }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setScanModalOpen(true)}
                                    title="Scan UPC barcode"
                                    aria-label="Scan UPC barcode"
                                    style={{ border: 'none', borderLeft: '1px solid #EFE6DA', backgroundColor: 'transparent', padding: '0 12px', height: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                                  >
                                    <svg width="24" height="18" viewBox="0 0 26 20" aria-hidden="true" focusable="false" style={{ display: 'block' }}>
                                      <g fill="#5A4A42">
                                        <rect x="1"  y="2" width="2" height="16" />
                                        <rect x="4"  y="2" width="1" height="16" />
                                        <rect x="6"  y="2" width="3" height="16" />
                                        <rect x="10" y="2" width="1" height="16" />
                                        <rect x="12" y="2" width="2" height="16" />
                                        <rect x="15" y="2" width="3" height="16" />
                                        <rect x="19" y="2" width="1" height="16" />
                                        <rect x="21" y="2" width="2" height="16" />
                                        <rect x="24" y="2" width="1" height="16" />
                                      </g>
                                    </svg>
                                  </button>
                                </div>
                                {peekReturnIndex !== null ? (
                                  <button
                                    type="button"
                                    onClick={() => { const t = peekReturnIndex; setPeekReturnIndex(null); selectItem(t); }}
                                    style={{ backgroundColor: '#5A4A42', color: '#FFF8F0', border: 'none', fontSize: '0.78rem', fontWeight: 700, padding: '0 12px', height: '2.6rem', borderRadius: '999px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                                    title="Return to where you were before searching"
                                  >
                                    {'\u21A9 Back'}
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setAddItemModalOpen(true)}
                                    title="Add item to this count without scanning"
                                    style={{ backgroundColor: '#FFFFFF', color: '#5A4A42', border: '1.5px solid #D9CEC2', fontSize: '0.85rem', fontWeight: 700, padding: '0 12px', height: '2.6rem', borderRadius: '999px', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, cursor: 'pointer' }}
                                  >
                                    + Item
                                  </button>
                                )}
                              </div>
                              {results.length > 0 && (
                                <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, zIndex: 50, backgroundColor: '#FFFFFF', border: '1px solid #C8B5A0', borderRadius: '4px', boxShadow: '0 -4px 12px rgba(0,0,0,0.12)', maxHeight: '40vh', overflowY: 'auto', marginBottom: '2px' }}>
                                  {results.map(({ ci, idx }) => {
                                    const s = ci.item_snapshot || {};
                                    const desc = s.description || s.name || 'Unknown';
                                    const loc = s.location || '';
                                    const counted = !!ci.is_counted;
                                    return (
                                      <div
                                        key={ci.id}
                                        onClick={() => { selectItemWithPeek(idx); setMobileSearchText(''); }}
                                        style={{ padding: '7px 9px', borderBottom: '1px solid #F0E8DC', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.78rem', color: '#5A4A42', backgroundColor: idx === selectedItemIndex ? '#F8F1E8' : '#FFFFFF' }}
                                      >
                                        <span style={{ flex: 1, marginRight: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{desc}</span>
                                        <span style={{ fontSize: '0.68rem', opacity: 0.8 }}>
                                          {locationCode(loc) || '\u2014'}{counted ? ` \u2713 ${displayCountForRow(ci)}` : ''}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {useFullItemRows && visibleItems.map((item) => {
                          const index = countItems.indexOf(item);
                          const isSelected = index === selectedItemIndex;
                          // parDisplayFor renders a missing PAR as '—' — "no
                          // PAR set" must not masquerade as "PAR 0".
                          const rowPar = parDisplayFor(item).text;
                          const rowCount = displayCountForRow(item);
                          const rowOrder = displayOrderForRow(item);
                          const rowIsManual = item.order_calculations_enabled && item.is_counted && parseOptionalNumber(item.manual_order_quantity) !== null;
                          const rowShowsNeed = !rowIsManual;
                          const rowName = item.item_snapshot?.description || item.item_snapshot?.name || 'Unknown Item';
                          const rowCountUnit = item.item_snapshot?.unit || item.par_unit || 'each';
                          // The unit the row's NUMBERS read in — the counted
                          // package when one is active. rowCountUnit stays
                          // the item's own unit because the format control
                          // states its equivalence against that ("1 BIB =
                          // 5 gal"), which is a different question.
                          const rowDisplayUnit = displayUnitForRow(item);
                          // Prev-count chips: other locations that have already been counted
                          // for the same inventory item in this session.
                          const rowInvId = item.inventory_item_id || item.item_snapshot?.id || item.id;
                          const rowPrevChips = countItems.reduce((acc, ci, si) => {
                            if (si === index) return acc;
                            const ciId = ci.inventory_item_id || ci.item_snapshot?.id || ci.id;
                            if (ciId !== rowInvId) return acc;
                            const hasCounted = ci.is_counted || (ci.current_count !== '' && ci.current_count != null);
                            if (!hasCounted) return acc;
                            const loc = ci.location || ci.item_snapshot?.location || 'Other';
                            acc.push({ abbr: locationCode(loc), value: displayCountForRow(ci), location: loc });
                            return acc;
                          }, []);

                          return (
                            <div key={item.id}>
                              <div
                                id={`count-item-${index}`}
                                className={`d-flex align-items-center p-2 mb-2 rounded border`}
                                style={{
                                  cursor: 'pointer',
                                  minHeight: '60px',
                                  transition: 'all 0.2s',
                                  backgroundColor: isSelected ? '#C8B5A0' : '#FFFFFF',
                                  color: isSelected ? '#FFFFFF' : 'inherit',
                                  borderColor: isSelected ? '#C8B5A0' : '#FAF6F2'
                                }}
                                onClick={() => selectItem(index)}
                              >
                                {/* The input reads through getEffectiveCount so a
                                    package-counted line shows the number that was
                                    typed (2 cases), not the normalized total its
                                    onChange handler stores (40 lb) — displaying
                                    one while writing the other is how "I typed 2
                                    and it became 40" happens.

                                    type="text", NEVER type="number": scrolling
                                    the list with the pointer over a focused
                                    number input spins its value instead of
                                    scrolling — type 3.1, scroll to the next
                                    item, and the browser walks the count down
                                    the step grid (3.1 → 2.75 → 2.5), caught on
                                    video. A text input has no spinner and no
                                    wheel behavior; only keystrokes change it. */}
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  autoComplete="off"
                                  data-count-input="1"
                                  className="form-control form-control-sm me-2"
                                  style={{ width: '72px', borderColor: '#D4C4B0', color: '#5A4A42' }}
                                  value={getEffectiveCount(index)}
                                  placeholder="0"
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={() => selectItemFromListInput(index)}
                                  onMouseDown={() => selectItemFromListInput(index)}
                                  onChange={(e) => {
                                    // Digits and one dot, same as the keypad.
                                    if (/^\d*\.?\d*$/.test(e.target.value)) {
                                      updateItemCount(index, e.target.value);
                                    }
                                  }}
                                />
                                <div className="d-flex align-items-center flex-grow-1" style={{ gap: '6px' }}>
                                  {item.is_counted && (
                                    <span style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#4CAF50', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.6rem', color: '#fff' }}>✓</span>
                                  )}
                                  <div style={{ minWidth: 0 }}>
                                    <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                                      {rowName}
                                    </div>
                                    <small style={{ 
                                      color: isSelected ? 'rgba(255,255,255,0.8)' : '#8B7B73',
                                      fontSize: '0.75rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '3px',
                                      flexWrap: 'wrap',
                                    }}>
                                      <span>{item.item_snapshot?.location || 'Unknown'} •</span>
                                      {canCountByPackage(item) ? (
                                        // Count as, inline: the package this
                                        // line is counted in. Never a vendor
                                        // selection for ordering.
                                        <span onClick={(event) => event.stopPropagation()}>
                                          <CountFormatControl
                                            presentation={presentationForItem(item)}
                                            countUnit={rowCountUnit}
                                            compact
                                            hideLabel
                                            onSelectFormat={(patch, choice) => handleCountFormatSelect(item, patch, choice)}
                                            onToggleFollowLatest={(next) => applyCountFormat(item, { follow_latest_purchase: next !== false })}
                                            onOpenPackageSheet={() => openPackageSheet(item)}
                                          />
                                        </span>
                                      ) : (
                                        <span>{item.item_snapshot?.vendor || 'Unknown'}</span>
                                      )}
                                    </small>
                                    <div className="d-flex align-items-center flex-wrap mt-1" style={{ gap: '4px' }}>
                                      <span className="badge" style={{ backgroundColor: '#E8DDD0', color: '#5A4A42', fontSize: '0.64rem' }}>
                                        PAR {rowPar}
                                      </span>
                                      {item.par_defaulted === true && (
                                        <button
                                          type="button"
                                          data-set-par-flag="1"
                                          data-count-item-id={item.id}
                                          aria-label={`Set PAR and minimum order for ${rowName}`}
                                          title="Review this item's starter PAR and minimum order"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            openFocusedParSetupFromCount(index, { reviewingAutoDefault: true });
                                          }}
                                          style={{
                                            minHeight: '28px',
                                            padding: '3px 8px',
                                            borderRadius: '999px',
                                            border: '1px solid #D89A00',
                                            backgroundColor: '#FFF3CD',
                                            color: '#7A4E00',
                                            fontSize: '0.65rem',
                                            fontWeight: 900,
                                            lineHeight: 1,
                                            letterSpacing: '0.04em',
                                            whiteSpace: 'nowrap',
                                            cursor: 'pointer',
                                          }}
                                        >
                                          SET PAR
                                        </button>
                                      )}
                                      <span className="badge" style={{ backgroundColor: '#F5E2D0', color: '#5A4A42', fontSize: '0.64rem' }}>
                                        COUNT {rowCount}
                                      </span>
                                      <span className="badge" style={{ backgroundColor: rowIsManual ? '#FF9800' : (rowOrder > 0 ? '#C8E6C9' : '#ECEFF1'), color: rowIsManual ? '#FFFFFF' : (rowOrder > 0 ? '#2E7D32' : '#607D8B'), fontSize: '0.64rem' }}>
                                        {rowShowsNeed ? 'NEED' : 'ORDER'} {rowOrder}{rowShowsNeed ? ` ${rowDisplayUnit}` : ''}
                                      </span>
                                      {rowPrevChips.map((chip, ci) => (
                                        <span
                                          key={ci}
                                          className="badge"
                                          style={{ backgroundColor: '#E3F2FD', color: '#1565C0', fontSize: '0.64rem' }}
                                          title={`${chip.location}: ${chip.value} counted`}
                                        >
                                          {chip.abbr} -{chip.value}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-end">
                                  {item.is_counted ? (
                                    <span className="badge" style={{ backgroundColor: '#4CAF50', color: '#fff' }}>✓ {displayCountForRow(item)}</span>
                                  ) : (
                                    <span className="badge" style={{ backgroundColor: '#E8DDD0', color: '#5A4A42' }}>—</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Keypad Card */}
                <div
                  className="col-md-5"
                  style={mobileTool ? { paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' } : undefined}
                >
                  {/* Rooms strip moved: previously rendered here at the
                      top of col-md-5; now rendered at the bottom of col-md-7
                      under the item tape so all navigation controls (item
                      tape / location tape / search) are grouped together. */}
                  <div className="card" ref={keypadCardRef} style={{ borderColor: '#C8B5A0', borderWidth: '2px', boxShadow: 'rgba(0,0,0,0.04) 1px 1px 3px', maxWidth: mobileTool ? 'min(28rem, calc(100% - 0.5rem))' : '22rem', margin: '0 auto', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                    <div className="card-body" style={{ backgroundColor: '#FAF8F6', padding: mobileTool ? '4px 8px' : '6px 0' }}>
                      <div data-count-header="1" style={{ maxWidth: mobileTool ? '100%' : '20rem', margin: mobileTool ? '0 auto 4px' : '0 auto 6px' }}>
                        {/* Wrapper so the offline-sync indicator can sit in the
                            unused space to the left of the item name. The h5
                            stays text-centered within the full width; the icon
                            is absolute-positioned to the left edge. */}
                        <div data-count-header-title="1" style={{ position: 'relative', marginBottom: '4px' }}>
                          {mobileTool && (
                            <OfflineSyncIndicator
                              syncStatus={currentItemSyncStatus}
                              isOnline={offlineSync.isOnline}
                            />
                          )}
                          <h5
                            ref={itemNameRef}
                            className="text-center"
                            data-name-expanded={mobileTool && nameExpanded ? '1' : undefined}
                            title={mobileTool ? (selectedItem.item_snapshot?.description || selectedItem.item_snapshot?.name || 'Unknown Item') : undefined}
                            onClick={mobileTool && (nameTruncated || nameExpanded) ? () => setNameExpanded((v) => !v) : undefined}
                            style={{
                              // Mobile: item name is the hero — ink, not accent-brown,
                              // so it reads as the strongest text on the card.
                              color: mobileTool ? '#3E2723' : '#7B4B2A',
                              fontFamily: 'Montserrat, sans-serif',
                              fontWeight: 800,
                              letterSpacing: '0.01em',
                              marginBottom: 0,
                              ...(mobileTool && (nameTruncated || nameExpanded) ? { cursor: 'pointer' } : null),
                            }}
                          >
                            {selectedItem.item_snapshot?.description || selectedItem.item_snapshot?.name || 'Unknown Item'}
                            {mobileTool && nameTruncated && !nameExpanded && (
                              <span
                                aria-hidden="true"
                                style={{ fontSize: '0.7rem', fontWeight: 700, color: '#B08968', marginLeft: '4px', whiteSpace: 'nowrap' }}
                              >
                                ⌄ more
                              </span>
                            )}
                          </h5>
                        </div>
                        {/* A canonical item has several vendor products under
                            it, so the card cannot answer "the vendor" or "the
                            price" with one value. The Cost cell shows the
                            range across linked products and the Vendor cell
                            becomes the flag that opens them for review. */}
                        <div data-count-meta-box="1" style={{ border: mobileTool ? 'none' : '1px solid #E8DDD0', borderRadius: '10px', backgroundColor: mobileTool ? 'transparent' : '#FFFFFF', padding: mobileTool ? '4px 0 0' : '6px 10px' }}>
                          {mobileTool ? (
                            // Vendor is the interactive node; Count as sits
                            // immediately below it to show the dependent flow.
                            <div style={{ display: 'grid', gridTemplateColumns: '0.9fr 1.35fr 0.9fr', gap: '0 8px', alignItems: 'start' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
                                <span style={{ fontSize: '0.55rem', color: '#A8988D', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', lineHeight: 1.3 }}>
                                  {countCardLinkage.isCanonical ? 'Cost' : 'Price'}
                                </span>
                                <span style={{ fontSize: '0.72rem', color: '#5A4A42', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }} title={String(countCardPriceLabel)}>
                                  {countCardPriceLabel}
                                </span>
                              </div>
                              <VendorUnitFlow
                                mobile
                                vendorLabel={countCardSourceLabel}
                                vendorValue={countCardSourceValue}
                                unit={countCardDisplayUnit}
                                countAsNode={countCardCountAs}
                                onCountSeveral={countCardCanCountSeveral ? () => openPackageSheet(selectedItem) : null}
                                countSeveralLabel={countCardMixedLabel}
                                entryCount={countCardEntryCount}
                              />
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
                                <span style={{ fontSize: '0.55rem', color: '#A8988D', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', lineHeight: 1.3 }}>
                                  Location
                                </span>
                                <span style={{ fontSize: '0.72rem', color: '#5A4A42', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }} title={selectedItem.item_snapshot?.location || 'Unknown'}>
                                  {selectedItem.item_snapshot?.location || 'Unknown'}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <>
                              {[
                                { label: countCardLinkage.isCanonical ? 'Cost' : 'Unit $', value: countCardPriceLabel },
                              ].map((row) => (
                                <div
                                  key={row.label}
                                  className="small"
                                  style={{
                                    color: '#6D5A4E',
                                    display: 'grid',
                                    gridTemplateColumns: '64px 1fr',
                                    columnGap: '8px',
                                    alignItems: 'center',
                                    marginBottom: '2px',
                                  }}
                                >
                                  <span className="fw-semibold" style={{ textAlign: 'left' }}>{row.label}</span>
                                  <span style={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(row.value)}>
                                    {truncateMeta(row.value)}
                                  </span>
                                </div>
                              ))}
                              <VendorUnitFlow
                                vendorLabel={countCardSourceLabel}
                                vendorValue={countCardSourceValue}
                                unit={countCardDisplayUnit}
                                countAsNode={countCardCountAs}
                                onCountSeveral={countCardCanCountSeveral ? () => openPackageSheet(selectedItem) : null}
                                countSeveralLabel={countCardMixedLabel}
                                entryCount={countCardEntryCount}
                              />
                              <div className="small" style={{ color: '#6D5A4E', display: 'grid', gridTemplateColumns: '64px 1fr', columnGap: '8px', alignItems: 'center' }}>
                                <span className="fw-semibold" style={{ textAlign: 'left' }}>Location</span>
                                <span style={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={selectedItem.item_snapshot?.location || 'Unknown'}>
                                  {truncateMeta(selectedItem.item_snapshot?.location || 'Unknown')}
                                </span>
                              </div>
                              <div className="d-flex justify-content-end" style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #F0E6D9' }}>
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={openEditItemModal}
                                  disabled={editItemSaving}
                                  style={{ borderColor: '#C8B5A0', color: '#5A4A42', fontWeight: 600, padding: '2px 10px' }}
                                >
                                  <i className="feather icon-edit-3 me-1"></i>
                                  Edit item
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Search + Edit item row moved: previously rendered
                          here under the item metadata; now rendered at the
                          bottom of col-md-7 under the location tape so all
                          navigation controls are grouped together. */}

                      {/* Action frame: PAR/OK + pill + count strip.
                          − and + extend past the frame's left/right borders;
                          their solid fills create the visual gap in the border. */}
                      <div data-count-action-frame="1" style={{
                        position: 'relative',
                        maxWidth: mobileTool ? '100%' : '20rem',
                        margin: mobileTool ? '2px auto 4px' : '4px auto 6px',
                        border: '2px solid #C8B5A0',
                        borderRadius: '14px',
                        padding: mobileTool ? '8px 10px 10px' : '8px 10px',
                        backgroundColor: '#FFFFFF',
                        overflowX: 'hidden',
                      }}>
                        {/* PAR / OK row.
                            mobileTool: PAR is now rendered inline with the count
                            row (see below) so it sits vertically centered against
                            the COUNT/ORDER tiles. Desktop keeps the stacked
                            label-over-badge layout.
                            The MOQ column that used to sit between them is gone:
                            a vendor's minimum is not an item fact, and the count
                            card states PAR, COUNT and NEED only. */}
                        {mobileTool ? null : (
                          <div className="row text-center g-2">
                            {/* On xs (small web screens) each metric renders its label
                                and value on one horizontal line (flex-row). On sm+
                                the original stacked label-over-badge layout is kept
                                (flex-sm-column). gap-1 keeps the pair tight. */}
                            <div
                              className={`${selectedItem.order_calculations_enabled ? 'col-6' : 'col-12'} d-flex flex-row flex-sm-column align-items-center justify-content-center gap-1`}
                              onClick={() => {
                                if (selectedItem.par_defaulted === true) {
                                  openFocusedParSetupFromCount(selectedItemIndex, { reviewingAutoDefault: true });
                                } else {
                                  openParsWizardFromCount(selectedItemIndex);
                                }
                              }}
                              style={{ cursor: 'pointer' }}
                              title="Tap to edit PAR (applies to every location for this item)"
                            >
                              {selectedItem.par_defaulted === true ? (
                                <button
                                  type="button"
                                  data-set-par-flag="1"
                                  data-count-item-id={selectedItem.id}
                                  aria-label={`Set PAR and minimum order for ${selectedItem.item_snapshot?.description || selectedItem.item_snapshot?.name || 'this item'}`}
                                  title="Review this item's starter PAR and minimum order"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openFocusedParSetupFromCount(selectedItemIndex, { reviewingAutoDefault: true });
                                  }}
                                  style={{
                                    minHeight: '28px',
                                    padding: '3px 8px',
                                    borderRadius: '999px',
                                    border: '1px solid #D89A00',
                                    backgroundColor: '#FFF3CD',
                                    color: '#7A4E00',
                                    fontSize: '0.65rem',
                                    fontWeight: 900,
                                    lineHeight: 1,
                                    letterSpacing: '0.04em',
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer',
                                  }}
                                >
                                  SET PAR
                                </button>
                              ) : (
                                <small style={{ color: '#8B7B73' }}>PAR</small>
                              )}
                              <FlashingBadge
                                flash={flashPar}
                                backgroundColor="#D4C4B0"
                                color="#FFFFFF"
                              >
                                {parDisplayFor(selectedItem).text}
                              </FlashingBadge>
                            </div>
                            {/* OK / ORDER column — desktop only; order info is
                                surfaced on mobile via the inline ORDER badge. */}
                            {selectedItem.order_calculations_enabled && (
                              <div
                                className="col-6 d-flex flex-row flex-sm-column align-items-center justify-content-center gap-1"
                              >
                                <small style={{ color: selectedItem.is_counted && selectedItem.shortage > 0 ? '#4CAF50' : '#8B7B73' }}>
                                  {selectedItem.is_counted && selectedItem.shortage > 0 ? (selectedShowsNeed ? 'NEED' : 'ORDER') : 'OK'}
                                </small>
                                <FlashingBadge
                                  value={selectedItem.is_counted && selectedItem.shortage > 0
                                    ? `${selectedOrderValue}${selectedShowsNeed ? ` ${selectedDisplayUnit}` : ''}`
                                    : '0'}
                                  flash={flashOrder}
                                  backgroundColor={selectedItem.is_counted && selectedItem.is_manually_overridden ? '#FF9800' : (selectedItem.is_counted && selectedItem.shortage > 0 ? '#4CAF50' : '#8B7B73')}
                                  color="#FFFFFF"
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Stable chip row: always reserves its minimum height on mobile
                            so the card doesn't reflow when chips appear/disappear.
                            On desktop the row only renders when chips exist. */}
                        {mobileTool ? (
                          /* On mobile the per-location chips row always reserves its
                             minimum height so the card stays stable. multiLocLabel is
                             NOT shown here — it lives exclusively in the fixed-size
                             Total-all-Locations slot in the count row below, where it
                             flashes on and fades off without reflowing the card. */
                          <div
                            data-chip-row="1"
                            className="text-center"
                            style={{ marginTop: (siblingChips && siblingChips.length > 0) ? '4px' : '0' }}
                          >
                            {siblingChips && siblingChips.map((chip, ci) => (
                              <span
                                key={ci}
                                className="badge"
                                style={{ backgroundColor: '#E3F2FD', color: '#1565C0', fontSize: '0.64rem' }}
                                title={`${chip.location}: ${chip.value} counted`}
                              >
                                {chip.abbr} -{chip.value}
                              </span>
                            ))}
                          </div>
                        ) : (multiLocLabel || siblingChips) ? (
                          <div className="text-center" style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px' }}>
                            {multiLocLabel && (
                              <span
                                style={{
                                  display: 'inline-block',
                                  maxWidth: '100%',
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  color: '#2E7D32',
                                  backgroundColor: '#E8F5E9',
                                  border: '1px solid #A5D6A7',
                                  borderRadius: '10px',
                                  padding: '4px 10px',
                                }}
                                title="Combined count across all locations for this item"
                              >
                                {multiLocLabel}
                              </span>
                            )}
                            {siblingChips && siblingChips.map((chip, ci) => (
                              <span
                                key={ci}
                                style={{
                                  display: 'inline-block',
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  color: '#1565C0',
                                  backgroundColor: '#E3F2FD',
                                  border: '1px solid #90CAF9',
                                  borderRadius: '10px',
                                  padding: '4px 10px',
                                }}
                                title={`${chip.location}: ${chip.value} counted`}
                              >
                                {chip.abbr} -{chip.value}
                              </span>
                            ))}
                          </div>
                        ) : null}

                        {/* Count row. On mobile: PAR flush-left | circular ± buttons +
                            fixed Total-all-Locations slot + count input | empty spacer.
                            PAR is vertically centered against the COUNT/ORDER tiles so
                            it sits on the tile midline, not in a top-row. The right
                            slot used to hold MOQ and is now a plain spacer.
                            On desktop: rectangular buttons extending past the frame edges. */}
                        {mobileTool ? (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                            marginTop: '3px',
                          }}>
                            {/* Three-region layout so COUNT is centered on the card's
                                horizontal axis (the same axis the centered keypad's middle
                                column sits on):
                                  [LEFT region flex:1] [COUNT flex:0] [RIGHT region flex:1]
                                Because the left and right regions share equal flex weight,
                                the fixed-width COUNT tile always lands exactly at the row's
                                midpoint — regardless of PAR digit counts, the ALL badge,
                                or whether the ORDER tile is shown (ORDER only fills space
                                inside the right region, it never shifts COUNT). */}

                            {/* LEFT region: PAR (flush-left) + minus + multiloc badge slot.
                                justify-content:space-between pins the minus/badge cluster to
                                the inner (COUNT-side) edge while PAR stays at the far left. */}
                            <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                              {!selectedShowOrder ? (
                                // Plain counts still hide the irrelevant PAR number, but a
                                // newly defaulted item uses this otherwise-empty slot for its
                                // explicit, non-blocking review action. Nothing pops while the
                                // counter enters quantities, and the centered COUNT layout does
                                // not move.
                                selectedItem.par_defaulted === true ? (
                                  <button
                                    type="button"
                                    data-set-par-flag="1"
                                    data-count-item-id={selectedItem.id}
                                    aria-label={`Set PAR and minimum order for ${selectedItem.item_snapshot?.description || selectedItem.item_snapshot?.name || 'this item'}`}
                                    title="Review this item's starter PAR and minimum order"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openFocusedParSetupFromCount(selectedItemIndex, { reviewingAutoDefault: true });
                                    }}
                                    style={{
                                      minHeight: '28px',
                                      padding: '3px 7px',
                                      borderRadius: '999px',
                                      border: '1px solid #D89A00',
                                      backgroundColor: '#FFF3CD',
                                      color: '#7A4E00',
                                      fontSize: '0.62rem',
                                      fontWeight: 900,
                                      lineHeight: 1,
                                      letterSpacing: '0.04em',
                                      whiteSpace: 'nowrap',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    SET PAR
                                  </button>
                                ) : (
                                  <div aria-hidden="true" />
                                )
                              ) : (
                              <div
                                onClick={() => {
                                  if (selectedItem.par_defaulted === true) {
                                    openFocusedParSetupFromCount(selectedItemIndex, { reviewingAutoDefault: true });
                                  } else {
                                    openParsWizardFromCount(selectedItemIndex);
                                  }
                                }}
                                style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0px', minWidth: '2rem', overflow: 'hidden' }}
                                title="Tap to edit PAR (applies to every location for this item)"
                              >
                                {selectedItem.par_defaulted === true ? (
                                  <button
                                    type="button"
                                    data-set-par-flag="1"
                                    data-count-item-id={selectedItem.id}
                                    aria-label={`Set PAR and minimum order for ${selectedItem.item_snapshot?.description || selectedItem.item_snapshot?.name || 'this item'}`}
                                    title="Review this item's starter PAR and minimum order"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openFocusedParSetupFromCount(selectedItemIndex, { reviewingAutoDefault: true });
                                    }}
                                    style={{
                                      minHeight: '28px',
                                      padding: '3px 7px',
                                      borderRadius: '999px',
                                      border: '1px solid #D89A00',
                                      backgroundColor: '#FFF3CD',
                                      color: '#7A4E00',
                                      fontSize: '0.62rem',
                                      fontWeight: 900,
                                      lineHeight: 1,
                                      letterSpacing: '0.04em',
                                      whiteSpace: 'nowrap',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    SET PAR
                                  </button>
                                ) : (
                                  <span style={{ color: '#9B8B82', fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1 }}>PAR</span>
                                )}
                                <span
                                  data-par-value="1"
                                  className={flashPar ? 'flash-once' : ''}
                                  style={{ fontSize: '1.6rem', fontWeight: 900, color: '#5A4A42', lineHeight: 1.1, fontFamily: 'Montserrat, sans-serif' }}
                                >
                                  {parDisplayFor(selectedItem).value ?? '—'}
                                </span>
                                {/* The unit, on its own line so the big number
                                    stays readable at arm's length. Without it a
                                    PAR of 12 beside a count in cases is two
                                    different shelves. */}
                                {parDisplayFor(selectedItem).unit && (
                                  <span style={{ color: '#9B8B82', fontSize: '0.52rem', fontWeight: 700, lineHeight: 1, maxWidth: '4rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {parDisplayFor(selectedItem).unit}
                                  </span>
                                )}
                              </div>
                              )}

                              {/* minus + multiloc badge slot, pinned to COUNT's left edge */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                {/* Minus — red circle */}
                                <button
                                  data-adjust-btn="minus"
                                  onClick={() => incrementCount(selectedItemIndex, -1)}
                                >−</button>

                                {/* Fixed-size slot for Total-all-Locations indicator.
                                    The slot is always present so the count input doesn't
                                    shift when the indicator appears/disappears. The badge
                                    inside uses a CSS flash-then-fade animation keyed on
                                    the item index + presence, so it re-triggers on each
                                    item navigation. */}
                                <div data-multiloc-slot="1">
                                  {multiLocLabel && (
                                    <div
                                      key={`ml-${selectedItemIndex}-1`}
                                      data-multiloc-badge="1"
                                    >
                                      <div style={{ fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', color: '#2E7D32' }}>ALL</div>
                                      <div style={{ fontSize: '0.68rem', fontWeight: 900, color: '#2E7D32', lineHeight: 1 }}>
                                        {(() => {
                                          const m = multiLocLabel.match(/:\s*(\S+)$/);
                                          return m ? m[1] : multiLocLabel;
                                        })()}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* CENTER: COUNT tile alone — flex:0 so it sits exactly on the
                                card's horizontal axis between the two equal-width regions.
                                Vertically: label pinned absolute at top, value centered then
                                nudged down ~half the label height to sit in the visual
                                midpoint of the space below the label. */}
                            <div
                              data-count-tile="1"
                              style={{ position: 'relative', flex: '0 0 6.5rem', width: '6.5rem', height: '4.2rem', minHeight: '4.2rem', backgroundColor: directOrderMode ? '#FBF6EE' : '#FFFFFF', border: `2px solid ${directOrderMode ? '#8A7868' : '#C8B5A0'}`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', overflow: 'hidden' }}
                            >
                              <span style={{ position: 'absolute', top: '3px', left: 0, right: 0, fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', color: directOrderMode ? '#8A7868' : '#A89890', letterSpacing: 0.5, lineHeight: 1.2, textAlign: 'center' }}>
                                {directOrderMode ? 'ORDER' : 'COUNT'}
                              </span>
                              {(() => {
                                const v = getEffectiveCount(selectedItemIndex);
                                const display = (v === '' || v == null) ? '0' : String(v);
                                // Shrink-to-fit: the tile is a fixed window (6.5rem), so
                                // long values scale the FONT down instead of wrapping or
                                // clipping. Three full digits fit at the base size; every
                                // extra character shrinks proportionally with no lower
                                // bound (counts never get long enough to vanish). A "."
                                // is roughly half a digit wide in Montserrat, so it
                                // counts as 0.5. The scale rides a CSS custom property
                                // so the iPhone SE / landscape !important font-size
                                // overrides in count-mobile.html shrink with it.
                                const digitCount = display.replace(/[^0-9]/g, '').length;
                                const narrowCount = display.length - digitCount;
                                const effectiveLen = Math.max(1, digitCount + narrowCount * 0.5);
                                const countScale = Math.min(1, 3 / effectiveLen);
                                const baseRem = selectedShowOrder ? 2.8 : 3.1;
                                return (
                                  <div
                                    data-count-value="1"
                                    aria-label="Count"
                                    style={{
                                      '--mm-count-scale': String(countScale),
                                      fontSize: `calc(${baseRem}rem * var(--mm-count-scale, 1))`,
                                      fontWeight: 900,
                                      color: '#3D2518',
                                      textAlign: 'center',
                                      fontFamily: 'Montserrat, sans-serif',
                                      lineHeight: 1,
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap',
                                      marginTop: '6px',
                                    }}
                                  >
                                    {display}
                                  </div>
                                );
                              })()}
                            </div>

                            {/* RIGHT region: ORDER tile + plus (pinned to COUNT's right edge)
                                + an empty right spacer. Mirrors the left region's flex weight so
                                COUNT stays centered; ORDER toggling on/off only redistributes
                                space inside this region. */}
                            <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                {/* ORDER tile — immediately to the right of COUNT when shown. */}
                                {selectedShowOrder && (
                                  <div
                                    data-order-tile="1"
                                    onClick={() => { setOrderEditValue(selectedOrderValue > 0 ? String(selectedOrderValue) : ''); setShowOrderEditModal(true); }}
                                    style={{ flex: '0 0 3.4rem', width: '3.4rem', height: '4.2rem', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start', backgroundColor: selectedOrderHasOverride ? '#7A5C1E' : '#8A7868', borderRadius: '12px', padding: '4px 4px 4px', cursor: 'pointer', transition: '0.2s', border: '1px solid #C8B5A0', boxSizing: 'border-box' }}
                                  >
                                    <span style={{ color: '#F5EDD8', fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.2, textAlign: 'center' }}>
                                      {selectedOrderHasOverride ? 'ORD ✎' : (selectedShowsNeed ? 'NEED' : 'ORDER')}
                                    </span>
                                    <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <div style={{ textAlign: 'center' }}>
                                        <div style={{ color: '#FFFFFF', fontSize: '1.55rem', fontWeight: 900, lineHeight: 1, fontFamily: 'Montserrat, sans-serif' }}>
                                          {selectedOrderValue}
                                        </div>
                                        {selectedShowsNeed && (
                                          <div style={{ color: '#F5EDD8', fontSize: '0.46rem', fontWeight: 700, lineHeight: 1.1, marginTop: '2px' }}>
                                            {selectedDisplayUnit}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Plus — green circle */}
                                <button
                                  data-adjust-btn="plus"
                                  onClick={() => incrementCount(selectedItemIndex, 1)}
                                >+</button>
                              </div>

                              {/* Far-right slot — the bottle-fraction tab on
                                  liquor/wine lines (it flips the card to the
                                  bottle slider), otherwise the empty spacer that
                                  keeps the ± cluster pinned to COUNT's edge. The
                                  slot used to hold an MOQ readout; a vendor
                                  minimum is not a counting fact and now lives
                                  only in Savings Review, where a vendor has been
                                  chosen. */}
                              {countCardBottleButton || <div aria-hidden="true" />}
                            </div>
                          </div>
                        ) : (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginTop: '8px',
                          }}>
                            {/* Middle group: count input + ORDER.
                                The − / + circular adjust buttons were removed
                                here so the web count tool matches the mobile
                                tool — the count is entered via the keypad or by
                                typing in the field. incrementCount() remains
                                defined and is still referenced elsewhere. */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {directOrderMode && (
                                <span style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#8A7868' }}>Order</span>
                              )}
                              <input
                                type="text"
                                className="form-control text-center"
                                placeholder="0"
                                inputMode="decimal"
                                autoComplete="off"
                                value={getEffectiveCount(selectedItemIndex)}
                                onChange={(e) => updateItemCount(selectedItemIndex, e.target.value)}
                                style={{ width: '5.4rem', flex: '0 0 5.4rem', fontSize: selectedShowOrder ? '1.45rem' : '1.85rem', fontWeight: 700, padding: '6px 4px', backgroundColor: '#FFFFFF', border: `2px solid ${directOrderMode ? '#8A7868' : '#C8B5A0'}`, color: '#3D2518', borderRadius: '10px', height: '3.3rem', minHeight: '3.3rem', textAlign: 'center' }}
                              />
                              {selectedShowOrder && (
                                <div
                                  onClick={() => { setOrderEditValue(selectedOrderValue > 0 ? String(selectedOrderValue) : ''); setShowOrderEditModal(true); }}
                                  style={{ flex: '0 0 2.8rem', height: '3.3rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: selectedOrderHasOverride ? '#FF9800' : '#4CAF50', borderRadius: '10px', padding: '2px 4px', cursor: 'pointer', transition: '0.2s', border: '1px solid #D4C4B0' }}
                                >
                                  <span style={{ color: '#fff', fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.2 }}>
                                    {selectedOrderHasOverride ? 'ORD ✎' : (selectedShowsNeed ? 'NEED' : 'ORDER')}
                                  </span>
                                  <span style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 900, lineHeight: 1.1 }}>
                                    {selectedOrderValue}{selectedShowsNeed ? ` ${selectedDisplayUnit}` : ''}
                                  </span>
                                </div>
                              )}
                              {/* Bottle-fraction tab (liquor/wine lines): flips
                                  the card to the bottle slider. Sits just right
                                  of the count field, or right of NEED when this
                                  is an ordering count. */}
                              {countCardBottleButton}
                            </div>
                          </div>
                        )}

                        {/* The typed packages read back in the item's own
                            unit — "2 bags = 8 lb" — so the conversion the
                            input now does invisibly stays checkable against
                            the case in hand. The "Count as" selector itself
                            lives in the meta card above, in the cell that
                            used to say "Unit". */}
                        {countCardEquivalence && (
                          <div
                            data-count-equivalence="1"
                            style={{ textAlign: 'center', marginTop: '5px', fontSize: '0.7rem', fontWeight: 600, color: '#8B7B73' }}
                          >
                            {countCardEquivalence}
                          </div>
                        )}
                      </div>

                      {showOrderEditModal && selectedItem.order_calculations_enabled && (
                        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(10,15,25,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setShowOrderEditModal(false)}>
                          <div style={{ backgroundColor: '#FAF8F6', borderRadius: '16px', padding: '24px', maxWidth: '320px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
                            <h6 style={{ color: '#3D2518', fontFamily: 'Montserrat, sans-serif', marginBottom: '4px' }}>
                              Edit Order Quantity{selectedPackageView ? ` (${selectedPackageView.unitLabel}s)` : ''}
                            </h6>
                            <small style={{ color: '#8B7B73', display: 'block', marginBottom: '16px' }}>
                              {selectedItem.item_snapshot?.description || selectedItem.item_snapshot?.name || 'Unknown Item'}
                            </small>
                            {/* The number typed here means the same thing the ORDER
                                badge shows: packages of the viewed vendor when a
                                package view is active. It is stored normalized, so
                                the hub report and allocation keep reading one unit.
                                type="text" for the same reason as the count inputs:
                                a wheel scroll over a focused number input edits the
                                value instead of scrolling. */}
                            <input
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              className="form-control mb-2"
                              autoFocus
                              placeholder={String(selectedOrderValue || 0)}
                              value={orderEditValue}
                              onChange={(e) => {
                                if (/^\d*\.?\d*$/.test(e.target.value)) setOrderEditValue(e.target.value);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const newVal = parseFloat(orderEditValue);
                                  if (Number.isFinite(newVal) && newVal >= 0) {
                                    const stored = selectedPackageView
                                      ? String(Math.round(newVal * selectedPackageView.per * 1e6) / 1e6)
                                      : orderEditValue;
                                    applyManualOrderOverride(selectedItemIndex, stored);
                                    setShowOrderEditModal(false);
                                  }
                                }
                              }}
                              style={{ borderColor: '#D4C4B0', fontSize: '1.2rem', textAlign: 'center' }}
                            />
                            <small style={{ color: '#8B7B73', display: 'block', marginBottom: '12px' }}>
                              Calculated: {calculatedOrderDisplayFor(selectedItem)}
                              {selectedPackageView ? ` ${selectedPackageView.unitLabel}${calculatedOrderDisplayFor(selectedItem) === 1 ? '' : 's'}` : ''}
                            </small>
                            <div className="d-flex" style={{ gap: '8px' }}>
                              <button
                                type="button"
                                className="btn btn-sm flex-grow-1"
                                style={{ backgroundColor: '#E8DDD0', color: '#5A4A42', border: '1px solid #D4C4B0' }}
                                onClick={() => setShowOrderEditModal(false)}
                              >
                                Cancel
                              </button>
                              {selectedOrderHasOverride && (
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  style={{ backgroundColor: '#fff', color: '#8B7B73', border: '1px solid #D4C4B0' }}
                                  onClick={async () => {
                                    clearManualOrderOverride(selectedItemIndex);
                                    setShowOrderEditModal(false);
                                    const item = countItems[selectedItemIndex];
                                    if (item?.id) {
                                      try {
                                        await supabase.from('count_items').update({
                                          manual_order_quantity: null,
                                          updated_at: new Date().toISOString()
                                        }).eq('id', item.id);
                                      } catch (e) {
                                        console.warn('[OrderOverride] DB reset failed:', e?.message);
                                      }
                                    }
                                  }}
                                >
                                  Reset
                                </button>
                              )}
                              <button
                                type="button"
                                className="btn btn-sm flex-grow-1 mm-brown-action"
                                style={{ backgroundColor: '#4CAF50', color: '#fff', border: '1px solid #388E3C' }}
                                onClick={async () => {
                                  const newVal = parseFloat(orderEditValue);
                                  if (!Number.isFinite(newVal) || newVal < 0) return;
                                  // Typed in the viewed package; stored normalized.
                                  const storedVal = selectedPackageView
                                    ? Math.round(newVal * selectedPackageView.per * 1e6) / 1e6
                                    : newVal;
                                  applyManualOrderOverride(selectedItemIndex, String(storedVal));
                                  setShowOrderEditModal(false);
                                  // Immediately persist to DB so override survives navigation
                                  const item = countItems[selectedItemIndex];
                                  if (item?.id) {
                                    try {
                                      await supabase.from('count_items').update({
                                        manual_order_quantity: storedVal,
                                        updated_at: new Date().toISOString()
                                      }).eq('id', item.id);
                                    } catch (e) {
                                      console.warn('[OrderOverride] DB persist failed:', e?.message);
                                    }
                                  }
                                }}
                              >
                                Confirm
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Game band / status rail. Mobile wears the brand's one dark
                          surface — espresso, not navy — with the amber accent from
                          the end-of-count scoreboard. Desktop keeps its navy band. */}
                      <div data-count-game-band="1" style={{
                        background: mobileTool
                          ? 'linear-gradient(135deg, #2E2018 0%, #221710 100%)'
                          : 'linear-gradient(135deg, #24364c 0%, #1a252f 100%)',
                        borderRadius: mobileTool ? '12px' : '8px',
                        padding: mobileTool ? '6px 12px 7px' : '5px 10px 4px',
                        color: mobileTool ? '#F6EFE6' : '#fff',
                        fontSize: mobileTool ? '0.85rem' : '0.72rem',
                        fontWeight: '600',
                        margin: mobileTool ? '0 auto 4px' : '0 auto 6px',
                        maxWidth: mobileTool ? '100%' : '20rem',
                        border: mobileTool ? '1px solid rgba(244, 194, 122, 0.22)' : '1px solid rgba(76, 175, 80, 0.15)',
                        boxShadow: mobileTool ? '0 1px 6px rgba(46, 32, 24, 0.22)' : '0 0 12px rgba(76, 175, 80, 0.08)',
                      }}>
                        {mobileTool ? (
                          // Mobile game band — one encouragement line (with the
                          // "Go {Name}" cheer folded in), then exactly four
                          // labeled stat chunks at glanceable sizes: time (hero),
                          // streak, pace (the rabbit — same mascot as the
                          // end-of-count scoreboard), and rooms left (promoted
                          // from small text to a real stat). Same data, fewer,
                          // bigger chunks. Idle state explains itself instead of
                          // showing a broken-looking "--:--".
                          (() => {
                            const bandLine = !firstCountCommitted
                              ? 'timer starts with your first tap'
                              : timerPaused
                                ? 'paused — any key resumes'
                                : encouragement;
                            const chunkValueStyle = (opts = {}) => ({
                              fontSize: opts.hero ? '1.02rem' : '0.92rem',
                              fontWeight: 800,
                              fontVariantNumeric: 'tabular-nums',
                              lineHeight: 1.15,
                              whiteSpace: 'nowrap',
                              color: opts.color || '#F6EFE6',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                            });
                            const chunkLabelStyle = {
                              fontSize: '0.55rem',
                              fontWeight: 700,
                              letterSpacing: '0.09em',
                              textTransform: 'uppercase',
                              color: 'rgba(246, 239, 230, 0.62)',
                              lineHeight: 1.2,
                              whiteSpace: 'nowrap',
                            };
                            const chunkStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0px', flexShrink: 0, minWidth: 0 };
                            return (
                              <>
                                <div
                                  key={`${encouragementKey}-${timerPaused ? 'p' : 'r'}`}
                                  data-band-line="1"
                                  style={{
                                    textAlign: 'center',
                                    fontSize: '0.76rem',
                                    fontWeight: 700,
                                    color: timerPaused && firstCountCommitted ? '#F4C27A' : '#F4C27A',
                                    letterSpacing: '0.02em',
                                    marginBottom: '4px',
                                    lineHeight: 1.2,
                                    animation: 'gameBandFadeIn 0.35s ease',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {currentUserName ? `Go, ${currentUserName} — ${bandLine}` : bandLine}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', columnGap: '8px', rowGap: '3px', flexWrap: 'wrap' }}>
                                  <span style={chunkStyle}>
                                    <span data-band-stat-value="1" style={chunkValueStyle({ hero: true, color: !firstCountCommitted ? 'rgba(246,239,230,0.55)' : timerPaused ? '#F4C27A' : '#F6EFE6' })}>
                                      {!firstCountCommitted ? (
                                        <>⏱ 0:00</>
                                      ) : timerPaused ? (
                                        <>⏸ {formatElapsed(elapsedSeconds)}</>
                                      ) : (
                                        <>
                                          <span data-game-green="1" style={{
                                            display: 'inline-block',
                                            width: '7px',
                                            height: '7px',
                                            borderRadius: '50%',
                                            backgroundColor: '#4CAF50',
                                            flexShrink: 0,
                                            animation: 'timerDotPulse 2s ease-in-out infinite',
                                          }} />
                                          {formatElapsed(elapsedSeconds)}
                                        </>
                                      )}
                                    </span>
                                    <span data-band-stat-label="1" style={chunkLabelStyle}>Time</span>
                                  </span>
                                  <span style={chunkStyle}>
                                    <span data-band-stat-value="1" style={chunkValueStyle()}>🔥 {countStreak}</span>
                                    <span data-band-stat-label="1" style={chunkLabelStyle}>Streak</span>
                                  </span>
                                  <span style={chunkStyle}>
                                    <span data-band-stat-value="1" style={chunkValueStyle()}>🐰 {itemsPerHour}/hr</span>
                                    <span data-band-stat-label="1" style={chunkLabelStyle}>Pace</span>
                                  </span>
                                  {locationsToGo != null ? (
                                    <span style={chunkStyle}>
                                      <span
                                        data-band-stat-value="1"
                                        {...(locationsToGo === 0 ? { 'data-game-green': '1' } : {})}
                                        style={chunkValueStyle({ color: locationsToGo === 0 ? '#A5D6A7' : '#F4C27A' })}
                                      >
                                        {locationsToGo === 0 ? '✓' : `📍 ${locationsToGo}`}
                                      </span>
                                      <span data-band-stat-label="1" style={chunkLabelStyle}>
                                        {locationsToGo === 0 ? 'All rooms' : (locationsToGo === 1 ? 'Room left' : 'Rooms left')}
                                      </span>
                                    </span>
                                  ) : (
                                    <span style={chunkStyle}>
                                      <span data-band-stat-value="1" style={chunkValueStyle()}>⭐ {itemsThisSession}</span>
                                      <span data-band-stat-label="1" style={chunkLabelStyle}>Items</span>
                                    </span>
                                  )}
                                </div>
                                {/* Full-width progress bar — amber while counting,
                                    calm green only at 100%. */}
                                <div data-band-bar="1" style={{
                                  marginTop: '6px',
                                  height: '5px',
                                  borderRadius: '99px',
                                  backgroundColor: 'rgba(255,255,255,0.16)',
                                  overflow: 'hidden',
                                }}>
                                  <div {...(progressPercent >= 100 ? { 'data-game-green': '1' } : {})} style={{
                                    height: '100%',
                                    width: `${progressPercent}%`,
                                    backgroundColor: progressPercent >= 100 ? '#A5D6A7' : '#F4C27A',
                                    borderRadius: '99px',
                                    transition: 'width 0.4s ease',
                                  }} />
                                </div>
                              </>
                            );
                          })()
                        ) : (
                          <>
                            {/* Go [Name] header (inside game band, centered at top) */}
                            {currentUserName && (
                              <div data-game-green="1" style={{
                                textAlign: 'center',
                                fontWeight: '700',
                                fontSize: '0.72rem',
                                color: '#4CAF50',
                                letterSpacing: '0.04em',
                                marginBottom: '2px',
                                textTransform: 'uppercase',
                              }}>
                                Go {currentUserName}
                              </div>
                            )}
                            {/* Stats row */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                              {/* Timer with status indicator */}
                              <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                {!firstCountCommitted ? (
                                  <span style={{ color: '#aaa', fontVariantNumeric: 'tabular-nums' }}>⏱ --:--</span>
                                ) : timerPaused ? (
                                  <span style={{ color: '#FFC107', fontVariantNumeric: 'tabular-nums' }}>⏸ {formatElapsed(elapsedSeconds)}</span>
                                ) : (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px', animation: 'timerPulse 2s ease-in-out infinite', fontVariantNumeric: 'tabular-nums' }}>
                                    <span data-game-green="1" style={{
                                      display: 'inline-block',
                                      width: '7px',
                                      height: '7px',
                                      borderRadius: '50%',
                                      backgroundColor: '#4CAF50',
                                      flexShrink: 0,
                                      animation: 'timerDotPulse 2s ease-in-out infinite',
                                    }} />
                                    {formatElapsed(elapsedSeconds)}
                                  </span>
                                )}
                              </span>
                              <span>🔥 {countStreak}</span>
                              <span>⚡ {itemsPerHour}/hr</span>
                              <span>⭐ {itemsThisSession}</span>
                              {/* Locations to go */}
                              {locationsToGo != null && (
                                <span {...(locationsToGo === 0 ? { 'data-game-green': '1' } : {})} style={{ color: locationsToGo === 0 ? '#4CAF50' : '#FFC107', fontWeight: '700' }}>
                                  {locationsToGo === 0
                                    ? '✓ all done'
                                    : `${locationsToGo} ${locationsToGo === 1 ? 'location' : 'locations'} to go`}
                                </span>
                              )}
                            </div>

                            {/* Encouragement text with fade */}
                            <div
                              key={encouragementKey}
                              data-game-green="1"
                              style={{
                                textAlign: 'center',
                                fontSize: '0.6rem',
                                fontWeight: '700',
                                color: '#4CAF50',
                                letterSpacing: '0.05em',
                                marginTop: '2px',
                                opacity: 1,
                                animation: 'gameBandFadeIn 0.35s ease',
                              }}
                            >
                              {encouragement}
                            </div>

                            {/* Progress bar */}
                            <div style={{
                              marginTop: '4px',
                              height: '3px',
                              borderRadius: '2px',
                              backgroundColor: 'rgba(255,255,255,0.15)',
                              overflow: 'hidden',
                            }}>
                              <div {...(progressPercent >= 100 ? { 'data-game-green': '1' } : {})} style={{
                                height: '100%',
                                width: `${progressPercent}%`,
                                backgroundColor: progressPercent >= 100 ? '#4CAF50' : progressPercent >= 50 ? '#8BC34A' : '#FFC107',
                                borderRadius: '2px',
                                transition: 'width 0.4s ease',
                              }} />
                            </div>
                          </>
                        )}
                      </div>

                      {/* Clip container — overflow:hidden keeps the sliding
                          keypad from peeking outside the card during the
                          swipe animation. Static; only the inner wrapper moves. */}
                      <div style={{ marginTop: '4px', overflow: mobileTool ? 'hidden' : 'visible' }}>
                        {/* Animation wrapper — gets a slide class on swipe nav (Task #746).
                            position:relative keeps the swipe-hint pill positioned correctly. */}
                        <div
                          style={{ position: 'relative' }}
                          className={mobileTool && keypadAnimClass ? keypadAnimClass : undefined}
                        >
                        {mobileTool && keypadSwipeHintState !== 'idle' && (
                          <div
                            className={`keypad-swipe-hint${keypadSwipeHintState === 'leaving' ? ' keypad-swipe-hint--leaving' : ''}`}
                            aria-hidden="true"
                          >
                            <span className="keypad-swipe-hint__pill">
                              <span className="keypad-swipe-hint__arrow-left">←</span>
                              Swipe to navigate items
                              <span className="keypad-swipe-hint__arrow-right">→</span>
                            </span>
                          </div>
                        )}
                        <div
                          className={`keypad-custom${mobileTool ? ' keypad-custom--mobile' : ''}`}
                          style={{ gridTemplateRows: 'repeat(4, 1fr)' }}
                          onTouchStart={mobileTool ? (e) => {
                            if (keypadSwipeHintState === 'visible') dismissKeypadSwipeHint();
                            if (e.touches.length !== 1) { keypadSwipeRef.current.active = false; return; }
                            const t = e.touches[0];
                            keypadSwipeRef.current = { x: t.clientX, y: t.clientY, active: true };
                          } : undefined}
                          onTouchMove={mobileTool ? (e) => {
                            if (e.touches.length !== 1) keypadSwipeRef.current.active = false;
                          } : undefined}
                          onTouchEnd={mobileTool ? (e) => {
                            const s = keypadSwipeRef.current;
                            if (!s.active) return;
                            keypadSwipeRef.current.active = false;
                            const t = e.changedTouches[0];
                            if (!t) return;
                            const dx = t.clientX - s.x;
                            const dy = t.clientY - s.y;
                            // 50px horizontal threshold, must be more horizontal
                            // than vertical so vertical scrubs don't navigate.
                            if (Math.abs(dx) < 50 || Math.abs(dx) <= Math.abs(dy)) return;
                            // Task #746: slide the keypad in the swipe direction.
                            if (dx < 0) triggerSwipeNav('left', nextItem);
                            else triggerSwipeNav('right', previousItem);
                          } : undefined}
                        >
                          <button 
                            onClick={() => appendToCount('1')}
                            style={{
                              backgroundColor: '#FFFFFF',
                              color: '#3D2518',
                              border: '2px solid #C8B5A0',
                            }}
                          >
                            1
                          </button>
                          <button 
                            onClick={() => appendToCount('2')}
                            style={{
                              backgroundColor: '#FFFFFF',
                              color: '#3D2518',
                              border: '2px solid #C8B5A0',
                            }}
                          >
                            2
                          </button>
                          <button 
                            onClick={() => appendToCount('3')}
                            style={{
                              backgroundColor: '#FFFFFF',
                              color: '#3D2518',
                              border: '2px solid #C8B5A0',
                            }}
                          >
                            3
                          </button>
                          <button 
                            onClick={() => appendToCount('4')}
                            style={{
                              backgroundColor: '#FFFFFF',
                              color: '#3D2518',
                              border: '2px solid #C8B5A0',
                            }}
                          >
                            4
                          </button>
                          <button 
                            onClick={() => appendToCount('5')}
                            style={{
                              backgroundColor: '#FFFFFF',
                              color: '#3D2518',
                              border: '2px solid #C8B5A0',
                            }}
                          >
                            5
                          </button>
                          <button 
                            onClick={() => appendToCount('6')}
                            style={{
                              backgroundColor: '#FFFFFF',
                              color: '#3D2518',
                              border: '2px solid #C8B5A0',
                            }}
                          >
                            6
                          </button>
                          <button 
                            onClick={() => appendToCount('7')}
                            style={{
                              backgroundColor: '#FFFFFF',
                              color: '#3D2518',
                              border: '2px solid #C8B5A0',
                            }}
                          >
                            7
                          </button>
                          <button 
                            onClick={() => appendToCount('8')}
                            style={{
                              backgroundColor: '#FFFFFF',
                              color: '#3D2518',
                              border: '2px solid #C8B5A0',
                            }}
                          >
                            8
                          </button>
                          <button 
                            onClick={() => appendToCount('9')}
                            style={{
                              backgroundColor: '#FFFFFF',
                              color: '#3D2518',
                              border: '2px solid #C8B5A0',
                            }}
                          >
                            9
                          </button>
                          <button 
                            onClick={() => appendToCount('.')}
                            style={{
                              backgroundColor: '#FFFFFF',
                              color: '#3D2518',
                              border: '2px solid #C8B5A0',
                            }}
                          >
                            .
                          </button>
                          <button 
                            onClick={() => appendToCount('0')}
                            style={{
                              backgroundColor: '#FFFFFF',
                              color: '#3D2518',
                              border: '2px solid #C8B5A0',
                            }}
                          >
                            0
                          </button>
                          <button 
                            onClick={() => backspaceCount()}
                            style={{
                              backgroundColor: '#F8F1E8',
                              color: '#6D5648',
                              border: '2px solid #C8B5A0',
                            }}
                          >
                            {/* Backspace icon. Explicit width/height because
                                the Tailwind w-5/h-5 classes aren't loaded on
                                count-mobile.html (no Tailwind), which made
                                the icon render as zero-size and look blank. */}
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.25" style={{ display: 'inline-block' }}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M18 6H9a2 2 0 00-1.414.586L3.586 10.586A2 2 0 003 12a2 2 0 00.586 1.414l4 4A2 2 0 009 18h9a2 2 0 002-2V8a2 2 0 00-2-2z"/>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10l-3 4M10 10l3 4"/>
                            </svg>
                          </button>
                        </div>
                        </div>{/* end animation wrapper */}
                      </div>{/* end clip container */}

                      <div className={mobileTool ? 'mt-1' : 'mt-2'} style={{ maxWidth: mobileTool ? '100%' : '20rem', margin: '0 auto' }}>
                        <div data-count-action-row="1" style={{ display: 'grid', gridTemplateColumns: mobileTool ? '1fr 1.1fr 0.85fr' : '1fr 1fr 1fr', gap: mobileTool ? '0.5rem' : '0.6rem', gridAutoRows: '2.75rem' }}>
                          <button 
                            onClick={() => addFraction('0.25')}
                            style={{
                              backgroundColor: '#FFFFFF',
                              color: '#3D2518',
                              border: '2px solid #C8B5A0',
                              fontSize: '0.9rem',
                              lineHeight: '1.2',
                              minHeight: '2.75rem',
                              height: 'auto',
                              aspectRatio: 'unset',
                            }}
                          >
                            +.25<br/><small>+¼</small>
                          </button>
                          {/* Slot 2 of the 3×2 action grid.
                              Desktop: Edit item button (the modal opener
                              that lives next to the search bar on mobile).
                              Mobile: Clear is relocated here so the
                              bottom-right slot can hold Done inline with
                              the action strip. */}
                          {mobileTool ? (
                            <button
                              type="button"
                              onClick={() => clearCount()}
                              style={{
                                backgroundColor: '#FFFFFF',
                                color: '#D32F2F',
                                border: '2px solid #C8B5A0',
                                fontSize: '0.65rem',
                                fontWeight: '700',
                                minHeight: '2.75rem',
                                height: 'auto',
                                aspectRatio: 'unset',
                                padding: '4px 2px',
                              }}
                            >
                              Clear
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => openEditItemModal()}
                              style={{
                                backgroundColor: '#F3EDE4',
                                color: '#5A4A42',
                                border: '2px solid #C8B5A0',
                                fontSize: '0.78rem',
                                fontWeight: '600',
                                minHeight: '2.75rem',
                                height: 'auto',
                                aspectRatio: 'unset',
                                padding: '0.35rem',
                              }}
                            >
                              Edit item
                            </button>
                          )}
                          {/* Slot 3 of the 3×2 action grid.
                              Desktop: Back navigates to the previous item.
                              Mobile: Back is replaced with the Edit item
                              modal opener so the user can change the PAR
                              and item details without leaving the keypad.
                              On mobile, Back is still available via the
                              browser back gesture and the bottom-tape. */}
                          {mobileTool ? (
                            <button
                              type="button"
                              onClick={() => openEditItemModal()}
                              style={{
                                backgroundColor: '#F3EDE4',
                                color: '#5A4A42',
                                border: '2px solid #C8B5A0',
                                fontSize: '0.72rem',
                                fontWeight: '700',
                                minHeight: '2.75rem',
                                height: 'auto',
                                aspectRatio: 'unset',
                                padding: '2px 4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '2px',
                              }}
                            >
                              Edit item
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={previousItem}
                              style={{
                                backgroundColor: '#FFFFFF',
                                color: '#5A4A42',
                                border: '2px solid #C8B5A0',
                                fontSize: '0.72rem',
                                fontWeight: '700',
                                minHeight: '2.75rem',
                                height: 'auto',
                                aspectRatio: 'unset',
                                padding: '2px 4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '2px',
                              }}
                            >
                              <i className="feather icon-chevron-left" style={{ fontSize: '0.9rem', lineHeight: 1 }}></i>
                              Back
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={countAndNext}
                            style={{
                              backgroundColor: mobileTool ? '#8A7868' : '#28a745',
                              color: '#fff',
                              border: mobileTool ? '2px solid #6B584B' : '2px solid #2f8f4a',
                              fontSize: '0.68rem',
                              fontWeight: '700',
                              minHeight: '2.75rem',
                              height: 'auto',
                              aspectRatio: 'unset',
                              padding: '4px 2px',
                              ...(mobileTool ? { whiteSpace: 'nowrap' } : null),
                            }}
                          >
                            {mobileTool ? 'Count Next ▶' : (<>Count<br/>Next ▶</>)}
                          </button>
                          <button 
                            onClick={() => addFraction('0.5')}
                            style={{
                              backgroundColor: '#FFFFFF',
                              color: '#3D2518',
                              border: '2px solid #C8B5A0',
                              fontSize: '0.9rem',
                              lineHeight: '1.2',
                              minHeight: '2.75rem',
                              height: 'auto',
                              aspectRatio: 'unset',
                            }}
                          >
                            +.5<br/><small>+½</small>
                          </button>
                          {/* Bottom-right slot of the 3×2 action grid.
                              Desktop keeps Clear here; mobile shows Done
                              inline with the strip (Clear has been moved
                              to slot 2 above). Done uses the same brown
                              palette and the same handleDoneClick
                              that the standalone Done button used. */}
                          {mobileTool ? (
                            <button
                              type="button"
                              onClick={handleDoneClick}
                              disabled={loading || Boolean(closeProgress)}
                              style={{
                                backgroundColor: '#8A7868',
                                color: '#fff',
                                border: '2px solid #6B584B',
                                fontSize: '0.78rem',
                                fontWeight: '700',
                                minHeight: '2.75rem',
                                height: 'auto',
                                aspectRatio: 'unset',
                                padding: '4px 2px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                              }}
                            >
                              Done
                            </button>
                          ) : (
                            <button
                              onClick={() => clearCount()}
                              style={{
                                backgroundColor: '#FFFFFF',
                                color: '#D32F2F',
                                border: '2px solid #C8B5A0',
                                fontSize: '0.65rem',
                                fontWeight: '700',
                                minHeight: '2.75rem',
                                height: 'auto',
                                aspectRatio: 'unset',
                                padding: '4px 2px'
                              }}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Session actions.
            Mobile no longer renders this row — Done now sits inline in
            the bottom-right slot of the action grid above. Desktop keeps
            the centered Done button at the bottom of the screen. */}
        {!mobileTool && (
          <div className="row mt-2">
            <div className="col-12">
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button
                  className="btn btn-success"
                  onClick={handleDoneClick}
                  disabled={loading || Boolean(closeProgress)}
                  style={{ minWidth: '140px', fontWeight: '700', fontSize: '1rem' }}
                >
                  <i className="feather icon-check me-1"></i> Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Item Modal — must be inside the counting return block */}
        {showEditItemModal && (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
              background: 'rgba(61,37,24,0.45)', zIndex: 10000,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              paddingLeft: '12px', paddingRight: '12px',
            }}
            onClick={() => !editItemSaving && setShowEditItemModal(false)}
          >
            <div
              style={{
                background: '#FEFCFA', borderRadius: '16px', padding: '1.1rem 1.1rem 1rem',
                maxWidth: '500px', width: '100%', maxHeight: 'calc(90vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))', overflowY: 'auto',
                boxShadow: '0 14px 34px rgba(61,37,24,0.25)', border: '1px solid #D4C4B0',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #E8DDD0' }}>
                <h5 style={{ margin: 0, color: '#5A4A42', fontWeight: 800, fontSize: '1.08rem' }}>Edit count item details</h5>
                <button type="button" disabled={editItemSaving}
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#8B7B73', lineHeight: 1 }}
                  onClick={() => setShowEditItemModal(false)}>&times;</button>
              </div>
              <p style={{ fontSize: '0.78rem', color: '#8B7B73', marginBottom: '14px' }}>
                Session edits stay on this count by default and won't change recurring defaults.
              </p>
              <div style={{ background: '#FFF9E6', border: '1px solid #F0D8A8', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#8a6d3b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                  Pricing &amp; units
                </div>
                <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>Unit price (each)</label>
                <input type="text" inputMode="decimal" autoComplete="off" className="form-control form-control-sm mb-2"
                  value={editItemDraft.unit_price} onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) setEditItemDraft((d) => ({ ...d, unit_price: e.target.value })); }} />
                <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>Case price</label>
                <input type="text" inputMode="decimal" autoComplete="off" className="form-control form-control-sm mb-2"
                  value={editItemDraft.case_price} onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) setEditItemDraft((d) => ({ ...d, case_price: e.target.value })); }} />
                <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>Unit of measure</label>
                <input type="text" className="form-control form-control-sm"
                  value={editItemDraft.unit} onChange={(e) => setEditItemDraft((d) => ({ ...d, unit: e.target.value }))} placeholder="each, case, lb…" />
              </div>
              <div style={{ background: '#F1F8EC', border: '1px solid #C8DDB4', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#3F6B26', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                  PAR
                </div>
                <p style={{ margin: '0 0 8px', color: '#4F7035', fontSize: '0.74rem' }}>
                  PAR applies to this item across every location in this count. Needs for already-counted rows update right away.
                </p>
                <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>PAR</label>
                <input type="text" inputMode="decimal" autoComplete="off" className="form-control form-control-sm"
                  value={editItemDraft.par_level}
                  onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) setEditItemDraft((d) => ({ ...d, par_level: e.target.value })); }} />
              </div>
              <div style={{ background: '#F5F8FF', border: '1px solid #D2DDF8', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#34538A', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                  Identity fields
                </div>
                <p style={{ margin: 0, color: '#48628F', fontSize: '0.75rem' }}>
                  If you change <strong>name, vendor, or unit</strong>, we will ask whether to create a new item for this count or permanently update the existing item across open orders and EOMs.
                </p>
              </div>
              <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>Description</label>
              <input type="text" className="form-control form-control-sm mb-2"
                value={editItemDraft.description} onChange={(e) => setEditItemDraft((d) => ({ ...d, description: e.target.value }))} />
              <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>Vendor</label>
              <div className="mb-2">
                <VendorAutocompleteInput
                  value={editItemDraft.vendor}
                  onChange={(nextValue) => setEditItemDraft((d) => ({ ...d, vendor: nextValue }))}
                  storeId={activeStoreId}
                  className="form-control form-control-sm"
                />
              </div>
              <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>Category</label>
              <input type="text" className="form-control form-control-sm mb-2"
                value={editItemDraft.category} onChange={(e) => setEditItemDraft((d) => ({ ...d, category: e.target.value }))} />
              <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>Location (this count)</label>
              <input type="text" className="form-control form-control-sm mb-3"
                value={editItemDraft.location} onChange={(e) => setEditItemDraft((d) => ({ ...d, location: e.target.value }))} />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-outline-secondary btn-sm" disabled={editItemSaving} onClick={() => setShowEditItemModal(false)}>Cancel</button>
                <button type="button" className="btn btn-success btn-sm" disabled={editItemSaving} onClick={() => void saveEditItemFromModal()}>
                  {editItemSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* "Available reports" on the EOP completed screen mounts the Reports
            component in embedded mode, exactly as the completed count cards do
            from the overview. The workflow modal hides while a report is open
            (its overlay sits above ReportModal's z-index) and returns
            untouched when the report closes. */}
        {embeddedReport && (
          <Reports
            embedded
            initialReportType={embeddedReport.type}
            initialSessionIds={embeddedReport.sessionIds}
            onEmbeddedClose={() => setEmbeddedReport(null)}
          />
        )}
        {!embeddedReport && (
        <CountWorkflowModal
          step={completeFlowStep}
          summary={completionWorkflow.summary || {}}
          locationChoice={{
            untouchedNames: getUntouchedRooms().map((room) => room.label).join(', '),
            nextGroupLabel: getNextRoomWithUncounted()?.label || null,
            groupNoun: (mobileTool || sortBy === 'location')
              ? 'location'
              : (sortBy === 'vendor' ? 'vendor' : 'category'),
          }}
          orderingEnabled={!!selectedSession?.order_calculations_enabled}
          isEopSession={isEOPSession(selectedSession)}
          nextAction={completionWorkflow.nextAction}
          savingsReviewId={completionWorkflow.savingsReviewId}
          error={completionWorkflow.error}
          isBusy={completionWorkflow.isBusy}
          onBack={() => setCompleteFlowStep(null)}
          onDoneWithLocation={() => {
            const nextRoom = getNextRoomWithUncounted();
            setCompleteFlowStep(null);
            if (nextRoom) advanceToRoom(nextRoom);
          }}
          onDoneWithCount={openCompletionReview}
          onFillUncounted={isSuperAdmin ? () => {
            setCompleteFlowStep(null);
            fillUncountedWithRandomCounts();
          } : null}
          onAbandon={mobileTool
            ? () => setCompleteFlowStep(COUNT_COMPLETION_STEP.ABANDON_CONFIRM)
            : null}
          onConfirmAbandon={async () => {
            setCompleteFlowStep(null);
            try {
              await offlineSync.discardSessionLocally(selectedSession?.id);
            } catch (_) {}
            if (onCancel) onCancel();
          }}
          onComplete={() => { void finalizeCompleteCountSession(); }}
          onDone={dismissCompletedCount}
          onOpenReport={(type) => openSessionReport(type, selectedSession)}
          onViewSavingsReview={(planId) => {
            window.location.href = `ordering.html?savings=${encodeURIComponent(planId)}`;
          }}
          onCreateDraftOrders={() => { void handleCreateDraftOrdersAfterCompletion(); }}
        />
        )}
        {/* Pars wizard — the counting screen returns separately from the
            overview, so it renders the wizard itself (PAR badge taps). */}
        {renderParsWizardModal()}

        {/* Tape edit mode: confirm deleting an item from the active count
            and the parent template. Mirrors the styled CustomModal pattern
            used for the no-PAR warning below. Yes removes the row from
            count_items in the current session and from count_template_items
            (and the sibling is_template count_session) so future counts of
            the same template skip it. Past completed counts are untouched —
            same blast radius as the par-edit propagation. */}
        <CustomModal
          isOpen={tapeDeleteConfirm !== null}
          onClose={cancelTapeDelete}
          title="Remove item"
          type="custom"
        >
          {tapeDeleteConfirm && (
            <div data-tape-delete-modal="1" style={{ width: '100%', textAlign: 'left' }}>
              <p style={{ color: '#3D2518', fontSize: 14, margin: '0 0 12px' }}>
                Are you sure you wish to delete{' '}
                <strong>{tapeDeleteConfirm.itemName}</strong> from this count and future counts of{' '}
                <strong>{tapeTemplateName || selectedSession?.template_name_snapshot || selectedSession?.name || 'this template'}</strong>?
              </p>
              <p style={{ color: '#8B7B73', fontSize: 12, fontStyle: 'italic', margin: '0 0 20px' }}>
                The item stays in your store catalog. Other counts already created from this template are not changed.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={cancelTapeDelete}
                  disabled={tapeDeletePending}
                  style={{
                    flex: 1, padding: '12px 0', borderRadius: 10,
                    border: '1px solid #D4C4B0', backgroundColor: '#E8DDD0',
                    color: '#5A4A42', fontWeight: 800, fontSize: 14,
                    cursor: tapeDeletePending ? 'not-allowed' : 'pointer',
                    opacity: tapeDeletePending ? 0.6 : 1,
                  }}
                >
                  No
                </button>
                <button
                  type="button"
                  onClick={confirmTapeDelete}
                  disabled={tapeDeletePending}
                  style={{
                    flex: 1, padding: '12px 0', borderRadius: 10,
                    border: '1px solid #B23A36', backgroundColor: '#D9534F',
                    color: '#fff', fontWeight: 800, fontSize: 14,
                    cursor: tapeDeletePending ? 'not-allowed' : 'pointer',
                    opacity: tapeDeletePending ? 0.7 : 1,
                  }}
                >
                  {tapeDeletePending ? 'Removing…' : 'Yes, remove'}
                </button>
              </div>
            </div>
          )}
        </CustomModal>

        {/* Focused PAR setup card. Opened by the durable SET PAR flag on any
            count, or by an explicit tap on a genuinely missing PAR in an
            ordering count; count entry itself never launches a modal. */}
        <CustomModal
          isOpen={parWarningModal !== null}
          onClose={() => { if (!parSetupSaving) setParWarningModal(null); }}
          type="custom"
        >
          {(() => {
            if (!parWarningModal) return null;
            const cardRow = countItems.find(
              (ci) => String(ci.id) === String(parWarningModal.countItemId)
            ) || null;
            const cardOptions = cardRow
              ? (pooledOptionsByItemId[cardRow.inventory_item_id]
                || pooledOptionsByItemId[String(cardRow.inventory_item_id)]
                || purchaseOptionsByItemId[cardRow.inventory_item_id]
                || purchaseOptionsByItemId[String(cardRow.inventory_item_id)]
                || [])
              : [];
            return (
              <CountParSetupCard
                key={parWarningModal.countItemId}
                itemName={parWarningModal.itemName}
                countValue={cardRow ? displayCountForRow(cardRow) : 0}
                unitLabel={cardRow ? displayUnitForRow(cardRow) : 'each'}
                countEntered={!!cardRow && hasCountEntry(cardRow.current_count)}
                initialPar={cardRow && parWarningModal.reviewingAutoDefault
                  ? parDisplayFor(cardRow).value
                  : null}
                purchaseOptions={cardOptions}
                initialPurchaseOptionId={cardRow?.selected_purchase_option_id ?? null}
                reviewingAutoDefault={parWarningModal.reviewingAutoDefault === true}
                busy={parSetupSaving}
                onSave={(values) => { void saveParSetupFromCard(values); }}
                onDismiss={() => setParWarningModal(null)}
              />
            );
          })()}
        </CustomModal>

        {completeFlowStep === 'offline-blocked' && (
          <CustomModal
            isOpen={true}
            onClose={() => {
              // The user can dismiss the secondary "no internet detected"
              // copy by tapping outside or pressing Esc — return to the
              // primary "Return to internet" message instead of closing the
              // whole flow, since the count is locked until the sync runs.
              if (offlineBlockedNoInternet) {
                setOfflineBlockedNoInternet(false);
                return;
              }
              // Primary message: do not allow dismissal — the user must
              // press the action button (which probes connectivity).
            }}
            title={offlineBlockedNoInternet ? 'No internet detected' : 'Count saved on this phone'}
            message={
              offlineBlockedNoInternet
                ? 'Countertop is not detecting internet. Check your device settings and try again.'
                : 'Your count is saved on this phone. Return to the internet to finish completing it.'
            }
            type="custom"
          >
            {offlineBlockedNoInternet ? (
              <button
                onClick={() => setOfflineBlockedNoInternet(false)}
                style={{
                  backgroundColor: '#3A2418',
                  border: '1px solid #5A3A28',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  minWidth: '80px',
                }}
              >
                OK
              </button>
            ) : (
              <button
                onClick={() => { void handleOfflineBlockedRetry(); }}
                disabled={offlineBlockedRetrying}
                style={{
                  backgroundColor: '#3A2418',
                  border: '1px solid #5A3A28',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: offlineBlockedRetrying ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  minWidth: '80px',
                  opacity: offlineBlockedRetrying ? 0.7 : 1,
                }}
              >
                {offlineBlockedRetrying ? 'Trying…' : 'Try again'}
              </button>
            )}
          </CustomModal>
        )}
        {appModalConfig && (
          <CustomModal
            isOpen={true}
            onClose={closeAppModal}
            title={appModalConfig.title}
            message={appModalConfig.message}
            type={appModalConfig.type}
            onConfirm={handleAppModalConfirm}
            confirmText={appModalConfig.confirmText}
            cancelText={appModalConfig.cancelText}
            confirmButtonStyle={appModalConfig.confirmButtonStyle}
          />
        )}
        <BackdateCopyModal
          modal={backdateCopyModal}
          onChangeDate={(value) => setBackdateCopyModal((m) => (m ? { ...m, date: value } : m))}
          onToggleRandomize={(value) => setBackdateCopyModal((m) => (m ? { ...m, randomize: value } : m))}
          onCancel={() => setBackdateCopyModal(null)}
          onConfirm={handleBackdateCopyConfirm}
        />
        {endOfRoomModal && (
          <CustomModal
            isOpen={true}
            onClose={() => setEndOfRoomModal(null)}
            title={endOfRoomModal.nextRoom ? 'Location complete' : 'All done!'}
            message={endOfRoomModal.nextRoom
              ? `All items in this location have been counted. Advance to "${endOfRoomModal.nextRoom.label}"?`
              : 'All items in this location have been counted. The session is fully counted!'}
            type={endOfRoomModal.nextRoom ? 'confirm' : 'alert'}
            onConfirm={endOfRoomModal.nextRoom ? handleEndOfRoomAdvance : undefined}
            confirmText="Advance to next room"
            cancelText="Stay in this location"
            confirmButtonStyle="primary"
          />
        )}
        {bottleCountOverlay}
        {closeProgress && (
          <CountCloseProgressIndicator
            subStep={closeProgress.subStep}
            error={closeProgress.error}
            onRetry={null}
          />
        )}
      </div>
    );
  }

  // ─── Count session empty / error fallback ─────────────────────
  // Shown when we navigated directly to count-session.html but items didn't load
  if (step === 'counting' && selectedSession && !loading && countItems.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '1rem', padding: '2rem' }}>
        <div style={{ fontSize: '2.5rem' }}>📋</div>
        <h3 style={{ margin: 0, color: '#3E2723' }}>No items in this session</h3>
        <p style={{ margin: 0, color: '#6D4C41', textAlign: 'center' }}>
          Session <strong>{selectedSession.name}</strong> has no count items yet.<br />
          You may need to add items or check if this session was set up correctly.
        </p>
        <button
          className="btn btn-outline-secondary"
          onClick={() => { if (onCancel) onCancel(); else window.history.back(); }}
        >
          ← Back to sessions
        </button>
      </div>
    );
  }

  // ─── Loading fallback for count-session.html ──────────────────
  if (step === 'counting' && loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center', color: '#6D4C41' }}>
          <div className="spinner-border" role="status" style={{ color: '#7B5E45' }} />
          <p style={{ marginTop: '1rem' }}>Loading count session…</p>
        </div>
      </div>
    );
  }

  const currentViewParam = new URLSearchParams(window.location.search).get('view');
  const useCountHtmlOverviewShell =
    window.location.pathname.includes('count.html') &&
    (currentViewParam === null || currentViewParam === '' || currentViewParam === 'overview');

  // ─── Mobile Overview Layout ───────────────────────────────────
  if (isMobile && step === 'select-session' && !useCountHtmlOverviewShell) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const calendarDays = Array.from({ length: 4 }, (_, i) => {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      return d;
    });
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const sessionsForDay = (day) => {
      const dayStr = day.toISOString().split('T')[0];
      return countSessions.filter(s => {
        if (s.is_template) return false;
        const sd = s.session_date ? new Date(s.session_date).toISOString().split('T')[0] : null;
        const cd = s.created_at ? new Date(s.created_at).toISOString().split('T')[0] : null;
        return sd === dayStr || cd === dayStr;
      });
    };

    const filteredSessions = getFilteredAndSortedSessions();
    const activeSessions = countSessions.filter(s => (s.status === 'in_progress' || s.status === 'paused') && !s.is_template);

    const calendarFiltered = selectedCalendarDay
      ? filteredSessions.filter(s => {
          const dayStr = selectedCalendarDay.toISOString().split('T')[0];
          const sd = s.session_date ? new Date(s.session_date).toISOString().split('T')[0] : null;
          const cd = s.created_at ? new Date(s.created_at).toISOString().split('T')[0] : null;
          return sd === dayStr || cd === dayStr;
        })
      : filteredSessions;

    const renderMobileCard = (session) => {
      const counted = session.items_counted || 0;
      const total = session.total_items || 0;
      const allCounted = (total > 0 && counted >= total) || session.status === 'completed';
      const someCounted = counted > 0 && !allCounted;
      const pct = total > 0 ? Math.round((counted / total) * 100) : 0;
      const statusColor = session.status === 'completed' || allCounted ? '#4CAF50'
        : session.status === 'in_progress' || session.status === 'paused' || someCounted ? '#FFA726' : '#D4C4B0';
      const isOrder = session.order_calculations_enabled;
      const icons = sessionCategoryIconCache[session.id] || [];

      const statusLabel = allCounted ? 'Done' : someCounted ? 'Counting' : (session.status === 'completed' ? 'Done' : 'Draft');
      const badgeBg = allCounted || session.status === 'completed' ? '#E8F5E9' : someCounted ? '#FFF3E0' : '#F5F0EB';
      const badgeColor = allCounted || session.status === 'completed' ? '#2E7D32' : someCounted ? '#E65100' : '#8B7B73';

      return (
        <div key={session.id} style={{
          backgroundColor: '#FAF6F2', border: '1px solid #E8DDD0', borderRadius: '10px',
          marginBottom: '6px', position: 'relative', overflow: 'visible'
        }}>
          <div
            onClick={() => window.location.href = `count-session.html?session=${session.id}`}
            style={{ display: 'flex', flexDirection: 'column', gap: '3px', padding: '10px 10px', cursor: 'pointer' }}
          >
            {/* Row 1: icon + title (full width, up to 2 lines) */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
              {isOrder ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: '2px' }}>
                  <rect x="1" y="10" width="15" height="9" rx="1.5" stroke={statusColor} strokeWidth="2" fill="none"/>
                  <path d="M16 13h3.4a1 1 0 0 1 .77.36l2.1 2.52a1 1 0 0 1 .23.64V19a1 1 0 0 1-1 1h-2" stroke={statusColor} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="7" cy="20.5" r="1.8" stroke={statusColor} strokeWidth="1.5" fill={statusColor}/>
                  <circle cx="19" cy="20.5" r="1.8" stroke={statusColor} strokeWidth="1.5" fill={statusColor}/>
                </svg>
              ) : (
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: statusColor, flexShrink: 0, marginTop: '5px' }} />
              )}
              <span style={{
                fontWeight: 700, fontSize: '1rem', color: '#3E2723',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                overflow: 'hidden', flex: 1, minWidth: 0, lineHeight: 1.3,
              }}>
                {session.name}
              </span>
            </div>
            {/* Row 2: metadata (items · date) */}
            <div style={{ fontSize: '0.75rem', color: '#8B7B73', paddingLeft: 'calc(14px + 7px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {total > 0 ? `${counted}/${total} items` : null}
              {total > 0 && ' · '}
              {formatDate(session.session_date)}
            </div>
            {/* Row 3: progress bar + percentage */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '1px' }}>
              <div style={{ flex: 1, height: '4px', borderRadius: '2px', backgroundColor: '#EDE6DE', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, backgroundColor: statusColor, borderRadius: '2px', transition: 'width 0.3s' }} />
              </div>
              <span style={{ fontSize: '0.65rem', color: '#8B7B73', flexShrink: 0, minWidth: '28px', textAlign: 'right' }}>{pct}%</span>
            </div>
            {/* Row 4: badge row — status badge + category icons; wraps as needed */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', marginTop: '2px' }}>
              <span style={{ fontSize: '0.65rem', fontWeight: 600, padding: '2px 7px', borderRadius: '8px', backgroundColor: badgeBg, color: badgeColor, whiteSpace: 'nowrap' }}>
                {statusLabel}
              </span>
              {icons.length > 0 && (
                <span style={{ display: 'inline-flex', gap: '1px' }}>
                  {icons.slice(0, 2).map((ic, i) => <span key={i} style={{ fontSize: '0.65rem', lineHeight: 1 }}>{ic}</span>)}
                </span>
              )}
            </div>
            {/* Available Reports — surfaced once the count is complete */}
            {session.status === 'completed' && (
              <div style={{ marginTop: '4px' }} onClick={(e) => e.stopPropagation()}>
                <CountCardAvailableReports session={session} onOpenReport={openSessionReport} compact />
              </div>
            )}
            {/* Row 5: primary action button + ellipsis menu */}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '2px' }} onClick={(e) => e.stopPropagation()}>
              {(() => {
                const baseBtn = { fontSize: '0.75rem', fontWeight: 700, padding: '22px 16px', borderRadius: '6px', border: 'none', color: '#fff', cursor: 'pointer', flex: '1 1 auto', textAlign: 'center', lineHeight: 1.2, whiteSpace: 'normal', wordBreak: 'break-word', display: 'flex', alignItems: 'center', justifyContent: 'center' };
                if (session.status === 'completed') {
                  return <button style={{ ...baseBtn, backgroundColor: '#757575' }} onClick={() => window.location.href = `count-session.html?session=${session.id}`}>Review</button>;
                }
                if (session.status === 'in_progress') {
                  return <button style={{ ...baseBtn, backgroundColor: '#FB8C00' }} onClick={() => window.location.href = `count-session.html?session=${session.id}`}>Continue</button>;
                }
                return <button style={{ ...baseBtn, backgroundColor: '#43A047' }} onClick={() => window.location.href = `count-session.html?session=${session.id}`}>Open</button>;
              })()}
              <PortalCardMenu
                menuKey={session.id}
                openMenuId={openMenuId}
                setOpenMenuId={setOpenMenuId}
                buttonStyle={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E8DDD0', borderRadius: '6px', backgroundColor: '#fff', color: '#8B7B73', cursor: 'pointer', fontSize: '0.9rem', lineHeight: 1, padding: 0, flexShrink: 0 }}
                items={[
                  { icon: '✏️', label: 'Edit Count', action: () => { setOpenMenuId(null); handleEditSession(session); } },
                  { icon: '👁️', label: 'Preview', action: () => { setOpenMenuId(null); handlePreviewItems(session); } },
                  ...(session.status !== 'completed' && !allCounted ? [{ icon: '✅', label: 'Complete', action: () => { setOpenMenuId(null); handleMarkComplete(session); } }] : []),
                  { icon: '📦', label: 'Archive', action: () => { setOpenMenuId(null); handleArchiveSession(session); } },
                  ...superAdminCardMenuItems(session),
                ]}
                minWidth="150px"
              />
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="mobile-count-overview">
        {/* 4-Day Calendar Strip */}
        <div className="mco-calendar-strip">
          {calendarDays.map((day, i) => {
            const daySessions = sessionsForDay(day);
            const isToday = i === 0;
            const isSelected = selectedCalendarDay && day.toISOString().split('T')[0] === selectedCalendarDay.toISOString().split('T')[0];
            return (
              <button key={i}
                className={`mco-cal-day${isSelected ? ' mco-cal-selected' : ''}${isToday ? ' mco-cal-today' : ''}`}
                onClick={() => setSelectedCalendarDay(isSelected ? null : day)}
              >
                <span className="mco-cal-dayname">{dayNames[day.getDay()]}</span>
                <span className="mco-cal-date">{day.getDate()}</span>
                <span className="mco-cal-month">{monthNames[day.getMonth()]}</span>
                {daySessions.length > 0 && (
                  <span className="mco-cal-dots">
                    {daySessions.slice(0, 3).map((s, j) => (
                      <span key={j} className="mco-cal-dot" style={{
                        backgroundColor: s.status === 'in_progress' ? '#FFA726' : s.status === 'completed' ? '#4CAF50' : '#D4C4B0'
                      }} />
                    ))}
                    {daySessions.length > 3 && <span className="mco-cal-dot-more">+{daySessions.length - 3}</span>}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {selectedCalendarDay && (
          <div className="mco-cal-filter-label">
            Showing counts for {monthNames[selectedCalendarDay.getMonth()]} {selectedCalendarDay.getDate()}
            <button className="mco-cal-clear" onClick={() => setSelectedCalendarDay(null)}>✕ Clear</button>
          </div>
        )}

        {/* Collapsible search/filter panel — toggled by the search icon in the chip row */}
        <AnimatedSearchPanel open={showSearchPanel}>
          <div style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8DDD0', borderRadius: '10px', padding: '8px', margin: '0 0 6px', boxShadow: 'rgba(0,0,0,0.04) 0 2px 6px' }}>
            <div className="mco-filter-row">
              <select className="mco-filter-select" value={filterByStatus} onChange={(e) => setFilterByStatus(e.target.value)}>
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
              <input className="mco-search-input" type="text" placeholder="Search..." value={searchText} onChange={(e) => setSearchText(e.target.value)} autoFocus />
            </div>
          </div>
        </AnimatedSearchPanel>

        {/* Mobile jump-to section chips + search icon toggle */}
        {!loading && (
          <div style={{ display: 'flex', gap: '6px', padding: '4px 0 6px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', alignItems: 'center' }}>
            {/* "📋 Other" chip intentionally stashed — section hidden for a future version */}
            {[{ label: '📅 EOP', anchor: 'mco-section-eop' }, { label: '🚚 Orders', anchor: 'mco-section-orders' }].map(({ label, anchor }) => (
              <button key={anchor} onClick={() => { const el = document.getElementById(anchor); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                style={{ fontSize: '0.65rem', fontWeight: 600, padding: '3px 10px', borderRadius: '12px', border: '1px solid #D9CEC2', backgroundColor: '#FAF6F2', color: '#5A4A42', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {label}
              </button>
            ))}
            <button
              onClick={() => setShowSearchPanel(p => !p)}
              title={showSearchPanel ? 'Close search' : 'Search & filter'}
              style={{ marginLeft: 'auto', flexShrink: 0, width: '46px', height: '46px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px', border: showSearchPanel ? '1px solid #1976D2' : '1px solid #D9CEC2', backgroundColor: showSearchPanel ? '#E3F2FD' : '#FAF6F2', color: showSearchPanel ? '#1976D2' : '#8B7B73', cursor: 'pointer', padding: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </button>
          </div>
        )}

        {/* Three sections: End of Period, Orders, Other Counting */}
        {loading ? (
          <div className="mco-section"><div className="mco-empty">Loading...</div></div>
        ) : (() => {
          const getSessionSection = (s) => {
            if (s.order_calculations_enabled === true) return 'orders';
            const cp = s.count_purpose || '';
            const im = s.inventory_mode || '';
            if (cp === 'period_end' || cp === 'full_inventory' || im === 'Financial' || im === 'Both') return 'end-of-period';
            return 'other';
          };
          const sortByAccessed = (arr) => [...arr].sort((a, b) =>
            new Date(b.last_accessed_at || b.updated_at || b.created_at) - new Date(a.last_accessed_at || a.updated_at || a.created_at)
          );
          const DOW_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const allSessions = calendarFiltered;
          const eopSessions = sortByAccessed(allSessions.filter(s => getSessionSection(s) === 'end-of-period'));
          const orderSessions = allSessions.filter(s => getSessionSection(s) === 'orders');
          // otherSessions intentionally stashed — "Other" section hidden for a future version

          const ordersByDay = {};
          const todayDow = new Date().getDay();
          orderSessions.forEach(s => {
            // A template with a schedule groups under its next scheduled
            // weekday, not the date its session row happened to be created.
            const schedDow = nextScheduleDow(s.ordering_cycle_config || null, todayDow);
            const d = s.session_date ? new Date(s.session_date + 'T00:00:00') : new Date(s.created_at);
            const dow = schedDow != null ? schedDow : d.getDay();
            if (!ordersByDay[dow]) ordersByDay[dow] = [];
            ordersByDay[dow].push(s);
          });
          const orderedDays = [];
          for (let i = 0; i < 7; i++) {
            const dayIdx = (todayDow + i) % 7;
            if (ordersByDay[dayIdx] && ordersByDay[dayIdx].length > 0) {
              orderedDays.push({ dayIdx, label: dayIdx === todayDow ? `Today (${DOW_SHORT[dayIdx]})` : DOW_NAMES[dayIdx], sessions: sortByAccessed(ordersByDay[dayIdx]) });
            }
          }

          // "📋 Other" entry intentionally stashed — section hidden for a future version
          const nonOrderSections = [
            { key: 'end-of-period', title: '📅 EOP', type: 'end-of-period', sessions: eopSessions },
          ];
          const sectionAnchorIds = { 'end-of-period': 'mco-section-eop', 'orders': 'mco-section-orders' };

          // ─── Mobile grouped list ──────────────────────────────────────────
          // Mirrors the desktop "Template card with collapsible older runs"
          // mental model on phone screens. Sessions in the flat array are
          // bucketed by count_group_id; each bucket renders as either:
          //   • A SessionTimelineStack group (when a Template parent exists or
          //     more than one run shares the bucket) — the Template badge
          //     stays on the header, the latest run is always visible inline,
          //     and older runs collapse behind "+N counts · <months>".
          //   • A flat renderMobileCard fallback for orphan singletons that
          //     never received a count_group_id (legacy rows).
          // Compute group lookup locally — the component-scope
          // `calendarGroupedSessions` constant is declared after the mobile
          // early-return branch, so referencing it here would crash with
          // a temporal-dead-zone ReferenceError. Recomputing here is cheap.
          const mobileGroupedLookup = getGroupedSessions();

          const buildMobileGroups = (flatSessions) => {
            const byGroup = new Map(); // groupId → { template, runs }
            const order = []; // preserve original sort order based on first appearance
            flatSessions.forEach(s => {
              const groupId = s.count_group_id || s.id;
              if (!byGroup.has(groupId)) {
                const fromCalendar = (mobileGroupedLookup && mobileGroupedLookup[groupId]) || null;
                byGroup.set(groupId, { template: fromCalendar?.template || null, runs: [] });
                order.push(groupId);
              }
              byGroup.get(groupId).runs.push(s);
            });
            return order.map(groupId => {
              const { template, runs } = byGroup.get(groupId);
              const byDateDescMobile = (a, b) =>
                new Date(b.created_at || b.session_date) - new Date(a.created_at || a.session_date);
              const runsSortedByDate = [...runs].sort(byDateDescMobile);
              const rep = template || runsSortedByDate[0];
              const isOrder = rep?.order_calculations_enabled === true;
              const cp = rep?.count_purpose || '';
              const im = rep?.inventory_mode || '';
              const isEOP = !isOrder && (cp === 'period_end' || cp === 'full_inventory' || im === 'Financial' || im === 'Both');
              // For EOP recurring stacks, prefer the most-recently-active run
              // (completed → in-progress → draft with items counted) as runs[0]
              // so the just-finished count stays as the headline, not an
              // untouched system-generated future-period draft.
              let runsSorted = runsSortedByDate;
              if (isEOP && template) {
                const isUntouchedDraft = (s) => s.status === 'draft' && !(s.items_counted > 0);
                const completedR = runs.filter(s => s.status === 'completed').sort(byDateDescMobile);
                const inProgressR = runs.filter(s => s.status === 'in_progress' || s.status === 'paused').sort(byDateDescMobile);
                const touchedDrafts = runs.filter(s => !isUntouchedDraft(s)).sort(byDateDescMobile);
                const headline = completedR[0] || inProgressR[0] || touchedDrafts[0] || runsSortedByDate[0];
                if (headline && headline.id !== runsSortedByDate[0]?.id) {
                  // Put headline first, then the rest in date-desc order.
                  runsSorted = [headline, ...runsSortedByDate.filter(s => s.id !== headline.id)];
                }
              }
              const seriesName = template
                ? (template.name || '')
                : ((runsSorted[0]?.name || '').replace(/\s*-\s*(Run\s*#?\d+|Copy\s*\d*|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec).*$/i, '').trim() || (runsSorted[0]?.name || ''));
              return { groupId, template, runs: runsSorted, name: seriesName, isOrder, isEOP };
            });
          };

          const renderMobileGroupedList = (flatSessions, emptyMsg = 'No counts here yet') => {
            if (!flatSessions || flatSessions.length === 0) {
              return <div className="mco-empty" style={{ fontSize: '0.75rem' }}>{emptyMsg}</div>;
            }
            const groups = buildMobileGroups(flatSessions);
            // Split into "needs grouping" (template OR multi-run) vs orphan singletons.
            const stackGroups = groups.filter(g => g.template || g.runs.length > 1);
            const singletonGroups = groups.filter(g => !g.template && g.runs.length === 1);

            const mobileRunRow = (session, group) => {
              const counted = session.items_counted || 0;
              const total = session.total_items || 0;
              const allCounted = (total > 0 && counted >= total) || session.status === 'completed';
              const someCounted = counted > 0 && !allCounted;
              const pct = total > 0 ? Math.round((counted / total) * 100) : 0;
              const isResumable = session.status === 'in_progress' || session.status === 'paused';
              const statusColor = allCounted ? '#4CAF50' : someCounted || isResumable ? '#FFA726' : '#D4C4B0';
              const isNewest = group.runs[0]?.id === session.id;
              const statusLabel = allCounted ? 'Complete' : session.status === 'in_progress' ? 'In Progress' : session.status === 'paused' ? 'Saved' : 'Draft';
              const badgeBg = allCounted ? '#E8F5E9' : isResumable ? '#FFF3E0' : '#F5F0EB';
              const badgeColor = allCounted ? '#2E7D32' : isResumable ? '#E65100' : '#8B7B73';
              const runTypeLabel = group.isOrder ? 'Order Run' : group.isEOP ? 'EOP Run' : 'Count Run';
              const baseBtn = { fontSize: '0.72rem', fontWeight: 700, padding: '22px 0', borderRadius: '6px', border: 'none', color: '#fff', cursor: 'pointer', width: '100%', textAlign: 'center', lineHeight: 1.2, whiteSpace: 'normal', wordBreak: 'break-word', display: 'flex', alignItems: 'center', justifyContent: 'center' };
              // Web/mobile parity: green = ready, orange = continue/started or saved,
              // grey = review/completed.
              const mobileRunIsOrder = group.isOrder;
              const mobileRunPrimaryLabel = session.status === 'completed'
                ? ((total === 0 || counted >= total) ? (mobileRunIsOrder ? 'View Order' : 'View Count') : 'Review')
                : isResumable
                  ? (mobileRunIsOrder ? 'Resume Order' : 'Resume Count')
                  : (mobileRunIsOrder ? 'Start Order' : 'Start Count');
              const mobileRunPrimaryColor = session.status === 'completed' ? '#757575'
                : isResumable ? '#FB8C00' : '#43A047';
              const primaryBtn = (
                <button style={{ ...baseBtn, backgroundColor: mobileRunPrimaryColor }}
                  onClick={() => { markRunViewed(session.id); window.location.href = `count-session.html?session=${session.id}`; }}>
                  {mobileRunPrimaryLabel}
                </button>
              );
              return (
                <div key={session.id} style={{
                  backgroundColor: '#FFFFFF', border: '1px solid #E0D5CC', borderLeft: '3px solid #C4B4A8',
                  borderRadius: '8px', padding: '8px 10px', marginBottom: '6px',
                  boxShadow: isNewest ? '0 1px 4px rgba(90,74,66,0.10)' : '0 1px 2px rgba(90,74,66,0.04)',
                }}>
                  {/* Run type label + Latest badge on one row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                    <span style={{ fontSize: '0.58rem', fontWeight: 700, color: '#9B8B83', textTransform: 'uppercase', letterSpacing: '0.07em', flexShrink: 0 }}>
                      {runTypeLabel}
                    </span>
                    {isNewest && group.runs.length > 1 && (
                      <span style={{ fontSize: '0.58rem', fontWeight: 700, color: '#1976D2', letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        · Latest
                      </span>
                    )}
                    <span style={{ fontSize: '0.58rem', fontWeight: 600, padding: '1px 6px', borderRadius: '8px', backgroundColor: badgeBg, color: badgeColor, whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 'auto' }}>
                      {statusLabel}
                    </span>
                  </div>
                  {/* Date row */}
                  <div style={{ fontSize: '0.8rem', fontWeight: isNewest ? 700 : 600, color: '#3E2723', marginBottom: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {formatDateShort(session.session_date || session.created_at)}
                  </div>
                  {/* Progress + counter name */}
                  <div style={{ fontSize: '0.7rem', color: '#8B7B73', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {counted}/{total} counted{session.created_by_name ? ` · ${session.created_by_name}` : ''}
                  </div>
                  {/* Progress bar */}
                  <div style={{ height: '4px', borderRadius: '2px', backgroundColor: '#EDE6DE', overflow: 'hidden', marginBottom: '8px' }}>
                    <div style={{ height: '100%', width: `${pct}%`, backgroundColor: statusColor, borderRadius: '2px', transition: 'width 0.3s' }} />
                  </div>
                  {/* Primary action */}
                  <div style={{ marginBottom: '4px' }}>{primaryBtn}</div>
                  {/* Status line — shown for review-state runs (completed but not fully counted) */}
                  {session.status === 'completed' && (total === 0 || counted < total) && (
                    <div style={{ fontSize: '0.68rem', color: '#8B7B73', marginBottom: '6px', fontStyle: 'italic' }}>
                      {counted}/{total} counted · Needs review
                    </div>
                  )}
                  {session.status === 'completed' && (
                    <div style={{ marginBottom: '6px' }}>
                      <CountCardAvailableReports session={session} onOpenReport={openSessionReport} compact />
                    </div>
                  )}
                  {/* Overflow menu */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <PortalCardMenu
                      menuKey={session.id}
                      openMenuId={openMenuId}
                      setOpenMenuId={setOpenMenuId}
                      buttonStyle={{ fontSize: '0.95rem', padding: '6px 12px', border: '1px solid #E8DDD0', borderRadius: '6px', backgroundColor: '#fff', color: '#8B7B73', cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}
                      items={[
                        { icon: '✏️', label: 'Edit Count', action: () => { setOpenMenuId(null); handleEditSession(session); } },
                        { icon: '👁️', label: 'Preview', action: () => { setOpenMenuId(null); handlePreviewItems(session); } },
                        ...(session.status !== 'completed' && !allCounted ? [{ icon: '✅', label: 'Mark Complete', action: () => { setOpenMenuId(null); handleMarkComplete(session); } }] : []),
                        { icon: '📦', label: 'Archive', action: () => { setOpenMenuId(null); handleArchiveSession(session); } },
                        ...superAdminCardMenuItems(session),
                      ]}
                      minWidth="150px"
                    />
                  </div>
                </div>
              );
            };

            const mcoConnL = '1px';
            const mcoSpine = '2px solid #D9CEC2';
            const mcoEventPalette = {
              started:      { node: '#4FC3F7', text: '#0277BD', label: 'Started' },
              completed:    { node: '#66BB6A', text: '#2E7D32', label: 'Completed' },
              notCompleted: { node: '#F9A825', text: '#8D6708', label: 'Not completed' },
              inProgress:   { node: '#1976D2', text: '#0D47A1', label: 'In progress' },
            };
            const mcoEventFmt = (d) => {
              const dt = new Date(d);
              return `${dt.getMonth() + 1}/${dt.getDate()} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
            };
            // Demoted event markers: small dot on spine + compact muted text.
            // These are secondary metadata — they must not compete visually with the white run cards.
            const mcoRenderEventMarker = (kind, dateStr) => {
              const p = mcoEventPalette[kind] || mcoEventPalette.started;
              return (
                <div style={{ marginLeft: mcoConnL, borderLeft: mcoSpine, position: 'relative', padding: '3px 0 3px 14px' }}>
                  <div style={{ position: 'absolute', left: '-4px', top: '50%', transform: 'translateY(-50%)', width: '7px', height: '7px', borderRadius: '50%', backgroundColor: p.node, boxShadow: '0 0 0 2px #F0E8DF' }} />
                  <span style={{ fontSize: '0.69rem', fontWeight: 600, color: p.text, opacity: 0.75, whiteSpace: 'nowrap' }}>
                    {p.label}{dateStr ? <span style={{ fontWeight: 400, opacity: 0.7 }}> · {dateStr}</span> : null}
                  </span>
                </div>
              );
            };
            const mcoRenderLeadIn = () => (
              <div style={{ marginLeft: mcoConnL, borderLeft: mcoSpine, height: '5px' }} />
            );
            const mcoRenderTerminator = (group) => {
              const isOrder = group.isOrder;
              const template = group.template;
              const ghostLabel = `Next ${isOrder ? 'order' : 'count'} will appear here`;
              const triggerNew = () => handleCountAgain(template);
              return (
                <>
                  <div style={{ marginLeft: mcoConnL, borderLeft: mcoSpine, height: '22px', position: 'relative' }}>
                    <div style={{ position: 'absolute', left: '-4px', bottom: '2px', width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '6px solid #7BC67E' }} />
                  </div>
                  <div style={{ marginLeft: mcoConnL, marginRight: '1px', marginBottom: '6px' }}>
                    <div role="button" tabIndex={0} onClick={triggerNew}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); triggerNew(); } }}
                      style={{ borderRadius: '999px', border: '1.5px dashed #D9CEC2', padding: '10px 20px', backgroundColor: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '10px', color: '#A89890', fontSize: '1.3rem', fontStyle: 'italic', opacity: 0.75, outline: 'none' }}>
                      <span style={{ width: '21px', height: '21px', borderRadius: '50%', border: '1.5px dashed #C9BCAE', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.27rem', fontStyle: 'normal', fontWeight: 600, color: '#A89890', lineHeight: 1, flexShrink: 0 }}>+</span>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ghostLabel}</span>
                    </div>
                  </div>
                </>
              );
            };

            return (
              <>
                {stackGroups.length > 0 && (
                  <SessionTimelineStack
                    isMobile={true}
                    groups={stackGroups}
                    expandedRunsSet={expandedStackChildren}
                    onToggleExpandedRuns={toggleStackChildrenExpansion}
                    formatDate={formatDate}
                    expandedOrder="oldest-first"
                    runsContainerPadding="0 0 8px"
                    containerSpine={false}
                    runsAreaTopBorder
                    runsAreaBackground="#FAF6F2"
                    suppressHiddenRunsControl
                    renderTimelineStart={(group, { hiddenCount, isExpanded, onToggle } = {}) => {
                      const historyLabel = group.isOrder ? 'Order History' : group.isEOP ? 'EOP History' : 'Count History';
                      const chipNoun = group.isOrder ? 'order' : 'count';
                      // Compact month range (3-letter abbreviations) covering the
                      // collapsed earlier counts within the last 12 months.
                      const chipMonths = hiddenCount > 0 ? earlierRunsMonthLabel(group.runs.slice(1)) : '';
                      const chip = hiddenCount > 0 ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onToggle && onToggle(); }}
                          aria-expanded={isExpanded}
                          aria-label={isExpanded
                            ? `Show only the latest ${chipNoun}`
                            : `Show ${hiddenCount} earlier ${chipNoun}${hiddenCount === 1 ? '' : 's'}${chipMonths ? ` from ${chipMonths}` : ''}`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            fontSize: '0.8rem', fontWeight: 700, color: '#5A4A42', whiteSpace: 'nowrap',
                            background: '#FAF6F2', border: '1px solid #C9BCAE',
                            borderRadius: '999px', padding: '6px 12px',
                            minHeight: '44px', minWidth: '44px', justifyContent: 'center',
                            cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
                            WebkitTapHighlightColor: 'transparent',
                          }}
                        >
                          {isExpanded
                            ? <>Latest only <span aria-hidden="true" style={{ fontSize: '0.63rem' }}>▾</span></>
                            : <>+{hiddenCount} {chipNoun}{hiddenCount === 1 ? '' : 's'}{chipMonths ? ` · ${chipMonths}` : ''} <span aria-hidden="true" style={{ fontSize: '0.63rem' }}>▸</span></>}
                        </button>
                      ) : null;
                      return (
                        <>
                          {group.template ? (
                            <div style={{ padding: '7px 10px 4px 10px', display: 'flex', alignItems: 'center', gap: '8px', minHeight: '44px' }}>
                              <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#5A4A42', letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {historyLabel}
                              </span>
                              <div style={{ flex: 1, height: '1px', backgroundColor: '#D9CEC2' }} />
                              {chip}
                            </div>
                          ) : (
                            chip && (
                              <div style={{ padding: '7px 10px 4px 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minHeight: '44px' }}>
                                {chip}
                              </div>
                            )
                          )}
                          {mcoRenderLeadIn()}
                        </>
                      );
                    }}
                    renderChildlessTerminator={(group) => mcoRenderTerminator(group)}
                    renderHeader={(group, latestRun) => {
                      const isTemplateParent = !!group.template;
                      const target = group.template || group.runs[0];
                      // Template edited after the newest run was created → show the
                      // static "Updated" pill (the old crossfading button label was
                      // retired — it read as a rendering glitch).
                      const templateUpdated = !!(isTemplateParent && latestRun
                        && group.template.updated_at && latestRun.created_at
                        && new Date(group.template.updated_at).getTime() > new Date(latestRun.created_at).getTime() + 60 * 1000);
                      // Setup never gates the button any more: PARs are set inside
                      // the count and the schedule is inferred when the count is
                      // marked Complete. Both remain editable from ⚙ Template Setup.
                      const mcoPrimaryLabel = group.isOrder ? 'New Order' : 'New Count';
                      const mcoPrimaryColor = isTemplateParent ? '#1976D2' : '#43A047';
                      const mcoHandlePrimary = (e) => {
                        e.stopPropagation();
                        handleCountAgain(target);
                      };
                      return (
                        <div style={{
                          display: 'flex', flexDirection: 'column', gap: '3px',
                          padding: isTemplateParent ? '8px 10px 10px 10px' : '10px 10px',
                          borderRadius: isTemplateParent ? '9px 9px 0 0' : undefined,
                          position: isTemplateParent ? 'relative' : undefined,
                        }}>
                          {/* Row 1: icon + title (full width, up to 2 lines) */}
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
                            <span style={{ fontSize: '1rem', flexShrink: 0, lineHeight: 1.3, paddingTop: '1px' }}>
                              {group.isOrder ? '🚚' : group.isEOP ? '📅' : '📋'}
                            </span>
                            <span style={{
                              fontWeight: 700, fontSize: '1rem', color: '#3E2723',
                              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                              overflow: 'hidden', flex: 1, minWidth: 0, lineHeight: 1.3,
                            }}>
                              {group.name}
                            </span>
                          </div>
                          {/* Row 2: schedule / frequency (frequency label only) */}
                          <div style={{ fontSize: '0.94rem', color: '#8B7B73', paddingLeft: 'calc(1rem + 7px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {isTemplateParent
                              ? (() => {
                                  // Only EOP templates inherit the store's signup-period schedule.
                                  // Vendor/order templates have their own cycle (or none), so they
                                  // must not be relabeled with the store EOP schedule.
                                  const cyc = group.template?.ordering_cycle_config
                                    || (group.isEOP ? inventoryPeriodConfigToCycle(storePeriodConfig) : null);
                                  const cycleLbl = cycleToFriendlyLabel(cyc);
                                  const _skipped = !!(cyc && cyc.preset === 'none' && cyc.skipped === true);
                                  if (_skipped) return <span style={{ fontStyle: 'italic', color: '#A89890' }}>No schedule</span>;
                                  return cycleLbl || 'Recurring';
                                })()
                              : latestRun ? `Latest: ${formatDate(latestRun.session_date || latestRun.created_at)}` : ''}
                          </div>
                          {/* Row 3: item / vendor / runs metadata */}
                          {(() => {
                            let metaText = null;
                            if (isTemplateParent) {
                              const itemCount = group.template?.total_items || group.runs[0]?.total_items;
                              const runCount = group.runs.length;
                              const parts = [];
                              if (itemCount) parts.push(`${itemCount} item${itemCount !== 1 ? 's' : ''}`);
                              parts.push(`${runCount} run${runCount !== 1 ? 's' : ''}`);
                              metaText = parts.join(' · ');
                            } else if (latestRun) {
                              const tot = latestRun.total_items || 0;
                              const cnt = latestRun.items_counted || 0;
                              if (tot > 0) metaText = `${cnt}/${tot} items`;
                            }
                            return metaText ? (
                              <div style={{ fontSize: '0.94rem', color: '#8B7B73', paddingLeft: 'calc(1rem + 7px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {metaText}
                              </div>
                            ) : null;
                          })()}
                          {/* Row 3b: created / last-used metadata. Mobile drops
                              the creator and uses compact dates so the single
                              line never stretches the narrow card. */}
                          {isTemplateParent && (() => {
                            const createdSrc = group.template || group.runs[group.runs.length - 1];
                            const lastUsedMs = Math.max(0, ...group.runs
                              .map(s => new Date(s.last_accessed_at || s.updated_at || s.created_at).getTime())
                              .filter(n => !Number.isNaN(n)));
                            const dateParts = [];
                            if (createdSrc?.created_at) dateParts.push(`Created ${formatDateShort(createdSrc.created_at)}`);
                            if (lastUsedMs > 0) dateParts.push(`Last used ${formatDateShort(lastUsedMs)}`);
                            return dateParts.length > 0 ? (
                              <div style={{ fontSize: '0.78rem', color: '#A89890', paddingLeft: 'calc(1rem + 7px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {dateParts.join(' · ')}
                              </div>
                            ) : null;
                          })()}
                          {/* Row 4: badge row — TEMPLATE, Nx, UPDATED; wraps as needed */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', paddingLeft: 'calc(1rem + 7px)', marginTop: '1px' }}>
                            {isTemplateParent && (
                              <span style={{
                                fontSize: '0.82rem', fontWeight: 700, padding: '2px 8px',
                                borderRadius: '6px', backgroundColor: '#EDE6DE', color: '#5A4A42',
                                letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap',
                              }}>
                                Template
                              </span>
                            )}
                            {group.runs.length > 1 && (
                              <span style={{
                                fontSize: '0.82rem', fontWeight: 700, padding: '2px 8px',
                                borderRadius: '10px', backgroundColor: '#EDE6DE', color: '#5A4A42',
                              }}>
                                {group.runs.length}×
                              </span>
                            )}
                            {templateUpdated && (
                              <span style={{
                                fontSize: '0.82rem', fontWeight: 700, padding: '2px 8px',
                                borderRadius: '8px', backgroundColor: '#FFF3E0', color: '#E65100',
                                border: '1px solid #FFB74D', letterSpacing: '0.04em', textTransform: 'uppercase',
                              }}>
                                Updated
                              </span>
                            )}
                          </div>
                          {/* Row 5: primary action (full-width) */}
                          <div style={{ paddingLeft: 'calc(1rem + 7px)', marginTop: '4px' }}>
                            <TemplateActionClusterWithConnector
                              isMobileOverview={true}
                              isTemplateParent={isTemplateParent}
                              showConnector={isTemplateParent}
                              clusterRow={true}
                              templateChip={null}
                              buttonElement={
                                <button onClick={mcoHandlePrimary}
                                  style={{
                                    fontSize: '1.1rem', fontWeight: 700, padding: '14px 0', borderRadius: '999px',
                                    minHeight: '48px', width: '100%',
                                    border: 'none',
                                    backgroundColor: mcoPrimaryColor,
                                    color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                                  }}>
                                  {mcoPrimaryLabel}
                                </button>
                              }
                              menuElement={null}
                            />
                            {/* Secondary row — stacked below primary, separated from primary tap target */}
                            {isTemplateParent && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px', alignItems: 'center' }}
                                onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => handleEditTemplate(group.template)}
                                  style={{ fontSize: '0.9rem', height: '36px', border: '1px solid #D9CEC2', borderRadius: '8px', backgroundColor: '#fff', color: '#5A4A42', padding: '0 14px', cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', fontFamily: 'inherit', fontWeight: 600 }}
                                  title="Edit this template"
                                >
                                  <span className="mm-tplcard__review">Review template<span aria-hidden="true" className="mm-tplcard__review-chev">›</span></span>
                                </button>
                                {/* The primary button used to divert here when setup
                                    was pending; it no longer does, so this is the way
                                    into Template Setup on this surface. */}
                                <button
                                  onClick={() => {
                                    const cyc = group.template?.ordering_cycle_config;
                                    const isSkipped = !!(cyc && cyc.preset === 'none' && cyc.skipped === true);
                                    const hasCycle = !!(cyc && cyc.preset && !isSkipped);
                                    const eopInherits = group.isEOP && !!inventoryPeriodConfigToCycle(storePeriodConfig);
                                    const tplItems = sessionItemsCache[group.template.id] || [];
                                    openTemplateSetupPicker({
                                      template: group.template,
                                      parsMissing: group.isOrder && !group.template?.direct_order_mode
                                        && !group.template?.pars_setup_skipped
                                        && tplItems.length > 0 && isParsSetupNeeded(tplItems),
                                      scheduleMissing: !hasCycle && !isSkipped && !eopInherits,
                                      isOrder: group.isOrder,
                                      isEOP: group.isEOP,
                                    });
                                  }}
                                  style={{ fontSize: '0.9rem', height: '36px', border: '1px solid #D9CEC2', borderRadius: '8px', backgroundColor: '#fff', color: '#5A4A42', padding: '0 14px', cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', fontFamily: 'inherit', fontWeight: 600 }}
                                  title="Schedule and PAR levels for this template"
                                >
                                  ⚙ Setup
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }}
                    renderRunRow={(session, { isFirst, isLast, index, runsToDisplay }) => {
                      const grp = stackGroups.find(g => g.runs.some(r => r.id === session.id));
                      const template = grp?.template || null;
                      const endTsFor = (s) => s.completed_at || (s.status === 'in_progress' ? (s.last_accessed_at || s.updated_at) : null);
                      const prevSession = isFirst ? null : runsToDisplay[index - 1];
                      const prevEndKind = !isFirst && (prevSession.completed_at ? 'completed' : prevSession.status === 'in_progress' ? 'inProgress' : 'notCompleted');
                      const prevEndTs = !isFirst ? endTsFor(prevSession) : null;
                      const curEndKind = session.completed_at ? 'completed' : session.status === 'in_progress' ? 'inProgress' : 'notCompleted';
                      const curEndTs = endTsFor(session);
                      return (
                        <div key={session.id}>
                          {!isFirst && mcoRenderEventMarker(prevEndKind, prevEndTs ? mcoEventFmt(prevEndTs) : null)}
                          {mcoRenderEventMarker('started', mcoEventFmt(session.created_at))}
                          <div style={{ marginLeft: mcoConnL, marginRight: '1px' }}>
                            {mobileRunRow(session, grp)}
                          </div>
                          {isLast && (
                            <>
                              {mcoRenderEventMarker(curEndKind, curEndTs ? mcoEventFmt(curEndTs) : null)}
                              {grp && mcoRenderTerminator(grp)}
                            </>
                          )}
                        </div>
                      );
                    }}
                  />
                )}
                {singletonGroups.length > 0 && (
                  <div>{singletonGroups.flatMap(g => g.runs).map(session => renderMobileCard(session))}</div>
                )}
              </>
            );
          };
          // ──────────────────────────────────────────────────────────────────
          return (
            <>
              {nonOrderSections.slice(0, 1).map(({ key, title, type, sessions: sectionSessions }) => (
                <div key={key} id={sectionAnchorIds[key]} className="mco-section">
                  <div className="mco-section-header" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0 8px' }}>
                    <span className="mco-section-title" style={{ fontSize: '1.55rem', fontWeight: 800, color: '#3E2723', display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0, letterSpacing: '-0.01em' }}>
                      {title}
                      <span className="mco-section-count" style={{ fontSize: '1.1rem', fontWeight: 800, color: '#5A4A42', padding: '2px 10px', borderRadius: '12px', backgroundColor: '#EDE6DE' }}>{sectionSessions.length}</span>
                    </span>
                    <button style={{ marginLeft: 'auto', flexShrink: 0, fontSize: '0.55rem', fontWeight: 700, padding: '3px 9px', borderRadius: '999px', border: 'none', backgroundColor: '#8B7B73', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onClick={() => window.location.href = `/count.html?view=count-creation&type=${type}`}>New Count Template</button>
                  </div>
                  <div className="mco-scroll-area">
                    {renderMobileGroupedList(sectionSessions)}
                  </div>
                </div>
              ))}

              <div id="mco-section-orders" className="mco-section">
                <div className="mco-section-header" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0 8px' }}>
                  <span className="mco-section-title" style={{ fontSize: '1.55rem', fontWeight: 800, color: '#3E2723', display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0, letterSpacing: '-0.01em' }}>
                    🚚 Orders
                    <span className="mco-section-count" style={{ fontSize: '1.1rem', fontWeight: 800, color: '#5A4A42', padding: '2px 10px', borderRadius: '12px', backgroundColor: '#EDE6DE' }}>{orderSessions.length}</span>
                  </span>
                  <button style={{ marginLeft: 'auto', flexShrink: 0, fontSize: '0.55rem', fontWeight: 700, padding: '3px 9px', borderRadius: '999px', border: 'none', backgroundColor: '#8B7B73', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    onClick={() => window.location.href = `/count.html?view=count-creation&type=order`}>New Order Template</button>
                </div>
                <div className="mco-scroll-area">
                  {orderSessions.length === 0 ? (
                    <div className="mco-empty" style={{ fontSize: '0.75rem' }}>No order counts yet</div>
                  ) : orderedDays.map(({ dayIdx, label, sessions: daySessions }) => (
                    <div key={dayIdx}>
                      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#8B7B73', padding: '8px 4px 2px', textTransform: 'uppercase', letterSpacing: '0.04em',
                        borderBottom: '1px solid #E8DDD0', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {label}
                        <span style={{ fontSize: '0.58rem', fontWeight: 600, padding: '1px 6px', borderRadius: '8px', backgroundColor: '#F5F0EB', color: '#8B7B73' }}>{daySessions.length}</span>
                      </div>
                      {renderMobileGroupedList(daySessions)}
                    </div>
                  ))}
                </div>
              </div>

              {nonOrderSections.slice(1).map(({ key, title, type, sessions: sectionSessions }) => (
                <div key={key} id={sectionAnchorIds[key]} className="mco-section">
                  <div className="mco-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '4px 0 6px' }}>
                    <span className="mco-section-title" style={{ fontSize: '1.55rem', fontWeight: 800, color: '#3E2723', display: 'inline-flex', alignItems: 'center', gap: '6px', letterSpacing: '-0.01em' }}>{title}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <span className="mco-section-count" style={{ fontSize: '1.1rem', fontWeight: 800, color: '#5A4A42', padding: '2px 10px', borderRadius: '12px', backgroundColor: '#EDE6DE' }}>{sectionSessions.length}</span>
                      <button style={{ fontSize: '0.55rem', fontWeight: 700, padding: '3px 9px', borderRadius: '999px', border: 'none', backgroundColor: '#8B7B73', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        onClick={() => window.location.href = `/count.html?view=count-creation&type=${type}`}>New Count Template</button>
                    </div>
                  </div>
                  <div className="mco-scroll-area">
                    {renderMobileGroupedList(sectionSessions)}
                  </div>
                </div>
              ))}
            </>
          );
        })()}

        {/* Preview Items Modal */}
        {previewSession && (
          <div className="mco-modal-overlay" onClick={() => setPreviewSession(null)}>
            <div className="mco-modal" onClick={(e) => e.stopPropagation()}>
              <div className="mco-modal-header">
                <span>👁️ {previewSession.name}</span>
                <button onClick={() => setPreviewSession(null)}>&times;</button>
              </div>
              <div className="mco-modal-meta">{previewSession.total_items || 0} items &middot; {previewSession.items_counted || 0} counted</div>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', padding: '6px 0 2px' }}>
                {[['default','Default'],['vendor','Vendor'],['location','Location'],['az','A–Z'],['counted','Counted']].map(([key, label]) => (
                  <button key={key} onClick={() => setPreviewSort(key)}
                    style={{ fontSize: '0.7rem', fontWeight: previewSort === key ? 700 : 500, padding: '3px 9px', borderRadius: '12px', border: `1px solid ${previewSort === key ? '#8B7B73' : '#D9CEC2'}`, backgroundColor: previewSort === key ? '#5A4A42' : '#FAF6F2', color: previewSort === key ? '#FEFCFA' : '#5A4A42', cursor: 'pointer', transition: 'all 0.12s' }}>
                    {label}
                  </button>
                ))}
              </div>
              {previewLoading ? (
                <div className="mco-empty">Loading items...</div>
              ) : previewItems.length === 0 ? (
                <div className="mco-empty">No items</div>
              ) : (
                <div className="mco-modal-items">
                  {sortPreviewItems(previewItems, previewSort).map((item, idx) => {
                    const snap = item.item_snapshot || {};
                    const name = snap.description || snap.name || item.description || item.name || 'Unknown';
                    const isCounted = item.is_counted || (item.counted_quantity != null && item.counted_quantity > 0);
                    return (
                      <div key={item.id ?? idx} className="mco-modal-item">
                        <span>{isCounted ? '✅' : '⬜'}</span>
                        <span className="mco-modal-item-name">{name}</span>
                        {renderVendorPurchaseBadge(item, snap, name) || ((snap.vendor || item.vendor) && <span style={{ fontSize: '0.72rem', color: '#8B7B73', marginLeft: '4px' }}>{snap.vendor || item.vendor}</span>)}
                        {renderPreparedBadge(item, snap)}
                      {snap.is_subrecipe === true && !isPreparedSnapshot(snap, item) && <span style={SUB_RECIPE_PILL_STYLE}>Sub-recipe</span>}
                        {snap.brand && <span style={{ fontSize: '0.72rem', color: '#8B7B73', marginLeft: '4px' }}>{snap.brand}</span>}
                        {snap.category && <span style={{ fontSize: '0.68rem', color: '#A89890', marginLeft: '4px', fontStyle: 'italic' }}>[{snap.category}]</span>}
                        {(snap.location || item.location) && <span style={{ fontSize: '0.68rem', color: '#A89890', marginLeft: '4px' }}>({snap.location || item.location})</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        {vendorProductsModal}
        {packageCountSheet}
        {parTransitionModal}
        {packageSwitchModal}
        {appModalConfig && (
          <CustomModal
            isOpen={true}
            onClose={closeAppModal}
            title={appModalConfig.title}
            message={appModalConfig.message}
            type={appModalConfig.type}
            onConfirm={handleAppModalConfirm}
            confirmText={appModalConfig.confirmText}
            cancelText={appModalConfig.cancelText}
            confirmButtonStyle={appModalConfig.confirmButtonStyle}
          />
        )}
        <BackdateCopyModal
          modal={backdateCopyModal}
          onChangeDate={(value) => setBackdateCopyModal((m) => (m ? { ...m, date: value } : m))}
          onToggleRandomize={(value) => setBackdateCopyModal((m) => (m ? { ...m, randomize: value } : m))}
          onCancel={() => setBackdateCopyModal(null)}
          onConfirm={handleBackdateCopyConfirm}
        />
        {endOfRoomModal && (
          <CustomModal
            isOpen={true}
            onClose={() => setEndOfRoomModal(null)}
            title={endOfRoomModal.nextRoom ? 'Location complete' : 'All done!'}
            message={endOfRoomModal.nextRoom
              ? `All items in this location have been counted. Advance to "${endOfRoomModal.nextRoom.label}"?`
              : 'All items in this location have been counted. The session is fully counted!'}
            type={endOfRoomModal.nextRoom ? 'confirm' : 'alert'}
            onConfirm={endOfRoomModal.nextRoom ? handleEndOfRoomAdvance : undefined}
            confirmText="Advance to next room"
            cancelText="Stay in this location"
            confirmButtonStyle="primary"
          />
        )}
        {embeddedReport && (
          <Reports
            embedded
            initialReportType={embeddedReport.type}
            initialSessionIds={embeddedReport.sessionIds}
            onEmbeddedClose={() => setEmbeddedReport(null)}
          />
        )}
      </div>
    );
  }
  // ─── End Mobile Overview ──────────────────────────────────────



  return (
    <div className="count-interface-root container-fluid" style={{
      backgroundColor: '#FEFCFA',
      paddingTop: '0px',
      paddingLeft: '0px',
      paddingRight: '0px',
      paddingBottom: isMobileOverview ? '76px' : '1rem'
    }}>
      {embeddedReport && (
        <Reports
          embedded
          initialReportType={embeddedReport.type}
          initialSessionIds={embeddedReport.sessionIds}
          onEmbeddedClose={() => setEmbeddedReport(null)}
        />
      )}
      <div className="card" style={{
        backgroundColor: '#FFFFFF',
        border: isMobileOverview ? 'none' : '1px solid #FAF6F2',
        borderRadius: isMobileOverview ? '0px' : undefined,
        boxShadow: isMobileOverview ? 'none' : undefined
      }}>
        <div className="card-body" style={{
          paddingTop: '0px',
          paddingLeft: isMobileOverview ? '1px' : '0px',
          paddingRight: isMobileOverview ? '1px' : '0px',
          paddingBottom: '0px'
        }}>
          {(
            <>
              {/* The old "Select a Count Session" subheading was removed — the
                  greeting line inside the list covers it with warmer copy. */}

              {/* Mobile-only collapsible search/filter panel — shown when showSearchPanel is true.
                  The sticky positioning lives on the outer wrapper because an `overflow: hidden`
                  ancestor (inside AnimatedSearchPanel, needed for the slide animation) would
                  otherwise break `position: sticky`. */}
              {isMobileOverview && detailGroupId == null && (
                <div style={{ position: 'sticky', top: '56px', zIndex: 30 }}>
                <AnimatedSearchPanel open={showSearchPanel}>
                  <div className="mb-3" style={{ maxWidth: '900px', backgroundColor: '#FFFFFF', border: '1px solid #E8DDD0', borderRadius: '10px', padding: '8px', boxShadow: 'rgba(0,0,0,0.04) 0 2px 6px', position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setShowSearchPanel(false)}
                      aria-label="Close search"
                      title="Close search"
                      style={{ position: 'absolute', top: '4px', right: '4px', width: '28px', height: '28px', border: 'none', backgroundColor: 'transparent', color: '#8B7B73', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}>
                      ×
                    </button>
                    <div className="mb-2" style={{ paddingRight: '32px' }}>
                      <input type="text" className="form-control form-control-sm" placeholder="Search count cards..." value={searchText} onChange={(e) => setSearchText(e.target.value)} style={{ borderColor: '#D4C4B0', color: '#5A4A42', fontSize: '0.85rem', minHeight: '40px' }} autoFocus />
                    </div>
                    <div className="d-flex gap-2">
                      <select className="form-select form-select-sm" value={filterByStatus} onChange={(e) => setFilterByStatus(e.target.value)} style={{ borderColor: '#D4C4B0', color: '#5A4A42', fontSize: '0.78rem', minHeight: '36px', paddingRight: '1.6rem' }}>
                        <option value="all">All</option><option value="draft">Draft</option><option value="in_progress">In Process</option><option value="completed">Completed</option>
                      </select>
                    </div>
                  </div>
                </AnimatedSearchPanel>
                </div>
              )}

              {/* Sort and Filter Controls — desktop, collapsible via search icon in chip row */}
              {!isMobileOverview && detailGroupId == null && (
                <AnimatedSearchPanel open={showSearchPanel}>
                <div className="mb-3" style={{
                  maxWidth: '900px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E8DDD0',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  boxShadow: 'rgba(0,0,0,0.04) 0 2px 6px',
                }}>
                  <div className="d-flex flex-wrap align-items-center gap-2" style={{ maxWidth: '720px' }}>
                    <select className="form-select form-select-sm" value={filterByOrder} onChange={(e) => setFilterByOrder(e.target.value)} style={{ borderColor: '#D4C4B0', color: '#8B7B73', fontSize: '0.68rem', width: 'auto', minWidth: '118px', maxWidth: '160px', paddingTop: '0.2rem', paddingBottom: '0.2rem' }}>
                      <option value="all">All Orders</option>
                      <option value="order">Order Enabled</option>
                      <option value="no-order">No Order</option>
                    </select>
                    <select className="form-select form-select-sm" value={filterByStatus} onChange={(e) => setFilterByStatus(e.target.value)} style={{ borderColor: '#D4C4B0', color: '#8B7B73', fontSize: '0.68rem', width: 'auto', minWidth: '118px', maxWidth: '160px', paddingTop: '0.2rem', paddingBottom: '0.2rem' }}>
                      <option value="all">All Status</option>
                      <option value="draft">Draft</option>
                      <option value="in_progress">In Process</option>
                      <option value="completed">Completed</option>
                    </select>
                    <select className="form-select form-select-sm" value={filterByStack} onChange={(e) => setFilterByStack(e.target.value)} style={{ borderColor: '#D4C4B0', color: '#8B7B73', fontSize: '0.68rem', width: 'auto', minWidth: '118px', maxWidth: '160px', paddingTop: '0.2rem', paddingBottom: '0.2rem' }}>
                      <option value="all">All Cards</option>
                      <option value="stacked">Runs Only</option>
                      <option value="single">Single Only</option>
                    </select>
                    <input type="text" className="form-control form-control-sm flex-grow-1" placeholder="Search sessions..." value={searchText} onChange={(e) => setSearchText(e.target.value)}
                      style={{ borderColor: '#D4C4B0', color: '#8B7B73', fontSize: '0.68rem', minWidth: '140px', maxWidth: '280px', paddingTop: '0.2rem', paddingBottom: '0.2rem' }} autoFocus />
                  </div>
                </div>
                </AnimatedSearchPanel>
              )}

              {/* Jump-to section navigation. Waits on the sessions read like
                  the list does — chips that scroll to sections which do not
                  exist yet are the piecemeal paint this shimmer exists to end. */}
              {sessionsLoaded && !isMobileOverview && detailGroupId == null && (
                <div style={{ maxWidth: '900px', marginBottom: '10px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {/* "📋 Other Counting" chip intentionally stashed — section hidden for a future version */}
                  {[
                    { label: '📅 End of Period', anchor: 'section-end-of-period' },
                    { label: '🚚 Ordering', anchor: 'section-orders' },
                  ].map(({ label, anchor }) => (
                    <button key={anchor} onClick={() => { const el = document.getElementById(anchor); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                      style={{ fontSize: '0.72rem', fontWeight: 600, padding: '4px 12px', borderRadius: '16px', border: '1px solid #D9CEC2', backgroundColor: '#FAF6F2', color: '#5A4A42', cursor: 'pointer' }}>
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {/* Keyframe for the "New Count Edited" pulse on the template
                  header primary action. count.html does not link the tailwind
                  bundle so the rule is injected inline. */}
              <style>{`@keyframes mmTemplateUpdatedPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(255,152,0,0.55); } 50% { box-shadow: 0 0 0 6px rgba(255,152,0,0); } } @keyframes mmFreshStartPulse { 0%, 100% { background-color: #43A047; } 50% { background-color: #1B5E20; } }`}</style>

              {/* Template summary card chrome. Real CSS (not inline styles) so
                  hover, :focus-visible and prefers-reduced-motion work — the
                  card's discoverable "View details & history ⌄" action must never
                  depend on hover (phones and tablets have none), so hover only
                  reinforces it. count.html does not link the tailwind bundle,
                  so the rules are injected here. */}
              <style>{`
                .mm-tplcard {
                  background-color: ${OV_CARD_BG};
                  border: 1px solid ${OV_LINE};
                  border-radius: 14px;
                  box-shadow: 0 1px 4px rgba(90,74,66,0.08);
                  transition: box-shadow 0.15s ease, border-color 0.15s ease;
                }
                .mm-tplcard:hover { box-shadow: 0 3px 10px rgba(90,74,66,0.14); border-color: #DCCFC0; }
                .mm-tplcard:focus-within { box-shadow: 0 3px 10px rgba(90,74,66,0.14); }
                /* The card body: one control holding the whole descriptive
                   column, so tapping the icon, the name, the metadata or the
                   trailing chevron all expand the template's inline details.
                   It is a div with role=button (not a <button>) so the "View
                   details & history" label can be a real sibling button instead
                   of nesting interactive elements. Reset to look like card
                   content, not a control. */
                .mm-tplcard__body {
                  display: flex;
                  align-items: flex-start;
                  gap: 10px;
                  flex: 1;
                  min-width: 0;
                  margin: 0;
                  padding: 0;
                  border: none;
                  background: none;
                  font: inherit;
                  color: inherit;
                  text-align: left;
                  cursor: pointer;
                  border-radius: 10px;
                }
                .mm-tplcard__body:focus-visible,
                .mm-tplcard__primary:focus-visible { outline: 3px solid #2E5E86; outline-offset: 3px; }
                .mm-tplcard__emoji { font-size: 1.5rem; line-height: 1.2; flex-shrink: 0; }
                .mm-tplcard__text { display: block; flex: 1; min-width: 0; }
                /* Long names and long creator/date metadata wrap onto another
                   line — the card grows taller instead of clipping information. */
                .mm-tplcard__name {
                  display: block;
                  font-weight: 700;
                  color: ${OV_INK};
                  line-height: 1.3;
                  overflow-wrap: break-word;
                }
                .mm-tplcard__status {
                  display: flex;
                  align-items: center;
                  gap: 7px;
                  margin-top: 6px;
                  font-size: 0.85rem;
                  color: ${OV_INK_SOFT};
                }
                .mm-tplcard__dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
                .mm-tplcard__bar {
                  display: block;
                  margin-top: 8px;
                  max-width: 260px;
                  height: 5px;
                  border-radius: 999px;
                  background-color: #EDE6DE;
                  overflow: hidden;
                }
                .mm-tplcard__bar-fill {
                  display: block;
                  height: 100%;
                  border-radius: 999px;
                  background-color: ${OV_ATTN_DOT};
                  transition: width 0.3s ease;
                }
                .mm-tplcard__meta {
                  display: block;
                  margin-top: 4px;
                  font-size: 0.72rem;
                  line-height: 1.5;
                  color: #A89890;
                  overflow-wrap: break-word;
                }
                /* The always-visible secondary label — quiet next to the filled
                   primary button, and never a bare chevron. The base rule also
                   styles the "Review template ›" spans on the detail screen and
                   the stack/mobile cards; the summary card's expander is a real
                   button (aria-expanded / aria-controls), so its control-only
                   resets live under the button-qualified selector. */
                .mm-tplcard__review {
                  display: inline-flex;
                  align-items: center;
                  gap: 4px;
                  margin-top: 8px;
                  font-size: 0.82rem;
                  font-weight: 700;
                  color: ${OV_ACTION};
                }
                button.mm-tplcard__review {
                  margin: 8px 0 0 34px; /* aligns with the text column (emoji + gap) */
                  padding: 0;
                  border: none;
                  background: none;
                  font-family: inherit;
                  line-height: inherit;
                  cursor: pointer;
                  border-radius: 8px;
                }
                .mm-tplcard__review-chev { font-size: 1.05rem; line-height: 1; }
                .mm-tplcard__review:hover,
                .mm-tplcard__review:focus-visible { color: #A34E31; text-decoration: underline; }
                button.mm-tplcard__review:focus-visible { outline: 3px solid #2E5E86; outline-offset: 3px; }
                .mm-tplcard__chev { color: #C7B8AC; font-size: 1.2rem; line-height: 1; align-self: center; flex-shrink: 0; transition: transform 0.18s ease; }
                .mm-tplcard__chev--open { transform: rotate(90deg); }
                /* Inline details & history — the region the card body / label
                   expand. Top divider separates it from the summary; the inner
                   wrapper animates in on mount (it renders only while expanded,
                   so nothing is computed or painted for collapsed cards). */
                .mm-tplcard__details { margin-top: 14px; padding-top: 14px; border-top: 1px solid ${OV_LINE}; }
                .mm-tplcard__details-in { animation: mmTplCardDetailsIn 0.18s ease-out; }
                @keyframes mmTplCardDetailsIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
                .mm-tplcard__details-heading { font-size: 0.72rem; font-weight: 800; letter-spacing: 0.09em; text-transform: uppercase; color: ${OV_INK_SOFT}; }
                .mm-tplcard__runrow {
                  display: flex;
                  align-items: center;
                  gap: 12px;
                  padding: 9px 2px;
                  border-top: 1px solid #F0E7DC;
                }
                /* The descriptive lines stack (date+status, marked-completed,
                   counted-by, items counted); the action button sits alongside
                   on desktop and drops below them on phones. */
                .mm-tplcard__runbody { flex: 1; min-width: 0; }
                .mm-tplcard__runhead { display: flex; align-items: baseline; gap: 10px; }
                .mm-tplcard__runline { margin-top: 2px; }
                .mm-tplcard__runaction { flex-shrink: 0; }
                .mm-tplcard__details button:focus-visible { outline: 3px solid #2E5E86; outline-offset: 1px; }
                /* Recent activity tray — the same calm card chrome as the
                   template cards. Every row is one block-level button; hover
                   only reinforces, the label alone carries the meaning. */
                .mm-ract {
                  background-color: ${OV_CARD_BG};
                  border: 1px solid ${OV_LINE};
                  border-radius: 14px;
                  box-shadow: 0 1px 4px rgba(90,74,66,0.08);
                }
                .mm-ract__row {
                  display: flex;
                  align-items: center;
                  gap: 10px;
                  width: 100%;
                  margin: 0;
                  border: none;
                  background: none;
                  font: inherit;
                  color: inherit;
                  text-align: left;
                  cursor: pointer;
                  border-radius: 10px;
                  transition: background-color 0.15s ease;
                }
                .mm-ract__row:hover { background-color: #F7F1EA; }
                .mm-ract__row:focus-visible,
                .mm-ract__toggle:focus-visible,
                .mm-ract__viewall:focus-visible { outline: 3px solid #2E5E86; outline-offset: 3px; }
                @media (max-width: 768px) {
                  /* Phone: the label sits in a taller strip so it stays an easy
                     target, well clear of the primary button below it. */
                  .mm-tplcard__review { min-height: 44px; }
                  /* Runs become stacked mini-cards — no sideways scrolling. */
                  .mm-tplcard__runrow { display: block; padding: 12px 14px; border: 1px solid ${OV_LINE}; border-radius: 12px; margin-top: 8px; }
                  .mm-tplcard__runhead { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
                  .mm-tplcard__runline { margin-top: 3px; }
                  .mm-tplcard__runaction { margin-top: 10px; }
                  /* Every activity row is a full-width touch target. */
                  .mm-ract__row { min-height: 44px; }
                }
                @media (prefers-reduced-motion: reduce) {
                  .mm-tplcard, .mm-tplcard__bar-fill, .mm-tplcard__chev { transition: none; }
                  .mm-tplcard__details-in { animation: none; }
                  .mm-ract__row { transition: none; }
                }
              `}</style>

              {/* Search icon toggle. Default sort (Newest) and the All/Latest/Drafts
                  overview filter still drive list state internally; their inline
                  chips were removed in favor of the search panel. */}
              {sessionsLoaded && detailGroupId == null && (
              <div style={{ maxWidth: '900px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowSearchPanel(p => !p)}
                  title={showSearchPanel ? 'Close search & filters' : 'Search & filter'}
                  style={{ marginLeft: 'auto', flexShrink: 0, width: '46px', height: '46px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px', border: showSearchPanel ? `1px solid ${OV_ACTION}` : '1px solid #D9CEC2', backgroundColor: showSearchPanel ? '#F9EAE2' : '#fff', color: showSearchPanel ? OV_ACTION : '#8B7B73', cursor: 'pointer', padding: 0 }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </button>
              </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0', maxWidth: '900px' }} key={`sessions-${countSessions.length}`}>
              {!sessionsLoaded ? (
                // The templates arrive one query after the bundle does. Until
                // then this is the whole overview — the same shimmer the static
                // placeholder in count.html paints before React even mounts, so
                // the two read as one continuous load rather than a handover.
                <ShimmerLoader style={{ minHeight: '52vh', background: 'transparent' }} />
              ) : (() => {
                // Get pre-fetched category icons for a session (uses component-level getCategoryIcon + sessionCategoryIconCache)
                const getSessionCategoryIcons = (sessionId) => sessionCategoryIconCache[sessionId] || [];

                const groupedSessions = getGroupedSessions();
                const groupIds = Object.keys(groupedSessions);

                const truckIcon = (size = 14, color = ORDER_TITLE_COLOR) => (
                  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M1 3h15v13H1V3z" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
                    <path d="M16 8h4l3 4v4h-7V8z" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
                    <circle cx="5.5" cy="18.5" r="2.5" stroke={color} strokeWidth="2"/>
                    <circle cx="18.5" cy="18.5" r="2.5" stroke={color} strokeWidth="2"/>
                  </svg>
                );
                const calendarIcon = (size = 14, color = EOP_TITLE_COLOR) => (
                  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                    <rect x="3" y="5" width="18" height="16" rx="2" stroke={color} strokeWidth="2"/>
                    <path d="M3 10h18" stroke={color} strokeWidth="2"/>
                    <path d="M8 3v4" stroke={color} strokeWidth="2" strokeLinecap="round"/>
                    <path d="M16 3v4" stroke={color} strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                );
                const chevronIcon = (isOpen, color = '#8B7B73') => (
                  <svg width="10" height="10" viewBox="0 0 10 10" style={{ flexShrink: 0, transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', opacity: 0.5 }}>
                    <path d="M2.5 1L7.5 5L2.5 9" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                );

                const handleDuplicateWithGuard = (session, showStaleGuard) => {
                  if (showStaleGuard) {
                    (async () => {
                      const ok = await showAppConfirm({ title: 'Copy older run?', message: 'This is not the newest run. Copy this older version?', confirmText: 'Copy', cancelText: 'Cancel', confirmButtonStyle: 'warning' });
                      closeAppModal();
                      if (ok) handleCountAgain(session);
                    })();
                  } else handleCountAgain(session);
                };

                const expandCard = (sessionId) => {
                  markRunViewed(sessionId);
                  setExpandedCards(prev => {
                    const next = new Set(prev);
                    if (next.has(sessionId)) {
                      next.delete(sessionId);
                    } else {
                      next.add(sessionId);
                      if (!sessionItemsCache[sessionId] && cardPreviewStatus[sessionId] !== 'loading') {
                        setCardPreviewStatus(p => ({ ...p, [sessionId]: 'loading' }));
                        (async () => {
                          try {
                            const { data: items, error } = await supabase.from('count_items').select('*').eq('count_session_id', sessionId).order('id');
                            if (!error && items) {
                              setSessionItemsCache(prev2 => ({ ...prev2, [sessionId]: items }));
                              setCardPreviewStatus(p => ({ ...p, [sessionId]: 'success' }));
                            } else {
                              setCardPreviewStatus(p => ({ ...p, [sessionId]: 'error' }));
                            }
                          } catch (err) {
                            console.error('Error loading card preview items:', err);
                            setCardPreviewStatus(p => ({ ...p, [sessionId]: 'error' }));
                          }
                        })();
                      }
                    }
                    return next;
                  });
                };

                const statusBadge = (session, counted, total, allCounted) => {
                  const label = session.status === 'completed' || allCounted ? 'Complete' : session.status === 'paused' ? 'Saved' : session.status === 'in_progress' || counted > 0 ? 'In Progress' : 'Draft';
                  const bg = session.status === 'completed' || allCounted ? '#E8F5E9' : session.status === 'in_progress' || session.status === 'paused' || counted > 0 ? '#FFF3E0' : '#F5F5F5';
                  const fg = session.status === 'completed' || allCounted ? '#2E7D32' : session.status === 'in_progress' || session.status === 'paused' || counted > 0 ? '#E65100' : '#8B7B73';
                  return <span style={{ fontSize: '0.62rem', fontWeight: 600, padding: '2px 8px', borderRadius: '12px', backgroundColor: bg, color: fg, whiteSpace: 'nowrap' }}>{label}</span>;
                };

                const progressBar = (counted, total) => {
                  const pct = total > 0 ? Math.round((counted / total) * 100) : 0;
                  const color = pct >= 100 ? '#4CAF50' : pct > 0 ? '#FFA726' : '#D4C4B0';
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ flex: 1, height: '4px', borderRadius: '2px', backgroundColor: '#EDE6DE', overflow: 'hidden', minWidth: '36px' }}>
                        <div style={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: '2px', transition: 'width 0.3s' }} />
                      </div>
                      <span style={{ fontSize: '0.68rem', color: '#8B7B73', whiteSpace: 'nowrap', minWidth: '32px', textAlign: 'right' }}>{counted}/{total}</span>
                    </div>
                  );
                };

                const actionBtnBase = { fontSize: '0.68rem', height: '28px', minWidth: '52px', border: 'none', color: '#fff', borderRadius: '6px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', cursor: 'pointer' };
                // Template primary pills are sized as a clear primary action — ~50%
                // larger than the surrounding session action buttons.
                const templatePillBtnBase = { ...actionBtnBase, fontSize: '0.78rem', height: '32px', minWidth: '60px' };

                const actionBtn = (session, counted, total, allCounted, someCounted, { showStaleGuard = false, pulseFresh = false } = {}) => {
                  const isInProgress = session.status === 'in_progress' || session.status === 'paused';
                  const isDraft = session.status === 'draft';
                  const isCompleted = session.status === 'completed';
                  const runIsOrder = !!session.order_calculations_enabled;
                  // Run-state primary button labels:
                  //   completed, all items counted → "View Count" / "View Order"
                  //   completed, not all items counted → "Review" (needs review)
                  //   in_progress → "Resume Count" / "Resume Order"
                  //   draft → "Start Count" / "Start Order"
                  // Web/mobile parity: green = ready, orange = resume/in-progress, grey = finalized.
                  if (isCompleted) {
                    // A zero-item run is considered fully counted (nothing to review).
                    // "Review" only applies when items exist but some weren't counted.
                    const countRatioComplete = total === 0 || counted >= total;
                    const label = countRatioComplete
                      ? (runIsOrder ? 'View Order' : 'View Count')
                      : 'Review';
                    return (
                      <button className="btn btn-sm" style={{ ...actionBtnBase, backgroundColor: '#757575', padding: '0 9px' }}
                        onClick={(e) => { e.stopPropagation(); markRunViewed(session.id); handleEditSession(session); }}>
                        {label}
                      </button>
                    );
                  }
                  if (isInProgress) {
                    const label = runIsOrder ? 'Resume Order' : 'Resume Count';
                    return (
                      <button className="btn btn-sm" style={{ ...actionBtnBase, backgroundColor: '#FB8C00', padding: '0 9px' }}
                        onClick={(e) => { e.stopPropagation(); markRunViewed(session.id); window.location.href = `count-session.html?session=${session.id}`; }}>
                        {label}
                      </button>
                    );
                  }
                  if (isDraft) {
                    const label = runIsOrder ? 'Start Order' : 'Start Count';
                    return (
                      <button className="btn btn-sm"
                        style={{ ...actionBtnBase, backgroundColor: '#43A047', padding: '0 9px', ...(pulseFresh ? { animation: 'mmFreshStartPulse 2.2s ease-in-out infinite' } : {}) }}
                        onClick={(e) => { e.stopPropagation(); markRunViewed(session.id); window.location.href = `count-session.html?session=${session.id}`; }}>
                        {label}
                      </button>
                    );
                  }
                  // Fallback (no explicit status) — treat as draft via handleCountAgain.
                  const fallbackLabel = runIsOrder ? 'Start Order' : 'Start Count';
                  return (
                    <button className="btn btn-sm"
                      style={{ ...actionBtnBase, backgroundColor: '#43A047', padding: '0 9px', ...(pulseFresh ? { animation: 'mmFreshStartPulse 2.2s ease-in-out infinite' } : {}) }}
                      onClick={(e) => { e.stopPropagation(); markRunViewed(session.id); handleCountAgain(session); }}>
                      {fallbackLabel}
                    </button>
                  );
                };

                const moreMenu = (session, allCounted, { showStaleGuard = false } = {}) => (
                  <PortalCardMenu
                    menuKey={session.id}
                    openMenuId={openMenuId}
                    setOpenMenuId={setOpenMenuId}
                    buttonStyle={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #D9CEC2', borderRadius: '6px', backgroundColor: 'transparent', color: '#8B7B73', cursor: 'pointer', fontSize: '0.9rem', lineHeight: 1, padding: 0 }}
                    items={[
                      { icon: '✏️', label: 'Edit Count', action: () => { setOpenMenuId(null); handleEditSession(session); } },
                      { icon: '👁️', label: 'Preview Items', action: () => { setOpenMenuId(null); handlePreviewItems(session); } },
                      ...(session.status !== 'completed' && !allCounted ? [{ icon: '✅', label: 'Mark Complete', action: () => { setOpenMenuId(null); handleMarkComplete(session); } }] : []),
                      { icon: '📦', label: 'Archive', action: () => { setOpenMenuId(null); handleArchiveSession(session); } },
                      ...superAdminCardMenuItems(session),
                    ]}
                  />
                );

                // Actions available on the Template parent card. Scoped to template metadata
                // edits; does not offer child-level actions like Mark Complete.
                // Note: the New Order/Count action delegates to handleNewRunClick (defined
                // below in the same closure) so it runs the same schedule + pars gates.
                // "Edit Template" is now a visible secondary button on the card and is
                // intentionally removed from this overflow menu. The menu carries only
                // lower-frequency overflow actions (future: duplicate/archive/delete/rename).
                // "Start New Count/Order" is retained here as a power-user shortcut.
                const templateMoreMenu = (template) => (
                  <PortalCardMenu
                    menuKey={template.id}
                    openMenuId={openMenuId}
                    setOpenMenuId={setOpenMenuId}
                    buttonStyle={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #D9CEC2', borderRadius: '6px', backgroundColor: 'transparent', color: '#8B7B73', cursor: 'pointer', fontSize: '0.9rem', lineHeight: 1, padding: 0 }}
                    items={[
                      { icon: '➕', label: template.order_calculations_enabled ? 'Start New Order' : 'Start New Count', action: () => { setOpenMenuId(null); handleNewRunClick(); } },
                    ]}
                    minWidth="160px"
                  />
                );

                const renderExpandedDetails = (session, { showStaleGuard = false } = {}) => {
                  const counted = session.items_counted || 0;
                  const total = session.total_items || 0;
                  const allCounted = (total > 0 && counted >= total) || session.status === 'completed';
                  const activeTab = cardPreviewTab[session.id] || 'details';
                  const cachedItems = sessionItemsCache[session.id] || [];
                  const fetchStatus = cardPreviewStatus[session.id] || 'idle';
                  const isLoadingItems = fetchStatus === 'loading';
                  const isFetchError = fetchStatus === 'error';

                  const vendorMap = {}, locationMap = {}, categoryMap = {};
                  cachedItems.forEach(i => {
                    const s = i.item_snapshot || {};
                    const v = s.vendor_name || s.vendor || '';
                    const l = i.location || s.location || '';
                    const cat = s.category || '';
                    if (v) vendorMap[v] = (vendorMap[v] || 0) + 1;
                    if (l) locationMap[l] = (locationMap[l] || 0) + 1;
                    if (cat) categoryMap[cat] = (categoryMap[cat] || 0) + 1;
                  });
                  const vendorEntries = Object.entries(vendorMap).sort((a, b) => a[0].localeCompare(b[0]));
                  const locationEntries = Object.entries(locationMap).sort((a, b) => a[0].localeCompare(b[0]));
                  const categoryEntries = Object.entries(categoryMap).sort((a, b) => a[0].localeCompare(b[0]));

                  const tabStyle = (tabId) => ({ fontSize: '0.72rem', fontWeight: activeTab === tabId ? 600 : 400, padding: '5px 10px', border: 'none', borderBottom: activeTab === tabId ? '2px solid #5A4A42' : '2px solid transparent', backgroundColor: 'transparent', color: activeTab === tabId ? '#5A4A42' : '#8B7B73', cursor: 'pointer', whiteSpace: 'nowrap' });
                  const listStyle = { maxHeight: '200px', overflowY: 'auto', padding: '4px 0' };
                  const liStyle = { padding: '4px 8px', fontSize: '0.78rem', color: '#5A4A42', borderBottom: '1px solid #F3EDE7', display: 'flex', alignItems: 'center', gap: '6px' };
                  const pillCount = (n) => <span style={{ fontSize: '0.65rem', fontWeight: 600, padding: '1px 6px', borderRadius: '10px', backgroundColor: '#EDE6DE', color: '#8B7B73', marginLeft: 'auto', flexShrink: 0 }}>{n}</span>;
                  const retryFetch = () => {
                    setCardPreviewStatus(p => ({ ...p, [session.id]: 'loading' }));
                    (async () => {
                      try {
                        const { data: items, error } = await supabase.from('count_items').select('*').eq('count_session_id', session.id).order('id');
                        if (!error && items) { setSessionItemsCache(prev => ({ ...prev, [session.id]: items })); setCardPreviewStatus(p => ({ ...p, [session.id]: 'success' })); }
                        else { setCardPreviewStatus(p => ({ ...p, [session.id]: 'error' })); }
                      } catch { setCardPreviewStatus(p => ({ ...p, [session.id]: 'error' })); }
                    })();
                  };
                  const loadingMsg = (msg = 'Loading...') => <div style={{ textAlign: 'center', padding: '1rem', color: '#8B7B73', fontSize: '0.8rem' }}>{msg}</div>;
                  const errorRetry = <div style={{ textAlign: 'center', padding: '1rem', color: '#A89890', fontSize: '0.8rem' }}>Failed to load. <button style={{ border: 'none', background: 'none', color: '#1976D2', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }} onClick={retryFetch}>Retry</button></div>;
                  const emptyMsg = (msg) => <div style={{ textAlign: 'center', padding: '1rem', color: '#A89890', fontSize: '0.8rem' }}>{msg}</div>;

                  return (
                    <div style={{ borderTop: '1px solid #EDE6DE', backgroundColor: '#FEFCFA' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '2px', borderBottom: '1px solid #EDE6DE', padding: '0 12px', overflowX: 'auto' }}>
                        {[
                          { id: 'details', label: 'Details' },
                          { id: 'items', label: `Items (${total})` },
                          { id: 'vendors', label: `Vendors (${vendorEntries.length})` },
                          { id: 'locations', label: `Locations (${locationEntries.length})` },
                          { id: 'categories', label: `Categories (${categoryEntries.length})` },
                        ].map(tab => (
                          <button key={tab.id} style={tabStyle(tab.id)}
                            onClick={() => setCardPreviewTab(prev => ({ ...prev, [session.id]: tab.id }))}>
                            {tab.label}
                          </button>
                        ))}
                      </div>
                      <div style={{ padding: isMobileOverview ? '8px 12px 10px' : '10px 16px 12px' }}>
                        {activeTab === 'details' && (
                          <>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobileOverview ? '3px 12px' : '4px 24px', marginBottom: '10px' }}>
                              {[
                                { label: 'Scheduled', value: formatDate(session.session_date) },
                                { label: session.is_copy ? 'Copied' : 'Created', value: formatDateTime(session.created_at) },
                                ...(session.status === 'in_progress' && session.count_commence_time ? [{ label: 'Started', value: formatTimeString(session.count_commence_time) }] : []),
                                ...(session.status === 'completed' && session.completed_at ? [{ label: 'Completed', value: formatDateTime(session.completed_at) }] : []),
                                { label: 'Last Activity', value: formatDateTime(session.updated_at) },
                                ...(isMobileOverview ? [] : [
                                  { label: 'Last Accessed By', value: counted === 0 ? 'Not yet' : (session.last_accessed_by_name || 'Unknown') },
                                  { label: 'Created By', value: session.created_by_name || 'Unknown' },
                                ]),
                                { label: 'Progress', value: `${counted} / ${total} items counted` },
                              ].map(r => (
                                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #F3EDE7' }}>
                                  <span style={{ fontSize: '0.75rem', color: '#8B7B73' }}>{r.label}</span>
                                  <span style={{ fontSize: '0.75rem', color: '#5A4A42', fontWeight: 500, textAlign: 'right' }}>{r.value}</span>
                                </div>
                              ))}
                            </div>
                            {(() => {
                              const actionBtnStyle = {
                                fontSize: isMobileOverview ? '0.8rem' : '0.7rem',
                                height: isMobileOverview ? '40px' : '28px',
                                minHeight: isMobileOverview ? '40px' : undefined,
                                border: '1px solid #D9CEC2',
                                backgroundColor: '#fff',
                                color: '#5A4A42',
                                padding: isMobileOverview ? '0 14px' : '0 10px',
                                borderRadius: '6px',
                                lineHeight: 1,
                                touchAction: 'manipulation',
                              };
                              return (
                                <div style={{ display: 'flex', gap: isMobileOverview ? '10px' : '6px', rowGap: isMobileOverview ? '10px' : '6px', flexWrap: 'wrap' }}>
                                  <button className="btn btn-sm" style={actionBtnStyle} onClick={() => handleEditSession(session)}>✏️ Edit Count</button>
                                  {session.status !== 'completed' && !allCounted && <button className="btn btn-sm" style={actionBtnStyle} onClick={() => handleMarkComplete(session)}>✅ Complete</button>}
                                  <button className="btn btn-sm" style={actionBtnStyle} onClick={() => handleArchiveSession(session)}>📦 Archive</button>
                                </div>
                              );
                            })()}
                          </>
                        )}
                        {activeTab === 'items' && (
                          isLoadingItems ? loadingMsg('Loading items...') : isFetchError ? errorRetry :
                          cachedItems.length === 0 ? emptyMsg('No items in this session') :
                          <div style={listStyle}>{cachedItems.map((item, idx) => { const snap = item.item_snapshot || {}; const name = snap.description || snap.name || item.description || item.name || 'Unknown'; const isCounted = item.is_counted || (item.counted_quantity != null && item.counted_quantity > 0); return <div key={idx} style={liStyle}><span style={{ fontSize: '0.72rem', flexShrink: 0 }}>{isCounted ? '✅' : '⬜'}</span><span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>{snap.brand && <span style={{ fontSize: '0.7rem', color: '#8B7B73', flexShrink: 0 }}>{snap.brand}</span>}</div>; })}</div>
                        )}
                        {activeTab === 'vendors' && (
                          isLoadingItems ? loadingMsg() : isFetchError ? errorRetry :
                          vendorEntries.length === 0 ? emptyMsg('No vendors assigned') :
                          <div style={listStyle}>{vendorEntries.map(([v, c]) => <div key={v} style={liStyle}><span style={{ flex: 1 }}>{v}</span>{pillCount(c)}</div>)}</div>
                        )}
                        {activeTab === 'locations' && (
                          isLoadingItems ? loadingMsg() : isFetchError ? errorRetry :
                          locationEntries.length === 0 ? emptyMsg('No locations set') :
                          <div style={listStyle}>{locationEntries.map(([l, c]) => <div key={l} style={liStyle}><span style={{ flex: 1 }}>{l}</span>{pillCount(c)}</div>)}</div>
                        )}
                        {activeTab === 'categories' && (
                          isLoadingItems ? loadingMsg() : isFetchError ? errorRetry :
                          categoryEntries.length === 0 ? emptyMsg('No categories set') :
                          <div style={listStyle}>{categoryEntries.map(([cat, c]) => <div key={cat} style={liStyle}><span style={{ flex: 1 }}>{cat}</span>{pillCount(c)}</div>)}</div>
                        )}
                      </div>
                    </div>
                  );
                };

                const renderSingleCard = (session) => {
                  const counted = session.items_counted || 0;
                  const total = session.total_items || 0;
                  const allCounted = (total > 0 && counted >= total) || session.status === 'completed';
                  const someCounted = counted > 0 && !allCounted;
                  const isOpen = expandedCards.has(session.id);
                  const isOrder = session.order_calculations_enabled;
                  const titleColor = getTemplateColor(session);

                  return (
                    <div key={session.id} style={{ backgroundColor: '#FAF6F2', border: '1px solid #E8DDD0', borderRadius: '10px', overflow: 'visible', position: 'relative', transition: 'box-shadow 0.15s', marginBottom: '6px' }}
                      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(90,74,66,0.1)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}>
                      <div style={{ textAlign: 'center', padding: '2px 12px 0' }}>
                        {statusBadge(session, counted, total, allCounted)}
                      </div>
                      <div onClick={() => expandCard(session.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: isMobileOverview ? '8px' : '12px', padding: isMobileOverview ? '2px 10px 10px' : '3px 16px 12px', cursor: 'pointer' }}>

                        {isOrder ? truckIcon(16) : (
                          <svg width="10" height="10" viewBox="0 0 10 10" style={{ flexShrink: 0, transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', opacity: 0.5 }}>
                            <path d="M2.5 1L7.5 5L2.5 9" fill="none" stroke="#8B7B73" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}

                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden' }}>
                            <span style={{ fontWeight: 600, fontSize: isMobileOverview ? '0.84rem' : '0.9rem', color: titleColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 0', minWidth: 0 }}>
                              {session.name}
                            </span>
                            {isMobileOverview && (() => { const icons = getSessionCategoryIcons(session.id); return icons.length > 0 ? <span style={{ display: 'inline-flex', gap: '1px', flexShrink: 0 }}>{icons.slice(0, 2).map((ic, i) => <span key={i} style={{ fontSize: '0.65rem', lineHeight: 1 }}>{ic}</span>)}</span> : null; })()}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#8B7B73', marginTop: '2px', display: 'flex', gap: '4px 6px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                            <span style={{ flexShrink: 0 }}>{formatDate(session.session_date)}</span>
                            <span style={{ color: '#D4C4B0', flexShrink: 0 }}>·</span>
                            <span style={{ flexShrink: 0 }}>{total} items</span>
                            {!isMobileOverview && <><span style={{ color: '#D4C4B0' }}>·</span><span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{session.created_by_name || 'Unknown'}</span></>}
                          </div>
                          {!isMobileOverview && (
                            <div style={{ fontSize: '0.66rem', color: '#A89890', marginTop: '1px' }}>
                              {allCounted
                                ? `Last completed ${formatDate(session.completed_at || session.updated_at || session.session_date || session.created_at)}`
                                : `Created ${formatDateTime(session.created_at)} · Accessed ${formatDateTime(session.updated_at)}`}
                            </div>
                          )}
                        </div>

                        {!isMobileOverview && (() => { const icons = getSessionCategoryIcons(session.id); return icons.length > 0 ? <div style={{ display: 'flex', gap: '3px', flexShrink: 0, alignItems: 'center' }}>{icons.map((ic, i) => <span key={i} style={{ fontSize: '1.4rem', lineHeight: 1 }} title={ic}>{ic}</span>)}</div> : null; })()}

                        <div style={{ flexShrink: 0, minWidth: isMobileOverview ? '55px' : '75px' }}>
                          {progressBar(counted, total)}
                        </div>

                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                          {actionBtn(session, counted, total, allCounted, someCounted)}
                          {moreMenu(session, allCounted)}
                        </div>
                      </div>

                      {session.status === 'completed' && (
                        <div style={{ padding: isMobileOverview ? '0 10px 10px' : '0 16px 12px' }}>
                          <CountCardAvailableReports session={session} onOpenReport={openSessionReport} compact={isMobileOverview} />
                        </div>
                      )}

                      {isOpen && renderExpandedDetails(session)}
                    </div>
                  );
                };

                const renderGroupCard = (groupId, sessionsInGroup, template = null, { forceLatestOnly = false } = {}) => {
                  // Display order within a stack: draft + in_progress first
                  // (date-desc), then completed below (date-desc). Used by the
                  // expanded-details metadata panel for "last edited" fallback.
                  const isActiveSession = (s) => s.status === 'draft' || s.status === 'in_progress' || s.status === 'paused';
                  const byDateDesc = (a, b) => new Date(b.created_at || b.session_date) - new Date(a.created_at || a.session_date);
                  const activeRuns = sessionsInGroup.filter(isActiveSession).sort(byDateDesc);
                  const completedRuns = sessionsInGroup.filter(s => !isActiveSession(s)).sort(byDateDesc);
                  const sortedRuns = [...activeRuns, ...completedRuns];
                  const DEFAULT_VISIBLE_CHILDREN = 1;
                  const showAllChildren = expandedStackChildren.has(groupId);
                  const hasChildren = sessionsInGroup.length > 0;
                  // For template-only groups (no children yet) fall back to the
                  // template itself so header chrome (name/date/id) still resolves.
                  const latestByDate = hasChildren
                    ? [...sessionsInGroup].sort(byDateDesc)[0]
                    : template;
                  // For EOP recurring stacks, prefer the most-recently-active
                  // run (completed → in-progress → draft with items counted)
                  // as the headline so a just-finished count is not displaced
                  // by an untouched system-generated future-period draft.
                  const _eopRep = template || (hasChildren ? sessionsInGroup[0] : null);
                  const _eopCp = _eopRep?.count_purpose || '';
                  const _eopIm = _eopRep?.inventory_mode || '';
                  const _isEOPStack = !(_eopRep?.order_calculations_enabled) &&
                    (_eopCp === 'period_end' || _eopCp === 'full_inventory' || _eopIm === 'Financial' || _eopIm === 'Both');
                  const _isUntouchedDraft = (s) => s.status === 'draft' && !(s.items_counted > 0);
                  const headlineRun = (() => {
                    if (!hasChildren) return template;
                    if (!_isEOPStack || !template) return latestByDate;
                    const completedR = sessionsInGroup.filter(s => s.status === 'completed').sort(byDateDesc);
                    if (completedR.length > 0) return completedR[0];
                    const inProgressR = sessionsInGroup.filter(s => s.status === 'in_progress' || s.status === 'paused').sort(byDateDesc);
                    if (inProgressR.length > 0) return inProgressR[0];
                    const touchedDrafts = sessionsInGroup.filter(s => !_isUntouchedDraft(s)).sort(byDateDesc);
                    if (touchedDrafts.length > 0) return touchedDrafts[0];
                    return latestByDate;
                  })();
                  // Children render oldest → newest so the timeline reads
                  // top-down with the most recent run anchored at the bottom
                  // (mental model: "here is what happened, here is now").
                  // When the stack is collapsed only the headline run is shown;
                  // "Show N earlier counts" reveals other runs in chronological order.
                  const chronologicalRuns = hasChildren
                    ? [...sessionsInGroup].sort((a, b) =>
                        new Date(a.created_at || a.session_date) - new Date(b.created_at || b.session_date))
                    : [];
                  const visibleRuns = !hasChildren
                    ? []
                    : (forceLatestOnly
                      ? [headlineRun]
                      : (showAllChildren ? chronologicalRuns : (() => {
                          // For EOP stacks, show the headline run (e.g. just-completed)
                          // rather than always the newest-by-date run.
                          if (_isEOPStack && template && headlineRun && headlineRun.id !== latestByDate?.id) {
                            return [headlineRun];
                          }
                          return chronologicalRuns.slice(-DEFAULT_VISIBLE_CHILDREN);
                        })()));
                  const hiddenCount = forceLatestOnly ? 0 : (chronologicalRuns.length - visibleRuns.length);
                  const latestRun = headlineRun;
                  const newestId = latestRun ? latestRun.id : null;
                  // Template (when present) carries the recurring name; else derive from newest child.
                  const displaySession = template || sessionsInGroup[0];
                  const seriesName = template
                    ? (template.name || '')
                    : ((displaySession.name || '').replace(/\s*-\s*(Run\s*#?\d+|Copy\s*\d*|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec).*$/i, '').trim() || displaySession.name);
                  const isOpen = expandedStacks.has(groupId);
                  // Group-wide total: a "Count Again" copy can land with total_items=0
                  // before the editor fills it in. Fall back to the largest known
                  // total across all runs in this group, then to items_counted.
                  const groupTotal = Math.max(
                    0,
                    ...sessionsInGroup.map(s => Number(s.total_items) || 0),
                    ...sessionsInGroup.map(s => Number(s.items_counted) || 0)
                  );
                  const latestCounted = latestRun.items_counted || 0;
                  const latestTotal = (Number(latestRun.total_items) || 0) || groupTotal;
                  const latestAllCounted = (latestTotal > 0 && latestCounted >= latestTotal) || latestRun.status === 'completed';
                  const rep = template || displaySession;
                  const isOrder = rep.order_calculations_enabled === true;
                  const repCp = rep.count_purpose || '';
                  const repIm = rep.inventory_mode || '';
                  const isEOP = !isOrder && (repCp === 'period_end' || repCp === 'full_inventory' || repIm === 'Financial' || repIm === 'Both');
                  const titleColor = getTemplateColor(rep);
                  const isTemplateParent = !!template;
                  // Last completion date across children (for Template parent helper line)
                  const lastCompletedAt = isTemplateParent
                    ? sortedRuns.map(s => s.completed_at).filter(Boolean).sort().reverse()[0] || null
                    : null;
                  // Pre-compute item/category metadata for both card face and expanded panel
                  const _metaItemsSourceId = template ? template.id : newestId;
                  const _metaItems = sessionItemsCache[_metaItemsSourceId] || [];
                  // Fallback chain: fetched rows → template.total_items (available immediately
                  // from the session list while the count_items prefetch is in flight) → groupTotal.
                  const _metaItemCount = _metaItems.length || Number(template?.total_items) || groupTotal;
                  const _metaCatTally = {};
                  const _metaVendorTally = {};
                  _metaItems.forEach(i => {
                    const snap = i.item_snapshot || {};
                    const cat = snap.category || snap.item_category || i.category || '';
                    if (cat) _metaCatTally[cat] = (_metaCatTally[cat] || 0) + 1;
                    const rawVendor = snap.vendor || i.vendor || '';
                    const resolved = rawVendor ? (resolveVendor(rawVendor) || rawVendor) : '';
                    if (resolved) _metaVendorTally[resolved] = (_metaVendorTally[resolved] || 0) + 1;
                  });
                  const _metaCategoryCount = Object.keys(_metaCatTally).length;
                  const _metaVendorCount = Object.keys(_metaVendorTally).length;

                  // PARs and the schedule are still OFFERED — the ⚙ badge opens
                  // Template Setup and both rows tick when they are filled in —
                  // but neither is pending work any more, so nothing here reads
                  // as a warning. PARs are set inside the count from the PAR
                  // badge; the schedule is inferred when the count is completed.
                  const _cardPreviewLoading = cardPreviewStatus[_metaItemsSourceId] === 'loading' && _metaItems.length === 0;
                  // parsMissing: only meaningful for order templates. Uses the shared
                  // isParsSetupNeeded helper (no item has a par yet) so the Template
                  // Setup checklist reads the same rows the wizard would edit. An
                  // explicit skip (pars_setup_skipped) counts as done.
                  const parsMissing = isOrder && !template?.direct_order_mode && !template?.pars_setup_skipped
                    && !_cardPreviewLoading
                    && _metaItems.length > 0
                    && isParsSetupNeeded(_metaItems);
                  // scheduleMissing: true when no real cycle is configured and user hasn't skipped
                  const _schedCyc = template?.ordering_cycle_config;
                  const _isSkippedSentinel = !!((_schedCyc) && _schedCyc.preset === 'none' && _schedCyc.skipped === true);
                  const _hasRealCycle = !!(_schedCyc && _schedCyc.preset && !_isSkippedSentinel);
                  const _eopInheritsStore = isEOP && !!inventoryPeriodConfigToCycle(storePeriodConfig);
                  const scheduleMissing = !_hasRealCycle && !_isSkippedSentinel && !_eopInheritsStore;

                  // Derived status text — single plain-English line that replaces
                  // overlapping badges. Computed here so it's available in both the
                  // header meta panel and the button-area IIFE below.
                  const templateStatusText = isTemplateParent ? (() => {
                    const n = activeRuns.length;
                    if (n > 0) return isOrder ? (n === 1 ? '1 active order' : `${n} active orders`) : (n === 1 ? '1 active count' : `${n} active counts`);
                    if (isOrder && template?.direct_order_mode) return 'Ready to order · Direct';
                    return isOrder ? 'Ready to order' : 'Ready to count';
                  })() : null;
                  const templateStatusDotColor = isTemplateParent
                    ? (activeRuns.length > 0 ? '#FB8C00' : '#4CAF50')
                    : null;

                  // handleNewRunClick — the single entry point for all "Start Order / New Count"
                  // actions on a template parent card. Defined here so the primary button, the
                  // kebab "New Order" menu item, and the childless-terminator all behave alike.
                  // Only applies when isTemplateParent; non-template paths are unaffected.
                  //
                  // Nothing is asked for first. The schedule prompt and the pars wizard
                  // that used to gate this are both gone: PARs are set in the count itself
                  // (the PAR badge opens the same wizard), and the schedule is inferred
                  // from the completion date rather than declared in advance. Both are still
                  // offered from ⚙ Template Setup for anyone who wants to set them up front.
                  const handleNewRunClick = async () => {
                    if (!isTemplateParent) return;
                    void markTemplateOnboardingSeen('interaction');
                    // A synthesized stand-in has no DB row; start the new run from
                    // the real latest child (which carries the group's count_group_id).
                    const sessionSource = isSyntheticTemplate(template) ? latestRun : (template || latestRun);
                    // Web-only guard: never show the popup inside a React Native WebView
                    // (the native app has its own start flow and never reaches this path,
                    // but guard defensively in case CountInterface is ever embedded).
                    if (typeof window !== 'undefined' && window.ReactNativeWebView) {
                      handleCountAgain(sessionSource, 'web');
                      return;
                    }
                    setCountStartChoiceModal({ session: sessionSource });
                  };

                  // Event-marker helpers — shared by renderRunRow (passed to SessionTimelineStack)
                  const connectorLeft = isMobileOverview ? 'calc(5% + 7px)' : 'calc(5% + 11px)';
                  const SPINE = '2px solid #D9CEC2';
                  const eventPalette = {
                    // Started/Created use a light blue — the run has begun,
                    // fresh and active. Distinct from inProgress (deeper blue)
                    // so the start marker reads as its own event.
                    started:      { node: '#4FC3F7', bg: '#E1F5FE', border: '#B3E5FC', text: '#0277BD', label: 'Started' },
                    created:      { node: '#4FC3F7', bg: '#E1F5FE', border: '#B3E5FC', text: '#0277BD', label: 'Created' },
                    // Completed uses a soft sage green — finished successfully.
                    // The previous red badge read as an error rather than a
                    // positive end state.
                    completed:    { node: '#66BB6A', bg: '#E8F5E9', border: '#C8E6C9', text: '#2E7D32', label: 'Completed' },
                    notCompleted: { node: '#F9A825', bg: '#FFF8E1', border: '#F1D57A', text: '#8D6708', label: 'Not completed' },
                    inProgress:   { node: '#1976D2', bg: '#E3F2FD', border: '#90CAF9', text: '#0D47A1', label: 'In progress' },
                    waiting:      { node: '#BDBDBD', bg: '#F2EEE8', border: '#D9CEC2', text: '#7A6B63', label: 'Waiting for next count' },
                  };
                  const nodeSize = isMobileOverview ? 10 : 10;
                  const pillPad = isMobileOverview ? '3px 10px' : '3px 10px';
                  const pillFont = isMobileOverview ? '0.82rem' : '0.72rem';
                  // Flowchart shape per event kind:
                  //   started / created / inProgress  → rectangle (step)
                  //   completed / notCompleted         → oval (end-of-run)
                  const chipShape = (kind) => {
                    if (kind === 'completed' || kind === 'notCompleted') return '50px';
                    return isMobileOverview ? '4px' : '5px';
                  };
                  // inProgress gets a dashed border to signal it is not yet terminal
                  const chipBorderStyle = (kind, border) =>
                    kind === 'inProgress' ? `1px dashed ${border}` : `1px solid ${border}`;

                  const renderEventMarker = (kind, dateStr, timeStr) => {
                    const p = eventPalette[kind] || eventPalette.created;
                    const showMeta = Boolean(dateStr);
                    // Desktop: demoted small-dot + muted-text marker so events
                    // read as spine annotations rather than oversized pills,
                    // matching the new mobile overview hierarchy where the
                    // nested white run cards are the visual primary.
                    if (!isMobileOverview) {
                      return (
                        <div style={{ marginLeft: connectorLeft, borderLeft: SPINE, position: 'relative', padding: '4px 0 4px 16px' }}>
                          <div style={{ position: 'absolute', left: '-4px', top: '50%', transform: 'translateY(-50%)', width: '7px', height: '7px', borderRadius: '50%', backgroundColor: p.node, boxShadow: '0 0 0 2px #FDFBF7' }} />
                          <span style={{ fontSize: '0.62rem', fontWeight: 600, color: p.text, opacity: 0.78, whiteSpace: 'nowrap' }}>
                            {p.label}
                            {showMeta && <span style={{ fontWeight: 400, opacity: 0.75 }}> · {dateStr}{timeStr ? ` ${timeStr}` : ''}</span>}
                          </span>
                        </div>
                      );
                    }
                    return (
                      <div style={{ marginLeft: connectorLeft, borderLeft: SPINE, position: 'relative', padding: '4px 0 4px 12px' }}>
                        <div style={{ position: 'absolute', left: `-${Math.round(nodeSize / 2) + 1}px`, top: '50%', transform: 'translateY(-50%)', width: `${nodeSize}px`, height: `${nodeSize}px`, borderRadius: '50%', backgroundColor: p.node, boxShadow: '0 0 0 3px #FDFBF7' }} />
                        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '8px', padding: pillPad, borderRadius: chipShape(kind), backgroundColor: p.bg, border: chipBorderStyle(kind, p.border), fontSize: pillFont, lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 600, color: p.text, letterSpacing: '0.01em' }}>{p.label}</span>
                          {showMeta && (<><span style={{ color: p.text, opacity: 0.4 }}>·</span><span style={{ color: p.text, opacity: 0.85, fontWeight: 500 }}>{dateStr}</span>{timeStr && <span style={{ color: p.text, opacity: 0.65, fontWeight: 500 }}>{timeStr}</span>}</>)}
                        </span>
                      </div>
                    );
                  };


                  // Mid-spine downward chevron — repeats the directional cue
                  // along the spine on long expanded stacks so users scrolling
                  // through a multi-run stack can always see "this flows down"
                  // without having to reach the terminal arrowhead at the
                  // bottom. Sits inline as its own thin spine segment between
                  // run cards; the chevron itself is centered on the spine
                  // line and uses the same muted stroke color as the spine.
                  const renderSpineChevron = () => (
                    <div style={{ marginLeft: connectorLeft, borderLeft: SPINE, position: 'relative', height: isMobileOverview ? '14px' : '18px' }}>
                      <svg width="10" height="6" viewBox="0 0 10 6" style={{ position: 'absolute', left: '-5px', top: '50%', transform: 'translateY(-50%)' }}>
                        <path d="M1 1 L5 5 L9 1" fill="none" stroke="#A89890" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  );

                  // Chooses which inter-card gaps host a mid-spine chevron.
                  // Stacks with 1–2 visible runs get no extra chevrons (the
                  // terminal arrowhead is already visible). Stacks with 3+
                  // visible runs get 2 chevrons (3–4 runs) or 3 chevrons
                  // (5+ runs), distributed evenly across the N-1 gaps using
                  // a uniform-within-gaps formula so they read as evenly
                  // spaced regardless of stack length.
                  const spineChevronGapsFor = (visibleRunCount) => {
                    if (visibleRunCount < 3) return new Set();
                    const gaps = visibleRunCount - 1;
                    const k = visibleRunCount <= 4 ? 2 : 3;
                    const out = new Set();
                    for (let i = 0; i < k; i++) {
                      out.add(Math.floor(((i + 0.5) * gaps) / k));
                    }
                    return out;
                  };

                  const renderTimelineLeadIn = () => (
                    <div style={{ marginLeft: connectorLeft, borderLeft: SPINE, height: '5px', position: 'relative' }} />
                  );

                  const renderChildlessTerminator = (_group) => {
                    const ghostLabel = `Next ${isOrder ? 'order' : 'count'} will appear here`;
                    // Route through handleNewRunClick so schedule + pars gates run
                    const triggerNew = () => handleNewRunClick();
                    return (
                      <>
                        <div style={{ marginLeft: connectorLeft, borderLeft: SPINE, height: isMobileOverview ? '22px' : '28px', position: 'relative' }}>
                          <div style={{
                            position: 'absolute',
                            left: '-4px',
                            bottom: '2px',
                            width: 0,
                            height: 0,
                            borderLeft: '4px solid transparent',
                            borderRight: '4px solid transparent',
                            borderTop: '6px solid #7BC67E',
                          }} />
                        </div>
                        <div style={{ marginLeft: connectorLeft, marginRight: isMobileOverview ? '6px' : '10px', marginBottom: isMobileOverview ? '6px' : '8px' }}>
                          <div
                            role="button"
                            tabIndex={0}
                            aria-label={ghostLabel}
                            title={ghostLabel}
                            onClick={triggerNew}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                triggerNew();
                              }
                            }}
                            style={{
                              borderRadius: '999px',
                              border: '1.5px dashed #D9CEC2',
                              padding: isMobileOverview ? '8px 16px' : '10px 20px',
                              backgroundColor: 'transparent',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              color: '#A89890',
                              fontSize: isMobileOverview ? '0.9rem' : '0.78rem',
                              fontStyle: 'italic',
                              opacity: 0.75,
                              transition: 'opacity 0.15s, background-color 0.15s, border-color 0.15s',
                              outline: 'none',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = '1';
                              e.currentTarget.style.backgroundColor = '#FBF7F1';
                              e.currentTarget.style.borderColor = '#C9BCAE';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = '0.75';
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.borderColor = '#D9CEC2';
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.opacity = '1';
                              e.currentTarget.style.backgroundColor = '#FBF7F1';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.opacity = '0.75';
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <span
                              aria-hidden="true"
                              style={{
                                width: isMobileOverview ? '17px' : '16px',
                                height: isMobileOverview ? '17px' : '16px',
                                borderRadius: '50%',
                                border: '1.5px dashed #C9BCAE',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: isMobileOverview ? '0.87rem' : '0.78rem',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                color: '#A89890',
                                lineHeight: 1,
                                flexShrink: 0,
                              }}
                            >
                              +
                            </span>
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {ghostLabel}
                            </span>
                          </div>
                        </div>
                      </>
                    );
                  };

                  return (
                    <SessionTimelineStack
                      key={`group-${groupId}`}
                      groups={[{
                        groupId,
                        template,
                        runs: forceLatestOnly
                          ? (hasChildren && latestByDate ? [latestByDate] : [])
                          : [...sessionsInGroup].sort(byDateDesc),
                        name: seriesName,
                        isOrder,
                        isEOP,
                      }]}
                      expandedRunsSet={expandedStackChildren}
                      onToggleExpandedRuns={toggleStackChildrenExpansion}
                      expandedOrder="oldest-first"
                      containerSpine={false}
                      runsContainerPadding="0 0 8px"
                      runsAreaTopBorder
                      runsAreaBackground="#FAF6F2"
                      formatDate={formatDate}
                      compactHiddenRunsControl={isMobileOverview}
                      renderTimelineStart={isTemplateParent ? () => (
                        <>
                          {/* Desktop: "Count History" / "Order History" / "EOP History"
                              divider sits below the template header, mirroring the
                              mobile overview's divider so the nested run cards read
                              as a clearly-labeled history list rather than a flat
                              continuation of the template body. */}
                          {!isMobileOverview && (
                            <div style={{ padding: '6px 16px 4px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#5A4A42', letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {isOrder ? 'Order History' : isEOP ? 'EOP History' : 'Count History'}
                              </span>
                              <div style={{ flex: 1, height: '1px', backgroundColor: '#D9CEC2' }} />
                            </div>
                          )}
                          {renderTimelineLeadIn()}
                        </>
                      ) : null}
                      renderChildlessTerminator={isTemplateParent ? renderChildlessTerminator : null}
                      renderHeader={(_g, _lr, _ctx) => (
                        <div onClick={() => { if (isTemplateParent) void markTemplateOnboardingSeen('interaction'); toggleStackExpansion(groupId); }}
                          style={{ display: 'flex', alignItems: 'center', gap: isMobileOverview ? '8px' : '12px', padding: isMobileOverview ? (isTemplateParent ? '3px 8px 11px 8px' : '7px 8px') : '12px 16px', cursor: 'pointer', borderRadius: isTemplateParent ? '9px 9px 0 0' : undefined, position: isTemplateParent ? 'relative' : undefined }}>

                        {/* Chevron always renders so every stack signals "expandable".
                            Orders get the truck to the right; EOP gets the calendar.
                            On mobile template cards both leading icons are hidden so the
                            cream meta panel can use the full available width. */}
                        {(!isMobileOverview || !isTemplateParent) && chevronIcon(isOpen)}
                        {isOrder && (!isMobileOverview || !isTemplateParent) && truckIcon(16)}
                        {isEOP && (!isMobileOverview || !isTemplateParent) && calendarIcon(14)}

                        <div style={{ flex: 1, minWidth: 0, ...(isTemplateParent ? { backgroundColor: '#F5EBDD', borderRadius: isMobileOverview ? '10px' : '8px', padding: isMobileOverview ? '5px 8px' : '8px 10px', overflow: 'visible', ...(isMobileOverview ? { minHeight: '52px' } : {}) } : { overflow: 'hidden' }) }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden' }}>
                            <span style={{ fontWeight: 700, fontSize: isMobileOverview ? '1.05rem' : '0.9rem', color: titleColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 auto', minWidth: isMobileOverview ? '90px' : 0 }}>
                              {seriesName}
                            </span>
                            {isMobileOverview && isTemplateParent && (() => { const icons = getSessionCategoryIcons(latestRun.id); return icons.length > 0 ? <CategoryIconFader icons={icons} sizeRem="2.0rem" /> : null; })()}
                            {isMobileOverview && !isTemplateParent && (() => { const icons = getSessionCategoryIcons(latestRun.id); return icons.length > 0 ? <span style={{ display: 'inline-flex', gap: '1px', flexShrink: 0 }}>{icons.slice(0, 2).map((ic, i) => <span key={i} style={{ fontSize: '0.65rem', lineHeight: 1 }}>{ic}</span>)}</span> : null; })()}
                            {/* The dark "Nx" badge that previously sat next to
                                the title on mobile was removed: it took space
                                from the title and didn't carry useful info.
                                Desktop still shows the "Used N times" pill. */}
                            {hasChildren && !isMobileOverview && (
                              <span style={{ fontSize: '0.62rem', fontWeight: 500, padding: '2px 7px', borderRadius: '10px', backgroundColor: '#EAD9C4', color: '#6B4F3A', flexShrink: 0, whiteSpace: 'nowrap' }}>
                                Used {sessionsInGroup.length} {sessionsInGroup.length === 1 ? 'time' : 'times'}
                              </span>
                            )}
                            {isTemplateParent ? (
                              <>
                                {!isMobileOverview && (
                                  <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: '12px', backgroundColor: '#5A4A42', color: '#FAF6F2', whiteSpace: 'nowrap', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Template</span>
                                )}
                                {(() => {
                                  // Subtle "Updated" badge — surfaces template edits since the
                                  // newest child run was created. Replaces the previous
                                  // "New Count Edited" button text per spec (badge only,
                                  // never inside the primary action label).
                                  const wasUpdated = template && hasChildren && latestRun && template.updated_at && latestRun.created_at
                                    && new Date(template.updated_at).getTime() > new Date(latestRun.created_at).getTime() + 60 * 1000;
                                  if (!wasUpdated) return null;
                                  return (
                                    <span title="Template was edited after the latest run was created"
                                      style={{ fontSize: '0.58rem', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', backgroundColor: '#FFF3E0', color: '#E65100', border: '1px solid #FFCC80', whiteSpace: 'nowrap', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                      Updated
                                    </span>
                                  );
                                })()}
                                {/* Template Setup stays one click away, but it no longer
                                    pulses for attention — PARs and the schedule are not
                                    outstanding work, so the badge is an entrance, not a
                                    nag. */}
                                {!isMobileOverview && (
                                  <span
                                    title="Template setup — click to review or adjust"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openTemplateSetupPicker({ template, parsMissing, scheduleMissing, isOrder, isEOP });
                                    }}
                                    style={{
                                      fontSize: '0.58rem', fontWeight: 700, padding: '2px 7px', borderRadius: '10px',
                                      backgroundColor: '#EAD9C4', color: '#6B4F3A', border: '1px solid #C9A882',
                                      whiteSpace: 'nowrap', letterSpacing: '0.04em', textTransform: 'uppercase',
                                      flexShrink: 0, cursor: 'pointer',
                                    }}>
                                    ⚙ Setup
                                  </span>
                                )}
                              </>
                            ) : (
                              statusBadge(latestRun, latestCounted, latestTotal, latestAllCounted)
                            )}
                          </div>
                          {isTemplateParent ? (
                            <>
                              <div style={{ fontSize: isMobileOverview ? '0.9rem' : '0.72rem', color: '#8B7B73', marginTop: '2px', display: 'flex', gap: '4px 6px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                {(() => {
                                  // Only EOP templates inherit the store's signup-period schedule.
                                  // Order/vendor templates without their own cycle keep the generic
                                  // "Recurring" wording so they're not mislabeled.
                                  const cyc = template?.ordering_cycle_config
                                    || (isEOP ? inventoryPeriodConfigToCycle(storePeriodConfig) : null);
                                  const cycleLbl = cycleToFriendlyLabel(cyc);
                                  const _skipped = !!(cyc && cyc.preset === 'none' && cyc.skipped === true);
                                  if (_skipped) {
                                    return <span style={{ flexShrink: 0, fontStyle: 'italic', color: '#A89890' }}>No schedule</span>;
                                  }
                                  return cycleLbl
                                    ? <span style={{ flexShrink: 0, fontWeight: 600, color: '#5A4A42' }}>{cycleLbl}</span>
                                    : <span style={{ flexShrink: 0 }}>{isMobileOverview ? 'Recurring' : 'Recurring count'}</span>;
                                })()}
                                {lastCompletedAt && <>
                                  <span style={{ color: '#D4C4B0', flexShrink: 0 }}>·</span>
                                  <span style={{ flexShrink: 0 }}>{isMobileOverview ? 'Last' : 'Last used'} {formatDate(lastCompletedAt)}</span>
                                </>}
                              </div>
                              {(() => {
                                const _metaLoading = cardPreviewStatus[_metaItemsSourceId] === 'loading' && _metaItems.length === 0;
                                const _itemsDisplay = _metaLoading && !Number(template?.total_items) && !groupTotal
                                  ? <span style={{ fontWeight: 600, color: '#C7B8AC' }}>—</span>
                                  : <span style={{ fontWeight: 600 }}>{_metaItemCount}</span>;
                                // A synthesized stand-in's items aren't prefetched, so show a
                                // neutral placeholder rather than a misleading "0" until the
                                // real template row lands and its categories/vendors resolve.
                                const _secondaryDisplay = (_metaLoading || (isSyntheticTemplate(template) && _metaItems.length === 0))
                                  ? <span style={{ fontWeight: 600, color: '#C7B8AC' }}>—</span>
                                  : <span style={{ fontWeight: 600 }}>{isOrder ? _metaVendorCount : _metaCategoryCount}</span>;
                                return (
                                  <div style={{ fontSize: isMobileOverview ? '0.82rem' : '0.66rem', color: '#A89890', marginTop: '3px', display: 'flex', gap: '4px 8px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                    <span style={{ flexShrink: 0 }}>{_itemsDisplay} {isMobileOverview ? 'itm' : 'items'}</span>
                                    <span style={{ flexShrink: 0 }}>·</span>
                                    <span style={{ flexShrink: 0 }}>{_secondaryDisplay} {isMobileOverview ? (isOrder ? 'vnd' : 'cat') : (isOrder ? 'vendors' : 'categories')}</span>
                                    <span style={{ flexShrink: 0 }}>·</span>
                                    <span style={{ flexShrink: 0 }}><span style={{ fontWeight: 600 }}>{sessionsInGroup.length}</span>{isMobileOverview ? <> runs · <span style={{ fontWeight: 600 }}>{activeRuns.length}</span> active</> : ` run${sessionsInGroup.length === 1 ? '' : 's'} (${activeRuns.length} active)`}</span>
                                  </div>
                                );
                              })()}
                              {/* Status text — single derived plain-English line */}
                              {templateStatusText && (
                                <div style={{ fontSize: isMobileOverview ? '0.8rem' : '0.63rem', fontWeight: 600, color: '#5A4A42', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: templateStatusDotColor, flexShrink: 0 }} />
                                  {templateStatusText}
                                </div>
                              )}
                              {/* PAR explanation for ordering templates missing PARs */}
                              {parsMissing && isOrder && (
                                <div style={{ fontSize: isMobileOverview ? '0.72rem' : '0.62rem', color: '#8B7B73', fontStyle: 'italic', marginTop: '3px', lineHeight: 1.45 }}>
                                  Add PARs to make ordering useful. PAR is the amount you want to keep on hand; Countertop subtracts what you count from it to work out what you need.
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ fontSize: isMobileOverview ? '0.9rem' : '0.72rem', color: '#8B7B73', marginTop: '2px', display: 'flex', gap: '4px 6px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                              <span style={{ flexShrink: 0 }}>{formatDate(latestRun.session_date || latestRun.created_at)}</span>
                              <span style={{ color: '#D4C4B0', flexShrink: 0 }}>·</span>
                              <span style={{ flexShrink: 0 }}>{latestTotal} items</span>
                              {!isMobileOverview && <><span style={{ color: '#D4C4B0' }}>·</span><span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{latestRun.created_by_name || 'Unknown'}</span></>}
                            </div>
                          )}
                          {!isMobileOverview && !isTemplateParent && (
                            <div style={{ fontSize: '0.66rem', color: '#A89890', marginTop: '1px' }}>
                              Created {formatDateTime(latestRun.created_at)} · Accessed {formatDateTime(latestRun.updated_at)}
                            </div>
                          )}
                          {!isMobileOverview && (() => { const icons = getSessionCategoryIcons(latestRun.id); return icons.length > 0 ? <div style={{ display: 'flex', gap: '3px', alignItems: 'center', marginTop: isTemplateParent ? '6px' : '3px', flexWrap: 'wrap' }}>{icons.map((ic, i) => <span key={i} style={{ fontSize: '1.3rem', lineHeight: 1 }}>{ic}</span>)}</div> : null; })()}
                        </div>

                        {!isTemplateParent && (
                          <div style={{ flexShrink: 0, minWidth: isMobileOverview ? '55px' : '75px' }}>
                            {progressBar(latestCounted, latestTotal)}
                          </div>
                        )}

                        {(() => {
                          const templateUpdatedSinceLatest = template && hasChildren && latestRun && template.updated_at && latestRun.created_at
                            && new Date(template.updated_at).getTime() > new Date(latestRun.created_at).getTime() + 60 * 1000;
                          const newRunLabel = isOrder ? 'New Order' : 'New Count';
                          // ⚙ Setup pill removed — setup is reachable via primary button (Add PARs / Finish Setup)
                          const templateChipEl = null;
                          let buttonEl;
                          if (isTemplateParent) {
                            const isEdited = templateUpdatedSinceLatest;
                            // The button always starts a run. It no longer diverts into a
                            // setup step first — there is nothing left that must be filled
                            // in before counting.
                            // Resume behavior is intentionally removed — active runs are
                            // reachable via their own row; the template card always starts fresh.
                            const primaryLabel = isOrder ? 'New Order' : 'New Count';
                            const primaryColor = isEdited ? '#FB8C00' : '#1976D2';
                            const primaryBtnEl = (
                              <button className="btn btn-sm mm-template-new-btn"
                                style={{ ...templatePillBtnBase, backgroundColor: primaryColor, padding: '0 16px', whiteSpace: 'nowrap', borderRadius: '999px', width: '100%' }}
                                onClick={() => handleNewRunClick()}>
                                {isEdited
                                  ? <TemplateUpdatedLabel label={primaryLabel} />
                                  : (() => {
                                    const _i = primaryLabel.indexOf(' ');
                                    if (_i <= 0) return primaryLabel;
                                    return (<>
                                      <span className="mm-tpl-line">{primaryLabel.slice(0, _i)}</span>
                                      <span className="mm-tpl-line">{primaryLabel.slice(_i + 1)}</span>
                                    </>);
                                  })()}
                              </button>
                            );
                            // Edit Template — always-visible secondary button
                            const editTplBtn = (
                              <button
                                style={{ fontSize: '0.72rem', height: '32px', border: 'none', borderRadius: '7px', backgroundColor: '#8B7B73', color: '#fff', padding: '0 10px', cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', fontWeight: 600, width: '100%' }}
                                onClick={() => { void markTemplateOnboardingSeen('interaction'); handleEditTemplate(template); }}
                                title="Edit this template's items and settings"
                              >
                                <span className="mm-tplcard__review">Review template<span aria-hidden="true" className="mm-tplcard__review-chev">›</span></span>
                              </button>
                            );
                            // Secondary row: only Edit Template beneath primary
                            const secondaryRowEl = (
                              <div style={{ display: 'flex', gap: '4px', marginTop: isMobileOverview ? '6px' : '3px' }}
                                onClick={(e) => e.stopPropagation()}>
                                {editTplBtn}
                              </div>
                            );
                            buttonEl = (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '2px' }}>
                                {primaryBtnEl}
                                {secondaryRowEl}
                              </div>
                            );
                          } else {
                            buttonEl = (
                              <button className="btn btn-sm mm-template-new-btn" style={{ ...templatePillBtnBase, backgroundColor: '#43A047', padding: '0 16px', whiteSpace: 'nowrap', borderRadius: '999px' }}
                                onClick={() => handleCountAgain(latestRun)}>
                                {(() => {
                                  const _i = newRunLabel.indexOf(' ');
                                  if (_i <= 0) return newRunLabel;
                                  return (<>
                                    <span className="mm-tpl-line">{newRunLabel.slice(0, _i)}</span>
                                    <span className="mm-tpl-line">{newRunLabel.slice(_i + 1)}</span>
                                  </>);
                                })()}
                              </button>
                            );
                          }
                          // Template cards no longer carry a "⋯" overflow — the
                          // primary action + Edit Template + (optional) Start New
                          // secondary buttons cover the supported actions.
                          const menuEl = isTemplateParent
                            ? null
                            : moreMenu(latestRun, latestAllCounted);
                          return (
                            <TemplateActionClusterWithConnector
                              isMobileOverview={isMobileOverview}
                              isTemplateParent={isTemplateParent}
                              showConnector={isTemplateParent || !!latestRun}
                              templateChip={templateChipEl}
                              buttonElement={buttonEl}
                              menuElement={menuEl}
                            />
                          );
                        })()}
                        </div>
                      )}
                      renderExpandedPanel={(_g) => isOpen && (() => {
                        // --- Stack metadata panel -------------------------------------------------
                        // Sources: template row (created_by / created_at / updated_at) when present,
                        // else the oldest child for "created" and the newest child for "last edited".
                        // Item classification uses the template's count_items (prefetched by the
                        // expandedStacks effect) with a fallback to the newest child's cached items.
                        const frequency = deriveFrequency(sortedRuns);
                        const oldestChild = [...sessionsInGroup].sort((a, b) =>
                          new Date(a.created_at || a.session_date) - new Date(b.created_at || b.session_date)
                        )[0];
                        const createdSource = template || oldestChild;
                        const editedSource = latestRun;
                        const itemsSourceId = template ? template.id : newestId;
                        const items = sessionItemsCache[itemsSourceId] || [];
                        const itemCount = items.length || (Number(rep.total_items) || 0);
                        // Category tally (EOP) or Vendor tally (Orders).
                        const catTally = {};
                        const vendorTally = {};
                        items.forEach(i => {
                          const snap = i.item_snapshot || {};
                          const cat = snap.category || snap.item_category || i.category || '';
                          if (cat) catTally[cat] = (catTally[cat] || 0) + 1;
                          const rawVendor = snap.vendor || i.vendor || '';
                          const resolved = rawVendor ? (resolveVendor(rawVendor) || rawVendor) : '';
                          if (resolved) vendorTally[resolved] = (vendorTally[resolved] || 0) + 1;
                        });
                        const topCategories = Object.entries(catTally).sort((a, b) => b[1] - a[1]).slice(0, 6);
                        const topVendors = Object.entries(vendorTally).sort((a, b) => b[1] - a[1]).slice(0, 6);
                        const categoryCount = Object.keys(catTally).length;
                        const vendorCount = Object.keys(vendorTally).length;
                        const itemsLoading = cardPreviewStatus[itemsSourceId] === 'loading' && items.length === 0;

                        const metaChip = (label, value) => (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                            <span style={{ fontSize: '0.6rem', color: '#A89890', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{label}</span>
                            <span style={{ fontSize: '0.78rem', color: '#3E2723', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
                          </div>
                        );

                        // Prefer the template's saved cycle (5-4-4 etc.) over the
                        // child-date heuristic: the heuristic reads "Every Saturday"
                        // for NRF fiscal presets because the first few period-ends
                        // happen to land on Saturdays. cycleToScheduleDisplay returns
                        // a stacked "next 3 dates" view for those presets and a
                        // single-line label for everything else (Every Saturday,
                        // 1st of month, Mon & Fri, Every 2 weeks on Mon, …).
                        const cycleDisplay = cycleToScheduleDisplay(template?.ordering_cycle_config);
                        const freqFallback = frequency ? { kind: 'label', text: frequency } : null;
                        const freqInfo = cycleDisplay || freqFallback;
                        const renderFrequencyChip = () => {
                          if (!freqInfo) return null;
                          if (freqInfo.kind === 'dates') {
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, gridColumn: 'span 2' }}>
                                <span style={{ fontSize: '0.6rem', color: '#A89890', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                                  {`Upcoming${freqInfo.label ? ` · ${freqInfo.label}` : ''}`}
                                </span>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                  {freqInfo.dates.map(d => (
                                    <span key={d} style={{ fontSize: '0.78rem', color: '#3E2723', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {formatDayAndDate(d)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          if (freqInfo.muted) {
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                                <span style={{ fontSize: '0.6rem', color: '#A89890', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Frequency</span>
                                <span style={{ fontSize: '0.78rem', color: '#A89890', fontStyle: 'italic', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{freqInfo.text}</span>
                              </div>
                            );
                          }
                          return metaChip('Frequency', freqInfo.text);
                        };
                        return (
                          <div style={{ borderTop: '1px solid #EDE6DE' }}>
                            <div style={{ padding: isMobileOverview ? '10px 12px' : '12px 16px', backgroundColor: '#FDFBF7' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: isMobileOverview ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: '10px 14px', marginBottom: '10px' }}>
                                {renderFrequencyChip()}
                                {metaChip('Created', `${formatDate(createdSource.created_at)} · ${createdSource.created_by_name || 'Unknown'}`)}
                                {metaChip('Last edited', `${formatDate(editedSource.updated_at || editedSource.created_at)} · ${editedSource.last_accessed_by_name || editedSource.created_by_name || 'Unknown'}`)}
                              </div>
                              {(isOrder ? topVendors.length > 0 : topCategories.length > 0) && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                                  <span style={{ fontSize: '0.6rem', color: '#A89890', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginRight: '4px' }}>
                                    {isOrder ? 'Vendors included' : 'Categories included'}
                                  </span>
                                  {(isOrder ? topVendors : topCategories).map(([label, n]) => (
                                    <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', padding: '3px 8px', borderRadius: '12px', backgroundColor: '#F3EDE7', color: '#5A4A42', border: '1px solid #E8DDD0' }}>
                                      {!isOrder && <span style={{ fontSize: '0.85rem', lineHeight: 1 }}>{getCategoryIcon(label)}</span>}
                                      <span style={{ fontWeight: 500 }}>{label}</span>
                                      <span style={{ color: '#8B7B73' }}>·</span>
                                      <span style={{ color: '#8B7B73' }}>{n}</span>
                                    </span>
                                  ))}
                                  {((isOrder ? vendorCount : categoryCount) > (isOrder ? topVendors.length : topCategories.length)) && (
                                    <span style={{ fontSize: '0.68rem', color: '#A89890' }}>
                                      +{(isOrder ? vendorCount : categoryCount) - (isOrder ? topVendors.length : topCategories.length)} more
                                    </span>
                                  )}
                                </div>
                              )}
                              {/* Count-straight-to-order switch surfaces on the template
                                  once it's on, so it can be turned back off from here. */}
                              {isOrder && template?.direct_order_mode && (
                                <div style={{ marginTop: '10px' }} onClick={(e) => e.stopPropagation()}>
                                  {renderDirectOrderToggle(template, { compact: true })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      renderRunRow={(session, { isFirst, isLast, index, runsToDisplay }) => {
                              const c = session.items_counted || 0;
                              const t = (Number(session.total_items) || 0) || groupTotal;
                              const ac = (t > 0 && c >= t) || session.status === 'completed';
                              const sc = c > 0 && !ac;
                              const isNw = session.id === newestId;
                              const isCardOpen = expandedCards.has(session.id);
                              const subIsOrder = session.order_calculations_enabled;
                              const subStatusColor = ac ? '#4CAF50' : sc ? '#FFA726' : '#D4C4B0';
                              const createdAt = new Date(session.created_at || session.session_date);
                              const ageMs = Date.now() - createdAt.getTime();
                              // Suppress NEW chip for completed runs on EOP recurring stacks:
                              // the headline is a just-finished count, not a new auto-created run.
                              // Non-EOP and non-template stacks retain original behavior.
                              const isUnviewedNew = isNw && ageMs >= 0 && ageMs < 24 * 60 * 60 * 1000 && !viewedRunIds.has(session.id)
                                && !(session.status === 'completed' && _isEOPStack && !!template);
                              const now = new Date();
                              const createdToday = isNw
                                && createdAt.getFullYear() === now.getFullYear()
                                && createdAt.getMonth() === now.getMonth()
                                && createdAt.getDate() === now.getDate();
                              const prevSession = isFirst ? null : runsToDisplay[index - 1];
                              const timeFmt = (d) => new Date(d).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
                              const mobileEventFmt = (d) => { const dt = new Date(d); return `${dt.getMonth()+1}/${dt.getDate()} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`; };

                              // Build the event marker(s) that sit above this card.
                              // - First card: only the Started marker for this run.
                              // - Subsequent card: previous run's end state (Completed /
                              //   Not completed / In progress) followed by this run's
                              //   Started marker. These form the "end of A → start of B"
                              //   bracket between adjacent cards.
                              // For in-progress states we surface the last-accessed
                              // timestamp so the marker still carries a date/time
                              // instead of reading as a bare label.
                              const endTsFor = (s) => (
                                s.completed_at
                                || (s.status === 'in_progress' ? (s.last_accessed_at || s.updated_at) : null)
                              );
                              const prevEndKind = !isFirst && (
                                prevSession.completed_at ? 'completed'
                                : prevSession.status === 'in_progress' ? 'inProgress'
                                : 'notCompleted'
                              );
                              const prevEndTs = !isFirst ? endTsFor(prevSession) : null;
                              const prevEndDate = prevEndTs ? formatDate(prevEndTs) : null;
                              const prevEndTime = prevEndTs ? timeFmt(prevEndTs) : null;

                              // Mid-spine chevron: rendered above this row's
                              // prev-end marker when this gap (between the
                              // prior card and this card) is one of the
                              // evenly-spaced positions chosen for the stack.
                              // Only kicks in for 3+ visible runs (expanded
                              // view); 1–2 run stacks keep just the bottom
                              // arrowhead.
                              const showSpineChevron = !isFirst
                                && spineChevronGapsFor(runsToDisplay.length).has(index - 1);

                              return (
                                <div key={session.id}>
                                  {showSpineChevron && renderSpineChevron()}
                                  {!isFirst && renderEventMarker(prevEndKind, isMobileOverview && prevEndTs ? mobileEventFmt(prevEndTs) : prevEndDate, isMobileOverview ? null : prevEndTime)}
                                  {renderEventMarker('started', isMobileOverview ? mobileEventFmt(session.created_at) : formatDate(session.created_at), isMobileOverview ? null : timeFmt(session.created_at))}
                                  {/* Card is anchored to the spine: its left edge
                                      sits at the connector line so the line reads as
                                      one stroke running through the entire stack. */}
                                  {(() => { const _runBg = isMobileOverview ? (isNw ? '#FEFCFA' : '#F9F5F0') : '#FFFFFF'; const _runHoverBg = isMobileOverview ? '#F3EDE7' : '#FBF7F1'; return (
                                  <div style={{
                                    marginLeft: connectorLeft,
                                    marginRight: isMobileOverview ? '6px' : '10px',
                                    borderRadius: isMobileOverview ? '0 8px 8px 0' : '8px',
                                    overflow: 'hidden',
                                    // Desktop: nested white run card with a subtle
                                    // brown left accent so each run reads as its own
                                    // card inside the cream template shell rather
                                    // than as a flat row continuing the parent.
                                    border: isMobileOverview ? '1px solid #E8DDCB' : '1px solid #E0D5CC',
                                    borderLeft: isMobileOverview ? SPINE : '3px solid #C4B4A8',
                                    boxShadow: isNw
                                      ? '0 1px 4px rgba(90,74,66,0.10)'
                                      : (isMobileOverview ? 'none' : '0 1px 2px rgba(90,74,66,0.04)'),
                                  }}>
                                    <div style={{ textAlign: 'center', padding: '2px 8px 0', backgroundColor: _runBg }}>
                                      {statusBadge(session, c, t, ac)}
                                    </div>
                                    <div onClick={() => { if (template) void markTemplateOnboardingSeen('interaction'); expandCard(session.id); }}
                                      style={{
                                        display: 'flex', alignItems: 'center', gap: isMobileOverview ? '6px' : '10px',
                                        padding: isMobileOverview ? '1px 10px 8px 10px' : '2px 16px 9px 12px',
                                        backgroundColor: _runBg,
                                        cursor: 'pointer', transition: 'background-color 0.1s',
                                        position: 'relative',
                                      }}
                                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = _runHoverBg; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = _runBg; }}>

                                      {subIsOrder ? (
                                        <div style={{ position: 'relative', zIndex: 1 }}>{truckIcon(12, subStatusColor)}</div>
                                      ) : (
                                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: subStatusColor, flexShrink: 0, position: 'relative', zIndex: 1 }} />
                                      )}

                                      <svg width="8" height="8" viewBox="0 0 10 10" style={{ flexShrink: 0, transition: 'transform 0.15s', transform: isCardOpen ? 'rotate(90deg)' : 'rotate(0deg)', opacity: 0.4 }}>
                                        <path d="M2.5 1L7.5 5L2.5 9" fill="none" stroke="#8B7B73" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>

                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                          {/* Date: compact "M/D/YY" on mobile so it
                                              stays on a single line with the Latest
                                              flag and "NEW" pill. Desktop keeps
                                              "May 1, 2026". Status is now on its own
                                              top row, centered, so the date row has
                                              more room. */}
                                          <span style={{
                                            fontWeight: isNw ? 600 : 500,
                                            fontSize: isMobileOverview ? '0.78rem' : '0.84rem',
                                            color: '#5A4A42',
                                            ...(isMobileOverview ? { whiteSpace: 'nowrap', flexShrink: 0 } : {}),
                                          }}>
                                            {isMobileOverview
                                              ? formatDateShort(session.session_date || session.created_at)
                                              : formatDate(session.session_date || session.created_at)}
                                          </span>
                                          {/* "Latest" flag — on mobile it's plain
                                              blue text (no pill background) so it
                                              doesn't read as a tappable button. */}
                                          {isNw && (isMobileOverview
                                            ? <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#1976D2', letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0 }}>Latest</span>
                                            : <span style={{ fontSize: '0.6rem', padding: '2px 8px', borderRadius: '10px', backgroundColor: '#1976D2', color: '#fff', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Latest Run</span>
                                          )}
                                          {isUnviewedNew && <span style={{ fontSize: '0.58rem', padding: '1px 6px', borderRadius: '8px', backgroundColor: '#FFF3E0', color: '#E65100', fontWeight: 700, letterSpacing: '0.04em' }}>NEW</span>}
                                        </div>
                                        {/* Counter name: on mobile force a single
                                            line with nowrap+ellipsis so it stops
                                            wrapping in the narrow card. Desktop
                                            output is unchanged. */}
                                        <div style={{
                                          fontSize: '0.7rem',
                                          color: '#8B7B73',
                                          marginTop: '1px',
                                          ...(isMobileOverview ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : {}),
                                        }}>
                                          {c}/{t}{isMobileOverview ? '' : ' counted'} · {session.created_by_name || 'Unknown'}
                                        </div>
                                      </div>

                                      <div style={{ flexShrink: 0, minWidth: isMobileOverview ? '50px' : '60px' }}>
                                        {progressBar(c, t)}
                                      </div>

                                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                        {actionBtn(session, c, t, ac, sc, { showStaleGuard: !isNw, pulseFresh: createdToday })}
                                        {moreMenu(session, ac, { showStaleGuard: !isNw })}
                                      </div>
                                    </div>

                                    {session.status === 'completed' && (
                                      <div style={{ backgroundColor: _runBg, padding: isMobileOverview ? '0 10px 8px' : '0 12px 9px' }}>
                                        <CountCardAvailableReports session={session} onOpenReport={openSessionReport} compact />
                                      </div>
                                    )}

                                    {isCardOpen && renderExpandedDetails(session, { showStaleGuard: !isNw })}
                                  </div>
                                  ); })()}
                                  {isLast && (
                                    <>
                                      {/* Current run's terminal state — the end-of-run oval that
                                          closes this run before the End oval.
                                          Required for single-run stacks and the newest run in
                                          collapsed/expanded views. */}
                                      {(() => {
                                        const curEndKind = session.completed_at ? 'completed'
                                          : session.status === 'in_progress' ? 'inProgress'
                                          : 'notCompleted';
                                        const curEndTs = endTsFor(session);
                                        const curEndDate = curEndTs
                                          ? (isMobileOverview ? mobileEventFmt(curEndTs) : formatDate(curEndTs))
                                          : null;
                                        const curEndTime = (curEndTs && !isMobileOverview) ? timeFmt(curEndTs) : null;
                                        return renderEventMarker(curEndKind, curEndDate, curEndTime);
                                      })()}
                                      {/* Spine drop with green arrowhead — terminates cleanly at
                                          the top edge of the End oval. No borderLeft on the oval
                                          itself so the spine visibly ends here rather than running
                                          alongside it. */}
                                      <div style={{ marginLeft: connectorLeft, borderLeft: SPINE, height: isMobileOverview ? '22px' : '28px', position: 'relative' }}>
                                        <div style={{
                                          position: 'absolute',
                                          left: '-4px',
                                          bottom: '2px',
                                          width: 0,
                                          height: 0,
                                          borderLeft: '4px solid transparent',
                                          borderRight: '4px solid transparent',
                                          borderTop: '6px solid #7BC67E',
                                        }} />
                                      </div>
                                      {/* Ghost-card End oval — flowchart End node. Spine drop above
                                          terminates into its top edge. No borderLeft here so the
                                          line reads as ending in the oval, not running beside it. */}
                                      {(() => {
                                        const ghostLabel = `Next ${isOrder ? 'order' : 'count'} will appear here`;
                                        const triggerNew = () => handleCountAgain(template || latestRun);
                                        return (
                                          <div style={{ marginLeft: connectorLeft, marginRight: isMobileOverview ? '6px' : '10px', marginBottom: isMobileOverview ? '6px' : '8px' }}>
                                            <div
                                              role="button"
                                              tabIndex={0}
                                              aria-label={ghostLabel}
                                              title={ghostLabel}
                                              onClick={triggerNew}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                  e.preventDefault();
                                                  triggerNew();
                                                }
                                              }}
                                              style={{
                                                borderRadius: '999px',
                                                border: '1.5px dashed #D9CEC2',
                                                padding: isMobileOverview ? '8px 16px' : '10px 20px',
                                                backgroundColor: 'transparent',
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                color: '#A89890',
                                                fontSize: isMobileOverview ? '0.72rem' : '0.78rem',
                                                fontStyle: 'italic',
                                                opacity: 0.75,
                                                transition: 'opacity 0.15s, background-color 0.15s, border-color 0.15s',
                                                outline: 'none',
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.opacity = '1';
                                                e.currentTarget.style.backgroundColor = '#FBF7F1';
                                                e.currentTarget.style.borderColor = '#C9BCAE';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.opacity = '0.75';
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                e.currentTarget.style.borderColor = '#D9CEC2';
                                              }}
                                              onFocus={(e) => {
                                                e.currentTarget.style.opacity = '1';
                                                e.currentTarget.style.backgroundColor = '#FBF7F1';
                                              }}
                                              onBlur={(e) => {
                                                e.currentTarget.style.opacity = '0.75';
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                              }}
                                            >
                                              <span
                                                aria-hidden="true"
                                                style={{
                                                  width: isMobileOverview ? '14px' : '16px',
                                                  height: isMobileOverview ? '14px' : '16px',
                                                  borderRadius: '50%',
                                                  border: '1.5px dashed #C9BCAE',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  fontSize: isMobileOverview ? '0.7rem' : '0.78rem',
                                                  fontStyle: 'normal',
                                                  fontWeight: 600,
                                                  color: '#A89890',
                                                  lineHeight: 1,
                                                  flexShrink: 0,
                                                }}
                                              >
                                                +
                                              </span>
                                              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {ghostLabel}
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </>
                                  )}
                                </div>
                              );
                      }}
                    />
                  );
                };

                // ── Redesigned template cards + detail screen ─────────────────
                // Derives everything the calm summary card and the template
                // detail screen need from a group. Mirrors the derivations that
                // used to live inline in renderGroupCard's template branch.
                const computeTemplateInfo = (groupId, sessionsInGroup, template) => {
                  const isActiveSession = (s) => s.status === 'draft' || s.status === 'in_progress' || s.status === 'paused';
                  const byDateDesc = (a, b) => new Date(b.created_at || b.session_date) - new Date(a.created_at || a.session_date);
                  const sortedRuns = [...sessionsInGroup].sort(byDateDesc);
                  const activeRuns = sessionsInGroup.filter(isActiveSession).sort(byDateDesc);
                  const completedRuns = sessionsInGroup.filter(s => !isActiveSession(s)).sort(byDateDesc);
                  const latestRun = sortedRuns[0] || template;
                  const rep = template || sessionsInGroup[0] || {};
                  const isOrder = rep.order_calculations_enabled === true;
                  const repCp = rep.count_purpose || '';
                  const repIm = rep.inventory_mode || '';
                  const isEOP = !isOrder && (repCp === 'period_end' || repCp === 'full_inventory' || repIm === 'Financial' || repIm === 'Both');
                  const titleColor = getTemplateColor(rep);
                  const seriesName = template ? (template.name || '') : (rep.name || '');
                  // The run the card's one button acts on: a running/paused count
                  // first, else the newest untouched draft. (The card opens the
                  // existing open run instead of quietly creating another one.)
                  const runningRuns = sessionsInGroup.filter(s => s.status === 'in_progress' || s.status === 'paused').sort(byDateDesc);
                  const draftRuns = sessionsInGroup.filter(s => s.status === 'draft').sort(byDateDesc);
                  const currentRun = runningRuns[0] || draftRuns[0] || null;
                  const currentRunStarted = !!currentRun && (currentRun.status !== 'draft' || (Number(currentRun.items_counted) || 0) > 0);
                  const groupTotal = Math.max(
                    0,
                    ...sessionsInGroup.map(s => Number(s.total_items) || 0),
                    ...sessionsInGroup.map(s => Number(s.items_counted) || 0)
                  );
                  const metaSourceId = template ? template.id : (latestRun ? latestRun.id : null);
                  const metaItems = sessionItemsCache[metaSourceId] || [];
                  const itemCount = metaItems.length || Number(template?.total_items) || groupTotal;
                  const catTally = {};
                  const vendorTally = {};
                  metaItems.forEach(i => {
                    const snap = i.item_snapshot || {};
                    const cat = snap.category || snap.item_category || i.category || '';
                    if (cat) catTally[cat] = (catTally[cat] || 0) + 1;
                    const rawVendor = snap.vendor || i.vendor || '';
                    const resolved = rawVendor ? (resolveVendor(rawVendor) || rawVendor) : '';
                    if (resolved) vendorTally[resolved] = (vendorTally[resolved] || 0) + 1;
                  });
                  const categoryCount = Object.keys(catTally).length;
                  const vendorCount = Object.keys(vendorTally).length;
                  const previewLoading = cardPreviewStatus[metaSourceId] === 'loading' && metaItems.length === 0;
                  const parsMissing = isOrder && !template?.direct_order_mode && !template?.pars_setup_skipped
                    && !previewLoading
                    && metaItems.length > 0
                    && isParsSetupNeeded(metaItems);
                  const schedCyc = template?.ordering_cycle_config;
                  const isSkippedSentinel = !!(schedCyc && schedCyc.preset === 'none' && schedCyc.skipped === true);
                  const hasRealCycle = !!(schedCyc && schedCyc.preset && !isSkippedSentinel);
                  const eopInheritsStore = isEOP && !!inventoryPeriodConfigToCycle(storePeriodConfig);
                  const scheduleMissing = !hasRealCycle && !isSkippedSentinel && !eopInheritsStore;
                  const newestByCreated = sortedRuns[0] || null;
                  const updatedSinceLatest = !!(template && newestByCreated && template.updated_at && newestByCreated.created_at
                    && new Date(template.updated_at).getTime() > new Date(newestByCreated.created_at).getTime() + 60 * 1000);
                  const cyc = template?.ordering_cycle_config
                    || (isEOP ? inventoryPeriodConfigToCycle(storePeriodConfig) : null);
                  const cycleLbl = isSkippedSentinel ? null : cycleToFriendlyLabel(cyc);
                  let nextOccurrence = null;
                  try { nextOccurrence = cyc && !isSkippedSentinel ? getNextOccurrence(cyc, new Date()) : null; } catch (_) {}
                  const lastCompletedAt = sessionsInGroup.map(s => s.completed_at).filter(Boolean).sort().reverse()[0] || null;
                  return {
                    sortedRuns, activeRuns, completedRuns, latestRun, template,
                    isOrder, isEOP, titleColor, seriesName,
                    currentRun, currentRunStarted, groupTotal,
                    metaSourceId, metaItems, itemCount, catTally, vendorTally, categoryCount, vendorCount,
                    previewLoading, parsMissing, scheduleMissing,
                    updatedSinceLatest, cycleLbl, nextOccurrence, lastCompletedAt,
                  };
                };

                // Single entry point for "start a brand-new run from a template".
                // Goes straight to the start-choice modal: the schedule prompt and
                // the pars wizard that used to run first are gone. PARs are set
                // in the count, the schedule is inferred on completion, and both are
                // still offered from ⚙ Template Setup.
                const startNewRunForTemplate = async (info) => {
                  const { template, latestRun } = info;
                  if (!template) return;
                  void markTemplateOnboardingSeen('interaction');
                  const sessionSource = isSyntheticTemplate(template) ? latestRun : (template || latestRun);
                  if (!sessionSource) return;
                  if (typeof window !== 'undefined' && window.ReactNativeWebView) {
                    handleCountAgain(sessionSource, 'web');
                    return;
                  }
                  setCountStartChoiceModal({ session: sessionSource });
                };

                // One human sentence that replaces the chip/badge/dot pile.
                const templateStatusFor = (info) => {
                  const noun = info.isOrder ? 'Order' : 'Count';
                  if (info.currentRun && info.currentRunStarted) {
                    const c = Number(info.currentRun.items_counted) || 0;
                    const t = (Number(info.currentRun.total_items) || 0) || info.groupTotal;
                    return { text: `${noun} in progress — ${c} of ${t} counted`, dot: OV_ATTN_DOT, showBar: true, barCounted: c, barTotal: t };
                  }
                  if (info.currentRun) {
                    const t = (Number(info.currentRun.total_items) || 0) || info.groupTotal;
                    return { text: `${noun} ready to start${t ? ` · ${t} items` : ''}`, dot: OV_GOOD_DOT };
                  }
                  if (info.updatedSinceLatest) {
                    return { text: `Updated since the last ${info.isOrder ? 'order' : 'count'}${info.cycleLbl ? ` · ${info.cycleLbl}` : ''}`, dot: OV_ATTN_DOT };
                  }
                  const parts = [];
                  if (info.cycleLbl) parts.push(info.cycleLbl);
                  if (info.lastCompletedAt) parts.push(`last ${info.isOrder ? 'ordered' : 'counted'} ${formatRelativeDate(info.lastCompletedAt)}`);
                  else parts.push(`not ${info.isOrder ? 'ordered' : 'counted'} yet`);
                  if (info.completedRuns.length > 0) parts.push(`used ${info.completedRuns.length} time${info.completedRuns.length === 1 ? '' : 's'}`);
                  return { text: parts.join(' · '), dot: OV_GOOD_DOT };
                };

                // The card's one state-dependent primary action.
                const templatePrimaryFor = (groupId, info) => {
                  if (info.currentRun) {
                    const run = info.currentRun;
                    const label = info.currentRunStarted
                      ? (info.isOrder ? 'Resume order' : 'Resume count')
                      : (info.isOrder ? 'Start order' : 'Start count');
                    return {
                      label,
                      onClick: () => {
                        void markTemplateOnboardingSeen('interaction');
                        markRunViewed(run.id);
                        window.location.href = `count-session.html?session=${run.id}`;
                      },
                    };
                  }
                  // No setup branch: an unconfigured template starts a count like
                  // any other. Template Setup is reached from the card's own
                  // Schedule / PAR buttons instead.
                  return {
                    label: info.isOrder ? 'Start order' : 'Start count',
                    helpTopicId: 'template.start_count',
                    tipContext: { template_id: info.template?.id ?? null, template_type: info.isOrder ? 'ordering' : 'eop' },
                    onClick: () => { void startNewRunForTemplate(info); },
                  };
                };

                const templateEmojiFor = (info) => {
                  const icons = info.latestRun ? getSessionCategoryIcons(info.latestRun.id) : [];
                  return icons[0] || (info.isOrder ? ORDER_TEMPLATE_ICON : EOP_TEMPLATE_ICON);
                };

                // Calm summary card: emoji + full title, one status sentence,
                // the created/last-used metadata, an always-visible "View
                // details & history ⌄" expander, one ≥48px primary button.
                //
                // Three actions, three sibling controls: the descriptive body
                // (a div with role=button) and the "View details & history"
                // label (a real button with aria-expanded/aria-controls) both
                // expand the card INLINE — template details, setup actions and
                // previous runs render in a <section> at the bottom of the same
                // article, without navigating — and the primary count button
                // only starts/resumes a count. Only one card is expanded at a
                // time (accordion). The separate template detail screen stays
                // reachable from "View full history" when a history runs past
                // the inline window.
                //
                // Same anatomy on mobile and desktop — desktop places the
                // primary button in a right column so wide cards use the extra
                // width.
                const renderTemplateSummaryCard = (groupId, sessionsInGroup, template) => {
                  const info = computeTemplateInfo(groupId, sessionsInGroup, template);
                  const status = templateStatusFor(info);
                  const primary = templatePrimaryFor(groupId, info);
                  const emoji = templateEmojiFor(info);
                  // Card metadata: when the template was created, when it was
                  // last used, and who created it. Mobile keeps the compact
                  // M/D/YY dates so the line fits a narrow card. Whatever does
                  // not fit now wraps to a second line — it is never ellipsized
                  // away.
                  const createdSource = template || info.sortedRuns[info.sortedRuns.length - 1] || null;
                  const lastUsedMs = Math.max(0, ...sessionsInGroup
                    .map(s => new Date(s.last_accessed_at || s.updated_at || s.created_at).getTime())
                    .filter(n => !Number.isNaN(n)));
                  const cardDateFmt = (d) => isMobileOverview ? formatDateShort(d) : formatDate(d);
                  const creator = createdSource?.created_by_name;
                  const showCreator = !isMobileOverview && creator && creator !== 'Unknown' && creator !== 'Not yet';
                  const metaParts = [];
                  if (createdSource?.created_at) metaParts.push(`Created ${cardDateFmt(createdSource.created_at)}${showCreator ? ` by ${creator}` : ''}`);
                  if (lastUsedMs > 0) metaParts.push(`Last used ${cardDateFmt(lastUsedMs)}`);
                  const metaLine = metaParts.join(' · ');
                  const noun = info.isOrder ? 'order' : 'count';
                  // Accordion: expanding this card collapses whichever card was
                  // open. The toggle never touches the scroller, so the list's
                  // scroll position and the triggering control's focus survive.
                  const isExpanded = inlineDetailsGroupId === groupId;
                  const detailsId = `mm-tplcard-details-${groupId}`;
                  const toggleDetails = () => {
                    void markTemplateOnboardingSeen('interaction');
                    setInlineDetailsGroupId(cur => (cur === groupId ? null : groupId));
                  };
                  const onBodyKeyDown = (e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDetails(); }
                  };
                  const primaryBtn = (
                    <HelpTooltip helpTopicId={primary.helpTopicId} context={primary.tipContext} section="template_card">
                      <button
                        type="button"
                        className="mm-tplcard__primary"
                        style={ovPrimaryBtnStyle()}
                        onClick={(e) => { e.stopPropagation(); primary.onClick(); }}
                      >
                        {primary.label}
                      </button>
                    </HelpTooltip>
                  );

                  // Inline details & history — computed only while the card is
                  // expanded (everything comes from already-loaded client-side
                  // state; expanding fires no query of its own).
                  let detailsContent = null;
                  if (isExpanded) {
                    const typeLabel = info.isOrder ? 'Order' : info.isEOP ? 'End of period' : 'Count';
                    const editedBy = [template?.last_accessed_by_name, template?.created_by_name]
                      .find(n => n && n !== 'Unknown' && n !== 'Not yet') || '';
                    const detailCell = (label, value) => (
                      <div key={label} style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '0.62rem', color: '#A89890', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>{label}</div>
                        <div style={{ fontSize: '0.82rem', color: OV_INK, fontWeight: 500, overflowWrap: 'break-word' }}>{value}</div>
                      </div>
                    );
                    const openSetupStep = ({ scheduleMissing, parsMissing }) => {
                      void markTemplateOnboardingSeen('interaction');
                      void openTemplateSetupPicker({ template, parsMissing, scheduleMissing, isOrder: info.isOrder, isEOP: info.isEOP });
                    };
                    // Previous runs — newest first, 3 by default, up to 12 once
                    // "View all" reveals them; longer histories end in a quiet
                    // "View full history" that opens the full detail screen.
                    const RUNS_DEFAULT = 3;
                    const RUNS_MAX = 12;
                    const allRuns = info.sortedRuns;
                    const showAllRuns = inlineHistoryGroupId === groupId;
                    const visibleRuns = showAllRuns ? allRuns.slice(0, RUNS_MAX) : allRuns.slice(0, RUNS_DEFAULT);
                    const runStatusWord = (run) => (run.status === 'completed' ? 'Completed' : run.status === 'in_progress' ? 'In progress' : run.status === 'paused' ? 'Paused' : 'Draft');
                    // Completed green, in progress (and paused) yellow, draft grey.
                    const runStatusColor = (run) => (run.status === 'completed' ? '#2E7D32' : run.status === 'in_progress' || run.status === 'paused' ? '#F9A825' : OV_INK_SOFT);
                    const renderRunRow = (run) => {
                      const c = Number(run.items_counted) || 0;
                      const t = (Number(run.total_items) || 0) || info.groupTotal;
                      const isDone = run.status === 'completed';
                      // Date the run was actually counted — completion time for
                      // finished runs, the latest touch otherwise. session_date
                      // is the SCHEDULED date and can sit in the future, so it
                      // never labels a run here.
                      const countedTs = isDone
                        ? (run.completed_at || run.updated_at || run.created_at)
                        : (run.last_accessed_at || run.updated_at || run.created_at);
                      const dateLbl = formatDate(countedTs);
                      const who = (run.created_by_name && run.created_by_name !== 'Unknown' && run.created_by_name !== 'Not yet') ? run.created_by_name : '';
                      // Runs never show money — no totals exist, so no $0.00.
                      const resultLbl = `${c} of ${t} items counted`;
                      const verb = isDone ? `View/Edit ${noun}` : 'Resume';
                      const actionName = `${isDone ? 'View/Edit' : 'Resume'} ${info.seriesName} ${noun} from ${dateLbl}, ${c.toLocaleString()} of ${t.toLocaleString()} items counted`;
                      // Completed → the same "view a finished run" the detail
                      // screen's history rows use (reopens that exact session);
                      // anything else resumes the existing run. Neither ever
                      // creates a new run.
                      const openRun = () => {
                        markRunViewed(run.id);
                        if (isDone) { void handleEditSession(run); return; }
                        window.location.href = `count-session.html?session=${run.id}`;
                      };
                      return (
                        <div key={run.id} className="mm-tplcard__runrow">
                          <div className="mm-tplcard__runbody">
                            <div className="mm-tplcard__runhead">
                              <span style={{ fontWeight: 600, fontSize: '0.86rem', color: OV_INK }}>{dateLbl}</span>
                              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: runStatusColor(run) }}>{runStatusWord(run)}</span>
                            </div>
                            {isDone && run.completed_at && (
                              <div className="mm-tplcard__runline" style={{ fontSize: '0.78rem', color: OV_INK_SOFT }}>
                                Marked completed on {formatDate(run.completed_at)} at {formatTimeOnly(run.completed_at)}
                              </div>
                            )}
                            {who && (
                              <div className="mm-tplcard__runline" style={{ fontSize: '0.8rem', color: OV_INK_SOFT, minWidth: 0, overflowWrap: 'break-word' }}>Counted by {who}</div>
                            )}
                            <div className="mm-tplcard__runline" style={{ fontSize: '0.8rem', color: OV_INK_SOFT }}>{resultLbl}</div>
                          </div>
                          <div className="mm-tplcard__runaction">
                            <button
                              type="button"
                              aria-label={actionName}
                              onClick={openRun}
                              style={{
                                fontFamily: 'inherit',
                                fontSize: '0.78rem',
                                fontWeight: 700,
                                minHeight: isMobileOverview ? '44px' : '34px',
                                width: isMobileOverview ? '100%' : 'auto',
                                padding: '0 14px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                border: `1.5px solid ${isDone ? OV_LINE : OV_ACTION}`,
                                backgroundColor: isDone ? 'transparent' : OV_ACTION,
                                color: isDone ? OV_ACTION : '#FFF6F1',
                              }}
                            >
                              {verb}
                            </button>
                          </div>
                        </div>
                      );
                    };
                    detailsContent = (
                      <div className="mm-tplcard__details-in">
                        <div className="mm-tplcard__details-heading">Template details</div>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobileOverview ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px 14px', marginTop: '8px' }}>
                          {detailCell('Type', typeLabel)}
                          {info.itemCount > 0 && detailCell('Items', info.itemCount)}
                          {(info.isOrder ? info.vendorCount : info.categoryCount) > 0 && detailCell(info.isOrder ? 'Vendors' : 'Categories', info.isOrder ? info.vendorCount : info.categoryCount)}
                          {detailCell('Schedule', info.cycleLbl || 'Not set')}
                          {template?.updated_at && detailCell('Last edited', `${formatDate(template.updated_at)}${editedBy ? ` by ${editedBy}` : ''}`)}
                        </div>

                        {/* Always here, never flagged as outstanding: the schedule is
                            inferred from completions and PARs can be set mid-count.
                            This is for the user who would rather say it up front, or
                            who wants to change what was inferred. One button, because
                            Template Setup is one panel listing whichever steps apply. */}
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
                          <button
                            type="button"
                            style={{ ...ovQuietBtnStyle({ fullWidth: isMobileOverview }), minHeight: '44px' }}
                            onClick={() => openSetupStep({ scheduleMissing: info.scheduleMissing, parsMissing: info.parsMissing })}
                          >
                            {info.isOrder && !template?.direct_order_mode ? '🗓️ Schedule & PARs' : '🗓️ Schedule'}
                          </button>
                          <button
                            type="button"
                            style={{ ...ovQuietBtnStyle({ fullWidth: isMobileOverview }), minHeight: '44px' }}
                            onClick={() => { void markTemplateOnboardingSeen('interaction'); void handleEditTemplate(template); }}
                            title="Edit this template's items, name and settings"
                          >
                            ✏️ Edit template
                          </button>
                          {/* Archive / Delete — same quiet-button format, red text to signal a destructive action */}
                          <button
                            type="button"
                            style={{ ...ovQuietBtnStyle({ fullWidth: isMobileOverview }), minHeight: '44px', color: '#C62828' }}
                            onClick={() => {
                              setTemplateArchiveConfirmText('');
                              setTemplateLifecycleModal({ template, info });
                            }}
                            title="Archive this template, or delete it if it has no items or history"
                          >
                            🗑️ Archive / Delete
                          </button>
                        </div>

                        {info.updatedSinceLatest && (
                          <div style={{ marginTop: '12px', backgroundColor: '#FDF3DF', border: '1px solid #F1DFB4', color: '#8A5A00', borderRadius: '10px', padding: '9px 12px', fontSize: '0.82rem', fontWeight: 600 }}>
                            ✏️ Updated since last {noun}. Changes will apply to the next {noun}.
                          </div>
                        )}

                        <div style={{ marginTop: '18px' }}>
                          <div className="mm-tplcard__details-heading">Previous runs</div>
                          {allRuns.length === 0 && (
                            <div style={{ fontSize: '0.85rem', color: '#A89890', padding: '8px 0', fontStyle: 'italic' }}>
                              Nothing here yet — your first {noun} will appear here. 🌳
                            </div>
                          )}
                          {allRuns.length > 0 && (
                            <div style={showAllRuns && visibleRuns.length > 6 ? { maxHeight: isMobileOverview ? '60vh' : '320px', overflowY: 'auto' } : undefined}>
                              {visibleRuns.map(renderRunRow)}
                            </div>
                          )}
                          {!showAllRuns && allRuns.length > RUNS_DEFAULT && (
                            <button
                              type="button"
                              onClick={() => setInlineHistoryGroupId(groupId)}
                              style={{ marginTop: '10px', border: `1px solid ${OV_LINE}`, backgroundColor: OV_CARD_BG, borderRadius: '999px', padding: '9px 16px', fontSize: '0.82rem', fontWeight: 700, color: OV_INK_SOFT, cursor: 'pointer', fontFamily: 'inherit', minHeight: '40px' }}
                            >
                              View all {allRuns.length} previous runs
                            </button>
                          )}
                          {showAllRuns && allRuns.length > RUNS_MAX && (
                            <button
                              type="button"
                              onClick={() => { void markTemplateOnboardingSeen('interaction'); openTemplateDetail(groupId); }}
                              style={{ marginTop: '10px', background: 'none', border: 'none', padding: '9px 2px', fontSize: '0.82rem', fontWeight: 700, color: OV_ACTION, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', minHeight: '40px' }}
                            >
                              View full history
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // The body is a div with role=button holding the descriptive
                  // column — icon, name, status, metadata and the trailing
                  // chevron — so every part of it expands the inline details.
                  // The "View details & history" label is its own real button
                  // (a sibling, never nested inside another button); the spans
                  // inside the body keep it phrasing-content-only in spirit.
                  return (
                    <article
                      key={`tpl-${groupId}`}
                      className="mm-tplcard"
                      aria-label={info.seriesName}
                      style={{
                        borderLeft: `4px solid ${info.titleColor}`,
                        padding: isMobileOverview ? '14px 14px 14px 14px' : '16px 18px',
                        marginBottom: '10px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            role="button"
                            tabIndex={0}
                            aria-expanded={isExpanded}
                            aria-controls={detailsId}
                            className="mm-tplcard__body"
                            onClick={toggleDetails}
                            onKeyDown={onBodyKeyDown}
                          >
                            <span aria-hidden="true" className="mm-tplcard__emoji">{emoji}</span>
                            <span className="mm-tplcard__text">
                              <span className="mm-tplcard__name" style={{ fontSize: isMobileOverview ? '1.02rem' : '1.05rem' }}>
                                {info.seriesName}
                              </span>
                              <span className="mm-tplcard__status">
                                <span className="mm-tplcard__dot" style={{ backgroundColor: status.dot }} />
                                <span style={{ minWidth: 0 }}>{status.text}</span>
                              </span>
                              {status.showBar && (
                                <span className="mm-tplcard__bar">
                                  <span className="mm-tplcard__bar-fill" style={{ width: `${status.barTotal > 0 ? Math.min(100, Math.round((status.barCounted / status.barTotal) * 100)) : 0}%` }} />
                                </span>
                              )}
                              {metaLine && (
                                <span className="mm-tplcard__meta">{metaLine}</span>
                              )}
                            </span>
                            <span aria-hidden="true" className={`mm-tplcard__chev${isExpanded ? ' mm-tplcard__chev--open' : ''}`}>›</span>
                          </div>
                          {/* The always-visible expander. Says what the card
                              body does, so nobody has to hover (or guess from a
                              bare chevron) to find out. The help tooltip is
                              supplementary — HelpTipButton keeps the button the
                              card body's immediate sibling and portals the
                              bubble above the page. */}
                          <HelpTipButton
                            helpTopicId="template.details_history"
                            context={{ template_id: info.template?.id ?? null, template_type: info.isOrder ? 'ordering' : 'eop' }}
                            page="templates"
                            section="template_card"
                            className="mm-tplcard__review"
                            aria-expanded={isExpanded}
                            aria-controls={detailsId}
                            aria-label={`${isExpanded ? 'Hide' : 'View'} details and history for ${info.seriesName}`}
                            onClick={toggleDetails}
                          >
                            {isExpanded ? 'Hide details & history' : 'View details & history'}
                            <span aria-hidden="true" className="mm-tplcard__review-chev">{isExpanded ? '⌃' : '⌄'}</span>
                          </HelpTipButton>
                        </div>
                        {!isMobileOverview && (
                          <div style={{ flexShrink: 0, width: '180px', alignSelf: 'center' }}>
                            {primaryBtn}
                          </div>
                        )}
                      </div>
                      {isMobileOverview && (
                        <div style={{ marginTop: '14px' }}>
                          {primaryBtn}
                        </div>
                      )}
                      {isExpanded && (
                        <section id={detailsId} className="mm-tplcard__details" aria-label={`Details and history for ${info.seriesName}`}>
                          {detailsContent}
                        </section>
                      )}
                    </article>
                  );
                };

                // Recent activity tray — every incomplete run touched in the
                // past 7 days, each row the same weight, for a quick resume
                // after a break. Replaces the old pinned orange hero: no
                // featured run, no oversized Resume button.
                const RECENT_ACTIVITY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
                const RECENT_ACTIVITY_COLLAPSED_ROWS = 3;
                const RECENT_ACTIVITY_SCROLL_AFTER = 12;
                // Last activity wins: never fall back to created_at while a
                // newer access/update timestamp exists.
                const recentActivityTs = (s) => s.last_accessed_at || s.updated_at || s.created_at;
                const renderRecentActivity = () => {
                  const nowMs = Date.now();
                  const candidates = [];
                  Object.keys(groupedSessions).forEach(gId => {
                    const g = groupedSessions[gId] || {};
                    (g.children || []).forEach(s => {
                      // Incomplete only; queryCountSessionsActive already drops
                      // archived/trashed/voided rows at fetch time.
                      if (s.status !== 'in_progress' && s.status !== 'paused' && s.status !== 'draft') return;
                      const ts = new Date(recentActivityTs(s)).getTime();
                      if (isNaN(ts) || nowMs - ts > RECENT_ACTIVITY_WINDOW_MS) return;
                      candidates.push({ run: s, group: g });
                    });
                  });
                  // Empty state: nothing featured — one compact neutral line.
                  if (candidates.length === 0) {
                    return (
                      <div style={{ fontSize: '0.82rem', color: '#A89890', padding: '2px 2px 12px', maxWidth: '900px' }}>
                        No incomplete counts or orders have been active in the past 7 days.
                      </div>
                    );
                  }
                  candidates.sort((a, b) => {
                    const diff = new Date(recentActivityTs(b.run)) - new Date(recentActivityTs(a.run));
                    if (diff !== 0) return diff;
                    return new Date(b.run.created_at) - new Date(a.run.created_at);
                  });

                  const expanded = recentActivityExpanded;
                  const scrollable = expanded && candidates.length > RECENT_ACTIVITY_SCROLL_AFTER;
                  const visibleRuns = expanded ? candidates : candidates.slice(0, RECENT_ACTIVITY_COLLAPSED_ROWS);
                  // The overview below IS the full history — scroll to it.
                  const scrollToFullList = () => {
                    const reduceMotion = typeof window.matchMedia === 'function'
                      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                    const el = document.getElementById('section-end-of-period') || document.getElementById('section-orders');
                    if (el) el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
                  };

                  return (
                    <section aria-labelledby="mm-ract-heading" className="mm-ract" style={{
                      maxWidth: '900px',
                      padding: isMobileOverview ? '10px 10px 6px' : '12px 16px 8px',
                      marginBottom: '16px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', padding: '2px 4px 8px' }}>
                        <h2 id="mm-ract-heading" style={{ margin: 0, fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', color: OV_INK_SOFT }}>Recent activity</h2>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: OV_INK_SOFT, flexShrink: 0 }}>{candidates.length} open</span>
                      </div>
                      <ul
                        id="mm-ract-list"
                        style={{
                          margin: 0, padding: 0, listStyle: 'none',
                          ...(scrollable ? {
                            // ~5 rows tall, with a subtle bottom fade hinting
                            // at the rest; the heading stays fixed above.
                            maxHeight: '236px', overflowY: 'auto',
                            WebkitMaskImage: 'linear-gradient(to bottom, #000 80%, transparent)',
                            maskImage: 'linear-gradient(to bottom, #000 80%, transparent)',
                          } : {}),
                        }}
                      >
                        {visibleRuns.map(({ run, group }) => {
                          const isOrder = run.order_calculations_enabled === true;
                          const c = Number(run.items_counted) || 0;
                          const t = Number(run.total_items) || 0;
                          const name = (group.template ? group.template.name : run.name) || 'Untitled';
                          // Last active user, falling back to the creator;
                          // hidden when neither resolves to a real name.
                          const who = [run.last_accessed_by_name, run.created_by_name]
                            .find(w => w && w !== 'Not yet' && w !== 'Unknown') || '';
                          const recency = formatRelativeTime(recentActivityTs(run));
                          const noun = isOrder ? 'order' : 'count';
                          const ariaParts = [`Resume ${noun} ${name}`, `${c} of ${t} items counted`];
                          const activeBits = [who, recency].filter(Boolean);
                          if (activeBits.length > 0) ariaParts.push(`last active ${activeBits.join(' ')}`);
                          return (
                            <li key={run.id} style={{ borderTop: `1px solid ${OV_LINE}` }}>
                              <button
                                type="button"
                                className="mm-ract__row"
                                aria-label={ariaParts.join(', ')}
                                onClick={() => {
                                  markRunViewed(run.id);
                                  window.location.href = `count-session.html?session=${run.id}`;
                                }}
                                style={{ padding: isMobileOverview ? '10px 6px' : '8px 8px' }}
                              >
                                <span style={{ flexShrink: 0, fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.07em', padding: '3px 7px', borderRadius: '999px', backgroundColor: '#F5F0EB', border: '1px solid #E3D8CC', color: OV_INK_SOFT }}>
                                  {isOrder ? 'ORDER' : 'COUNT'}
                                </span>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ display: 'block', fontWeight: 700, fontSize: '0.9rem', color: OV_INK, lineHeight: 1.3, overflowWrap: 'break-word' }}>{name}</span>
                                  <span style={{ display: 'block', fontSize: '0.76rem', color: OV_INK_SOFT, marginTop: '2px', overflowWrap: 'break-word' }}>
                                    {c} of {t} counted{who ? ` · ${who}` : ''}{recency ? ` · ${recency}` : ''}
                                  </span>
                                </span>
                                <span aria-hidden="true" style={{ color: '#C7B8AC', fontSize: '1.15rem', lineHeight: 1, flexShrink: 0, alignSelf: 'center' }}>›</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                      {candidates.length > RECENT_ACTIVITY_COLLAPSED_ROWS && (
                        <button
                          type="button"
                          className="mm-ract__toggle"
                          aria-expanded={expanded}
                          aria-controls="mm-ract-list"
                          onClick={() => setRecentActivityExpanded(v => !v)}
                          style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderTop: `1px solid ${OV_LINE}`, margin: 0, padding: '10px 8px 6px', color: OV_ACTION, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                        >
                          {expanded ? 'Show fewer ↑' : `Show all ${candidates.length} open from the past 7 days ↓`}
                        </button>
                      )}
                      {scrollable && (
                        <button
                          type="button"
                          className="mm-ract__viewall"
                          onClick={scrollToFullList}
                          style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', margin: 0, padding: '8px 8px 6px', color: OV_INK_SOFT, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                        >
                          View all counts and orders
                        </button>
                      )}
                    </section>
                  );
                };

                // "Jun 10" (adds the year only when it isn't this year).
                const historyDateLabel = (d) => {
                  if (!d) return '';
                  const dt = new Date(d);
                  if (isNaN(dt.getTime())) return '';
                  const opts = { month: 'short', day: 'numeric' };
                  if (dt.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
                  return dt.toLocaleDateString(undefined, opts);
                };

                // Template detail screen — the full run history, stats, setup
                // and Edit Template. Cards now expand their details inline; this
                // screen is reached from an expanded card's "View full history"
                // when a history runs past the inline window. The back button
                // (and the browser/OS back gesture) returns to the list.
                const renderTemplateDetailScreen = (groupId, sessionsInGroup, template) => {
                  const info = computeTemplateInfo(groupId, sessionsInGroup, template);
                  const primary = templatePrimaryFor(groupId, info);
                  const emoji = templateEmojiFor(info);
                  const noun = info.isOrder ? 'order' : 'count';
                  const historyRuns = info.sortedRuns; // newest first
                  const HISTORY_DEFAULT = 6;
                  const showAllHistory = expandedStackChildren.has(groupId);
                  const visibleHistory = showAllHistory ? historyRuns : historyRuns.slice(0, HISTORY_DEFAULT);
                  const hiddenHistory = historyRuns.length - visibleHistory.length;
                  const topCategories = Object.entries(info.isOrder ? info.vendorTally : info.catTally)
                    .sort((a, b) => b[1] - a[1]).slice(0, 6);
                  const secondaryCountLabel = info.isOrder
                    ? `${info.vendorCount} vendor${info.vendorCount === 1 ? '' : 's'}`
                    : `${info.categoryCount} categor${info.categoryCount === 1 ? 'y' : 'ies'}`;

                  const runGlyph = (session) => {
                    const c = Number(session.items_counted) || 0;
                    const t = (Number(session.total_items) || 0) || info.groupTotal;
                    if (session.status === 'completed') {
                      const full = t === 0 || c >= t;
                      return full
                        ? <span style={{ color: OV_GOOD_DOT, fontWeight: 800 }}>✓</span>
                        : <span style={{ color: OV_ATTN_DOT, fontWeight: 800 }}>◐</span>;
                    }
                    if (session.status === 'in_progress' || session.status === 'paused') {
                      return <span style={{ color: OV_ATTN_DOT, fontWeight: 800 }}>●</span>;
                    }
                    return <span style={{ color: '#C7B8AC', fontWeight: 800 }}>○</span>;
                  };

                  const runPhrase = (session) => {
                    const c = Number(session.items_counted) || 0;
                    const t = (Number(session.total_items) || 0) || info.groupTotal;
                    const who = session.created_by_name || '';
                    if (session.status === 'completed') {
                      const full = t === 0 || c >= t;
                      return full
                        ? `Completed${who ? ` by ${who}` : ''} · ${t} items`
                        : `Closed early — ${c} of ${t} counted${who ? ` · ${who}` : ''}`;
                    }
                    if (session.status === 'in_progress' || session.status === 'paused') {
                      return `In progress — ${c} of ${t}${who ? ` · ${who}` : ''}`;
                    }
                    return c > 0
                      ? `Started — ${c} of ${t} counted`
                      : `Not started yet${who ? ` · created by ${who}` : ''}`;
                  };

                  const metaCell = (label, value) => (
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.62rem', color: '#A89890', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>{label}</div>
                      <div style={{ fontSize: '0.82rem', color: OV_INK, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
                    </div>
                  );
                  const createdSource = template || historyRuns[historyRuns.length - 1];
                  const editedSource = historyRuns[0] || template;

                  return (
                    <div style={{ maxWidth: isMobileOverview ? '100%' : '680px', padding: isMobileOverview ? '0 2px' : 0 }}>
                      <button
                        type="button"
                        onClick={closeTemplateDetail}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', minHeight: '44px', background: 'none', border: 'none', color: OV_ACTION, fontWeight: 700, fontSize: '0.95rem', padding: '6px 4px', cursor: 'pointer', fontFamily: 'inherit' }}
                      >
                        ‹ Back to counts
                      </button>

                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '2px 2px 0' }}>
                        <span aria-hidden="true" style={{ fontSize: '1.8rem', lineHeight: 1.15, flexShrink: 0 }}>{emoji}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: '1.22rem', color: OV_INK, lineHeight: 1.25 }}>{info.seriesName}</div>
                          <div style={{ fontSize: '0.85rem', color: OV_INK_SOFT, marginTop: '4px' }}>
                            {[info.cycleLbl || 'No schedule', `${info.itemCount} items`, secondaryCountLabel].join(' · ')}
                          </div>
                        </div>
                      </div>

                      {info.updatedSinceLatest && (
                        <div style={{ margin: '12px 2px 0', backgroundColor: '#FDF3DF', border: '1px solid #F1DFB4', color: '#8A5A00', borderRadius: '10px', padding: '9px 12px', fontSize: '0.82rem', fontWeight: 600 }}>
                          ✏️ Template updated since the last {noun} — the next {noun} will use the new items.
                        </div>
                      )}

                      {/* An order template with no PARs yet gets one quiet line
                          explaining what PARs are for — not a setup checklist, and
                          nothing to dismiss. They can be entered from the PAR badge
                          on any item while counting. */}
                      {info.isOrder && info.parsMissing && (
                        <div style={{ margin: '12px 2px 0', fontSize: '0.8rem', color: OV_INK_SOFT, lineHeight: 1.45 }}>
                          No PAR levels yet — PAR is the amount you want to keep on hand, and
                          Countertop subtracts what you count from it to work out what you need.
                          You can set them as you count, or from Schedule &amp; PARs above.
                        </div>
                      )}

                      {/* Current run hero, or the start button when idle */}
                      {info.currentRun ? (
                        <div style={{
                          backgroundColor: OV_ACTION, borderRadius: '16px', color: '#FFF6F1',
                          padding: '14px 16px 16px', margin: '14px 2px 0',
                        }}>
                          <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', opacity: 0.85 }}>
                            {info.currentRunStarted ? `${info.isOrder ? 'Order' : 'Count'} in progress` : `${info.isOrder ? 'Order' : 'Count'} ready to start`}
                          </div>
                          {(() => {
                            const c = Number(info.currentRun.items_counted) || 0;
                            const t = (Number(info.currentRun.total_items) || 0) || info.groupTotal;
                            const pct = t > 0 ? Math.min(100, Math.round((c / t) * 100)) : 0;
                            const who = info.currentRun.created_by_name || '';
                            return (
                              <>
                                <div style={{ fontSize: '1.1rem', fontWeight: 800, margin: '3px 0 2px' }}>{c} of {t} counted</div>
                                <div style={{ fontSize: '0.82rem', opacity: 0.92 }}>
                                  {who ? `${who} started ` : 'Started '}{formatRelativeDate(info.currentRun.created_at)}{formatTimeOnly(info.currentRun.created_at) ? ` at ${formatTimeOnly(info.currentRun.created_at)}` : ''}
                                </div>
                                <div style={{ height: '6px', backgroundColor: 'rgba(255,255,255,0.28)', borderRadius: '999px', margin: '10px 0 12px', overflow: 'hidden' }}>
                                  <div style={{ width: `${pct}%`, height: '100%', backgroundColor: '#FFFFFF', borderRadius: '999px' }} />
                                </div>
                              </>
                            );
                          })()}
                          <button
                            type="button"
                            onClick={() => primary.onClick()}
                            style={{ ...ovPrimaryBtnStyle(), backgroundColor: '#FFFFFF', color: OV_ACTION }}
                          >
                            {primary.label}
                          </button>
                          <button
                            type="button"
                            onClick={() => { void startNewRunForTemplate(info); }}
                            style={{ display: 'block', width: '100%', marginTop: '8px', background: 'none', border: 'none', color: '#FFF6F1', opacity: 0.9, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', padding: '6px', fontFamily: 'inherit' }}
                          >
                            Start another {noun} instead
                          </button>
                        </div>
                      ) : (
                        <div style={{ margin: '14px 2px 0' }}>
                          <button type="button" style={ovPrimaryBtnStyle()} onClick={() => primary.onClick()}>
                            {primary.label}
                          </button>
                        </div>
                      )}

                      {/* Edit template — always available, not just during setup */}
                      <div style={{ margin: '10px 2px 0' }}>
                        <button
                          type="button"
                          style={ovQuietBtnStyle()}
                          onClick={() => { void markTemplateOnboardingSeen('interaction'); handleEditTemplate(template); }}
                          title="Edit this template's items, name and settings"
                        >
                          <span className="mm-tplcard__review">Review template<span aria-hidden="true" className="mm-tplcard__review-chev">›</span></span>
                        </button>
                      </div>

                      {/* Archive / Delete — same quiet-button format, red text to signal a destructive action */}
                      <div style={{ margin: '8px 2px 0' }}>
                        <button
                          type="button"
                          style={{ ...ovQuietBtnStyle(), color: '#C62828' }}
                          onClick={() => {
                            setTemplateArchiveConfirmText('');
                            setTemplateLifecycleModal({ template, info });
                          }}
                          title="Archive this template, or delete it if it has no items or history"
                        >
                          🗑️ Archive / Delete
                        </button>
                      </div>

                      {!info.currentRun && (info.nextOccurrence || info.scheduleMissing) && (
                        <div style={{ margin: '12px 4px 0', fontSize: '0.85rem', color: OV_INK_SOFT }}>
                          🗓️ Next {noun}:{' '}
                          {(() => {
                            try {
                              const dateStr = String(info.nextOccurrence || '');
                              const d = dateStr ? new Date(dateStr) : null;
                              if (d && !Number.isNaN(d.getTime())) {
                                return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
                              }
                            } catch (_) { /* fall through to No schedule link */ }
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  void markTemplateOnboardingSeen('interaction');
                                  void openTemplateSetupPicker({
                                    template,
                                    parsMissing: info.parsMissing,
                                    scheduleMissing: true,
                                    isOrder: info.isOrder,
                                    isEOP: info.isEOP,
                                  });
                                }}
                                style={{ 
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  margin: 0,
                                  color: '#2E5E86',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  font: 'inherit',
                                  fontSize: 'inherit',
                                }}
                              >
                                No schedule
                              </button>
                            );
                          })()}
                        </div>
                      )}

                      {info.completedRuns.length > 0 && (
                        <div style={{ margin: '10px 4px 0', fontSize: '0.85rem', color: '#2E7D32', fontWeight: 600 }}>
                          ✓ {info.isOrder ? 'Ordered' : 'Counted'} {info.completedRuns.length} time{info.completedRuns.length === 1 ? '' : 's'}
                        </div>
                      )}

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 14px', margin: '16px 4px 0' }}>
                        {createdSource && metaCell('Created', `${formatDate(createdSource.created_at)}${formatTimeOnly(createdSource.created_at) ? `, ${formatTimeOnly(createdSource.created_at)}` : ''}${createdSource.created_by_name ? ` · ${createdSource.created_by_name}` : ''}`)}
                        {editedSource && metaCell('Last activity', (() => {
                          const lastTs = editedSource.last_accessed_at || editedSource.updated_at || editedSource.created_at;
                          const lastBy = (editedSource.last_accessed_by_name && editedSource.last_accessed_by_name !== 'Not yet')
                            ? editedSource.last_accessed_by_name
                            : editedSource.created_by_name;
                          return `${formatDate(lastTs)}${formatTimeOnly(lastTs) ? `, ${formatTimeOnly(lastTs)}` : ''}${lastBy ? ` · ${lastBy}` : ''}`;
                        })())}
                      </div>

                      {topCategories.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', margin: '12px 4px 0' }}>
                          {topCategories.map(([label, n]) => (
                            <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.76rem', padding: '4px 10px', borderRadius: '999px', backgroundColor: '#F3EDE7', color: '#5A4A42', border: `1px solid ${OV_LINE}` }}>
                              {!info.isOrder && <span style={{ fontSize: '0.9rem', lineHeight: 1 }}>{getCategoryIcon(label)}</span>}
                              <span style={{ fontWeight: 600 }}>{label}</span>
                              <span style={{ color: OV_INK_SOFT }}>{n}</span>
                            </span>
                          ))}
                        </div>
                      )}

                      {info.isOrder && template?.direct_order_mode && (
                        <div style={{ margin: '12px 2px 0' }}>
                          {renderDirectOrderToggle(template, { compact: true })}
                        </div>
                      )}

                      {/* History — simple rows, newest first, like order tracking */}
                      <div style={{ margin: '22px 2px 0' }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', color: OV_INK_SOFT, marginBottom: '2px' }}>
                          History
                        </div>
                        {historyRuns.length === 0 && (
                          <div style={{ fontSize: '0.85rem', color: '#A89890', padding: '10px 2px', fontStyle: 'italic' }}>
                            Nothing here yet — your first {noun} will appear here. 🌳
                          </div>
                        )}
                        {visibleHistory.map(session => {
                          const c = Number(session.items_counted) || 0;
                          const t = (Number(session.total_items) || 0) || info.groupTotal;
                          const ac = (t > 0 && c >= t) || session.status === 'completed';
                          const sc = c > 0 && !ac;
                          const isNewest = info.latestRun && session.id === info.latestRun.id;
                          const isRowOpen = expandedCards.has(session.id);
                          const startedTime = formatTimeOnly(session.created_at);
                          // Two runs on the same date are told apart by their
                          // start time; the "last used" line only appears once
                          // the run was touched again after creation (the
                          // migration backfilled last_accessed_at = created_at).
                          const lastUsedTs = session.last_accessed_at || session.updated_at;
                          const lastUsedTime = lastUsedTs && lastUsedTs !== session.created_at ? formatTimeOnly(lastUsedTs) : '';
                          const lastUsedDate = lastUsedTime ? formatDate(lastUsedTs) : '';
                          const sameDay = lastUsedTime && formatDate(session.created_at) === lastUsedDate;
                          const lastUsedBy = (session.last_accessed_by_name && session.last_accessed_by_name !== 'Not yet')
                            ? session.last_accessed_by_name
                            : '';
                          return (
                            <div key={session.id}>
                              <div
                                onClick={() => expandCard(session.id)}
                                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 2px', borderTop: '1px solid #F0E7DC', cursor: 'pointer', minHeight: '52px' }}
                              >
                                <span style={{ width: '20px', textAlign: 'center', fontSize: '0.95rem', flexShrink: 0 }}>{runGlyph(session)}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 600, fontSize: '0.92rem', color: OV_INK }}>
                                    {historyDateLabel(session.session_date || session.created_at)}{startedTime ? ` · ${startedTime}` : ''}
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: OV_INK_SOFT, marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {runPhrase(session)}
                                  </div>
                                  {lastUsedTime && (
                                    <div style={{ fontSize: '0.74rem', color: '#A89890', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      Last used {sameDay ? lastUsedTime : `${lastUsedDate}, ${lastUsedTime}`}{lastUsedBy ? ` by ${lastUsedBy}` : ''}
                                    </div>
                                  )}
                                </div>
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                  {actionBtn(session, c, t, ac, sc, { showStaleGuard: !isNewest })}
                                  {moreMenu(session, ac, { showStaleGuard: !isNewest })}
                                </div>
                              </div>
                              {session.status === 'completed' && (
                                <div style={{ padding: '0 2px 10px 32px' }}>
                                  <CountCardAvailableReports session={session} onOpenReport={openSessionReport} compact />
                                </div>
                              )}
                              {isRowOpen && (
                                <div style={{ border: `1px solid ${OV_LINE}`, borderRadius: '10px', overflow: 'hidden', margin: '0 0 10px 30px' }}>
                                  {renderExpandedDetails(session, { showStaleGuard: !isNewest })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {hiddenHistory > 0 && (
                          <button
                            type="button"
                            onClick={() => toggleStackChildrenExpansion(groupId)}
                            style={{ margin: '10px 0 0', border: `1px solid ${OV_LINE}`, backgroundColor: OV_CARD_BG, borderRadius: '999px', padding: '9px 16px', fontSize: '0.82rem', fontWeight: 700, color: OV_INK_SOFT, cursor: 'pointer', fontFamily: 'inherit', minHeight: '40px' }}
                          >
                            Show {hiddenHistory} earlier {info.isOrder ? (hiddenHistory === 1 ? 'order' : 'orders') : (hiddenHistory === 1 ? 'count' : 'counts')}
                          </button>
                        )}
                        {showAllHistory && historyRuns.length > HISTORY_DEFAULT && (
                          <button
                            type="button"
                            onClick={() => toggleStackChildrenExpansion(groupId)}
                            style={{ margin: '10px 0 0', border: `1px solid ${OV_LINE}`, backgroundColor: OV_CARD_BG, borderRadius: '999px', padding: '9px 16px', fontSize: '0.82rem', fontWeight: 700, color: OV_INK_SOFT, cursor: 'pointer', fontFamily: 'inherit', minHeight: '40px' }}
                          >
                            Show fewer
                          </button>
                        )}
                      </div>
                    </div>
                  );
                };

                // Classify a group into a section based on representative session flags.
                // Prefer the Template row (carries the recurring definition) then newest child.
                const getGroupSection = (groupId) => {
                  const g = groupedSessions[groupId] || {};
                  const rep = g.template || (g.children && g.children[0]) || {};
                  if (rep.order_calculations_enabled === true) return 'orders';
                  const cp = rep.count_purpose || '';
                  const im = rep.inventory_mode || '';
                  // An ordering-purpose template is an order even if order_calculations_enabled
                  // hasn't been flipped on yet. Keep this in sync with isEopTemplate()
                  // (prepCardHelpers.js / setup-progress) and detectFormat (templates-convert),
                  // so the setup review never disagrees with this section grouping.
                  if (cp === 'ordering') return 'orders';
                  if (cp === 'period_end' || cp === 'full_inventory' || im === 'Financial' || im === 'Both') return 'end-of-period';
                  return 'other';
                };

                // Sections sort by max(last_accessed_at) across children (templates don't have real activity)
                const groupMaxAccessed = (groupId) => {
                  const g = groupedSessions[groupId] || {};
                  const members = g.children || [];
                  if (members.length === 0) return 0;
                  return Math.max(...members.map(s => new Date(s.last_accessed_at || s.updated_at || s.created_at).getTime()));
                };
                const sectionSortedGroupIds = [...groupIds].sort((a, b) => groupMaxAccessed(b) - groupMaxAccessed(a));

                // Filter chip predicate. "All" and "Latest" keep every group
                // (Latest only changes the per-stack presentation). "Drafts"
                // filters by the status of the group's children.
                const groupMatchesFilter = (groupId) => {
                  if (overviewFilter === 'all' || overviewFilter === 'latest') return true;
                  const g = groupedSessions[groupId] || {};
                  const children = g.children || [];
                  if (overviewFilter === 'drafts') {
                    return children.some(c => c.status === 'draft' && !((Number(c.items_counted) || 0) > 0));
                  }
                  return true;
                };
                const filteredGroupIds = sectionSortedGroupIds.filter(groupMatchesFilter);

                // 'other' key intentionally stashed — "Other Counting" section hidden for a future version
                const sectionGroupIds = {
                  'end-of-period': filteredGroupIds.filter(id => getGroupSection(id) === 'end-of-period'),
                  'orders': filteredGroupIds.filter(id => getGroupSection(id) === 'orders'),
                };

                const sectionConfig = [
                  { key: 'end-of-period', label: '📅 End of Period', anchor: 'section-end-of-period', type: 'end-of-period', color: EOP_TITLE_COLOR,
                    helpTopicId: 'template.eop_filter', newHelpTopicId: 'template.create_eop' },
                  { key: 'orders', label: '🚚 Ordering', anchor: 'section-orders', type: 'order', color: ORDER_TITLE_COLOR,
                    helpTopicId: 'template.ordering_filter', newHelpTopicId: 'template.create_ordering' },
                  // { key: 'other', ... } intentionally stashed — "Other Counting" section hidden for a future version
                ];

                const sectionHeaderStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '8px 0 6px', marginBottom: '10px', borderBottom: '2px solid #EDE6DE' };
                // Template creation is the rarest action on the screen — a
                // quiet "+ New template" text button instead of a filled pill.
                const newBtnStyle = { fontSize: isMobileOverview ? '0.88rem' : '0.8rem', fontWeight: 700, padding: '10px 10px', borderRadius: '10px', border: 'none', backgroundColor: 'transparent', color: OV_ACTION, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'inherit' };

                const DOW_NAMES_DT = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                const DOW_SHORT_DT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                const todayDowDt = new Date().getDay();

                const renderGroupOrSingle = (groupId) => {
                  const { template, children } = groupedSessions[groupId] || { template: null, children: [] };
                  const kids = children || [];
                  if (kids.length === 0 && !template) return null;
                  const forceLatestOnly = overviewFilter === 'latest';
                  // Template groups get the calm summary card (details and
                  // history expand inline; the full detail screen stays one
                  // tap away via "View full history").
                  if (template) return renderTemplateSummaryCard(groupId, kids, template);
                  // Legacy: multiple children with no template row → stacked card.
                  if (kids.length > 1) return renderGroupCard(groupId, kids, null, { forceLatestOnly });
                  // Legacy: single child, no template (count_group_id is null → lazy-create was skipped).
                  return kids.map(session => renderSingleCard(session));
                };

                const renderNonOrderSection = ({ key, label, anchor, type, color, helpTopicId, newHelpTopicId }) => {
                  const ids = sectionGroupIds[key];
                  return (
                    <div key={key} id={anchor} style={{ marginBottom: '20px' }}>
                      <div style={sectionHeaderStyle}>
                        <HelpTooltip helpTopicId={helpTopicId} section="template_filters">
                          <span style={{ fontWeight: 700, fontSize: isMobileOverview ? '1.05rem' : '0.95rem', color, flexShrink: 0 }}>{label} <span style={{ fontWeight: 400, fontSize: isMobileOverview ? '0.9rem' : '0.78rem', color: '#8B7B73' }}>({ids.length})</span></span>
                        </HelpTooltip>
                        <HelpTooltip helpTopicId={newHelpTopicId} section="template_filters">
                          <button style={newBtnStyle} onClick={() => { void markTemplateOnboardingSeen('interaction'); window.location.href = `/count.html?view=count-creation&type=${type}`; }}>+ New Count Template</button>
                        </HelpTooltip>
                      </div>
                      {ids.length === 0 ? (
                        <div style={{ fontSize: '0.85rem', color: '#A89890', padding: '10px 0 4px' }}>Nothing to count yet — tap “+ New template” to create your first count. 🌳</div>
                      ) : (
                        <>
                          {ids.map(gId => renderGroupOrSingle(gId))}
                        </>
                      )}
                    </div>
                  );
                };

                const renderOrdersSection = () => {
                  const orderIds = sectionGroupIds['orders'];
                  const ordersByDow = {};
                  orderIds.forEach(gId => {
                    const g = groupedSessions[gId] || {};
                    // Bucket by the template's schedule when one is set — a
                    // Monday order created on a Sunday belongs under Monday.
                    // Fall back to the newest child's scheduled date (the
                    // Template placeholder date is not meaningful).
                    const rep = (g.children && g.children[0]) || g.template || {};
                    const schedDow = nextScheduleDow(g.template?.ordering_cycle_config || rep.ordering_cycle_config || null, todayDowDt);
                    const d = rep.session_date ? new Date(rep.session_date + 'T00:00:00') : new Date(rep.created_at);
                    const dow = schedDow != null ? schedDow : d.getDay();
                    if (!ordersByDow[dow]) ordersByDow[dow] = [];
                    ordersByDow[dow].push(gId);
                  });
                  const ordDays = [];
                  for (let i = 0; i < 7; i++) {
                    const dayIdx = (todayDowDt + i) % 7;
                    if (ordersByDow[dayIdx] && ordersByDow[dayIdx].length > 0) {
                      ordDays.push({ dayIdx, label: dayIdx === todayDowDt ? `Today (${DOW_SHORT_DT[dayIdx]})` : DOW_NAMES_DT[dayIdx], groupIds: ordersByDow[dayIdx] });
                    }
                  }
                  return (
                    <div key="orders" id="section-orders" style={{ marginBottom: '20px' }}>
                      <div style={sectionHeaderStyle}>
                        <HelpTooltip helpTopicId={sectionConfig[1].helpTopicId} section="template_filters">
                          <span style={{ fontWeight: 700, fontSize: isMobileOverview ? '1.05rem' : '0.95rem', color: ORDER_TITLE_COLOR, flexShrink: 0 }}>🚚 Ordering <span style={{ fontWeight: 400, fontSize: isMobileOverview ? '0.9rem' : '0.78rem', color: '#8B7B73' }}>({orderIds.length})</span></span>
                        </HelpTooltip>
                        <HelpTooltip helpTopicId={sectionConfig[1].newHelpTopicId} section="template_filters">
                          <button style={newBtnStyle} onClick={() => { void markTemplateOnboardingSeen('interaction'); window.location.href = `/count.html?view=count-creation&type=order`; }}>+ New Order Template</button>
                        </HelpTooltip>
                      </div>
                      {orderIds.length === 0 ? (
                        <div style={{ fontSize: '0.85rem', color: '#A89890', padding: '10px 0 4px' }}>No order templates yet — tap “+ New template” to create one. 🚚</div>
                      ) : ordDays.map(({ dayIdx, label: dayLabel, groupIds: dayGIds }) => (
                        <div key={dayIdx} style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#8B7B73', padding: '6px 2px 3px', ...(isMobileOverview ? {} : { textTransform: 'uppercase', letterSpacing: '0.04em' }),
                            borderBottom: '1px solid #E8DDD0', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {isMobileOverview
                              ? `${dayIdx === todayDowDt ? 'Today' : DOW_NAMES_DT[dayIdx]} (${dayGIds.length})`
                              : <>{dayLabel}<span style={{ fontSize: '0.6rem', fontWeight: 600, padding: '1px 6px', borderRadius: '8px', backgroundColor: '#F5F0EB', color: '#8B7B73' }}>{dayGIds.length}</span></>}
                          </div>
                          {dayGIds.map(gId => renderGroupOrSingle(gId))}
                        </div>
                      ))}
                    </div>
                  );
                };

                // Template detail screen replaces the list while open.
                if (detailGroupId != null) {
                  const detailGroup = groupedSessions[detailGroupId];
                  if (detailGroup && detailGroup.template) {
                    return renderTemplateDetailScreen(detailGroupId, detailGroup.children || [], detailGroup.template);
                  }
                  // Group vanished (filters / reload) — fall through to the list.
                }

                const greetingHour = new Date().getHours();
                const greeting = greetingHour < 12 ? 'Good morning' : greetingHour < 17 ? 'Good afternoon' : 'Good evening';

                return (
                  <>
                    <div style={{ fontSize: '0.92rem', color: '#8B7B73', margin: '2px 2px 12px' }}>
                      {greeting} 👋 — ready when you are.
                    </div>
                    {renderRecentActivity()}
                    {!tplOnboardingAcked && (
                      <div style={{ fontSize: '0.94rem', backgroundColor: '#FFF8F0', border: '1px solid #F5CBA7', borderRadius: '8px', padding: isMobileOverview ? '10px 12px' : '14px 18px', marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.94rem', color: '#6B4F3A' }}>We created starter templates from your items.</div>
                        <div style={{ color: '#5A4A42', lineHeight: 1.5 }}>
                          Use Count Templates for inventory value and end-of-period counts. Use Ordering Templates when you want Countertop to suggest vendor orders from PAR levels.
                        </div>
                        <button
                          style={{ alignSelf: 'flex-end', fontSize: '0.7rem', fontWeight: 600, padding: '3px 10px', borderRadius: '6px', border: '1px solid #D9CEC2', backgroundColor: '#fff', color: '#8B7B73', cursor: 'pointer' }}
                          onClick={() => { void markTemplateOnboardingSeen('dismissed'); }}>
                          Got it
                        </button>
                      </div>
                    )}
                    {renderNonOrderSection(sectionConfig[0])}
                    {renderOrdersSection()}
                    {/* renderNonOrderSection for "Other Counting" intentionally stashed — hidden for a future version */}
                  </>
                );
              })()}
            </div>
            </>
          )}

          {/* Archive & Trash links — held back with the list so the loading
              state is the shimmer alone, not a shimmer with two stray buttons
              parked under it. */}
          {sessionsLoaded && detailGroupId == null && (
          <div className="mt-3 d-flex gap-2 flex-wrap">
            <a
              href="/session-archive.html"
              className="btn btn-sm"
              style={{
                backgroundColor: '#2a2a4a',
                borderColor: '#3a3a5e',
                color: '#8a8aae',
                fontSize: '0.78rem',
                fontWeight: '600'
              }}
            >
              📦 Session Archive
            </a>
            <a
              href="/session-trash.html"
              className="btn btn-sm"
              style={{
                backgroundColor: '#e0e0e0',
                borderColor: '#ccc',
                color: '#555',
                fontSize: '0.78rem',
                fontWeight: '600'
              }}
            >
              🗑️ Trash
            </a>
          </div>
          )}
        </div>
      </div>

      {showEditItemModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(61,37,24,0.45)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px',
          }}
          onClick={() => !editItemSaving && setShowEditItemModal(false)}
        >
          <div
            style={{
              background: '#FEFCFA',
              borderRadius: '16px',
              padding: '1.1rem 1.1rem 1rem',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 14px 34px rgba(61,37,24,0.25)',
              border: '1px solid #D4C4B0',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #E8DDD0' }}>
              <h5 style={{ margin: 0, color: '#5A4A42', fontWeight: 800, fontSize: '1.08rem' }}>Edit count item details</h5>
              <button
                type="button"
                disabled={editItemSaving}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#8B7B73', lineHeight: 1 }}
                onClick={() => setShowEditItemModal(false)}
              >
                &times;
              </button>
            </div>
            <p style={{ fontSize: '0.78rem', color: '#8B7B73', marginBottom: '14px' }}>
              Session edits stay on this count by default and won't change recurring defaults.
            </p>
            <div
              style={{
                background: '#FFF9E6',
                border: '1px solid #F0D8A8',
                borderRadius: '10px',
                padding: '10px 12px',
                marginBottom: '12px',
              }}
            >
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#8a6d3b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                Pricing &amp; units
              </div>
              <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>Unit price (each)</label>
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                className="form-control form-control-sm mb-2"
                value={editItemDraft.unit_price}
                onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) setEditItemDraft((d) => ({ ...d, unit_price: e.target.value })); }}
              />
              <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>Case price</label>
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                className="form-control form-control-sm mb-2"
                value={editItemDraft.case_price}
                onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) setEditItemDraft((d) => ({ ...d, case_price: e.target.value })); }}
              />
              <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>Unit of measure</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={editItemDraft.unit}
                onChange={(e) => setEditItemDraft((d) => ({ ...d, unit: e.target.value }))}
                placeholder="each, case, lb…"
              />
            </div>
            <div style={{ background: '#F1F8EC', border: '1px solid #C8DDB4', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#3F6B26', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                PAR
              </div>
              <p style={{ margin: '0 0 8px', color: '#4F7035', fontSize: '0.74rem' }}>
                PAR applies to this item across every location in this count. Needs for already-counted rows update right away.
              </p>
              <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>PAR</label>
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                className="form-control form-control-sm"
                value={editItemDraft.par_level}
                onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) setEditItemDraft((d) => ({ ...d, par_level: e.target.value })); }}
              />
            </div>
            <div style={{ background: '#F5F8FF', border: '1px solid #D2DDF8', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#34538A', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                Identity fields
              </div>
              <p style={{ margin: 0, color: '#48628F', fontSize: '0.75rem' }}>
                If you change <strong>name, vendor, or unit</strong>, we will ask whether to create a new item for this count or permanently update the existing item across open orders and EOMs.
              </p>
            </div>
            <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>Description</label>
            <input
              type="text"
              className="form-control form-control-sm mb-2"
              value={editItemDraft.description}
              onChange={(e) => setEditItemDraft((d) => ({ ...d, description: e.target.value }))}
            />
            <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>Vendor</label>
            <div className="mb-2">
              <VendorAutocompleteInput
                value={editItemDraft.vendor}
                onChange={(nextValue) => setEditItemDraft((d) => ({ ...d, vendor: nextValue }))}
                storeId={activeStoreId}
                className="form-control form-control-sm"
              />
            </div>
            <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>Category</label>
            <input
              type="text"
              className="form-control form-control-sm mb-2"
              value={editItemDraft.category}
              onChange={(e) => setEditItemDraft((d) => ({ ...d, category: e.target.value }))}
            />
            <label className="form-label small fw-bold mb-1" style={{ color: '#5A4A42' }}>Location (this count)</label>
            <input
              type="text"
              className="form-control form-control-sm mb-3"
              value={editItemDraft.location}
              onChange={(e) => setEditItemDraft((d) => ({ ...d, location: e.target.value }))}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-outline-secondary btn-sm" disabled={editItemSaving} onClick={() => setShowEditItemModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-success btn-sm" disabled={editItemSaving} onClick={() => void saveEditItemFromModal()}>
                {editItemSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Items Modal */}
      {previewSession && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(90,74,66,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          onClick={() => setPreviewSession(null)}>
          <div style={{ background: '#FEFCFA', borderRadius: '14px', maxWidth: '520px', width: '92%', maxHeight: 'calc(75vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.22)', border: '1px solid #E8DDD0', overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}>
            {/* Sticky header */}
            <div style={{ padding: '1rem 1.25rem 0.75rem', borderBottom: '1px solid #E8DDD0', backgroundColor: '#FAF6F2', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: '#5A4A42', letterSpacing: '-0.01em' }}>
                  👁️ {previewSession.name}
                </h3>
                <button style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#8B7B73', lineHeight: 1, padding: '2px 4px', borderRadius: '6px' }}
                  onClick={() => setPreviewSession(null)}>&times;</button>
              </div>
              <div style={{ fontSize: '0.78rem', color: '#8B7B73', marginBottom: '0.6rem' }}>
                {previewSession.total_items || 0} items &middot; {previewSession.items_counted || 0} counted
              </div>
              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                {[['default','Default'],['vendor','Vendor'],['location','Location'],['az','A–Z'],['counted','Counted']].map(([key, label]) => (
                  <button key={key} onClick={() => setPreviewSort(key)}
                    style={{ fontSize: '0.7rem', fontWeight: previewSort === key ? 700 : 500, padding: '3px 10px', borderRadius: '12px', border: `1px solid ${previewSort === key ? '#8B7B73' : '#D9CEC2'}`, backgroundColor: previewSort === key ? '#5A4A42' : '#fff', color: previewSort === key ? '#FEFCFA' : '#5A4A42', cursor: 'pointer', transition: 'all 0.12s', lineHeight: 1.4 }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {/* Scrollable item list */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {previewLoading ? (
                <div style={{ textAlign: 'center', padding: '2.5rem', color: '#8B7B73' }}>Loading items...</div>
              ) : previewItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2.5rem', color: '#A89890' }}>No items in this session</div>
              ) : (
                sortPreviewItems(previewItems, previewSort).map((item, idx, arr) => {
                  const snap = item.item_snapshot || {};
                  const name = snap.description || snap.name || item.description || item.name || 'Unknown';
                  const isCounted = item.is_counted || (item.counted_quantity != null && item.counted_quantity > 0);
                  return (
                    <div key={item.id ?? idx} style={{ display: 'flex', alignItems: 'center', padding: '0.55rem 1.25rem', borderBottom: idx < arr.length - 1 ? '1px solid #F0EBE5' : 'none', fontSize: '0.84rem', color: '#5A4A42', minHeight: '2.4rem', transition: 'background-color 0.1s' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FAF6F2'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}>
                      <span style={{ marginRight: '0.6rem', fontSize: '0.8rem', flexShrink: 0 }}>{isCounted ? '✅' : '⬜'}</span>
                      <span style={{ flex: 1, fontWeight: 500 }}>{name}</span>
                      {renderVendorPurchaseBadge(item, snap, name) || ((snap.vendor || item.vendor) && <span style={{ fontSize: '0.72rem', color: '#8B7B73', marginLeft: '0.5rem', flexShrink: 0 }}>{snap.vendor || item.vendor}</span>)}
                      {renderPreparedBadge(item, snap)}
                      {snap.is_subrecipe === true && !isPreparedSnapshot(snap, item) && <span style={SUB_RECIPE_PILL_STYLE}>Sub-recipe</span>}
                      {snap.brand && <span style={{ fontSize: '0.72rem', color: '#8B7B73', marginLeft: '0.5rem', flexShrink: 0 }}>{snap.brand}</span>}
                      {snap.category && <span style={{ fontSize: '0.68rem', color: '#A89890', marginLeft: '0.4rem', fontStyle: 'italic', flexShrink: 0 }}>[{snap.category}]</span>}
                      {(snap.location || item.location) && <span style={{ fontSize: '0.68rem', color: '#A89890', marginLeft: '0.4rem', flexShrink: 0 }}>({snap.location || item.location})</span>}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      {vendorProductsModal}
        {packageCountSheet}
        {parTransitionModal}
      {packageSwitchModal}
      {appModalConfig && (
        <CustomModal
          isOpen={true}
          onClose={closeAppModal}
          title={appModalConfig.title}
          message={appModalConfig.message}
          type={appModalConfig.type}
          onConfirm={handleAppModalConfirm}
          confirmText={appModalConfig.confirmText}
          cancelText={appModalConfig.cancelText}
          confirmButtonStyle={appModalConfig.confirmButtonStyle}
        />
      )}
      <BackdateCopyModal
        modal={backdateCopyModal}
        onChangeDate={(value) => setBackdateCopyModal((m) => (m ? { ...m, date: value } : m))}
        onCancel={() => setBackdateCopyModal(null)}
        onConfirm={handleBackdateCopyConfirm}
      />
      {endOfRoomModal && (
        <CustomModal
          isOpen={true}
          onClose={() => setEndOfRoomModal(null)}
          title={endOfRoomModal.nextRoom ? 'Location complete' : 'All done!'}
          message={endOfRoomModal.nextRoom
            ? `All items in this location have been counted. Advance to "${endOfRoomModal.nextRoom.label}"?`
            : 'All items in this location have been counted. The session is fully counted!'}
          type={endOfRoomModal.nextRoom ? 'confirm' : 'alert'}
          onConfirm={endOfRoomModal.nextRoom ? handleEndOfRoomAdvance : undefined}
          confirmText="Advance to next room"
          cancelText="Stay in this location"
          confirmButtonStyle="primary"
        />
      )}

      {/* Stack Comparison Report Modal */}
      {stackReportGroupId && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
          paddingLeft: '12px', paddingRight: '12px', overflowY: 'auto'
        }} onClick={closeStackReport}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '760px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)', overflow: 'hidden'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Modal header */}
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #E8DDD0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#FAF6F2' }}>
              <div>
                <span style={{ fontWeight: '700', fontSize: '1rem', color: '#5A4A42' }}>Run Comparison</span>
                {stackReportData && (
                  <span className="badge" style={{ backgroundColor: '#5A4A42', color: '#fff', fontSize: '0.6rem', marginLeft: '8px', padding: '2px 8px' }}>
                    {stackReportData.sessions.length} runs
                  </span>
                )}
              </div>
              <button onClick={closeStackReport} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#8B7B73', lineHeight: 1 }}>✕</button>
            </div>

            {/* Modal body */}
            <div style={{ padding: '16px 18px', overflowX: 'auto' }}>
              {stackReportLoading && (
                <div style={{ textAlign: 'center', padding: '32px', color: '#8B7B73' }}>Loading comparison data…</div>
              )}
              {!stackReportLoading && stackReportData && (() => {
                const { sessions, itemMap } = stackReportData;
                const itemKeys = Object.keys(itemMap).sort((a, b) => {
                  const ia = itemMap[a], ib = itemMap[b];
                  const locCmp = (ia.location || '').localeCompare(ib.location || '');
                  return locCmp !== 0 ? locCmp : (ia.name || '').localeCompare(ib.name || '');
                });
                if (itemKeys.length === 0) {
                  return <div style={{ color: '#8B7B73', textAlign: 'center', padding: '24px' }}>No item data found for these runs.</div>;
                }
                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#F5F1EC' }}>
                        <th style={{ padding: '7px 10px', textAlign: 'left', color: '#5A4A42', fontWeight: '700', borderBottom: '2px solid #E8DDD0', minWidth: '140px' }}>Item</th>
                        {sessions.map(s => (
                          <th key={s.id} style={{ padding: '7px 10px', textAlign: 'center', color: '#5A4A42', fontWeight: '600', borderBottom: '2px solid #E8DDD0', minWidth: '90px', whiteSpace: 'nowrap' }}>
                            {formatDate(s.session_date || s.created_at)}
                            {s.id === sessions[0].id && <div style={{ fontSize: '0.6rem', color: '#8B7B73', fontWeight: '400' }}>newest</div>}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {itemKeys.map((key, rowIdx) => {
                        const item = itemMap[key];
                        const quantities = sessions.map(s => item.rows[s.id]);
                        return (
                          <tr key={key} style={{ backgroundColor: rowIdx % 2 === 0 ? '#fff' : '#FAF6F2' }}>
                            <td style={{ padding: '6px 10px', color: '#5A4A42', borderBottom: '1px solid #F0EBE5' }}>
                              <div style={{ fontWeight: '500' }}>{item.name}</div>
                              {item.location && <div style={{ fontSize: '0.68rem', color: '#8B7B73' }}>{item.location}</div>}
                            </td>
                            {sessions.map((s, sIdx) => {
                              const qty = item.rows[s.id];
                              const prevQty = sIdx < sessions.length - 1 ? item.rows[sessions[sIdx + 1].id] : undefined;
                              const qtyNum = parseFloat(qty);
                              const prevNum = parseFloat(prevQty);
                              const hasChange = !isNaN(qtyNum) && !isNaN(prevNum) && qtyNum !== prevNum;
                              const increased = hasChange && qtyNum > prevNum;
                              return (
                                <td key={s.id} style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #F0EBE5', fontWeight: sIdx === 0 ? '600' : '400', color: qty === undefined ? '#C8B5A0' : hasChange ? (increased ? '#2E7D32' : '#C62828') : '#5A4A42' }}>
                                  {qty !== undefined ? qty : '—'}
                                  {hasChange && <span style={{ fontSize: '0.65rem', marginLeft: '3px' }}>{increased ? '▲' : '▼'}</span>}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    {/* Pars wizard — opens from Start Order when no item has a par yet */}
    {renderParsWizardModal()}
    {/* Template Archive / Delete — from the red button in the template detail view.
        Empty templates (no items, no runs) get a simple delete confirm; templates
        with items or history show their status and require typing "confirm" to archive. */}
    {templateLifecycleModal && (() => {
      const m = templateLifecycleModal;
      const lifecycleInfo = m.info || {};
      const noun = lifecycleInfo.isOrder ? 'order' : 'count';
      const nounPlural = lifecycleInfo.isOrder ? 'orders' : 'counts';
      const itemCount = Number(lifecycleInfo.itemCount) || 0;
      const pastRuns = Array.isArray(lifecycleInfo.completedRuns) ? lifecycleInfo.completedRuns.length : 0;
      const allRuns = Array.isArray(lifecycleInfo.sortedRuns) ? lifecycleInfo.sortedRuns.length : 0;
      const hasRunInProgress = !!lifecycleInfo.currentRun;
      const isEmptyTemplate = itemCount === 0 && allRuns === 0 && !hasRunInProgress;
      const confirmMatched = templateArchiveConfirmText.trim().toLowerCase() === 'confirm';
      const templateName = lifecycleInfo.seriesName || m.template?.name || 'this template';
      const dangerBtn = (enabled) => ({
        fontSize: '0.85rem', fontWeight: 700, padding: '9px 18px', borderRadius: '9px',
        border: '1px solid #B71C1C', backgroundColor: '#C62828', color: '#fff',
        cursor: enabled && !templateLifecycleBusy ? 'pointer' : 'not-allowed',
        opacity: enabled && !templateLifecycleBusy ? 1 : 0.5,
      });
      const cancelBtn = {
        fontSize: '0.85rem', fontWeight: 600, padding: '9px 16px', borderRadius: '9px',
        border: '1px solid #D4C4B0', backgroundColor: '#fff', color: '#5A4A42', cursor: 'pointer',
      };
      return (
        <div onClick={() => { if (!templateLifecycleBusy) closeTemplateLifecycleModal(); }}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)', paddingLeft: '16px', paddingRight: '16px' }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#FDFBF7', borderRadius: '14px', border: '1px solid #D9CEC2', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', minWidth: '300px', maxWidth: '400px', width: '100%', overflow: 'hidden' }}>
            <div style={{ padding: '16px 18px 12px', borderBottom: '1px solid #EDE6DE', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#3E2723' }}>
                {isEmptyTemplate ? `Delete "${templateName}"?` : `Archive "${templateName}"?`}
              </span>
              <button onClick={() => { if (!templateLifecycleBusy) closeTemplateLifecycleModal(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#A89890', fontSize: '1.1rem', lineHeight: 1, padding: '2px 4px' }}>✕</button>
            </div>
            <div style={{ padding: '14px 18px' }}>
              {isEmptyTemplate ? (
                <p style={{ margin: 0, fontSize: '0.86rem', color: '#5A4A42', lineHeight: 1.5 }}>
                  This template has no items and no {noun} history. Deleting is permanent and can't be undone.
                </p>
              ) : (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                    <span style={{ fontSize: '0.76rem', padding: '4px 10px', borderRadius: '999px', backgroundColor: '#F3EDE7', color: '#5A4A42', border: '1px solid #D9CEC2', fontWeight: 600 }}>
                      {itemCount} item{itemCount === 1 ? '' : 's'}
                    </span>
                    <span style={{ fontSize: '0.76rem', padding: '4px 10px', borderRadius: '999px', backgroundColor: '#F3EDE7', color: '#5A4A42', border: '1px solid #D9CEC2', fontWeight: 600 }}>
                      {pastRuns} past {pastRuns === 1 ? noun : nounPlural}
                    </span>
                    {hasRunInProgress && (
                      <span style={{ fontSize: '0.76rem', padding: '4px 10px', borderRadius: '999px', backgroundColor: '#FDF3DF', color: '#8A5A00', border: '1px solid #F1DFB4', fontWeight: 600 }}>
                        1 {noun} in progress
                      </span>
                    )}
                  </div>
                  <p style={{ margin: '0 0 10px', fontSize: '0.86rem', color: '#5A4A42', lineHeight: 1.5 }}>
                    Archiving hides this template from your counts list. Past {nounPlural} are archived with it and stay in your reports — you can recover them from the Archive anytime.
                  </p>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#5A4A42', marginBottom: '4px' }}>
                    Type <strong>confirm</strong> to continue
                  </label>
                  <input
                    type="text"
                    value={templateArchiveConfirmText}
                    onChange={(e) => setTemplateArchiveConfirmText(e.target.value)}
                    placeholder="confirm"
                    autoFocus
                    style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.88rem', padding: '9px 12px', borderRadius: '9px', border: '1px solid #D4C4B0', backgroundColor: '#fff', color: '#3E2723', fontFamily: 'inherit' }}
                  />
                </>
              )}
            </div>
            <div style={{ padding: '0 18px 16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { if (!templateLifecycleBusy) closeTemplateLifecycleModal(); }} style={cancelBtn}>
                Cancel
              </button>
              {isEmptyTemplate ? (
                <button type="button" disabled={templateLifecycleBusy} onClick={() => { void handleTemplateLifecycleDelete(); }} style={dangerBtn(true)}>
                  {templateLifecycleBusy ? 'Deleting…' : 'Delete template'}
                </button>
              ) : (
                <button type="button" disabled={!confirmMatched || templateLifecycleBusy} onClick={() => { void handleTemplateLifecycleArchive(); }} style={dangerBtn(confirmMatched)}>
                  {templateLifecycleBusy ? 'Archiving…' : 'Archive template'}
                </button>
              )}
            </div>
          </div>
        </div>
      );
    })()}
    {/* Setup Picker Modal — opens when the user clicks ⚙ Setup badge */}
    {setupPickerModal && (() => {
      const m = setupPickerModal;
      const scheduleTitle = m.isOrder ? 'When do you place this order?' : 'When do you perform this count?';
      const scheduleIntro = m.isOrder
        ? 'Set a schedule to plan the next run date, or skip to start manually.'
        : 'Set a schedule to plan the next count date, or skip to start manually.';
      const handlePickSchedule = async () => {
        if (typeof window.showScheduleRequiredModal !== 'function') return;
        // Dismiss the picker first so the Bootstrap schedule modal is not hidden behind the React overlay
        const templateSnapshot = m.template;
        setSetupPickerModal(null);
        const chosen = await window.showScheduleRequiredModal(templateSnapshot.ordering_cycle_config || null, {
          includeEopPresets: m.isEOP,
          title: scheduleTitle,
          intro: scheduleIntro,
          noScheduleOption: true,
          noScheduleLabel: 'No schedule — I\'ll start manually',
          setupHeader: m.scheduleMissing
            ? 'This template has no schedule yet. You do not have to set one — completing a count sets it to match the day you counted, and keeps adjusting it as your rhythm changes. Set one here to say it yourself instead.'
            : null,
        });
        if (chosen === null) return;
        try {
          await persistScheduleToTemplate(templateSnapshot.id, chosen);
        } catch (e) {
          console.error('Failed to persist schedule from setup picker:', e);
        }
      };
      const handlePickPars = async () => {
        const templateSnapshot = m.template;
        setSetupPickerModal(null);
        try {
          const templateItems = await fetchTemplateItemsForPars(templateSnapshot.id);
          // Only a template with no pars at all is "first-time setup" — that flag
          // blanks every input and shows the never-set-up banner. Passing it
          // unconditionally hid pars the user had already entered.
          const firstTime = isParsSetupNeeded(templateItems);
          const result = await showParsWizard(templateSnapshot, templateItems, firstTime);
          if (result === 'cancel' || !result) return;
          if (result.type === 'skip') {
            // Skip counts as completing the step: record it so the checklist
            // reads done and the card's Start button reveals.
            try {
              await persistParsSetupSkipped(templateSnapshot.id);
            } catch (se) {
              console.warn('[ParsWizard] Failed to record par skip:', se);
            }
            return;
          }
          await saveParsWizardResult(templateSnapshot.id, result);
        } catch (e) {
          console.error('[ParsWizard] Failed to open/save pars from setup picker:', e);
          showAppAlert({
            title: 'Could not save par levels',
            message: `${e.message || 'Par levels could not be saved.'} Please try again.`,
            confirmButtonStyle: 'warning',
          });
        }
      };
      // "Skip setup" is gone with the gate it existed for. It wrote two
      // sentinels — {preset:'none', skipped:true} and pars_setup_skipped —
      // purely so setupNeeded would clear and the card's button would stop
      // reading "Setup". Nothing blocks a count now, so there is nothing to
      // skip past; Close is the way out. Both sentinels are still READ, so
      // templates that carry one from before keep behaving as they did.
      const rows = [];
      // PARs are not used in "count straight to order" mode, so the par row is
      // hidden once the switch below is on.
      if (m.isOrder && !m.template?.direct_order_mode) {
        rows.push({ key: 'pars', done: !m.parsMissing, iconImg: { src: '/par-levels-icon.png', alt: 'Par and min levels icon' }, title: 'Par Levels', desc: 'Set target quantities so order suggestions are accurate. You can also set these while counting.', action: handlePickPars,
          helpTopicId: 'template.par_moq' });
      }
      rows.push({ key: 'schedule', done: !m.scheduleMissing, icon: '🗓️', title: 'Schedule', desc: m.scheduleMissing ? `${scheduleTitle} Left alone, it follows whenever you complete counts.` : scheduleTitle, action: handlePickSchedule,
        helpTopicId: 'template.schedule' });
      return (
        <div onClick={() => setSetupPickerModal(null)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)', paddingLeft: '16px', paddingRight: '16px' }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#FDFBF7', borderRadius: '14px', border: '1px solid #D9CEC2', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', minWidth: '300px', maxWidth: '400px', width: '100%', overflow: 'visible' }}>
            <div style={{ padding: '16px 18px 12px', borderBottom: '1px solid #EDE6DE', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#3E2723' }}>Template Setup</span>
              <button onClick={() => setSetupPickerModal(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#A89890', fontSize: '1.1rem', lineHeight: 1, padding: '2px 4px' }}>✕</button>
            </div>
            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {rows.map((row) => {
                const isDone = row.done;
                return (
                  <HelpTooltip key={row.key} helpTopicId={row.helpTopicId}
                    context={{ template_id: m.template?.id ?? null, template_type: m.isOrder ? 'ordering' : 'eop' }}
                    section="template_settings">
                  <div
                    onClick={row.action}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '10px',
                      border: `1px solid ${isDone ? '#C8E6C9' : '#D9CEC2'}`,
                      backgroundColor: isDone ? '#F1F8F2' : '#FAF6F2',
                      cursor: 'pointer',
                      opacity: isDone ? 0.75 : 1,
                      transition: 'background-color 0.15s',
                    }}>
                    {row.iconImg
                      ? <img src={row.iconImg.src} alt={row.iconImg.alt} style={{ height: '1.3rem', width: 'auto', flexShrink: 0, display: 'block' }} />
                      : <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>{row.icon}</span>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#3E2723', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {row.title}
                        {/* No "Recommended" pill: an unset step is not a step
                            behind, it is a step the app fills in for you. */}
                        {!isDone && (
                          <span style={{ fontSize: '0.58rem', fontWeight: 700, padding: '1px 6px', borderRadius: '8px', backgroundColor: '#F0EAE3', color: '#8B7B73', border: '1px solid #DCD1C4', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                            Optional
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.76rem', color: '#8B7B73', marginTop: '2px' }}>{row.desc}</div>
                    </div>
                    {isDone
                      ? <span style={{ fontSize: '1rem', color: '#4CAF50', flexShrink: 0 }}>✓</span>
                      : <span style={{ fontSize: '0.8rem', color: '#A89890', flexShrink: 0 }}>›</span>
                    }
                  </div>
                  </HelpTooltip>
                );
              })}
              {/* Count-straight-to-order switch — order templates only. */}
              {m.isOrder && (
                <HelpTooltip helpTopicId="template.direct_order"
                  context={{ template_id: m.template?.id ?? null, template_type: 'ordering', direct_order_enabled: !!m.template?.direct_order_mode }}
                  section="template_settings">
                  {renderDirectOrderToggle(m.template)}
                </HelpTooltip>
              )}
            </div>
            <div style={{ padding: '10px 18px', borderTop: '1px solid #EDE6DE', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setSetupPickerModal(null)}
                style={{ fontSize: '0.82rem', fontWeight: 600, padding: '6px 16px', borderRadius: '8px', border: '1px solid #D4C4B0', backgroundColor: '#fff', color: '#5A4A42', cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    {/* Count-straight-to-order confirmation — shown before turning the switch ON. */}
    {directOrderConfirm && (
      <div onClick={() => setDirectOrderConfirm(null)}
        style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(10,15,25,0.6)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)', paddingLeft: '16px', paddingRight: '16px' }}>
        <div onClick={(e) => e.stopPropagation()}
          style={{ backgroundColor: '#FDFBF7', borderRadius: '16px', border: '1px solid #D9CEC2', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', maxWidth: '360px', width: '100%', overflow: 'hidden', fontFamily: "'Montserrat', sans-serif" }}>
          <div style={{ padding: '20px 20px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <span style={{ fontSize: '1.5rem' }}>🎯</span>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#3E2723' }}>Count straight to order?</span>
            </div>
            <p style={{ margin: 0, fontSize: '0.86rem', color: '#5A4A42', lineHeight: 1.5 }}>
              Your counts become the order amount — you'll enter what you need, not what's on hand. PAR won't be used, and on-hand quantity isn't recorded.
            </p>
          </div>
          <div style={{ padding: '0 20px 18px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setDirectOrderConfirm(null)}
              style={{ fontSize: '0.85rem', fontWeight: 600, padding: '9px 16px', borderRadius: '9px', border: '1px solid #D4C4B0', backgroundColor: '#fff', color: '#5A4A42', cursor: 'pointer' }}>
              Keep PARs
            </button>
            <button type="button" onClick={confirmDirectOrderOn}
              style={{ fontSize: '0.85rem', fontWeight: 700, padding: '9px 18px', borderRadius: '9px', border: '1px solid #6E5A48', backgroundColor: '#8A7868', color: '#fff', cursor: 'pointer' }}>
              Turn on
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Count Start Choice Modal — choose where/how to run the new count (uses CustomModal) */}
    {countStartChoiceModal && (() => {
      const choiceSession = countStartChoiceModal.session;
      const isOrderChoice = !!(choiceSession?.order_calculations_enabled);
      const noun = isOrderChoice ? 'order' : 'count';
      const Noun = isOrderChoice ? 'Order' : 'Count';
      const handleClose = () => setCountStartChoiceModal(null);
      const handleWeb = () => { setCountStartChoiceModal(null); handleCountAgain(choiceSession, 'web'); };
      const handleLater = () => { setCountStartChoiceModal(null); handleCountAgain(choiceSession, 'later'); };
      const handleMobile = () => { setCountStartChoiceModal(null); handleCountAgain(choiceSession, 'mobile'); };
      const optionBtn = (accent, hoverBg, onClick, icon, label, desc, helpTopicId) => (
        // The ⓘ help trigger is corner-pinned OUTSIDE the option button (no
        // nested interactive elements) and opens the shared help tooltip.
        <div style={{ position: 'relative' }}>
          <button
            onClick={onClick}
            style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 14px', borderRadius: '8px', border: `1.5px solid ${accent}`, backgroundColor: '#FDFBF7', cursor: 'pointer', width: '100%', textAlign: 'left', fontFamily: "'Montserrat', sans-serif", marginBottom: '8px' }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = hoverBg; }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#FDFBF7'; }}
          >
            <span style={{ fontSize: '1.4rem', flexShrink: 0, lineHeight: 1 }}>{icon}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'rgb(90, 74, 66)', marginBottom: '2px' }}>{label}</div>
              <div style={{ fontSize: '0.76rem', color: 'rgb(90, 74, 66)', opacity: 0.75, lineHeight: 1.4 }}>{desc}</div>
            </div>
          </button>
          {helpTopicId && (
            <HelpInfoButton helpTopicId={helpTopicId} page="templates" section="start_choice"
              context={{ template_id: choiceSession?.id ?? null, template_type: isOrderChoice ? 'ordering' : 'eop' }}
              style={{ position: 'absolute', top: '6px', right: '6px', zIndex: 1 }} />
          )}
        </div>
      );
      return (
        <CustomModal
          isOpen={true}
          onClose={handleClose}
          title={`How would you like to ${noun}?`}
          type="custom"
          messageAlign="left"
        >
          {optionBtn('#7BB342', '#F1F8F2', handleWeb, '💻', `Open ${Noun} Tool`, `Start ${noun}ing now in the web app. You'll be taken to the ${noun} screen immediately.`, 'countstart.web_tool')}
          {optionBtn('#C8B58A', '#FAF6EE', handleLater, '🕐', `Start Now, ${Noun} Later`, `Create the session and come back to it later. It will appear in your in-progress list.`, 'countstart.start_later')}
          {optionBtn('#7B9ED9', '#EEF3FB', handleMobile, '📱', 'Use Countertop-App Mobile', `Create the session and ${noun} using the mobile app. We'll give you instructions to get started.`, 'countstart.mobile')}
          <button
            onClick={handleClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(168, 152, 144)', fontSize: '0.82rem', fontWeight: 500, display: 'block', margin: '4px auto 0', fontFamily: "'Montserrat', sans-serif" }}
          >
            Cancel
          </button>
        </CustomModal>
      );
    })()}

    {/* Mobile Instructions Panel — shown after session created with mobile mode (uses CustomModal) */}
    {mobileInstructionSession && (() => {
      const handleClose = () => setMobileInstructionSession(null);
      return (
        <CustomModal
          isOpen={true}
          onClose={handleClose}
          title="Ready to count on mobile"
          type="custom"
          messageAlign="left"
        >
          <div style={{ marginBottom: '14px', fontFamily: "'Montserrat', sans-serif" }}>
            <p style={{ margin: '0 0 10px', fontSize: '0.88rem', color: 'rgb(90, 74, 66)', lineHeight: 1.55 }}>
              Your session <strong>"{mobileInstructionSession.name}"</strong> has been created. To continue on your phone:
            </p>
            <ol style={{ margin: '0 0 12px', paddingLeft: '20px', fontSize: '0.84rem', color: 'rgb(90, 74, 66)', lineHeight: 1.7 }}>
              <li>Open <strong>Countertop-App</strong> on your mobile device.</li>
              <li>Make sure you're signed in to the same account.</li>
              <li>Tap the <strong>Counts</strong> tab — your new session will appear at the top under <em>In Progress</em>.</li>
              <li>Tap the session to begin counting.</li>
            </ol>
            <p style={{ margin: '0', fontSize: '0.78rem', color: 'rgb(90, 74, 66)', opacity: 0.7, lineHeight: 1.4 }}>
              Don't have the app yet? Search for <strong>Countertop-App</strong> in the App Store or Google Play.
            </p>
          </div>
          <button
            onClick={handleClose}
            style={{ backgroundColor: 'rgb(200, 181, 160)', border: '1px solid rgb(200, 181, 160)', color: 'white', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', minWidth: '80px', fontFamily: "'Montserrat', sans-serif" }}
            onMouseOver={(e) => { e.currentTarget.style.opacity = '0.9'; }}
            onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            Got it
          </button>
        </CustomModal>
      );
    })()}
    </div>
  );
};

export default CountInterface;
